import { useState, useEffect, useRef, Component, Fragment } from 'react';
import './App.css';
import supabase from './supabase';

const API = 'https://nomos-production.up.railway.app';

// Module-level token store — immune to React closure staleness
let _nomosToken = null;

// Predictive slip detection — pure function, no React state
function getGatePace(gs, allGates, program) {
  if (!gs || gs.status === 'complete' || gs.status === 'idle') return null;

  const today = new Date();
  const totalGates = 6;

  // Resolve gate close date: use planned_close_date if set, else estimate from program timeline
  let closeDate;
  if (gs.planned_close_date) {
    closeDate = new Date(gs.planned_close_date);
  } else if (program?.program_start_date && program?.launch_date) {
    const start = new Date(program.program_start_date);
    const launch = new Date(program.launch_date);
    const totalMs = launch - start;
    closeDate = new Date(start.getTime() + (gs.gate_number / totalGates) * totalMs);
  } else {
    return null;
  }

  // Resolve gate start date
  let gateStart;
  if (gs.gate_number === 1) {
    gateStart = program?.program_start_date ? new Date(program.program_start_date) : null;
  } else if (gs.planned_close_date) {
    const prev = (allGates || []).find(g => g.gate_number === gs.gate_number - 1 && g.planned_close_date);
    gateStart = prev?.planned_close_date ? new Date(prev.planned_close_date) : null;
  }
  // Fallback: estimate from program timeline
  if (!gateStart && program?.program_start_date && program?.launch_date) {
    const start = new Date(program.program_start_date);
    const launch = new Date(program.launch_date);
    const totalMs = launch - start;
    gateStart = new Date(start.getTime() + ((gs.gate_number - 1) / totalGates) * totalMs);
  }
  if (!gateStart) return null;

  const daysRemaining = Math.round((closeDate - today) / 86400000);
  if (daysRemaining < 0 && gs.completion_percent < 100) {
    return { status: 'overdue', daysLate: Math.abs(daysRemaining) };
  }

  const totalDays = Math.max(1, Math.round((closeDate - gateStart) / 86400000));
  const daysElapsed = Math.max(0, Math.round((today - gateStart) / 86400000));
  if (daysElapsed <= 0) return null;

  const expectedPct = Math.min(100, (daysElapsed / totalDays) * 100);
  const gap = expectedPct - gs.completion_percent;

  if (gap > 20) {
    const projectedDays = gs.completion_percent > 0 ? Math.round(daysElapsed / (gs.completion_percent / 100)) : null;
    const projectedDate = projectedDays ? new Date(gateStart.getTime() + projectedDays * 86400000) : null;
    const daysLate = projectedDate ? Math.round((projectedDate - closeDate) / 86400000) : null;
    return { status: 'behind', gap: Math.round(gap), projectedDate, daysLate };
  }

  return { status: 'on_pace' };
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Nomos render error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ backgroundColor: '#0a0a0a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto Mono', monospace", flexDirection: 'column', gap: '20px', padding: '40px' }}>
          <div style={{ color: '#ef4444', fontSize: '10px', letterSpacing: '3px' }}>SYSTEM ERROR</div>
          <div style={{ color: '#ffffff', fontSize: '18px', fontWeight: '700' }}>Something went wrong</div>
          <div style={{ color: '#888', fontSize: '11px', maxWidth: '400px', textAlign: 'center', lineHeight: 1.6 }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </div>
          <button onClick={() => window.location.reload()} style={{ backgroundColor: '#22d3ee', border: 'none', color: '#000', padding: '12px 28px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px' }}>
            RELOAD APP
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Module-level so React never sees a new component reference on re-render
function ThemeToggle({ darkMode, onToggle, style }) {
  return (
    <button style={style} onClick={onToggle}>
      {darkMode ? 'LIGHT' : 'DARK'}
    </button>
  );
}

function MicBtn({ fieldId, value, onSet, activeFieldId, onStart, dimColor, subColor }) {
  const active = activeFieldId === fieldId;
  return (
    <button
      onClick={() => onStart(fieldId, value, onSet)}
      title={active ? 'Stop listening' : 'Voice input'}
      style={{ background: 'none', border: `1px solid ${active ? '#ef4444' : dimColor}`, borderRadius: '4px', cursor: 'pointer', padding: '2px 7px', fontSize: '11px', color: active ? '#ef4444' : subColor, lineHeight: 1.4, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {active ? '⏹' : '🎙'}
    </button>
  );
}

function MicBtnWide({ fieldId, value, onSet, activeFieldId, onStart, surfaceAlt, border, dim }) {
  const active = activeFieldId === fieldId;
  return (
    <button onClick={() => onStart(fieldId, value, onSet)} style={{
      background: active ? '#ef4444' : surfaceAlt, border: `1px solid ${active ? '#ef4444' : border}`,
      borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
      fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '1px', color: active ? '#fff' : dim,
      boxShadow: active ? '0 0 10px #ef444430' : 'none', flexShrink: 0,
    }}>
      <span style={{ fontSize: '12px' }}>{active ? '⏹' : '🎙'}</span>
      <span>{active ? 'STOP' : 'SPEAK'}</span>
      {active && <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#fff', display: 'inline-block' }} />}
    </button>
  );
}

function NotificationBell({ notifications, open, onToggle, darkMode }) {
  const unread = notifications.filter(n => !n.read).length;
  const t = darkMode
    ? { bg: '#1a1a2e', border: '#334155', text: '#e2e8f0', sub: '#64748b', surface: '#0f172a', hover: '#1e293b' }
    : { bg: '#ffffff', border: '#e2e8f0', text: '#1e293b', sub: '#64748b', surface: '#f8fafc', hover: '#f1f5f9' };
  const eventColor = { BLOCKED: '#ef4444', COMPLETE: '#10b981', AT_RISK: '#f59e0b', CASCADE: '#8b5cf6' };

  return (
    <div data-notif-root style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        style={{ background: 'none', border: `1px solid ${open ? '#6366f1' : 'transparent'}`, borderRadius: '6px', cursor: 'pointer', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', color: darkMode ? '#94a3b8' : '#475569' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: '2px', right: '2px', width: '14px', height: '14px', backgroundColor: '#ef4444', borderRadius: '50%', fontSize: '8px', fontWeight: 'bold', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto Mono', monospace" }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: '340px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.18)', zIndex: 500, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px 10px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '10px', letterSpacing: '2px', fontWeight: 'bold', color: t.text, fontFamily: "'Roboto Mono', monospace" }}>NOTIFICATIONS</span>
            {unread > 0 && <span style={{ fontSize: '9px', color: '#6366f1', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>{unread} UNREAD</span>}
          </div>
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 18px', textAlign: 'center', color: t.sub, fontSize: '11px', fontFamily: "'Roboto Mono', monospace" }}>No notifications yet</div>
            ) : notifications.slice(0, 30).map(n => (
              <div key={n.id} style={{ padding: '12px 18px', borderBottom: `1px solid ${t.border}`, backgroundColor: n.read ? 'transparent' : (darkMode ? '#1e1b4b18' : '#eef2ff60'), cursor: 'default' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: eventColor[n.event_type] || '#64748b', marginTop: '5px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                      <span style={{ fontSize: '9px', letterSpacing: '1.5px', fontWeight: 'bold', color: eventColor[n.event_type] || t.sub, fontFamily: "'Roboto Mono', monospace" }}>{n.event_type}</span>
                      <span style={{ fontSize: '9px', color: t.sub, fontFamily: "'Roboto Mono', monospace", flexShrink: 0 }}>G{n.gate_number}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: t.text, marginBottom: '2px', lineHeight: 1.4 }}>{n.message}</div>
                    <div style={{ fontSize: '9px', color: t.sub, fontFamily: "'Roboto Mono', monospace" }}>{n.program_name} · {n.lane?.toUpperCase()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DriTypeahead({ value, onChange, suggestions, darkMode }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const t = darkMode
    ? { bg: '#0d1117', border: '#334155', text: '#e2e8f0', dim: '#64748b', surface: '#1e293b', accent: '#6366f1', hover: '#1e293b' }
    : { bg: '#ffffff', border: '#d1d5db', text: '#111827', dim: '#6b7280', surface: '#f9fafb', accent: '#6366f1', hover: '#f3f4f6' };
  const filtered = suggestions.filter(s => s && s.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => { setQuery(value || ''); }, [value]);

  const select = (name) => {
    setQuery(name);
    setOpen(false);
    onChange(name);
    inputRef.current?.blur();
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={query}
        placeholder="Assign a name or role"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={e => {
          setTimeout(() => {
            setOpen(false);
            if (e.target.value !== value) onChange(e.target.value);
          }, 150);
        }}
        style={{ width: '100%', padding: '6px 8px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '11px', boxSizing: 'border-box', fontFamily: 'inherit' }}
      />
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 9999, maxHeight: '180px', overflowY: 'auto', marginTop: '2px' }}>
          {filtered.map(s => (
            <div
              key={s}
              onMouseDown={() => select(s)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '11px', color: t.text, borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: '8px' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = t.hover}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: t.accent, flexShrink: 0 }}>
                {s.slice(0, 2).toUpperCase()}
              </div>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ShareGroupInput({ teamMembers, selected, onChange, darkMode }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const t = darkMode
    ? { bg: '#0d1117', border: '#334155', text: '#e2e8f0', dim: '#94a3b8', surface: '#1e293b', accent: '#0d9488', hover: '#1e293b', chip: '#1e293b', chipBorder: '#334155' }
    : { bg: '#ffffff', border: '#d1d5db', text: '#111827', dim: '#6b7280', surface: '#f8fafc', accent: '#0d9488', hover: '#f0fdfb', chip: '#f0fdfb', chipBorder: '#99f6e4' };

  const filtered = teamMembers.filter(m => {
    if (!query.trim()) return false;
    const q = query.toLowerCase();
    return (m.full_name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)) && !selected.includes(m.email);
  });

  const add = (email) => {
    if (!email || selected.includes(email)) return;
    onChange([...selected, email]);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const addFromQuery = () => {
    const val = query.trim();
    if (!val) return;
    // Check if it matches a team member name
    const match = teamMembers.find(m => m.full_name?.toLowerCase() === val.toLowerCase());
    if (match?.email) { add(match.email); return; }
    // Treat as raw email
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) add(val);
  };

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
          {selected.map(email => {
            const member = teamMembers.find(m => m.email === email);
            const label = member?.full_name || email;
            return (
              <div key={email} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: t.chip, border: `1px solid ${t.chipBorder}`, borderRadius: '4px', padding: '4px 8px', fontSize: '11px', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>
                <span>{label}</span>
                <button onMouseDown={() => onChange(selected.filter(e => e !== email))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '13px', padding: 0, lineHeight: 1 }}>×</button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder={selected.length === 0 ? 'Type a name to add...' : 'Add another...'}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addFromQuery(); } }}
          autoComplete="off"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: t.bg, border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontSize: '12px', fontFamily: "'Roboto Mono', monospace", outline: 'none', WebkitAppearance: 'none' }}
        />
        {open && filtered.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: t.bg, border: `1px solid ${t.border}`, borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 9999, maxHeight: '200px', overflowY: 'auto', marginTop: '2px' }}>
            {filtered.map(m => (
              <div key={m.email} onMouseDown={() => add(m.email)}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '12px', color: t.text, display: 'flex', alignItems: 'center', gap: '10px', borderBottom: `1px solid ${t.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = t.hover}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: t.accent + '20', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: t.accent, flexShrink: 0 }}>
                  {(m.full_name || m.email).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontFamily: "'Roboto Mono', monospace", fontSize: '11px' }}>{m.full_name || m.email}</div>
                  <div style={{ fontSize: '10px', color: t.dim }}>{m.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ fontSize: '10px', color: t.dim, marginTop: '5px', fontFamily: "'Roboto Mono', monospace" }}>Press Enter or comma to add a raw email address</div>
    </div>
  );
}

function MyProfileModal({ open, onClose, session, authFetch, darkMode, isMobile, initialName, initialTitle, initialAvatar, initialEmail, onSaved }) {
  const [name, setName] = useState(initialName || '');
  const [title, setTitle] = useState(initialTitle || '');
  const [avatarUrl, setAvatarUrl] = useState(initialAvatar || null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setName(initialName || ''); setTitle(initialTitle || ''); setAvatarUrl(initialAvatar || null); }, [initialName, initialTitle, initialAvatar]);

  const t = darkMode
    ? { bg: '#0f1117', surface: '#1a1f2e', border: '#2a3040', text: '#e2e8f0', muted: '#64748b', accent: '#22d3ee', dim: '#475569', input: '#0f1117' }
    : { bg: '#f8fafc', surface: '#ffffff', border: '#e2e8f0', text: '#0f172a', muted: '#64748b', accent: '#0891b2', dim: '#94a3b8', input: '#ffffff' };

  if (!open) return null;

  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const r = await authFetch(`${API}/profile/avatar`, { method: 'POST', body: fd });
      const d = await r.json();
      if (d.avatar_url) { setAvatarUrl(d.avatar_url); onSaved({ avatar_url: d.avatar_url }); }
    } finally { setUploading(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    await authFetch(`${API}/profile`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ full_name: name, title }) });
    onSaved({ full_name: name, title });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const inp = { width: '100%', padding: '10px 12px', backgroundColor: t.input, border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '12px', boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
      <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '420px', fontFamily: "'Roboto Mono', monospace", position: 'relative' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '3px' }}>MY PROFILE</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.muted, fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>

        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `2px solid ${t.accent}40`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '24px', fontWeight: '700', color: t.accent }}>{initials}</span>}
            </div>
            {uploading && <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>...</div>}
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ fontSize: '9px', color: t.accent, background: 'none', border: `1px solid ${t.accent}40`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', letterSpacing: '1px' }}>
            {uploading ? 'UPLOADING...' : 'UPLOAD PHOTO'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadAvatar} />
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '6px' }}>FULL NAME</div>
            <input style={inp} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '6px' }}>TITLE / ROLE</div>
            <input style={inp} placeholder="e.g. Senior Industrial Designer" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '6px' }}>EMAIL</div>
            <div style={{ ...inp, color: t.dim, backgroundColor: t.border + '30', cursor: 'default' }}>{initialEmail || '—'}</div>
          </div>
        </div>

        <button onClick={saveProfile} disabled={saving} style={{ width: '100%', padding: '11px', backgroundColor: saved ? '#10b981' : t.accent, border: 'none', color: '#000', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
          {saved ? '✓ SAVED' : saving ? 'SAVING...' : 'SAVE PROFILE'}
        </button>
      </div>
    </div>
  );
}

function ProfileViewModal({ open, onClose, profile, darkMode, authFetch }) {
  const [workload, setWorkload] = useState(null);
  const [wLoading, setWLoading] = useState(false);
  const API_URL = 'https://nomos-production.up.railway.app';
  const t = darkMode
    ? { bg: '#0f1117', surface: '#1a1f2e', border: '#2a3040', text: '#e2e8f0', muted: '#64748b', accent: '#22d3ee', dim: '#475569', borderDim: '#1e2a3a' }
    : { bg: '#f8fafc', surface: '#ffffff', border: '#e2e8f0', text: '#0f172a', muted: '#64748b', accent: '#0891b2', dim: '#94a3b8', borderDim: '#f1f5f9' };
  const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
  const gateNames = ['Inception', 'Concept Review', 'Eng Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

  useEffect(() => {
    if (!open || !profile?.id || !authFetch) return;
    setWLoading(true); setWorkload(null);
    authFetch(`${API_URL}/users/${profile.id}/workload`)
      .then(r => r.json()).then(d => { if (!d.error) setWorkload(d); })
      .catch(() => {}).finally(() => setWLoading(false));
  }, [open, profile?.id]);

  if (!open || !profile) return null;
  const p = workload?.profile || profile;
  const initials = (p.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const laneColor = laneColorMap[p.lane] || t.accent;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
      <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '85vh', overflowY: 'auto', fontFamily: "'Roboto Mono', monospace" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: laneColor + '20', border: `2px solid ${laneColor}50`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '18px', fontWeight: '700', color: laneColor }}>{initials}</span>}
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>{p.full_name}</div>
              {p.title && <div style={{ fontSize: '11px', color: t.muted, marginTop: '2px' }}>{p.title}</div>}
              <div style={{ display: 'flex', gap: '5px', marginTop: '5px', flexWrap: 'wrap' }}>
                {p.lane && <span style={{ fontSize: '8px', color: laneColor, letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${laneColor}40`, borderRadius: '3px', padding: '2px 5px' }}>{p.lane.toUpperCase()}</span>}
                {p.role === 'admin' && <span style={{ fontSize: '8px', color: t.accent, letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.accent}40`, borderRadius: '3px', padding: '2px 5px' }}>ADMIN</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.muted, fontSize: '18px', cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
        {profile.email && <div style={{ padding: '8px 24px 0' }}><a href={`mailto:${profile.email}`} style={{ fontSize: '11px', color: t.accent, textDecoration: 'none' }}>{profile.email}</a></div>}

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {wLoading ? (
            <div style={{ textAlign: 'center', color: t.muted, fontSize: '11px', padding: '20px 0' }}>Loading workload...</div>
          ) : workload ? (
            <>
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {[
                  { label: 'PROGRAMS', value: workload.programs.length, color: t.text },
                  { label: 'OPEN TASKS', value: workload.openTasks, color: workload.openTasks > 0 ? t.accent : t.muted },
                  { label: 'OVERDUE', value: workload.overdueTasks, color: workload.overdueTasks > 0 ? '#ef4444' : t.muted },
                ].map(s => (
                  <div key={s.label} style={{ backgroundColor: t.bg, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '8px', color: t.muted, letterSpacing: '1px', marginTop: '2px' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Programs */}
              {workload.programs.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '8px' }}>PROGRAMS</div>
                  {workload.programs.map(prog => (
                    <div key={prog.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: t.bg, border: `1px solid ${t.borderDim}`, borderRadius: '6px', marginBottom: '5px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: t.text }}>{prog.name}</div>
                      <div style={{ fontSize: '9px', color: t.muted }}>G{prog.current_gate} · {(gateNames[prog.current_gate - 1] || '').toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Open tasks they own */}
              {workload.driMilestones.filter(m => !m.completed).length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '8px' }}>OPEN TASKS THEY OWN</div>
                  {workload.driMilestones.filter(m => !m.completed).map(m => {
                    const overdue = m.due_date && new Date(m.due_date) < new Date();
                    return (
                      <div key={m.id} style={{ padding: '8px 12px', backgroundColor: t.bg, border: `1px solid ${overdue ? '#ef444440' : t.borderDim}`, borderRadius: '6px', marginBottom: '5px' }}>
                        <div style={{ fontSize: '11px', color: overdue ? '#ef4444' : t.text }}>{m.text}</div>
                        <div style={{ fontSize: '9px', color: t.muted, marginTop: '2px' }}>
                          {m.gate_statuses?.programs?.name} · {m.gate_statuses?.lane} G{m.gate_statuses?.gate_number}
                          {m.due_date && ` · Due ${new Date(m.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          {overdue && <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '700' }}>OVERDUE</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recently approved */}
              {workload.approvedMilestones.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1.5px', marginBottom: '8px' }}>RECENTLY APPROVED</div>
                  {workload.approvedMilestones.slice(0, 5).map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: t.bg, border: `1px solid ${t.borderDim}`, borderRadius: '6px', marginBottom: '5px' }}>
                      <div style={{ fontSize: '11px', color: t.text }}>{m.text}</div>
                      <div style={{ fontSize: '9px', color: '#10b981', flexShrink: 0, marginLeft: '8px' }}>✓ {m.signed_off_at ? new Date(m.signed_off_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: t.muted, fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No workload data yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── FIRST RUN FLOW ─────────────────────────────────────────────────────────────
function FirstRunFlow({ onComplete, darkMode, t, authFetch, isMobile, startVoiceFor, voiceFieldId }) {
  const [step, setStep] = useState(0);
  const [launchName, setLaunchName] = useState('');
  const [launchType, setLaunchType] = useState('physical');
  const [locations, setLocations] = useState('');
  const [description, setDescription] = useState('');
  const [launchDate, setLaunchDate] = useState('');
  const [selectedTeams, setSelectedTeams] = useState(['design', 'finance', 'operations', 'installation', 'engineering']);
  const [output, setOutput] = useState(null);
  const [transitionMsgIdx, setTransitionMsgIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const TEAMS = ['Design', 'Finance', 'Operations', 'Installation', 'Engineering'];
  const TRANSITION_MSGS = [
    'Mapping your launch timeline...',
    'Building your executive summary...',
    'Briefing each team...',
    'Calculating key milestones...',
    'Preparing your living document...',
  ];
  const GATE_COLORS = ['#6366f1', '#8b5cf6', '#0d9488', '#f59e0b', '#ef4444', '#10b981'];
  const GATE_NAMES_SHORT = ['INCEPTION', 'CONCEPT', 'ENG. REVIEW', 'PROTO 1', 'PROTO 2', 'MASS PROD'];

  useEffect(() => {
    if (step !== 4) return;
    let tick = 0;
    const iv = setInterval(() => {
      tick++;
      setTransitionMsgIdx(prev => (prev + 1) % TRANSITION_MSGS.length);
      setProgress(prev => Math.min(prev + 16, 88));
    }, 1800);
    return () => clearInterval(iv);
  }, [step]);

  const toggleTeam = (team) => {
    const lower = team.toLowerCase();
    setSelectedTeams(prev => prev.includes(lower) ? prev.filter(x => x !== lower) : [...prev, lower]);
  };

  const submit = async () => {
    if (selectedTeams.length === 0) { setError('Select at least one team.'); return; }
    setError('');
    setStep(4);
    setProgress(10);
    try {
      const res = await authFetch(`${API}/programs/first-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ launchName: launchName.trim(), launchType, locations, description: description.trim(), launchDate, teams: selectedTeams }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setProgress(100);
      setOutput(data);
      setTimeout(() => setStep(5), 700);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setStep(3);
    }
  };

  const mono = "'Roboto Mono', monospace";
  const accent = t.accent;
  const bg = t.bg;
  const text = t.text;
  const surface = t.surface;
  const border = t.border;
  const dim = t.dim;
  const muted = t.muted;

  const wrap = {
    position: 'fixed', inset: 0, backgroundColor: bg, zIndex: 800,
    display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto',
  };

  const ProgressBar = ({ current }) => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 810, height: '3px', backgroundColor: border }}>
      <div style={{ height: '100%', width: `${(current / 3) * 100}%`, backgroundColor: accent, transition: 'width 0.4s ease' }} />
    </div>
  );

  // STEP 0 — Welcome
  if (step === 0) return (
    <div style={wrap}>
      <style>{`@keyframes nomosFirstRunPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.92)} }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '40px 24px', maxWidth: '520px', width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: isMobile ? '28px' : '44px', letterSpacing: '10px', fontWeight: '900', color: accent, fontFamily: mono, marginBottom: '40px' }}>NOMOS</div>
        <div style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: '700', color: text, lineHeight: 1.3, marginBottom: '16px' }}>Welcome to Nomos.</div>
        <div style={{ fontSize: isMobile ? '14px' : '16px', color: dim, lineHeight: 1.8, marginBottom: '48px', maxWidth: '400px' }}>
          I have 3 questions. Answer them and I will build your live launch plan — executive summary, phase gate timeline, and a team briefing document ready to share.
        </div>
        <button
          onClick={() => setStep(1)}
          style={{ backgroundColor: accent, border: 'none', color: '#000', padding: '16px 56px', fontFamily: mono, fontSize: '12px', letterSpacing: '3px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
          GET STARTED →
        </button>
      </div>
    </div>
  );

  // STEP 1 — What are you launching
  if (step === 1) return (
    <div style={wrap}>
      <ProgressBar current={1} />
      <div style={{ padding: isMobile ? '60px 24px 40px' : '80px 48px 40px', maxWidth: '660px', width: '100%' }}>
        <div style={{ fontSize: '10px', color: accent, letterSpacing: '3px', fontFamily: mono, marginBottom: '24px' }}>QUESTION 1 OF 3</div>
        <div style={{ fontSize: isMobile ? '24px' : '34px', fontWeight: '700', color: text, lineHeight: 1.3, marginBottom: '8px' }}>What are you launching?</div>
        <div style={{ fontSize: '14px', color: dim, marginBottom: '32px', lineHeight: 1.7, maxWidth: '500px' }}>
          Give it a name, tell us how it's going out, then describe it.
        </div>

        {/* Launch Name */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '9px', color: dim, letterSpacing: '2px', fontFamily: mono, marginBottom: '8px' }}>LAUNCH NAME</div>
          <div style={{ position: 'relative' }}>
            <input
              autoFocus
              type="text"
              value={launchName}
              onChange={e => setLaunchName(e.target.value)}
              placeholder="e.g. iPhone 17 Store Launch, Nike Air Max Global"
              style={{ width: '100%', padding: '16px 52px 16px 18px', backgroundColor: surface, border: `1px solid ${border}`, borderRadius: '8px', fontSize: '15px', color: text, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
            />
            <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '10px' }}>
              <MicBtnWide fieldId="fr-name" value={launchName} onSet={setLaunchName} activeFieldId={voiceFieldId} onStart={startVoiceFor} surfaceAlt={darkMode ? '#1e293b' : '#f1f5f9'} border={border} dim={dim} />
            </div>
          </div>
        </div>

        {/* Launch Type toggle */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '9px', color: dim, letterSpacing: '2px', fontFamily: mono, marginBottom: '8px' }}>LAUNCH TYPE</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[['physical', 'PHYSICAL STORES'], ['online', 'ONLINE ONLY'], ['both', 'STORES + ONLINE']].map(([val, label]) => (
              <button key={val} onClick={() => setLaunchType(val)} style={{
                flex: 1, padding: '12px 8px', borderRadius: '6px', cursor: 'pointer', fontFamily: mono, fontSize: '9px', letterSpacing: '1.5px', fontWeight: '700', transition: 'all 0.15s',
                backgroundColor: launchType === val ? accent : surface, border: `2px solid ${launchType === val ? accent : border}`, color: launchType === val ? '#000' : dim,
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Number of locations — only for physical or both */}
        {launchType !== 'online' && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '9px', color: dim, letterSpacing: '2px', fontFamily: mono, marginBottom: '8px' }}>NUMBER OF LOCATIONS <span style={{ color: muted, fontWeight: '400' }}>(OPTIONAL)</span></div>
            <input
              type="number"
              min="1"
              value={locations}
              onChange={e => setLocations(e.target.value)}
              placeholder="e.g. 450"
              style={{ width: '160px', padding: '14px 18px', backgroundColor: surface, border: `1px solid ${border}`, borderRadius: '8px', fontSize: '15px', color: text, fontFamily: mono, boxSizing: 'border-box', outline: 'none' }}
            />
          </div>
        )}

        {/* Description */}
        <div style={{ marginBottom: '0' }}>
          <div style={{ fontSize: '9px', color: dim, letterSpacing: '2px', fontFamily: mono, marginBottom: '8px' }}>DESCRIBE IT</div>
          <div style={{ position: 'relative' }}>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is it, who is it for, and what makes it different?"
              rows={5}
              style={{ width: '100%', padding: '18px', paddingBottom: '52px', backgroundColor: surface, border: `1px solid ${border}`, borderRadius: '8px', fontSize: '14px', color: text, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box', outline: 'none' }}
            />
            <div style={{ position: 'absolute', bottom: '12px', right: '12px' }}>
              <MicBtnWide fieldId="fr-desc" value={description} onSet={setDescription} activeFieldId={voiceFieldId} onStart={startVoiceFor} surfaceAlt={darkMode ? '#1e293b' : '#f1f5f9'} border={border} dim={dim} />
            </div>
          </div>
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', fontFamily: mono }}>{error}</div>}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => { if (!launchName.trim()) { setError('Please enter a launch name.'); return; } if (!description.trim()) { setError('Please describe your launch.'); return; } setError(''); setStep(2); }}
            style={{ backgroundColor: accent, border: 'none', color: '#000', padding: '14px 44px', fontFamily: mono, fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
            NEXT →
          </button>
        </div>
      </div>
    </div>
  );

  // STEP 2 — When does it need to launch
  if (step === 2) return (
    <div style={wrap}>
      <ProgressBar current={2} />
      <div style={{ padding: isMobile ? '60px 24px 40px' : '80px 48px 40px', maxWidth: '660px', width: '100%' }}>
        <div style={{ fontSize: '10px', color: accent, letterSpacing: '3px', fontFamily: mono, marginBottom: '24px' }}>QUESTION 2 OF 3</div>
        <div style={{ fontSize: isMobile ? '24px' : '34px', fontWeight: '700', color: text, lineHeight: 1.3, marginBottom: '8px' }}>When does it need to launch?</div>
        <div style={{ fontSize: '14px', color: dim, marginBottom: '48px', lineHeight: 1.7, maxWidth: '500px' }}>
          This is the date your product hits stores or ships to customers. Nomos will work backwards from this date to build your phase gate timeline.
        </div>
        <input
          autoFocus
          type="date"
          value={launchDate}
          onChange={e => setLaunchDate(e.target.value)}
          style={{ padding: '18px 20px', backgroundColor: surface, border: `2px solid ${border}`, borderRadius: '8px', fontSize: isMobile ? '16px' : '22px', color: text, fontFamily: mono, width: '100%', boxSizing: 'border-box', outline: 'none', cursor: 'pointer' }}
        />
        {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', fontFamily: mono }}>{error}</div>}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setStep(1)} style={{ background: 'none', border: `1px solid ${border}`, borderRadius: '6px', padding: '12px 24px', cursor: 'pointer', fontSize: '11px', color: dim, fontFamily: mono, letterSpacing: '1px' }}>← BACK</button>
          <button
            onClick={() => { if (!launchDate) { setError('Please select a launch date.'); return; } setError(''); setStep(3); }}
            style={{ backgroundColor: accent, border: 'none', color: '#000', padding: '14px 44px', fontFamily: mono, fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
            NEXT →
          </button>
        </div>
      </div>
    </div>
  );

  // STEP 3 — Which teams
  if (step === 3) return (
    <div style={wrap}>
      <ProgressBar current={3} />
      <div style={{ padding: isMobile ? '60px 24px 40px' : '80px 48px 40px', maxWidth: '660px', width: '100%' }}>
        <div style={{ fontSize: '10px', color: accent, letterSpacing: '3px', fontFamily: mono, marginBottom: '24px' }}>QUESTION 3 OF 3</div>
        <div style={{ fontSize: isMobile ? '24px' : '34px', fontWeight: '700', color: text, lineHeight: 1.3, marginBottom: '8px' }}>Which teams are involved?</div>
        <div style={{ fontSize: '14px', color: dim, marginBottom: '40px', lineHeight: 1.7, maxWidth: '500px' }}>
          Select every team with a role in this launch. Nomos will write a specific briefing for each one.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
          {TEAMS.map(team => {
            const lower = team.toLowerCase();
            const sel = selectedTeams.includes(lower);
            return (
              <button key={team} onClick={() => toggleTeam(team)} style={{
                padding: '14px 28px', borderRadius: '8px', cursor: 'pointer', fontFamily: mono, fontSize: '11px', letterSpacing: '2px', fontWeight: '600', transition: 'all 0.15s',
                backgroundColor: sel ? accent : surface, border: `2px solid ${sel ? accent : border}`, color: sel ? '#000' : dim,
              }}>{team.toUpperCase()}</button>
            );
          })}
        </div>
        <div style={{ fontSize: '11px', color: dim, marginBottom: '8px' }}>{selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''} selected</div>
        {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontFamily: mono }}>{error}</div>}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setStep(2)} style={{ background: 'none', border: `1px solid ${border}`, borderRadius: '6px', padding: '12px 24px', cursor: 'pointer', fontSize: '11px', color: dim, fontFamily: mono, letterSpacing: '1px' }}>← BACK</button>
          <button onClick={submit} style={{ backgroundColor: accent, border: 'none', color: '#000', padding: '14px 44px', fontFamily: mono, fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
            BUILD MY PLAN →
          </button>
        </div>
      </div>
    </div>
  );

  // STEP 4 — Transition / loading
  if (step === 4) return (
    <div style={wrap}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '40px 24px', maxWidth: '480px', width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: '72px', fontWeight: '900', color: accent, fontFamily: mono, marginBottom: '36px', animation: 'nomosFirstRunPulse 1.6s ease-in-out infinite' }}>N</div>
        <div style={{ fontSize: '16px', color: text, fontWeight: '600', marginBottom: '8px', minHeight: '26px' }}>{TRANSITION_MSGS[transitionMsgIdx]}</div>
        <div style={{ fontSize: '13px', color: dim, marginBottom: '48px', lineHeight: 1.7 }}>Thanks for sharing the details of your launch.<br />Nomos is building your plan.</div>
        <div style={{ width: '100%', height: '4px', backgroundColor: border, borderRadius: '2px', overflow: 'hidden', marginBottom: '12px' }}>
          <div style={{ height: '100%', width: `${progress}%`, backgroundColor: accent, borderRadius: '2px', transition: 'width 0.8s ease' }} />
        </div>
        <div style={{ fontSize: '9px', color: dim, fontFamily: mono, letterSpacing: '2px' }}>{progress < 99 ? 'BUILDING YOUR PLAN...' : 'ALMOST DONE...'}</div>
      </div>
    </div>
  );

  // STEP 5 — Living Document output
  if (step === 5 && output) return (
    <div style={wrap}>
      <div style={{ width: '100%', maxWidth: '780px', padding: isMobile ? '36px 20px 80px' : '56px 48px 100px' }}>
        <div style={{ fontSize: '9px', color: accent, letterSpacing: '3px', fontFamily: mono, marginBottom: '14px' }}>YOUR LAUNCH PLAN — LIVE DOCUMENT</div>
        <div style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: '800', color: text, lineHeight: 1.2, marginBottom: '24px' }}>{launchName}</div>
        <div style={{ fontSize: '15px', color: dim, lineHeight: 1.8, marginBottom: '48px', paddingBottom: '48px', borderBottom: `1px solid ${border}` }}>{output.executiveSummary}</div>

        {/* Phase Gate Timeline */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ fontSize: '9px', color: text, letterSpacing: '3px', fontFamily: mono, fontWeight: '700', marginBottom: '32px' }}>PHASE GATE TIMELINE</div>
          {!isMobile ? (
            <div style={{ position: 'relative', paddingBottom: '32px' }}>
              <div style={{ position: 'absolute', top: '10px', left: '0', right: '0', height: '2px', backgroundColor: border }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                {output.timeline.map((g, i) => (
                  <div key={g.gate} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${100 / 6}%` }}>
                    <div style={{ width: '16px', height: '16px', backgroundColor: GATE_COLORS[i], borderRadius: '3px', transform: 'rotate(45deg)', zIndex: 1, flexShrink: 0, border: `3px solid ${bg}`, boxSizing: 'border-box' }} />
                    <div style={{ marginTop: '12px', fontSize: '7px', letterSpacing: '1px', color: GATE_COLORS[i], fontFamily: mono, fontWeight: '700', textAlign: 'center' }}>{GATE_NAMES_SHORT[i]}</div>
                    <div style={{ fontSize: '9px', color: dim, fontFamily: mono, textAlign: 'center', marginTop: '3px' }}>{g.date ? new Date(g.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                    <div style={{ marginTop: '8px', fontSize: '10px', color: muted, textAlign: 'center', lineHeight: 1.5, maxWidth: '95px' }}>{g.focus}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {output.timeline.map((g, i) => (
                <div key={g.gate} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: GATE_COLORS[i], borderRadius: '2px', transform: 'rotate(45deg)', flexShrink: 0 }} />
                    {i < 5 && <div style={{ width: '1px', height: '48px', backgroundColor: border, marginTop: '6px' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '8px', color: GATE_COLORS[i], fontFamily: mono, fontWeight: '700', marginBottom: '2px' }}>{GATE_NAMES_SHORT[i]}</div>
                    <div style={{ fontSize: '10px', color: dim, fontFamily: mono, marginBottom: '4px' }}>{g.date ? new Date(g.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                    <div style={{ fontSize: '12px', color: muted, lineHeight: 1.6 }}>{g.focus}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team Briefings */}
        {output.teamBriefings && Object.keys(output.teamBriefings).length > 0 && (
          <div style={{ marginBottom: '48px', paddingBottom: '48px', borderBottom: `1px solid ${border}` }}>
            <div style={{ fontSize: '9px', color: text, letterSpacing: '3px', fontFamily: mono, fontWeight: '700', marginBottom: '20px' }}>TEAM BRIEFINGS</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
              {Object.entries(output.teamBriefings).map(([team, briefing]) => (
                <div key={team} style={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: '8px', padding: '18px 20px' }}>
                  <div style={{ fontSize: '8px', color: accent, letterSpacing: '2px', fontFamily: mono, fontWeight: '700', marginBottom: '10px' }}>{team.toUpperCase()}</div>
                  <div style={{ fontSize: '13px', color: dim, lineHeight: 1.7 }}>{briefing}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Risks */}
        {output.keyRisks && output.keyRisks.length > 0 && (
          <div style={{ marginBottom: '56px' }}>
            <div style={{ fontSize: '9px', color: text, letterSpacing: '3px', fontFamily: mono, fontWeight: '700', marginBottom: '18px' }}>KEY RISKS TO WATCH</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {output.keyRisks.map((risk, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444', marginTop: '7px', flexShrink: 0 }} />
                  <div style={{ fontSize: '14px', color: dim, lineHeight: 1.6 }}>{risk}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={() => onComplete(output.programId)}
            style={{ backgroundColor: accent, border: 'none', color: '#000', padding: '18px 60px', fontFamily: mono, fontSize: '12px', letterSpacing: '3px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
            ENTER NOMOS →
          </button>
          <div style={{ fontSize: '12px', color: dim }}>This document is live. It updates as your team logs progress.</div>
        </div>
      </div>
    </div>
  );

  return null;
}

// ── PUBLIC BRIEF VIEW ────────────────────────────────────────────────────────
const PUB_GATE_COLORS = ['#6366f1','#8b5cf6','#0d9488','#f59e0b','#ef4444','#10b981'];
const PUB_GATE_NAMES = ['INCEPTION','CONCEPT','ENG. REVIEW','PROTO 1','PROTO 2','MASS PROD'];
const PUB_STATUS_MAP = { complete: 'COMPLETE', on_track: 'IN PROGRESS', at_risk: 'IN PROGRESS', blocked: 'NEEDS ATTENTION', idle: 'NOT STARTED' };

function PublicBriefView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    fetch(`${API}/public/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject('not_found'))
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto Mono', monospace" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '13px', color: '#0d9488', letterSpacing: '3px', marginBottom: '12px' }}>NOMOS</div>
        <div style={{ fontSize: '11px', color: '#94a3b8', letterSpacing: '2px' }}>LOADING BRIEF...</div>
      </div>
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto Mono', monospace" }}>
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '13px', color: '#0d9488', letterSpacing: '3px', marginBottom: '24px' }}>NOMOS</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '12px', fontFamily: "'Inter', sans-serif" }}>Brief not found</div>
        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '32px' }}>This link may have expired or sharing has been disabled.</div>
        <button onClick={() => window.location.href = 'https://app.nomoslaunch.com'} style={{ background: '#0d9488', border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer' }}>ENTER NOMOS →</button>
      </div>
    </div>
  );

  const launchDate = data.launchDate ? new Date(data.launchDate + 'T12:00:00') : null;
  const daysToLaunch = launchDate ? Math.round((launchDate - new Date()) / 86400000) : null;

  // Build per-gate summary across all lanes
  const gatesByNumber = {};
  (data.gates || []).forEach(g => {
    if (!gatesByNumber[g.gate_number]) gatesByNumber[g.gate_number] = [];
    gatesByNumber[g.gate_number].push(g);
  });

  // Build per-lane summary: most recent non-complete gate
  const laneOrder = ['design','finance','operations','installation','engineering'];
  const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
  const laneGates = {};
  (data.gates || []).forEach(g => {
    if (!laneGates[g.lane]) laneGates[g.lane] = [];
    laneGates[g.lane].push(g);
  });

  const px = isMobile ? '20px' : '60px';

  return (
    <div style={{ background: '#fff', minHeight: '100vh', fontFamily: "'Inter', sans-serif", color: '#0f172a' }}>

      {/* ── HERO ── */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: isMobile ? '48px 20px 56px' : '72px 60px 80px', color: '#fff' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ fontSize: '10px', color: '#0d9488', letterSpacing: '4px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '20px' }}>NOMOS · LAUNCH BRIEF</div>
          <h1 style={{ fontSize: isMobile ? '36px' : '56px', fontWeight: '800', margin: '0 0 16px', lineHeight: 1.1, letterSpacing: '-1px' }}>{data.programName}</h1>
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginTop: '24px' }}>
            {launchDate && (
              <div>
                <div style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>LAUNCH DATE</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#e2e8f0' }}>{launchDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
              </div>
            )}
            {daysToLaunch !== null && (
              <div>
                <div style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>T-MINUS</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: daysToLaunch < 0 ? '#ef4444' : daysToLaunch < 60 ? '#f59e0b' : '#0d9488' }}>
                  {daysToLaunch > 0 ? `${daysToLaunch} days` : daysToLaunch === 0 ? 'TODAY' : 'PAST LAUNCH DATE'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── EXECUTIVE SUMMARY ── */}
      {data.executiveSummary && (
        <div style={{ padding: isMobile ? '48px 20px' : '64px 60px', maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ fontSize: '9px', color: '#0d9488', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '16px' }}>EXECUTIVE SUMMARY</div>
          <p style={{ fontSize: isMobile ? '16px' : '19px', lineHeight: 1.8, color: '#1e293b', margin: 0 }}>{data.executiveSummary}</p>
        </div>
      )}

      {/* ── PHASE GATE TIMELINE ── */}
      <div style={{ background: '#f8fafc', padding: isMobile ? '48px 20px' : '64px 60px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ fontSize: '9px', color: '#0f172a', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '40px' }}>PHASE GATE TIMELINE</div>
          {!isMobile ? (
            <div style={{ position: 'relative', paddingBottom: '40px' }}>
              <div style={{ position: 'absolute', top: '10px', left: '0', right: '0', height: '2px', backgroundColor: '#e2e8f0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                {PUB_GATE_NAMES.map((name, i) => {
                  const gNum = i + 1;
                  const gatesForNum = gatesByNumber[gNum] || [];
                  const allComplete = gatesForNum.length > 0 && gatesForNum.every(g => g.status === 'complete');
                  const hasClose = gatesForNum[0]?.planned_close_date;
                  const col = PUB_GATE_COLORS[i];
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${100/6}%` }}>
                      <div style={{ width: '20px', height: '20px', backgroundColor: allComplete ? col : '#fff', border: `3px solid ${col}`, borderRadius: '4px', transform: 'rotate(45deg)', zIndex: 1, flexShrink: 0, boxSizing: 'border-box' }} />
                      <div style={{ marginTop: '14px', fontSize: '8px', letterSpacing: '1px', color: col, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', textAlign: 'center' }}>{name}</div>
                      {hasClose && <div style={{ fontSize: '9px', color: '#94a3b8', fontFamily: "'Roboto Mono', monospace", textAlign: 'center', marginTop: '3px' }}>{new Date(hasClose + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {PUB_GATE_NAMES.map((name, i) => {
                const gNum = i + 1;
                const gatesForNum = gatesByNumber[gNum] || [];
                const hasClose = gatesForNum[0]?.planned_close_date;
                const col = PUB_GATE_COLORS[i];
                return (
                  <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ width: '14px', height: '14px', backgroundColor: '#fff', border: `3px solid ${col}`, borderRadius: '3px', transform: 'rotate(45deg)', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '9px', color: col, fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>{name}</div>
                      {hasClose && <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: "'Roboto Mono', monospace" }}>{new Date(hasClose + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── TEAM PANELS ── */}
      {Object.keys(laneGates).length > 0 && (
        <div style={{ padding: isMobile ? '48px 20px' : '64px 60px', maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ fontSize: '9px', color: '#0f172a', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '24px' }}>TEAM STATUS</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {laneOrder.filter(l => laneGates[l]).map(lane => {
              const gates = laneGates[lane] || [];
              // Find current active gate
              const activeGate = gates.find(g => g.status !== 'complete' && g.status !== 'idle') || gates[gates.length - 1];
              const pct = activeGate?.completion_percent || 0;
              const statusLabel = PUB_STATUS_MAP[activeGate?.status] || 'IN PROGRESS';
              const statusColor = activeGate?.status === 'complete' ? '#10b981' : activeGate?.status === 'blocked' ? '#ef4444' : activeGate?.status === 'at_risk' ? '#f59e0b' : '#0d9488';
              const laneColor = laneColorMap[lane] || '#0d9488';
              return (
                <div key={lane} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px 22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <div style={{ fontSize: '9px', fontWeight: '700', color: laneColor, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace" }}>{lane.toUpperCase()}</div>
                    <span style={{ fontSize: '8px', fontWeight: '700', color: statusColor, fontFamily: "'Roboto Mono', monospace", background: statusColor + '18', padding: '3px 8px', borderRadius: '3px', letterSpacing: '1px' }}>{statusLabel}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px', fontFamily: "'Roboto Mono', monospace" }}>GATE {activeGate?.gate_number || 1} · {pct}% COMPLETE</div>
                  <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: laneColor, borderRadius: '2px' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TEAM BRIEFINGS (Gate 1 notes) ── */}
      {(data.teamBriefings || []).length > 0 && (
        <div style={{ background: '#f8fafc', padding: isMobile ? '48px 20px' : '64px 60px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ fontSize: '9px', color: '#0f172a', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '24px' }}>TEAM BRIEFINGS</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
              {data.teamBriefings.map((b, i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px 22px' }}>
                  <div style={{ fontSize: '8px', color: laneColorMap[b.lane] || '#0d9488', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '10px' }}>{(b.lane || '').toUpperCase()}</div>
                  <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.7 }}>{b.notes}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── WHY / VISION / WHO / SUCCESS ── */}
      {(data.whyItMatters || data.endVision || data.targetCustomer || data.successCriteria) && (
        <div style={{ padding: isMobile ? '48px 20px' : '64px 60px', maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '24px' }}>
            {data.whyItMatters && (
              <div>
                <div style={{ fontSize: '9px', color: '#0d9488', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>WHY IT MATTERS</div>
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.8, margin: 0 }}>{data.whyItMatters}</p>
              </div>
            )}
            {data.endVision && (
              <div>
                <div style={{ fontSize: '9px', color: '#0d9488', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>END VISION</div>
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.8, margin: 0 }}>{data.endVision}</p>
              </div>
            )}
            {data.targetCustomer && (
              <div>
                <div style={{ fontSize: '9px', color: '#0d9488', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>WHO EXPERIENCES IT</div>
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.8, margin: 0 }}>{data.targetCustomer}</p>
              </div>
            )}
            {data.successCriteria && (
              <div>
                <div style={{ fontSize: '9px', color: '#0d9488', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>SUCCESS CRITERIA</div>
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.8, margin: 0 }}>{data.successCriteria}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CTA ── */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: isMobile ? '64px 20px' : '80px 60px', textAlign: 'center' }}>
        <div style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", marginBottom: '20px' }}>READY TO RUN YOUR LAUNCH?</div>
        <h2 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: '#fff', margin: '0 0 32px', letterSpacing: '-0.5px' }}>The launch operating system<br />for physical product teams.</h2>
        <button onClick={() => window.location.href = 'https://app.nomoslaunch.com'} style={{ background: '#0d9488', border: 'none', color: '#fff', padding: '16px 48px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer' }}>
          ENTER NOMOS →
        </button>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background: '#0f172a', padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '10px', color: '#475569', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>Powered by NOMOS · The Launch Operating System</div>
      </div>
    </div>
  );
}

function App() {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showTeamDirectory, setShowTeamDirectory] = useState(false);
  const [consequences, setConsequences] = useState(null);
  const [consDetailPanel, setConsDetailPanel] = useState(null); // { type: 'team'|'finance', lane?, metric? }
  const [teamPanelMilestones, setTeamPanelMilestones] = useState({}); // gateId → milestone[]
  const [focusCollapsed, setFocusCollapsed] = useState(false);
  const [focusSolutions, setFocusSolutions] = useState({}); // 'lane-gate' → { action, timeEstimate, basis }
  const [focusSolutionsLoading, setFocusSolutionsLoading] = useState(false);
  const [visitDiff, setVisitDiff] = useState([]); // [{ lane, gate, from, to }]
  const [visitDiffDismissed, setVisitDiffDismissed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [editingGate, setEditingGate] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [question, setQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('nomosTheme') === 'dark');
  const [readinessSubTab, setReadinessSubTab] = useState('current');
  const [currentGateMilestones, setCurrentGateMilestones] = useState([]);
  const [currentGateMilestonesLoading, setCurrentGateMilestonesLoading] = useState(false);
  const [timelineSubTab, setTimelineSubTab] = useState('live');
  const [expandedLanePill, setExpandedLanePill] = useState(null);

  // Auth state
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const sessionRef = useRef(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState('signin');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyBanner, setPasskeyBanner] = useState(false);
  const [passkeyRegistering, setPasskeyRegistering] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const [authCompany, setAuthCompany] = useState(null);
  const [realUserRole, setUserRole] = useState('member');
  const [realUserLane, setUserLane] = useState(null);
  const [previewLane, setPreviewLane] = useState(null); // admin "Preview As" override
  // These shadow the raw state — all render code automatically gets the preview value
  const userRole = previewLane ? 'member' : realUserRole;
  const userLane = previewLane || realUserLane;
  const [memberScopeMode, setMemberScopeMode] = useState('lane'); // 'lane' | 'full'
  const [memberView, setMemberView] = useState(false);
  const [authLane, setAuthLane] = useState('');
  const [authResetMode, setAuthResetMode] = useState(false);
  const [authResetSent, setAuthResetSent] = useState(false);
  const [signupType, setSignupType] = useState('member');
  const [showWelcome, setShowWelcome] = useState(() => localStorage.getItem('nomos_new_admin') === '1');
  const [showFirstRun, setShowFirstRun] = useState(false);
  const [showInviteNudge, setShowInviteNudge] = useState(() => localStorage.getItem('nomos_invite_nudge') === '1');
  const [showBlueprintBanner, setShowBlueprintBanner] = useState(false);
  const [authCompanyName, setAuthCompanyName] = useState('');
  const [createdCompanyCode, setCreatedCompanyCode] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [slackWebhook, setSlackWebhook] = useState('');
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackResult, setSlackResult] = useState(null);
  const [programActivity, setProgramActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteRows, setInviteRows] = useState([{ id: 1, name: '', email: '', lane: '', role: 'member', existing: false }]);
  const [inviteResults, setInviteResults] = useState({});
  const [invitePaste, setInvitePaste] = useState('');
  const [invitePasteOpen, setInvitePasteOpen] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteTypeaheadRow, setInviteTypeaheadRow] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [programActionsOpen, setProgramActionsOpen] = useState(false);
  const [askPanelOpen, setAskPanelOpen] = useState(false);
  const [scoreBreakdownOpen, setScoreBreakdownOpen] = useState(false);
  const [activityLaneFilter, setActivityLaneFilter] = useState('all');
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [myAvatarUrl, setMyAvatarUrl] = useState(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [myTitle, setMyTitle] = useState('');
  const [myFullName, setMyFullName] = useState('');
  const [myProfileOpen, setMyProfileOpen] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [programSearch, setProgramSearch] = useState('');
  const [programFilter, setProgramFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [programImages, setProgramImages] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', launch_date: '', budget_total: '',
    num_locations: '', launched_locations: '', unit_cost: '',
    units_per_location: '2', avg_daily_revenue_per_location: '',
    expected_uplift_percent: '2', retrofit_cost_per_location: '750',
    markets: '', program_start_date: '',
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [inceptionAnalysis, setInceptionAnalysis] = useState('');
  const [inceptionLoading, setInceptionLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(false);
  const [programForm, setProgramForm] = useState({});
  const [demoLoading, setDemoLoading] = useState(false);
  const [landingMode, setLandingMode] = useState(() => sessionStorage.getItem('nomos_entered') !== 'true');
  const [briefForm, setBriefForm] = useState({ what_is_it: '', end_vision: '', why_it_matters: '', target_customer: '', success_criteria: '', product_image_url: '' });
  const [briefSaving, setBriefSaving] = useState(false);
  const [briefSaved, setBriefSaved] = useState(false);
  const [briefDraftLoading, setBriefDraftLoading] = useState(false);
  const [briefEditMode, setBriefEditMode] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareGroupOpen, setShareGroupOpen] = useState(false);
  const [shareGroupEmails, setShareGroupEmails] = useState([]);
  const [shareGroupSending, setShareGroupSending] = useState(false);
  const [shareGroupResult, setShareGroupResult] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainSaved, setDomainSaved] = useState(false);
  const [briefRequestOpen, setBriefRequestOpen] = useState(false);
  const [briefRequestText, setBriefRequestText] = useState('');
  const [briefRequestSending, setBriefRequestSending] = useState(false);
  const [createStep, setCreateStep] = useState('intake');
  const [intakeText, setIntakeText] = useState('');
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeFilled, setIntakeFilled] = useState(false);
  const [voiceFieldId, setVoiceFieldId] = useState(null);
  const [alertOpen, setAlertOpen] = useState(null); // 'hard' | 'soft' | null

  // Brief snapshot bar — editable fields + locks
  const [lockedFields, setLockedFields] = useState({});
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [customMarket, setCustomMarket] = useState('');
  const [briefInlineEdit, setBriefInlineEdit] = useState(null); // 'locations' | 'launch'
  const [briefInlineVal, setBriefInlineVal] = useState('');

  // Gate planning state
  const [execSummary, setExecSummary] = useState('');
  const [execSummaryLoading, setExecSummaryLoading] = useState(false);
  const [execSummaryError, setExecSummaryError] = useState('');
  const [execActions, setExecActions] = useState([]);
  const [riskIntel, setRiskIntel] = useState([]);
  const [riskIntelLoading, setRiskIntelLoading] = useState(false);
  const [adaptiveData, setAdaptiveData] = useState(null);
  const [adaptiveLoading, setAdaptiveLoading] = useState(false);
  const [adaptiveError, setAdaptiveError] = useState('');
  const [nomosFeedback, setNomosFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importSuggestions, setImportSuggestions] = useState(null);
  const [importApplying, setImportApplying] = useState({});
  const [importApplied, setImportApplied] = useState({});

  // Resolution path state (keyed by `${sourceLane}-${sourceGate}-${targetLane}-${targetGate}`)
  const [resolutions, setResolutions] = useState({});
  const [resolutionLoading, setResolutionLoading] = useState({});
  const [expandedResolution, setExpandedResolution] = useState(null);

  // Lane view state
  const [laneViewOpen, setLaneViewOpen] = useState(false);
  const [laneViewLane, setLaneViewLane] = useState(null);
  const [laneViewData, setLaneViewData] = useState({ gates: [], milestones: {} });
  const [laneViewLoading, setLaneViewLoading] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskResources, setTaskResources] = useState({});
  const [addingResourceTo, setAddingResourceTo] = useState(null);
  const [newResourceLabel, setNewResourceLabel] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');
  const [currentUser, setCurrentUser] = useState(() => localStorage.getItem('nomos_user') || '');
  const [userNamePrompt, setUserNamePrompt] = useState(false);
  const [pendingSignOff, setPendingSignOff] = useState(null);
  const [expandedGateInLane, setExpandedGateInLane] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null); // { milestoneId, gateId }
  const [approvalNote, setApprovalNote] = useState('');

  // People layer — team leads + DRIs per lane (set in Brief, auto-feed into Lane View)
  const [teamLeads, setTeamLeads] = useState({});
  const [laneDris, setLaneDris] = useState({});
  const [programMembers, setProgramMembers] = useState([]);
  const [laneDriInput, setLaneDriInput] = useState({});

  // Launch wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    name: '', launch_date: '', mass_production_date: '', program_start_date: '', markets: '', num_locations: '', budget_total: '',
    what_is_it: '', why_it_matters: '', end_vision: '', target_customer: '',
    unit_cost: '', units_per_location: '2', avg_daily_revenue_per_location: '',
    expected_uplift_percent: '2', retrofit_cost_per_location: '750', launched_locations: '0',
  });
  const [wizardBriefLoading, setWizardBriefLoading] = useState(false);
  const [wizardBriefDone, setWizardBriefDone] = useState(false);
  const [wizardIntakeAnalyzed, setWizardIntakeAnalyzed] = useState(false);
  const [wizardGateDates, setWizardGateDates] = useState(['', '', '', '', '', '']);
  const [blueprintGenerating, setBlueprintGenerating] = useState(false);
  const [blueprintModalOpen, setBlueprintModalOpen] = useState(false);
  const [blueprintMpDate, setBlueprintMpDate] = useState('');

  const [gateModalTab, setGateModalTab] = useState('milestones');
  const [milestones, setMilestones] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [newMilestoneText, setNewMilestoneText] = useState('');
  const [expandedMilestoneId, setExpandedMilestoneId] = useState(null);
  const [gateAiQuestion, setGateAiQuestion] = useState('');
  const [gateAiAnswer, setGateAiAnswer] = useState('');
  const [gateAiLoading, setGateAiLoading] = useState(false);
  const [savedNoteId, setSavedNoteId] = useState(null);
  const [teamSaving, setTeamSaving] = useState(false);
  const [addingCustomTeam, setAddingCustomTeam] = useState(false);
  const [newCustomTeamName, setNewCustomTeamName] = useState('');
  const [workspaceView, setWorkspaceView] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState('gates');
  const [mailboxMessages, setMailboxMessages] = useState([]);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxUnread, setMailboxUnread] = useState({});
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [mailboxSection, setMailboxSection] = useState('inbox'); // 'inbox' | 'sent' | 'drafts'
  const [drafts, setDrafts] = useState(() => { try { return JSON.parse(localStorage.getItem('nomos_drafts') || '[]'); } catch { return []; } });
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [workspaceNotes, setWorkspaceNotes] = useState('');
  const [workspaceNotesSaving, setWorkspaceNotesSaving] = useState(false);
  const [workspaceDocsSaving, setWorkspaceDocsSaving] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');
  const [newDocUrl, setNewDocUrl] = useState('');
  const [expandedWorkspaceTask, setExpandedWorkspaceTask] = useState(null);
  const [workspaceTaskNote, setWorkspaceTaskNote] = useState('');
  const [wsResLabel, setWsResLabel] = useState('');
  const [wsResUrl, setWsResUrl] = useState('');
  const [wsResUploading, setWsResUploading] = useState(false);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myLaneActions, setMyLaneActions] = useState([]);
  const [laneActionsLoading, setLaneActionsLoading] = useState(false);
  const [programLaneActions, setProgramLaneActions] = useState([]);
  const [generatingActions, setGeneratingActions] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [superAdminUsers, setSuperAdminUsers] = useState([]);
  const [superAdminLoading, setSuperAdminLoading] = useState(false);
  const [superAdminAction, setSuperAdminAction] = useState(null); // { type: 'reset'|'remove', user }
  const [superAdminWorking, setSuperAdminWorking] = useState(false);

  useEffect(() => {
    localStorage.setItem('nomosTheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setAvatarMenuOpen(false); }, [landingMode]);

  useEffect(() => {
    if (!avatarMenuOpen) return;
    const close = (e) => { if (!e.target.closest('[data-avatar-menu]')) setAvatarMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [avatarMenuOpen]);

  useEffect(() => {
    if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().then(ok => setPasskeyAvailable(ok)).catch(() => {});
    }
  }, []);

  // Presence heartbeat — pings every 60s when window is focused and user is logged in
  useEffect(() => {
    if (!session) return;
    const API_URL = 'https://nomos-production.up.railway.app';
    const ping = () => {
      if (document.visibilityState === 'visible' && sessionRef.current) {
        const token = sessionRef.current?.access_token;
        if (token) fetch(`${API_URL}/presence/ping`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    };
    ping();
    const interval = setInterval(ping, 60000);
    document.addEventListener('visibilitychange', ping);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', ping); };
  }, [session]);

  // Auth listener
  useEffect(() => {
    // If the URL has an auth callback (magic link implicit flow), mark as entered
    // so the app doesn't show the landing page after sign-in completes
    const hasAuthInUrl = window.location.hash.includes('access_token') || window.location.search.includes('code=');
    if (hasAuthInUrl) sessionStorage.setItem('nomos_entered', 'true');

    supabase.auth.getSession().then(({ data: { session } }) => {
      _nomosToken = session?.access_token || null;
      setSession(session);
      sessionRef.current = session;
      if (session) { setLandingMode(false); sessionStorage.setItem('nomos_entered', 'true'); }
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      _nomosToken = session?.access_token || null;
      setSession(session);
      sessionRef.current = session;
      if (session) {
        setLandingMode(false);
        sessionStorage.setItem('nomos_entered', 'true');
        fetch(`${API}/me`, { headers: { 'Authorization': `Bearer ${session.access_token}` } })
          .then(r => r.json()).then(d => { if (d.company) { setAuthCompany(d.company); setSlackWebhook(d.company.slack_webhook_url || ''); } if (d.role) setUserRole(d.role); if (d.lane) setUserLane(d.lane); if (d.avatar_url) setMyAvatarUrl(d.avatar_url); if (d.title) setMyTitle(d.title); if (d.full_name) setMyFullName(d.full_name); }).catch(() => {});
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Realtime notifications
  useEffect(() => {
    if (!session || !authCompany?.id) return;
    authFetch(`${API}/notifications`).then(r => r.json()).then(data => setNotifications(Array.isArray(data) ? data : [])).catch(() => {});
    const channel = supabase.channel(`notif-${authCompany.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `company_id=eq.${authCompany.id}` },
        (payload) => setNotifications(prev => [payload.new, ...prev]))
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [session, authCompany?.id]);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-notif-root]')) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  // Proper in-app back navigation: program → programs list → landing
  useEffect(() => {
    const hasAuthInUrl = window.location.hash.includes('access_token') || window.location.search.includes('code=');
    if (!hasAuthInUrl) window.history.replaceState({ page: 'landing' }, '');
    const onPop = (e) => {
      const page = e.state?.page;
      if (page === 'program') {
        // going back to programs list
        setSelectedProgram(null); setConsequences(null);
      } else if (page === 'programs') {
        // going back to landing
        setLandingMode(true); setDemoMode(false); setNotifications([]);
        if (window.__demoNotifChannel) { supabase.removeChannel(window.__demoNotifChannel); window.__demoNotifChannel = null; }
        sessionStorage.removeItem('nomos_entered');
        window.history.replaceState({ page: 'landing' }, '');
      } else {
        // already at landing, let browser go back naturally
        setLandingMode(true); setDemoMode(false); setNotifications([]);
        if (window.__demoNotifChannel) { supabase.removeChannel(window.__demoNotifChannel); window.__demoNotifChannel = null; }
        sessionStorage.removeItem('nomos_entered');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!session) return;
    authFetch(`${API}/programs`, { headers: { 'Authorization': `Bearer ${session.access_token}` } })
      .then(res => res.json())
      .then(data => { const progs = Array.isArray(data) ? data : []; setPrograms(progs); setLoading(false); if (progs.length === 0) setShowFirstRun(true); })
      .catch(() => setLoading(false));
    authFetch(`${API}/portfolio`).then(r => r.json()).then(d => setPortfolio(d)).catch(() => {});
    authFetch(`${API}/team`).then(r => r.json()).then(d => { if (Array.isArray(d)) setTeamMembers(d); }).catch(() => {});
    authFetch(`${API}/notifications/backfill`, { method: 'POST' }).catch(() => {});
  }, [session]);

  useEffect(() => {
    if (selectedProgram?.id) {
      try { setLockedFields(JSON.parse(localStorage.getItem(`nomos_locks_${selectedProgram.id}`) || '{}')); }
      catch { setLockedFields({}); }
    }
    setMarketsOpen(false);
    setBriefInlineEdit(null);
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (selectedProgram && !nomosFeedback.length && !feedbackLoading) {
      setFeedbackLoading(true);
      const url = demoMode
        ? `${API}/demo/programs/${selectedProgram.id}/feedback`
        : `${API}/programs/${selectedProgram.id}/feedback`;
      const fetcher = demoMode ? fetch(url, { method: 'POST' }) : authFetch(url, { method: 'POST' });
      setFeedbackError(null);
      fetcher
        .then(r => r.json())
        .then(d => {
          console.log('[Nomos Feedback]', d);
          if (d.feedback && d.feedback.length > 0) {
            setNomosFeedback(d.feedback);
          } else if (d.error) {
            setFeedbackError(d.error + (d.detail ? ': ' + d.detail : ''));
          }
        })
        .catch(e => { console.error('[Nomos Feedback error]', e); setFeedbackError(e.message); })
        .finally(() => setFeedbackLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (activeTab === 'exec' && selectedProgram) {
      if (!execSummary && !execSummaryLoading) generateExecSummary();
      if (!riskIntel.length && !riskIntelLoading) generateRiskIntel();
    }
    if (activeTab === 'adaptive' && selectedProgram && !demoMode) {
      if (!adaptiveData && !adaptiveLoading) generateAdaptive();
    }
    if ((activeTab === 'gates' || activeTab === 'timeline') && selectedProgram) {
      setCurrentGateMilestonesLoading(true);
      const url = demoMode
        ? `${API}/demo/programs/${selectedProgram.id}/current-gate-milestones`
        : `${API}/programs/${selectedProgram.id}/current-gate-milestones`;
      (demoMode ? fetch(url) : authFetch(url))
        .then(r => r.json()).then(d => setCurrentGateMilestones(d.gates || [])).catch(() => {})
        .finally(() => setCurrentGateMilestonesLoading(false));
    }
    if (activeTab === 'activity' && selectedProgram && !demoMode) {
      setActivityLoading(true);
      authFetch(`${API}/programs/${selectedProgram.id}/activity`)
        .then(r => r.json()).then(d => setProgramActivity(Array.isArray(d) ? d : [])).catch(() => {})
        .finally(() => setActivityLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProgram?.id]);

  useEffect(() => {
    if (consDetailPanel?.type !== 'team' || !selectedProgram) { setTeamPanelMilestones({}); return; }
    const laneGates = (selectedProgram.gates || []).filter(g => g.lane === consDetailPanel.lane);
    const fetcher = (gateId) => {
      const url = demoMode
        ? `${API}/demo/gates/${gateId}/milestones`
        : `${API}/gates/${gateId}/milestones`;
      const req = demoMode ? fetch(url) : authFetch(url);
      return req.then(r => r.json()).then(ms => ({ gateId, ms: Array.isArray(ms) ? ms : [] })).catch(() => ({ gateId, ms: [] }));
    };
    Promise.all(laneGates.map(g => fetcher(g.id))).then(results => {
      const map = {};
      results.forEach(({ gateId, ms }) => { map[gateId] = ms; });
      setTeamPanelMilestones(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consDetailPanel?.type, consDetailPanel?.lane, selectedProgram?.id]);

  // "Since last visit" diff — compare gate statuses to last stored snapshot
  useEffect(() => {
    if (!selectedProgram?.gates?.length) return;
    setVisitDiffDismissed(false);
    const key = `nomos_snap_${selectedProgram.id}`;
    const newStatuses = Object.fromEntries(
      selectedProgram.gates.map(g => [`${g.lane}-${g.gate_number}`, g.status])
    );
    try {
      const old = JSON.parse(localStorage.getItem(key) || 'null');
      if (old?.statuses) {
        const notable = ['blocked', 'at_risk', 'complete'];
        const diffs = [];
        Object.entries(newStatuses).forEach(([k, newSt]) => {
          const oldSt = old.statuses[k];
          if (oldSt && oldSt !== newSt && (notable.includes(newSt) || notable.includes(oldSt))) {
            const [lane, gate] = k.split('-');
            diffs.push({ lane, gate: parseInt(gate), from: oldSt, to: newSt });
          }
        });
        setVisitDiff(diffs);
      } else {
        setVisitDiff([]);
      }
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), statuses: newStatuses }));
    } catch { setVisitDiff([]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram?.id]);

  // Auto-fetch AI-predicted solutions for TODAY'S FOCUS items on program load
  useEffect(() => {
    if (!selectedProgram?.gates) { setFocusSolutions({}); return; }
    const today = new Date();
    const currentGate = selectedProgram.current_gate || 1;
    const items = [];
    selectedProgram.gates.filter(g => g.status === 'blocked' && g.gate_number <= currentGate + 1)
      .forEach(g => items.push({ lane: g.lane, gate: g.gate_number, issue: (g.notes || '').replace(/\[NOMOS[^\]]*\][^\n]*/g, '').trim().slice(0, 120) || 'Gate is BLOCKED — requires immediate attention' }));
    (consequences?.forward_violations || []).forEach(v => {
      items.push({ lane: v.requiredLane, gate: v.requiredGate, issue: `On pace to miss ${v.dependentLane} G${v.dependentGate} dependency — projected ${Math.round(v.projectedReqPct || 0)}% vs ${v.minCompletion}% required` });
    });
    selectedProgram.gates.filter(g => g.status === 'at_risk' && g.planned_close_date).forEach(g => {
      const d = Math.ceil((new Date(g.planned_close_date + 'T00:00:00') - today) / 86400000);
      if (d < 5) items.push({ lane: g.lane, gate: g.gate_number, issue: d < 0 ? `Gate close was ${Math.abs(d)}d ago — still AT RISK at ${g.completion_percent}%` : `Gate close in ${d}d — AT RISK at ${g.completion_percent}%` });
    });
    const seen = new Set();
    const uniqueItems = items.filter(i => { const k = `${i.lane}-${i.gate}`; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 3);
    if (!uniqueItems.length) { setFocusSolutions({}); return; }
    setFocusSolutions({});
    setFocusSolutionsLoading(true);
    const url = demoMode ? `${API}/demo/programs/${selectedProgram.id}/focus-solutions` : `${API}/programs/${selectedProgram.id}/focus-solutions`;
    const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: uniqueItems }) };
    (demoMode ? fetch(url, opts) : authFetch(url, opts))
      .then(r => r.json()).then(d => setFocusSolutions(d.solutions || {})).catch(() => {})
      .finally(() => setFocusSolutionsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram?.id]);

  const refreshProgram = (id) => {
    const base = demoMode ? `${API}/demo/programs/${id}` : `${API}/programs/${id}`;
    const fetcher = demoMode ? fetch : authFetch;
    return Promise.all([
      fetcher(base).then(r => r.json()),
      fetch(`${API}/programs/${id}/consequences`).then(r => r.json()).catch(() => null),
    ]).then(([p, c]) => {
      setSelectedProgram(p);
      setConsequences(c);
      // Derive teamLeads/laneDris from program_members (user-ID-backed); fall back to JSON fields
      if (p?.program_members?.length) {
        setProgramMembers(p.program_members);
        const leads = {}, dris = {};
        p.program_members.forEach(pm => {
          const name = pm.profiles?.full_name || '';
          if (!name) return;
          if (pm.role === 'lead') leads[pm.lane] = name;
          else { if (!dris[pm.lane]) dris[pm.lane] = []; dris[pm.lane].push(name); }
        });
        setTeamLeads(prev => ({ ...(p.team_leads || {}), ...leads }));
        setLaneDris(prev => ({ ...(p.lane_dris || {}), ...dris }));
      } else {
        setProgramMembers([]);
        if (p?.team_leads && typeof p.team_leads === 'object') setTeamLeads(p.team_leads);
        if (p?.lane_dris && typeof p.lane_dris === 'object') setLaneDris(p.lane_dris);
      }
      // Stale exec summary is misleading after any gate change — clear it
      setExecSummary('');
      setExecSummaryError('');
      setExecActions([]);
      setRiskIntel([]);
      // Sync gate statuses/completion in any open lane view
      if (p?.gates) {
        setLaneViewData(prev => ({
          ...prev,
          gates: (prev.gates || []).map(g => {
            const fresh = p.gates.find(fg => fg.id === g.id);
            return fresh ? { ...g, status: fresh.status, completion_percent: fresh.completion_percent, completed_at: fresh.completed_at } : g;
          }),
        }));
      }
    }).catch(() => {});
  };

  const selectProgram = (program) => {
    window.history.pushState({ page: 'program', id: program.id }, '');
    refreshProgram(program.id).then(() => {
      if (userRole === 'member' && userLane) {
        setWorkspaceView(userLane); // so sendMessage knows from_lane
        loadLaneData(userLane, program.id);
        loadMailbox(userLane, program.id);
      }
    });
    loadMailboxUnread(program.id);
    setActiveTab(userRole === 'executive' ? 'exec' : (userRole === 'member' && userLane) ? 'my_work' : 'overview');
    if (userRole === 'member' && userLane) setMemberScopeMode('lane');
    setAskPanelOpen(false);
    setActivityLaneFilter('all');
    setShowTeamDirectory(false);
    setAiAnswer('');
    setInceptionAnalysis('');
    setExecSummary('');
    setExecSummaryError('');
    setExecSummaryLoading(false);
    setExecActions([]);
    setRiskIntel([]);
    setRiskIntelLoading(false);
    setAdaptiveData(null);
    setAdaptiveLoading(false);
    setAdaptiveError('');
    setReadinessSubTab('current');
    setTimelineSubTab('live');
    setCurrentGateMilestones([]);
    setNomosFeedback([]);
    setFeedbackError(null);
    setResolutions({});
    setResolutionLoading({});
    setExpandedResolution(null);
    setDuplicateOpen(false);
    setDuplicateName('');
    setBriefEditMode(false);
    setBriefRequestOpen(false);
    setShowMyTasks(false);
    setShowSuperAdmin(false);
    setShowBlueprintBanner(false);
    setBriefForm({
      what_is_it: program.what_is_it || '',
      end_vision: program.end_vision || '',
      why_it_matters: program.why_it_matters || '',
      target_customer: program.target_customer || '',
      success_criteria: program.success_criteria || '',
      product_image_url: program.product_image_url || '',
    });
    setProgramImages([]);
    fetch(`${API}/programs/${program.id}/images`).then(r => r.json()).then(d => setProgramImages(d.images || [])).catch(() => {});
    setTeamLeads(typeof program.team_leads === 'object' && program.team_leads ? program.team_leads : {});
    setLaneDris(typeof program.lane_dris === 'object' && program.lane_dris ? program.lane_dris : {});
    setLaneDriInput({});
    // Close any open panels so prior program's data doesn't bleed through
    setLaneViewOpen(false);
    setWorkspaceView(null);
    setWorkspaceTab('gates');
    setWorkspaceNotes('');
    setLaneViewData({ gates: [], milestones: {} });
    setTaskResources({});
    setExpandedGateInLane(null);
    setExpandedWorkspaceTask(null);
  };

  const fetchMyTasks = () => {
    setMyTasksLoading(true);
    authFetch(`${API}/my-tasks`)
      .then(r => r.json())
      .then(data => setMyTasks(Array.isArray(data) ? data : []))
      .catch(() => setMyTasks([]))
      .finally(() => setMyTasksLoading(false));
  };

  const fetchMyLaneActions = () => {
    setLaneActionsLoading(true);
    authFetch(`${API}/my-lane-actions`)
      .then(r => r.json())
      .then(data => setMyLaneActions(Array.isArray(data) ? data : []))
      .catch(() => setMyLaneActions([]))
      .finally(() => setLaneActionsLoading(false));
  };

  const completeLaneAction = (actionId) => {
    authFetch(`${API}/lane-actions/${actionId}/complete`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: true }),
    }).then(() => {
      setMyLaneActions(prev => prev.filter(a => a.id !== actionId));
      setProgramLaneActions(prev => prev.map(a => a.id === actionId ? { ...a, completed: true } : a));
    }).catch(() => {});
  };

  const fetchProgramLaneActions = (programId) => {
    authFetch(`${API}/programs/${programId}/lane-actions`)
      .then(r => r.json())
      .then(data => setProgramLaneActions(Array.isArray(data) ? data : []))
      .catch(() => setProgramLaneActions([]));
  };

  const fetchSuperAdminUsers = () => {
    setSuperAdminLoading(true);
    authFetch(`${API}/superadmin/users`)
      .then(r => r.json())
      .then(data => setSuperAdminUsers(Array.isArray(data) ? data : []))
      .catch(() => setSuperAdminUsers([]))
      .finally(() => setSuperAdminLoading(false));
  };

  const superAdminResetPassword = (user) => {
    setSuperAdminWorking(true);
    authFetch(`${API}/superadmin/users/${user.id}/reset-password`, { method: 'POST' })
      .then(r => r.json())
      .then(d => { setSuperAdminAction(null); alert(d.error ? `Error: ${d.error}` : `Password reset email sent to ${d.email}`); })
      .finally(() => setSuperAdminWorking(false));
  };

  const superAdminRemoveUser = (user) => {
    setSuperAdminWorking(true);
    authFetch(`${API}/superadmin/users/${user.id}`, { method: 'DELETE' })
      .then(r => r.json())
      .then(d => {
        setSuperAdminAction(null);
        if (d.error) { alert(`Error: ${d.error}`); return; }
        setSuperAdminUsers(prev => prev.filter(u => u.id !== user.id));
      })
      .finally(() => setSuperAdminWorking(false));
  };

  const completeMyTask = (milestoneId) => {
    authFetch(`${API}/milestones/${milestoneId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: true }),
    })
      .then(() => setMyTasks(prev => prev.filter(t => t.id !== milestoneId)))
      .catch(() => {});
  };

  const loadMilestones = (gateStatusId) => {
    setMilestonesLoading(true);
    if (demoMode) {
      return fetch(`${API}/demo/gates/${gateStatusId}/milestones`)
        .then(r => r.json())
        .then(data => { const ms = Array.isArray(data) ? data : []; setMilestones(ms); return ms; })
        .catch(() => { setMilestones([]); return []; })
        .finally(() => setMilestonesLoading(false));
    }
    return authFetch(`${API}/gates/${gateStatusId}/milestones`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) { setMilestones([]); return []; }
        if (data.length === 0) {
          return authFetch(`${API}/gates/${gateStatusId}/milestones/seed`, { method: 'POST' })
            .then(r => r.json())
            .then(seeded => { const ms = Array.isArray(seeded) ? seeded : []; setMilestones(ms); return ms; });
        }
        setMilestones(data);
        return data;
      })
      .catch(() => { setMilestones([]); return []; })
      .finally(() => setMilestonesLoading(false));
  };

  const openEdit = (gs) => {
    setConsDetailPanel(null);
    setEditingGate(gs);
    setEditForm({
      status: gs.status,
      completion_percent: gs.completion_percent,
      notes: gs.notes || '',
      days_delayed: gs.days_delayed || 0,
      cost_impact: gs.cost_impact || 0,
      planned_close_date: gs.planned_close_date || '',
      blocker_category: gs.blocker_category || '',
    });
    setGateModalTab('milestones');
    setGateAiAnswer('');
    setGateAiQuestion('');
    setNewMilestoneText('');
    setExpandedMilestoneId(null);
    loadMilestones(gs.id).then(loadedMs => {
      if (gs.gate_number <= 1 || demoMode) return;
      const g1 = (selectedProgram.gates || []).find(g => g.lane === gs.lane && g.gate_number === 1);
      if (!g1) return;
      authFetch(`${API}/gates/${g1.id}/milestones`)
        .then(r => r.json())
        .then(g1Ms => {
          if (!Array.isArray(g1Ms)) return;
          loadedMs.forEach(m => {
            if (m.dri_user_id || (m.dri && m.dri.trim())) return;
            const g1m = g1Ms.find(gm => gm.sort_order === m.sort_order);
            if (g1m?.dri_user_id) {
              updateMilestoneField(m.id, 'dri_user_id', g1m.dri_user_id);
              if (g1m.dri) updateMilestoneField(m.id, 'dri', g1m.dri);
            }
          });
        })
        .catch(() => {});
    });
  };

  const saveGate = () => {
    const wasNotComplete = editingGate.status !== 'complete';
    const nowComplete = editForm.status === 'complete';
    setSaving(true);
    authFetch(`${API}/gates/${editingGate.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    })
      .then(() => refreshProgram(selectedProgram.id))
      .then(() => {
        setSaving(false);
        if (wasNotComplete && nowComplete) {
          setGateModalTab('ai');
          askGateAi(true);
        }
      })
      .catch(() => setSaving(false));
  };

  const toggleMilestone = (milestone) => {
    authFetch(`${API}/milestones/${milestone.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: !milestone.completed }),
    })
      .then(r => r.json())
      .then(data => {
        setMilestones(prev => prev.map(m => m.id === data.id ? data : m));
        // Refresh program so Timeline, Readiness, and Consequences reflect the new completion %
        refreshProgram(selectedProgram.id);
      })
      .catch(() => {});
  };

  const updateMilestoneField = (milestoneId, field, value) => {
    authFetch(`${API}/milestones/${milestoneId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    })
      .then(r => r.json())
      .then(data => setMilestones(prev => prev.map(m => m.id === data.id ? data : m)))
      .catch(() => {});
  };

  const addMilestone = () => {
    if (!newMilestoneText.trim() || !editingGate) return;
    authFetch(`${API}/gates/${editingGate.id}/milestones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: newMilestoneText }),
    })
      .then(r => r.json())
      .then(data => {
        setMilestones(prev => [...prev, data]);
        setNewMilestoneText('');
        refreshProgram(selectedProgram.id);
      })
      .catch(() => {});
  };

  const deleteMilestone = (milestoneId) => {
    authFetch(`${API}/milestones/${milestoneId}`, { method: 'DELETE' })
      .then(() => {
        setMilestones(prev => prev.filter(m => m.id !== milestoneId));
        refreshProgram(selectedProgram.id);
      })
      .catch(() => {});
  };

  const askGateAi = (autoReview = false) => {
    if (!editingGate) return;
    if (!autoReview && !gateAiQuestion.trim()) return;
    setGateAiLoading(true);
    setGateAiAnswer('');
    authFetch(`${API}/gates/${editingGate.id}/ai-review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: autoReview ? '' : gateAiQuestion }),
    })
      .then(r => r.json())
      .then(data => {
        setGateAiAnswer(data.answer || data.error || 'No response.');
        setGateAiLoading(false);
        if (!autoReview) setGateAiQuestion('');
      })
      .catch(() => { setGateAiAnswer('Unable to reach Nomos.'); setGateAiLoading(false); });
  };

  const askNomos = (q) => {
    const text = q || question;
    if (!text.trim() || !selectedProgram) return;
    setAiLoading(true);
    setAiAnswer('');
    if (q) setQuestion(q);
    authFetch(`${API}/programs/${selectedProgram.id}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: text, user_lane: userLane || null }),
    })
      .then(r => r.json())
      .then(data => { setAiAnswer(data.answer || data.error || 'No response.'); setAiLoading(false); })
      .catch(() => { setAiAnswer('Unable to reach Nomos. Check your connection.'); setAiLoading(false); });
  };

  const generateExecSummary = async () => {
    if (!selectedProgram) return;
    const programId = selectedProgram.id;
    setExecSummaryLoading(true);
    setExecSummaryError('');
    let headers = {};
    if (!demoMode) {
      const { data: { session: s } } = await supabase.auth.getSession();
      const token = s?.access_token || _nomosToken || sessionRef.current?.access_token;
      if (!token) { setExecSummaryError('Not signed in — please refresh and sign in again.'); setExecSummaryLoading(false); return; }
      headers['Authorization'] = `Bearer ${token}`;
    }
    const url = demoMode ? `${API}/demo/programs/${programId}/exec-summary` : `${API}/programs/${programId}/exec-summary`;
    fetch(url, { method: 'POST', headers })
      .then(r => r.json().then(data => ({ ok: r.ok, status: r.status, data })))
      .then(({ ok, status, data }) => {
        if (!ok || data.error) throw new Error(data.error || `HTTP ${status}`);
        setExecSummary(data.summary || '');
        setExecActions(data.actions || []);
        setExecSummaryLoading(false);
      })
      .catch(err => {
        setExecSummaryError(`Summary failed: ${err.message}`);
        setExecSummaryLoading(false);
      });
  };

  const generateRiskIntel = async () => {
    if (!selectedProgram) return;
    const programId = selectedProgram.id;
    setRiskIntelLoading(true);
    let headers = {};
    if (!demoMode) {
      const { data: { session: s } } = await supabase.auth.getSession();
      const token = s?.access_token || _nomosToken || sessionRef.current?.access_token;
      if (!token) { setRiskIntelLoading(false); return; }
      headers['Authorization'] = `Bearer ${token}`;
    }
    const url = demoMode ? `${API}/demo/programs/${programId}/risk-intelligence` : `${API}/programs/${programId}/risk-intelligence`;
    fetch(url, { method: 'POST', headers })
      .then(r => r.json().then(data => ({ ok: r.ok, status: r.status, data })))
      .then(({ ok, status, data }) => {
        if (!ok || data.error) { console.error('risk-intelligence:', data.error || `HTTP ${status}`); }
        setRiskIntel(data.risks || []);
        setRiskIntelLoading(false);
      })
      .catch(err => { console.error('risk-intelligence fetch:', err.message); setRiskIntelLoading(false); });
  };

  const generateAdaptive = async () => {
    if (!selectedProgram || demoMode) return;
    setAdaptiveLoading(true);
    setAdaptiveError('');
    setAdaptiveData(null);
    authFetch(`${API}/programs/${selectedProgram.id}/adaptive-analysis`, { method: 'POST' })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setAdaptiveError(data.error); return; }
        setAdaptiveData(data);
      })
      .catch(err => setAdaptiveError(err.message))
      .finally(() => setAdaptiveLoading(false));
  };

  const patchProgram = (updates) => {
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    }).then(() => refreshProgram(selectedProgram.id));
  };

  // ── LANE VIEW ────────────────────────────────────────────────────────────────

  const fetchLaneData = async (pid, lane) => {
    const url = demoMode
      ? `${API}/demo/programs/${pid}/lane/${lane}`
      : `${API}/programs/${pid}/lane/${lane}`;
    const res = demoMode ? await fetch(url) : await authFetch(url);
    return res.json();
  };

  const openLaneView = async (lane, programId) => {
    const pid = programId || selectedProgram?.id;
    setLaneViewLane(lane);
    setLaneViewOpen(true);
    setLaneViewLoading(true);
    setExpandedTaskId(null);
    setExpandedGateInLane(null);
    setTaskResources({});
    const lead = teamLeads[lane];
    if (lead) { setCurrentUser(lead); localStorage.setItem('nomos_user', lead); }
    try {
      const data = await fetchLaneData(pid, lane);
      setLaneViewData({ gates: data?.gates || [], milestones: data?.milestones || {} });
      const activeGate = (data.gates || []).find(g => g.status !== 'complete' && g.status !== 'idle') || (data.gates || [])[0];
      if (activeGate) setExpandedGateInLane(activeGate.id);
    } catch (e) { console.error(e); }
    setLaneViewLoading(false);
  };

  const loadLaneData = async (lane, programId) => {
    const pid = programId || selectedProgram?.id;
    if (!pid) return;
    setLaneViewLoading(true);
    setExpandedGateInLane(null);
    setLaneViewData({ gates: [], milestones: {} });
    try {
      const data = await fetchLaneData(pid, lane);
      setLaneViewData({ gates: data?.gates || [], milestones: data?.milestones || {} });
      const activeGate = (data.gates || []).find(g => g.status !== 'complete' && g.status !== 'idle') || (data.gates || [])[0];
      if (activeGate) setExpandedGateInLane(activeGate.id);
    } catch (e) { console.error(e); }
    setLaneViewLoading(false);
  };

  const openWorkspace = (lane, programId) => {
    setWorkspaceView(lane);
    setWorkspaceTab('gates');
    const tw = (selectedProgram?.team_workspace || {})[lane] || {};
    setWorkspaceNotes(tw.notes || '');
    // Scope the approver name to this lane's team lead — prevents cross-lane sign-off bleed
    const lead = teamLeads[lane];
    if (lead) { setCurrentUser(lead); localStorage.setItem('nomos_user', lead); }
    else { setCurrentUser(''); localStorage.removeItem('nomos_user'); }
    loadLaneData(lane, programId);
    loadMailbox(lane, programId);
  };

  const loadMailbox = (lane, programId) => {
    if (!session || !programId) return;
    setMailboxLoading(true);
    setSelectedMessage(null);
    authFetch(`${API}/programs/${programId}/messages`)
      .then(r => r.json()).then(data => { setMailboxMessages(Array.isArray(data) ? data : []); setMailboxLoading(false); })
      .catch(() => setMailboxLoading(false));
    // Mark as read
    authFetch(`${API}/programs/${programId}/messages/read`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
    // Refresh unread count
    authFetch(`${API}/programs/${programId}/messages/unread`).then(r => r.json()).then(d => setMailboxUnread(d || {})).catch(() => {});
  };

  const loadMailboxUnread = (programId) => {
    if (!session || !programId) return;
    authFetch(`${API}/programs/${programId}/messages/unread`).then(r => r.json()).then(d => setMailboxUnread(d || {})).catch(() => {});
  };

  const sendMessage = async () => {
    if (!composeBody.trim() || !composeTo) return;
    setComposeSending(true);
    const recipient = teamMembers.find(m => m.id === composeTo);
    const isUserId = !!recipient;
    try {
      const res = await authFetch(`${API}/programs/${selectedProgram.id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_user_id: isUserId ? composeTo : null,
          to_name: isUserId ? (recipient?.full_name || '') : composeTo,
          to_lane: recipient?.lane || '',
          from_lane: workspaceView || userLane || '',
          subject: composeSubject,
          body: composeBody,
        }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to send message.'); setComposeSending(false); return; }
      setComposeSending(false); setComposeOpen(false); setComposeTo(''); setComposeSubject(''); setComposeBody('');
      if (editingDraftId) {
        const updated = drafts.filter(d => d.id !== editingDraftId);
        setDrafts(updated); localStorage.setItem('nomos_drafts', JSON.stringify(updated));
        setEditingDraftId(null);
      }
      setMailboxSection('sent');
      loadMailbox(workspaceView || userLane, selectedProgram.id);
    } catch (e) {
      alert('Unable to send message. Check your connection.');
      setComposeSending(false);
    }
  };

  const saveDraft = () => {
    if (!composeTo && !composeSubject && !composeBody.trim()) return;
    const draft = { id: editingDraftId || Date.now().toString(), to: composeTo, subject: composeSubject, body: composeBody, savedAt: new Date().toISOString(), programId: selectedProgram?.id };
    const updated = editingDraftId ? drafts.map(d => d.id === editingDraftId ? draft : d) : [...drafts, draft];
    setDrafts(updated); localStorage.setItem('nomos_drafts', JSON.stringify(updated));
    setComposeOpen(false); setEditingDraftId(null); setMailboxSection('drafts');
  };

  const deleteDraft = (id) => {
    const updated = drafts.filter(d => d.id !== id);
    setDrafts(updated); localStorage.setItem('nomos_drafts', JSON.stringify(updated));
  };

  const openDraft = (draft) => {
    setComposeTo(draft.to); setComposeSubject(draft.subject); setComposeBody(draft.body);
    setEditingDraftId(draft.id); setComposeOpen(true); setMailboxSection('inbox');
  };

  const addProgramMember = async (userId, lane, role) => {
    const r = await authFetch(`${API}/programs/${selectedProgram.id}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, lane, role }),
    });
    const newMember = await r.json();
    if (newMember.id) {
      setProgramMembers(prev => {
        const filtered = prev.filter(m => !(m.user_id === userId && m.lane === lane));
        const updated = [...filtered, newMember];
        syncTeamFromMembers(updated);
        return updated;
      });
    }
  };

  const removeProgramMember = async (memberId) => {
    await authFetch(`${API}/program-members/${memberId}`, { method: 'DELETE' });
    setProgramMembers(prev => {
      const updated = prev.filter(m => m.id !== memberId);
      syncTeamFromMembers(updated);
      return updated;
    });
  };

  const promoteProgramMember = async (memberId) => {
    const r = await authFetch(`${API}/program-members/${memberId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: 'lead' }),
    });
    const updated = await r.json();
    if (updated.id) {
      setProgramMembers(prev => {
        const next = prev.map(m => {
          if (m.id === memberId) return { ...m, role: 'lead' };
          if (m.lane === updated.lane) return { ...m, role: 'member' }; // demote old lead
          return m;
        });
        syncTeamFromMembers(next);
        return next;
      });
    }
  };

  const syncTeamFromMembers = (members) => {
    const leads = {}, dris = {};
    members.forEach(pm => {
      const name = pm.profiles?.full_name || '';
      if (!name) return;
      if (pm.role === 'lead') leads[pm.lane] = name;
      else { if (!dris[pm.lane]) dris[pm.lane] = []; dris[pm.lane].push(name); }
    });
    setTeamLeads(prev => ({ ...prev, ...leads }));
    setLaneDris(prev => ({ ...prev, ...dris }));
  };

  const toggleWorkspaceMilestone = (milestone, gateId) => {
    authFetch(`${API}/milestones/${milestone.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: !milestone.completed }),
    })
      .then(r => r.json())
      .then(data => {
        setLaneViewData(prev => ({
          ...prev,
          milestones: { ...prev.milestones, [gateId]: (prev.milestones[gateId] || []).map(m => m.id === data.id ? data : m) },
        }));
        refreshProgram(selectedProgram.id);
      });
  };

  const saveWorkspaceNotes = () => {
    setWorkspaceNotesSaving(true);
    const current = selectedProgram?.team_workspace || {};
    const updated = { ...current, [workspaceView]: { ...(current[workspaceView] || {}), notes: workspaceNotes } };
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_workspace: updated }),
    }).then(() => refreshProgram(selectedProgram.id)).finally(() => setWorkspaceNotesSaving(false));
  };

  const saveWorkspaceDocs = (docs) => {
    setWorkspaceDocsSaving(true);
    const current = selectedProgram?.team_workspace || {};
    const updated = { ...current, [workspaceView]: { ...(current[workspaceView] || {}), documents: docs } };
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_workspace: updated }),
    }).then(() => refreshProgram(selectedProgram.id)).finally(() => setWorkspaceDocsSaving(false));
  };

  const loadTaskResources = async (milestoneId) => {
    if (demoMode || taskResources[milestoneId] !== undefined) return;
    try {
      const res = await authFetch(`${API}/milestones/${milestoneId}/resources`);
      const data = await res.json();
      setTaskResources(prev => ({ ...prev, [milestoneId]: Array.isArray(data) ? data : [] }));
    } catch (e) { setTaskResources(prev => ({ ...prev, [milestoneId]: [] })); }
  };

  const signOffTask = async (milestoneId, gateId, note = '') => {
    try {
      const res = await authFetch(`${API}/milestones/${milestoneId}/signoff`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signed_off_by: myFullName || 'Unknown', approval_note: note || undefined }),
      });
      const updated = await res.json();
      if (updated?.error) return;
      setLaneViewData(prev => {
        const newMs = (prev.milestones[gateId] || []).map(m => m.id === milestoneId ? updated : m);
        const pct = newMs.length ? Math.round(newMs.filter(m => m.completed).length / newMs.length * 100) : 0;
        return {
          ...prev,
          milestones: { ...prev.milestones, [gateId]: newMs },
          gates: (prev.gates || []).map(g => g.id === gateId ? { ...g, completion_percent: pct } : g),
        };
      });
      if (selectedProgram?.id) refreshProgram(selectedProgram.id);
    } catch {}
  };

  const unsignOffTask = async (milestoneId, gateId) => {
    try {
      await authFetch(`${API}/milestones/${milestoneId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signed_off_by: null, signed_off_at: null, approval_note: null }),
      });
      setLaneViewData(prev => ({
        ...prev,
        milestones: {
          ...prev.milestones,
          [gateId]: (prev.milestones[gateId] || []).map(m =>
            m.id === milestoneId ? { ...m, signed_off_by: null, signed_off_at: null, approval_note: null } : m
          ),
        },
      }));
      if (selectedProgram?.id) refreshProgram(selectedProgram.id);
    } catch {}
  };

  const updateTaskField = async (milestoneId, gateId, field, value) => {
    try {
      const res = await authFetch(`${API}/milestones/${milestoneId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      const updated = await res.json();
      if (updated?.error) return;
      setLaneViewData(prev => {
        const newMs = (prev.milestones[gateId] || []).map(m => m.id === milestoneId ? updated : m);
        const pct = newMs.length ? Math.round(newMs.filter(m => m.completed).length / newMs.length * 100) : 0;
        return {
          ...prev,
          milestones: { ...prev.milestones, [gateId]: newMs },
          gates: (prev.gates || []).map(g => g.id === gateId ? { ...g, completion_percent: pct } : g),
        };
      });
      if (selectedProgram?.id) refreshProgram(selectedProgram.id);
    } catch {}
  };

  const addTaskResource = async (milestoneId) => {
    if (!newResourceLabel.trim() || !newResourceUrl.trim()) return;
    const url = newResourceUrl.startsWith('http') ? newResourceUrl : `https://${newResourceUrl}`;
    const res = await authFetch(`${API}/milestones/${milestoneId}/resources`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: newResourceLabel.trim(), url, resource_type: 'link' }),
    });
    const created = await res.json();
    setTaskResources(prev => ({ ...prev, [milestoneId]: [...(prev[milestoneId] || []), created] }));
    setNewResourceLabel(''); setNewResourceUrl(''); setAddingResourceTo(null);
  };

  const deleteTaskResource = async (resourceId, milestoneId) => {
    await authFetch(`${API}/resources/${resourceId}`, { method: 'DELETE' });
    setTaskResources(prev => ({ ...prev, [milestoneId]: (prev[milestoneId] || []).filter(r => r.id !== resourceId) }));
  };

  const toggleLock = (field) => {
    const updated = { ...lockedFields, [field]: !lockedFields[field] };
    setLockedFields(updated);
    localStorage.setItem(`nomos_locks_${selectedProgram.id}`, JSON.stringify(updated));
  };

  const toggleMarket = (market) => {
    const current = (selectedProgram.markets || '').split(',').map(m => m.trim()).filter(Boolean);
    const updated = current.includes(market) ? current.filter(m => m !== market) : [...current, market];
    patchProgram({ markets: updated.join(', ') });
  };

  const addCustomMarket = () => {
    const val = customMarket.trim().toUpperCase();
    if (!val) return;
    const current = (selectedProgram.markets || '').split(',').map(m => m.trim()).filter(Boolean);
    if (!current.includes(val)) patchProgram({ markets: [...current, val].join(', ') });
    setCustomMarket('');
  };

  const fetchResolution = (key, c) => {
    if (resolutionLoading[key]) return;
    setResolutionLoading(prev => ({ ...prev, [key]: true }));
    setExpandedResolution(key);
    authFetch(`${API}/programs/${selectedProgram.id}/resolution`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceLane: c.sourceLane, sourceGate: c.sourceGate, targetLane: c.targetLane, targetGate: c.targetGate }),
    })
      .then(r => r.json())
      .then(data => {
        setResolutions(prev => ({ ...prev, [key]: data }));
        setResolutionLoading(prev => ({ ...prev, [key]: false }));
      })
      .catch(() => setResolutionLoading(prev => ({ ...prev, [key]: false })));
  };

  const generateBlueprintForProgram = () => {
    if (!selectedProgram) return;
    setBlueprintModalOpen(false);
    setBlueprintGenerating(true);
    authFetch(`${API}/programs/${selectedProgram.id}/generate-blueprint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mass_production_date: blueprintMpDate || null }),
    })
      .then(() => {
        setBlueprintGenerating(false);
        setShowBlueprintBanner(true);
        setBlueprintMpDate('');
        // Reload gate data so new milestones appear
        if (editingGate) loadMilestones(editingGate.id);
      })
      .catch(() => {
        setBlueprintGenerating(false);
        setBlueprintMpDate('');
      });
  };

  const runInception = () => {
    if (!selectedProgram) return;
    setInceptionLoading(true);
    setInceptionAnalysis('');
    authFetch(`${API}/programs/${selectedProgram.id}/ai-inception`, { method: 'POST' })
      .then(r => r.json())
      .then(data => { setInceptionAnalysis(data.analysis || data.error || ''); setInceptionLoading(false); })
      .catch(() => setInceptionLoading(false));
  };

  const createProgram = () => {
    if (!createForm.name.trim() || !createForm.launch_date || !createForm.budget_total) return;
    setCreateSaving(true);
    const body = {
      ...createForm,
      budget_total: parseFloat(createForm.budget_total),
      num_locations: parseInt(createForm.num_locations) || 0,
      launched_locations: parseInt(createForm.launched_locations) || 0,
      unit_cost: parseFloat(createForm.unit_cost) || 0,
      units_per_location: parseInt(createForm.units_per_location) || 2,
      avg_daily_revenue_per_location: parseFloat(createForm.avg_daily_revenue_per_location) || 0,
      expected_uplift_percent: parseFloat(createForm.expected_uplift_percent) || 0,
      retrofit_cost_per_location: parseFloat(createForm.retrofit_cost_per_location) || 750,
    };
    authFetch(`${API}/programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(newProgram => {
        setPrograms(prev => [...prev, newProgram]);
        setCreating(false);
        setCreateForm({
          name: '', launch_date: '', budget_total: '',
          num_locations: '', launched_locations: '', unit_cost: '',
          units_per_location: '2', avg_daily_revenue_per_location: '',
          expected_uplift_percent: '2', retrofit_cost_per_location: '750',
          markets: '', program_start_date: '',
        });
        setCreateSaving(false);
        selectProgram(newProgram);
      })
      .catch(() => setCreateSaving(false));
  };

  const sendTestEmail = () => {
    setTestEmailSending(true);
    setTestEmailResult(null);
    authFetch(`${API}/test-email`, { method: 'POST' })
      .then(r => r.json())
      .then(d => setTestEmailResult(d.error ? { ok: false, msg: d.error } : { ok: true, msg: `Sent to ${d.sentTo}` }))
      .catch(e => setTestEmailResult({ ok: false, msg: e.message }))
      .finally(() => setTestEmailSending(false));
  };

  const saveSlackWebhook = () => {
    setSlackSaving(true);
    setSlackResult(null);
    authFetch(`${API}/company/webhook`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slack_webhook_url: slackWebhook.trim() || null }) })
      .then(r => r.json())
      .then(d => setSlackResult(d.error ? { ok: false, msg: d.error } : { ok: true, msg: slackWebhook.trim() ? 'Webhook saved — gate alerts will now post to Slack/Teams.' : 'Webhook cleared.' }))
      .catch(e => setSlackResult({ ok: false, msg: e.message }))
      .finally(() => setSlackSaving(false));
  };

  const addInviteRow = () => setInviteRows(r => [...r, { id: Date.now(), name: '', email: '', lane: '', role: 'member', existing: false }]);
  const removeInviteRow = (id) => setInviteRows(r => r.length > 1 ? r.filter(row => row.id !== id) : r);
  const updateInviteRow = (id, field, value) => setInviteRows(r => r.map(row => row.id === id ? { ...row, [field]: value } : row));

  const parsePaste = () => {
    const lines = invitePaste.split(/[\n]+/).map(l => l.trim()).filter(Boolean);
    const parsed = lines.map((line, i) => {
      const parts = line.split(/,|\t/).map(p => p.trim());
      const hasAt = parts.some(p => p.includes('@'));
      const emailPart = parts.find(p => p.includes('@')) || '';
      const namePart = parts.find(p => !p.includes('@')) || '';
      if (!emailPart && !namePart) return null;
      return { id: Date.now() + i, name: namePart, email: emailPart, lane: parts[2] || '', role: 'member', existing: false };
    }).filter(Boolean);
    if (parsed.length > 0) {
      setInviteRows(prev => {
        const filled = prev.filter(r => r.name || r.email);
        return [...filled, ...parsed, { id: Date.now() + 9999, name: '', email: '', lane: '', role: 'member', existing: false }];
      });
      setInvitePaste('');
      setInvitePasteOpen(false);
    }
  };

  const sendAllInvites = async () => {
    const valid = inviteRows.filter(r => r.name.trim() && r.email.trim() && !r.existing);
    if (!valid.length) return;
    setInviteSending(true);
    setInviteResults({});
    const results = {};
    await Promise.all(valid.map(async row => {
      try {
        const r = await authFetch(`${API}/invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: row.email.trim(), full_name: row.name.trim(), lane: row.lane || undefined, role: row.role || 'member' }) });
        const d = await r.json();
        results[row.id] = r.ok ? { ok: true } : { ok: false, msg: d.error || 'Failed' };
      } catch(e) { results[row.id] = { ok: false, msg: e.message }; }
    }));
    setInviteResults(results);
    setInviteSending(false);
    const anyFailed = Object.values(results).some(r => !r.ok);
    if (!anyFailed) {
      setInviteRows([{ id: Date.now(), name: '', email: '', lane: '', existing: false }]);
    } else {
      setInviteRows(prev => prev.filter(row => !results[row.id]?.ok));
    }
    authFetch(`${API}/team`).then(r => r.json()).then(d => { if (Array.isArray(d)) setTeamMembers(d); });
  };

  const loadProgramImages = () => {
    if (!selectedProgram) return;
    fetch(`${API}/programs/${selectedProgram.id}/images`)
      .then(r => r.json())
      .then(d => setProgramImages(d.images || []))
      .catch(() => {});
  };

  const uploadProductImage = async (file) => {
    if (!file || !selectedProgram) return;
    setImageUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    authFetch(`${API}/programs/${selectedProgram.id}/upload-image`, { method: 'POST', body: formData })
      .then(r => r.json())
      .then(d => {
        if (d.error) { alert('Upload failed: ' + d.error); return; }
        loadProgramImages();
      })
      .catch(e => alert('Upload failed: ' + e.message))
      .finally(() => setImageUploading(false));
  };

  const deleteImage = (imageId) => {
    authFetch(`${API}/program-images/${imageId}`, { method: 'DELETE' })
      .then(() => loadProgramImages())
      .catch(() => {});
  };

  const saveProgram = () => {
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(programForm),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.error) { alert('Save failed: ' + data.error); return; }
        refreshProgram(selectedProgram.id);
        setEditingProgram(false);
      })
      .catch(e => alert('Network error: ' + e.message));
  };

  const saveBrief = (overrideForm) => {
    const formToSave = overrideForm || briefForm;
    const finalDris = { ...laneDris };
    Object.entries(laneDriInput).forEach(([lane, text]) => {
      if (text && text.trim()) finalDris[lane] = [...(finalDris[lane] || []), text.trim()];
    });
    setLaneDris(finalDris);
    setLaneDriInput({});
    setBriefSaving(true);
    setBriefSaved(false);
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formToSave, team_leads: teamLeads, lane_dris: finalDris }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.error) return;
        setBriefSaved(true);
        setTimeout(() => setBriefSaved(false), 2500);
        return refreshProgram(selectedProgram.id);
      })
      .finally(() => setBriefSaving(false));
  };

  const saveTeams = () => {
    const finalDris = { ...laneDris };
    Object.entries(laneDriInput).forEach(([lane, text]) => {
      if (text && text.trim()) finalDris[lane] = [...(finalDris[lane] || []), text.trim()];
    });
    setLaneDris(finalDris);
    setLaneDriInput({});
    setTeamSaving(true);
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_leads: teamLeads, lane_dris: finalDris }),
    })
      .then(r => r.json())
      .then(data => { if (data?.error) alert('Save failed: ' + data.error); else refreshProgram(selectedProgram.id); })
      .finally(() => setTeamSaving(false));
  };

  const draftBrief = () => {
    setBriefDraftLoading(true);
    authFetch(`${API}/programs/${selectedProgram.id}/ai-draft-brief`, { method: 'POST' })
      .then(r => r.json())
      .then(data => {
        if (data.what_is_it) setBriefForm(prev => ({ ...prev, ...data }));
        setBriefDraftLoading(false);
      })
      .catch(() => setBriefDraftLoading(false));
  };


  const loadDemo = () => {
    setDemoLoading(true);
    authFetch(`${API}/demo/seed-sds`, { method: 'POST' })
      .then(r => r.json())
      .then(data => authFetch(`${API}/programs`).then(r => r.json()).then(all => {
        setPrograms(all);
        setDemoLoading(false);
        const demo = all.find(p => p.id === data.program_id);
        if (demo) selectProgram(demo);
      }))
      .catch(() => setDemoLoading(false));
  };

  const enterApp = () => {
    sessionStorage.setItem('nomos_entered', 'true');
    setLandingMode(false);
  };

  const openFirstRun = () => setShowFirstRun(true);

  const onFirstRunComplete = async (programId) => {
    setShowFirstRun(false);
    const all = await authFetch(`${API}/programs`).then(r => r.json()).catch(() => []);
    const progs = Array.isArray(all) ? all : [];
    setPrograms(progs);
    const newProg = progs.find(p => p.id === programId);
    if (newProg) selectProgram(newProg);
  };

  const openCreateModal = () => {
    setWizardStep(1);
    setWizardIntakeAnalyzed(false);
    setWizardBriefDone(false);
    setWizardBriefLoading(false);
    setWizardGateDates(['', '', '', '', '', '']);
    setBlueprintGenerating(false);
    setWizardData({
      name: '', launch_date: '', mass_production_date: '', program_start_date: '', markets: '', num_locations: '', budget_total: '',
      what_is_it: '', why_it_matters: '', end_vision: '', target_customer: '',
      unit_cost: '', units_per_location: '2', avg_daily_revenue_per_location: '',
      expected_uplift_percent: '2', retrofit_cost_per_location: '750', launched_locations: '0',
    });
    setIntakeText('');
    setCreating(true);
  };

  const enterAndExploreDemo = () => {
    setDemoMode(true);
    sessionStorage.setItem('nomos_entered', 'true');
    setLandingMode(false);
    setLoading(true);
    window.history.pushState({ page: 'programs' }, '');
    Promise.all([
      fetch(`${API}/demo/programs`).then(r => r.json()),
      fetch(`${API}/demo/portfolio`).then(r => r.json()).catch(() => null),
      fetch(`${API}/demo/notifications`).then(r => r.json()).catch(() => []),
    ]).then(([programs, portfolioData, notifData]) => {
      const progs = Array.isArray(programs) ? programs : [];
      setPrograms(progs);
      if (portfolioData) setPortfolio(portfolioData);
      if (Array.isArray(notifData)) setNotifications(notifData);
      setLoading(false);
      // Realtime subscription for demo notifications
      const ids = progs.map(p => p.id);
      if (ids.length > 0) {
        const channel = supabase.channel('demo-notif')
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `program_id=in.(${ids.join(',')})` },
            (payload) => setNotifications(prev => [payload.new, ...prev]))
          .subscribe();
        // Store cleanup ref — demo channel removed when user exits demo
        window.__demoNotifChannel = channel;
      }
    }).catch(() => setLoading(false));
  };

  const enterAndBuildProgram = () => {
    sessionStorage.setItem('nomos_entered', 'true');
    setLandingMode(false);
    openFirstRun();
  };

  const startVoiceFor = (fieldId, currentVal, setter) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input requires Chrome or Edge.'); return; }
    if (window._nomosRecognition) { window._nomosRecognition.stop(); window._nomosRecognition = null; }
    if (voiceFieldId === fieldId) { setVoiceFieldId(null); return; }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalSoFar = currentVal ? currentVal.trimEnd() + ' ' : '';
    recognition.onresult = (e) => {
      let interim = '';
      let newFinal = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) newFinal += e.results[i][0].transcript + ' ';
        else interim += e.results[i][0].transcript;
      }
      if (newFinal) finalSoFar += newFinal;
      setter(finalSoFar + interim);
    };
    recognition.onend = () => { setVoiceFieldId(null); setter(finalSoFar.trim()); };
    recognition.onerror = () => { setVoiceFieldId(null); };
    window._nomosRecognition = recognition;
    recognition.start();
    setVoiceFieldId(fieldId);
  };

  const runIntake = () => {
    if (!intakeText.trim()) return;
    setIntakeLoading(true);
    authFetch(`${API}/programs/ai-intake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: intakeText }),
    })
      .then(r => r.json())
      .then(data => {
        setCreateForm(prev => ({
          ...prev,
          name:                data.name                || prev.name,
          launch_date:         data.launch_date         || prev.launch_date,
          program_start_date:  data.program_start_date  || prev.program_start_date,
          markets:             data.markets             || prev.markets,
          num_locations:       data.num_locations ? String(data.num_locations) : prev.num_locations,
          budget_total:        data.budget_total  ? String(data.budget_total)  : prev.budget_total,
        }));
        if (data.what_is_it) setBriefForm(prev => ({ ...prev, what_is_it: data.what_is_it }));
        setIntakeFilled(true);
        setIntakeLoading(false);
        setCreateStep('form');
      })
      .catch(() => setIntakeLoading(false));
  };

  const runWizardIntake = () => {
    if (!intakeText.trim()) return;
    setIntakeLoading(true);
    authFetch(`${API}/programs/ai-intake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: intakeText }),
    })
      .then(r => r.json())
      .then(data => {
        setWizardData(prev => ({
          ...prev,
          name:               data.name               || prev.name,
          launch_date:        data.launch_date         || prev.launch_date,
          program_start_date: data.program_start_date  || prev.program_start_date,
          markets:            data.markets             || prev.markets,
          num_locations:      data.num_locations ? String(data.num_locations) : prev.num_locations,
          budget_total:       data.budget_total  ? String(data.budget_total)  : prev.budget_total,
          what_is_it:         data.what_is_it    || prev.what_is_it,
        }));
        setWizardIntakeAnalyzed(true);
        setIntakeLoading(false);
      })
      .catch(() => setIntakeLoading(false));
  };

  const draftWizardBrief = (data) => {
    setWizardBriefLoading(true);
    authFetch(`${API}/ai/draft-brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(r => r.json())
      .then(brief => {
        setWizardData(prev => ({
          ...prev,
          what_is_it:      brief.what_is_it      || prev.what_is_it,
          why_it_matters:  brief.why_it_matters   || prev.why_it_matters,
          end_vision:      brief.end_vision       || prev.end_vision,
          target_customer: brief.target_customer  || prev.target_customer,
        }));
        setWizardBriefDone(true);
        setWizardBriefLoading(false);
      })
      .catch(() => setWizardBriefLoading(false));
  };

  const calcWizardGateDates = (start, launch) => {
    try {
      const s = new Date(start), l = new Date(launch);
      if (isNaN(s) || isNaN(l) || l <= s) return [];
      const interval = (l - s) / 6;
      return Array.from({ length: 6 }, (_, i) => {
        const d = new Date(s.getTime() + interval * (i + 1));
        return d.toISOString().split('T')[0];
      });
    } catch { return []; }
  };

  const createFromWizard = () => {
    if (!wizardData.name.trim() || !wizardData.launch_date) return;
    setCreateSaving(true);
    const body = {
      name: wizardData.name,
      launch_date: wizardData.launch_date,
      program_start_date: wizardData.program_start_date,
      markets: wizardData.markets,
      budget_total: parseFloat(wizardData.budget_total) || 0,
      num_locations: parseInt(wizardData.num_locations) || 0,
      launched_locations: parseInt(wizardData.launched_locations) || 0,
      unit_cost: parseFloat(wizardData.unit_cost) || 0,
      units_per_location: parseInt(wizardData.units_per_location) || 2,
      avg_daily_revenue_per_location: parseFloat(wizardData.avg_daily_revenue_per_location) || 0,
      expected_uplift_percent: parseFloat(wizardData.expected_uplift_percent) || 0,
      retrofit_cost_per_location: parseFloat(wizardData.retrofit_cost_per_location) || 750,
      gate_dates: wizardGateDates.every(d => d) ? wizardGateDates : undefined,
    };
    authFetch(`${API}/programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(newProgram => {
        if (newProgram.error || !newProgram.id) { setCreateSaving(false); return; }
        const brief = {};
        if (wizardData.what_is_it)      brief.what_is_it      = wizardData.what_is_it;
        if (wizardData.why_it_matters)  brief.why_it_matters  = wizardData.why_it_matters;
        if (wizardData.end_vision)      brief.end_vision      = wizardData.end_vision;
        if (wizardData.target_customer) brief.target_customer = wizardData.target_customer;
        const patchBrief = Object.keys(brief).length
          ? authFetch(`${API}/programs/${newProgram.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(brief) })
          : Promise.resolve();
        return patchBrief.then(() => {
          setPrograms(prev => {
            if (prev.length === 0) {
              localStorage.setItem('nomos_invite_nudge', '1');
              setShowInviteNudge(true);
              localStorage.removeItem('nomos_new_admin');
              setShowWelcome(false);
            }
            return [...prev, newProgram];
          });
          setCreating(false);
          setCreateSaving(false);
          // Phase 2: generate blueprint
          setBlueprintGenerating(true);
          authFetch(`${API}/programs/${newProgram.id}/generate-blueprint`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mass_production_date: wizardData.mass_production_date || null }),
          })
            .then(() => {
              setBlueprintGenerating(false);
              setShowBlueprintBanner(true);
              selectProgram(newProgram);
            })
            .catch(() => {
              setBlueprintGenerating(false);
              selectProgram(newProgram);
            });
        });
      })
      .catch(() => setCreateSaving(false));
  };

  const goToLanding = () => {
    sessionStorage.removeItem('nomos_entered');
    setLandingMode(true);
    setDemoMode(false);
    setSelectedProgram(null);
    setConsequences(null);
    setAskPanelOpen(false);
    setNotifications([]);
    if (window.__demoNotifChannel) { supabase.removeChannel(window.__demoNotifChannel); window.__demoNotifChannel = null; }
    window.history.pushState({ page: 'landing' }, '');
  };

  const deleteProgram = () => {
    if (!window.confirm(`Delete "${selectedProgram.name}"? This cannot be undone.`)) return;
    authFetch(`${API}/programs/${selectedProgram.id}`, { method: 'DELETE' })
      .then(() => {
        setPrograms(prev => prev.filter(p => p.id !== selectedProgram.id));
        setSelectedProgram(null);
        setConsequences(null);
      });
  };

  const markAsLaunched = (reopen = false) => {
    const newStatus = reopen ? 'on_track' : 'complete';
    const msg = reopen
      ? `Reopen "${selectedProgram.name}" as active?`
      : `Mark "${selectedProgram.name}" as launched? This will move it to your launch archive.`;
    if (!window.confirm(msg)) return;
    authFetch(`${API}/programs/${selectedProgram.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    }).then(r => r.json()).then(updated => {
      if (updated?.id) {
        setPrograms(prev => prev.map(p => p.id === updated.id ? { ...p, status: newStatus } : p));
        setSelectedProgram(prev => ({ ...prev, status: newStatus }));
      }
    });
  };

  const duplicateProgram = async () => {
    if (!selectedProgram || duplicating) return;
    setDuplicating(true);
    const name = duplicateName.trim() || `${selectedProgram.name} (Copy)`;
    const res = await authFetch(`${API}/programs/${selectedProgram.id}/duplicate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const newProg = await res.json();
    setDuplicating(false);
    setDuplicateOpen(false);
    setDuplicateName('');
    if (newProg?.id) {
      setPrograms(prev => [newProg, ...prev]);
      const full = await authFetch(`${API}/programs/${newProg.id}`).then(r => r.json());
      selectProgram(full);
    }
  };

  const exportGateReport = () => {
    if (!selectedProgram) return;
    const lanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
    const gateNames = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
    const statusColor = { blocked: '#ef4444', at_risk: '#f59e0b', on_track: '#10b981', complete: '#6366f1', not_started: '#94a3b8' };
    const statusLabel = { blocked: 'BLOCKED', at_risk: 'AT RISK', on_track: 'ON TRACK', complete: 'COMPLETE', not_started: 'NOT STARTED' };
    const getGs = (lane, gate) => (selectedProgram.gates || []).find(g => g.lane === lane && g.gate_number === gate);
    const fm = consequences?.financial_model || {};
    const dr = daysRemaining(selectedProgram.launch_date);

    const gateGrid = lanes.map(lane => {
      const row = gateNames.map((_, gi) => {
        const gs = getGs(lane, gi + 1);
        const st = gs?.status || 'not_started';
        const pct = gs?.completion_percent || 0;
        const col = statusColor[st] || '#94a3b8';
        return `<td style="padding:8px 10px;border:1px solid #e2e8f0;text-align:center;vertical-align:middle;">
          <div style="display:inline-block;background:${col}22;border:1px solid ${col}66;border-radius:4px;padding:4px 8px;font-size:10px;color:${col};font-weight:700;letter-spacing:1px;white-space:nowrap;">${st === 'not_started' ? '—' : statusLabel[st]}</div>
          ${pct > 0 ? `<div style="margin-top:4px;font-size:9px;color:#64748b;">${pct}%</div>` : ''}
        </td>`;
      }).join('');
      return `<tr><td style="padding:8px 12px;font-weight:700;font-size:11px;letter-spacing:1px;color:#1e293b;background:#f8fafc;border:1px solid #e2e8f0;">${lane.toUpperCase()}</td>${row}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Gate Status Report — ${selectedProgram.name}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1e293b; background: #fff; padding: 48px; font-size: 13px; }
  @media print {
    body { padding: 24px; }
    .no-print { display: none !important; }
    @page { margin: 1.5cm; }
  }
  h1 { font-size: 28px; font-weight: 800; letter-spacing: -0.5px; color: #0f172a; }
  .meta { display: flex; gap: 32px; margin: 20px 0 32px; flex-wrap: wrap; }
  .meta-item { }
  .meta-label { font-size: 9px; letter-spacing: 2px; color: #64748b; margin-bottom: 4px; font-family: monospace; }
  .meta-value { font-size: 16px; font-weight: 700; color: #0f172a; }
  .section-title { font-size: 10px; letter-spacing: 3px; color: #64748b; font-family: monospace; margin: 32px 0 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th { padding: 10px 12px; background: #0f172a; color: #fff; font-size: 10px; letter-spacing: 1.5px; font-family: monospace; text-align: center; border: 1px solid #0f172a; }
  th:first-child { text-align: left; min-width: 110px; }
  .fin-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 8px; }
  .fin-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
  .fin-label { font-size: 9px; letter-spacing: 2px; color: #64748b; font-family: monospace; margin-bottom: 6px; }
  .fin-value { font-size: 20px; font-weight: 800; color: #0f172a; }
  .logo { display: flex; align-items: center; gap: 10px; margin-bottom: 32px; }
  .logo-box { width: 32px; height: 32px; background: #0891b2; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #000; font-weight: 900; font-size: 16px; }
  .logo-text { font-family: monospace; font-size: 16px; font-weight: 700; letter-spacing: 3px; }
  .status-badge { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 10px; font-weight: 700; letter-spacing: 1.5px; font-family: monospace; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; font-family: monospace; letter-spacing: 1px; }
  .print-btn { position: fixed; bottom: 24px; right: 24px; background: #0891b2; color: #000; border: none; padding: 12px 24px; font-family: monospace; font-size: 11px; letter-spacing: 2px; font-weight: 700; cursor: pointer; border-radius: 6px; }
  .alerts { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 16px; margin-bottom: 8px; }
  .alert-row { display: flex; gap: 10px; align-items: flex-start; padding: 6px 0; border-bottom: 1px solid #fed7aa22; }
  .alert-row:last-child { border-bottom: none; }
</style>
</head>
<body>
<div class="logo">
  <div class="logo-box">N</div>
  <span class="logo-text">NOMOS</span>
  <span style="font-family:monospace;font-size:10px;letter-spacing:2px;color:#64748b;margin-left:4px;">LAUNCH OS</span>
</div>

<h1>${selectedProgram.name}</h1>
<div style="margin-top:8px;">
  <span class="status-badge" style="background:${statusColor[selectedProgram.status] || '#10b981'}22;color:${statusColor[selectedProgram.status] || '#10b981'};border:1px solid ${statusColor[selectedProgram.status] || '#10b981'}66;">
    ${(statusLabel[selectedProgram.status] || 'ON TRACK')}
  </span>
</div>

<div class="meta">
  <div class="meta-item"><div class="meta-label">LAUNCH DATE</div><div class="meta-value">${selectedProgram.launch_date || '—'}</div></div>
  <div class="meta-item"><div class="meta-label">T-MINUS</div><div class="meta-value" style="color:${dr < 60 ? '#ef4444' : '#0891b2'}">${dr > 0 ? dr + ' days' : 'PAST DUE'}</div></div>
  <div class="meta-item"><div class="meta-label">CURRENT GATE</div><div class="meta-value">G${selectedProgram.current_gate} — ${gateNames[(selectedProgram.current_gate || 1) - 1] || ''}</div></div>
  <div class="meta-item"><div class="meta-label">BUDGET</div><div class="meta-value">$${((selectedProgram.budget_total || 0) / 1000000).toFixed(1)}M</div></div>
  <div class="meta-item"><div class="meta-label">LOCATIONS</div><div class="meta-value">${(selectedProgram.num_locations || 0).toLocaleString()}</div></div>
  <div class="meta-item"><div class="meta-label">MARKETS</div><div class="meta-value" style="font-size:13px;">${selectedProgram.markets || 'Global'}</div></div>
</div>

<div class="section-title">GATE STATUS GRID — ALL LANES</div>
<table>
  <thead><tr>
    <th></th>
    ${gateNames.map((n, i) => `<th>G${i + 1}<br><span style="font-size:8px;font-weight:400;opacity:0.7;">${n.split(' ').slice(0, 2).join(' ')}</span></th>`).join('')}
  </tr></thead>
  <tbody>${gateGrid}</tbody>
</table>

${fm.stranded_asset_risk > 0 || fm.revenue_impact_30d > 0 ? `
<div class="section-title">FINANCIAL EXPOSURE</div>
<div class="fin-grid">
  ${fm.stranded_asset_risk > 0 ? `<div class="fin-card"><div class="fin-label">STRANDED ASSET RISK</div><div class="fin-value" style="color:#ef4444;">$${(fm.stranded_asset_risk / 1000000).toFixed(1)}M</div></div>` : ''}
  ${fm.revenue_impact_30d > 0 ? `<div class="fin-card"><div class="fin-label">30-DAY REVENUE IMPACT</div><div class="fin-value" style="color:#f59e0b;">$${(fm.revenue_impact_30d / 1000).toFixed(0)}K</div></div>` : ''}
  ${fm.retrofit_total > 0 ? `<div class="fin-card"><div class="fin-label">RETROFIT COST</div><div class="fin-value">$${(fm.retrofit_total / 1000).toFixed(0)}K</div></div>` : ''}
</div>` : ''}

${(consequences?.dependency_alerts || []).length > 0 ? `
<div class="section-title">DEPENDENCY ALERTS (${consequences.dependency_alerts.length})</div>
<div class="alerts">
${consequences.dependency_alerts.slice(0, 8).map(a => `<div class="alert-row">
  <div style="flex-shrink:0;width:6px;height:6px;border-radius:50%;background:${a.severity === 'hard' ? '#ef4444' : '#f59e0b'};margin-top:4px;"></div>
  <div>
    <div style="font-size:10px;font-weight:700;color:${a.severity === 'hard' ? '#ef4444' : '#f59e0b'};font-family:monospace;letter-spacing:1px;margin-bottom:2px;">${a.source_lane?.toUpperCase()} G${a.source_gate} → ${a.target_lane?.toUpperCase()} G${a.target_gate}</div>
    <div style="font-size:11px;color:#475569;">${a.reason || ''}</div>
  </div>
</div>`).join('')}
</div>` : ''}

<div class="footer">
  <span>NOMOS LAUNCH OS · Gate Status Report</span>
  <span>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
</div>

<button class="no-print print-btn" onclick="window.print()">PRINT / SAVE PDF</button>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // Presence: active = <2min, away = 2-15min, offline = >15min
  const getPresence = (lastActiveAt) => {
    if (!lastActiveAt) return 'offline';
    const mins = (Date.now() - new Date(lastActiveAt).getTime()) / 60000;
    if (mins < 2) return 'active';
    if (mins < 15) return 'away';
    return 'offline';
  };
  const presenceColor = { active: '#10b981', away: '#f59e0b', offline: '#6b7280' };
  const presenceLabel = { active: 'Active now', away: 'Away', offline: 'Offline' };

  const getStatusColor = (status) => {
    if (status === 'blocked')  return '#ef4444';
    if (status === 'at_risk')  return '#f59e0b';
    if (status === 'complete') return '#10b981';
    if (status === 'ready')    return '#22d3ee';
    if (status === 'on_track') return '#10b981';
    if (status === 'delayed')  return '#ef4444';
    return darkMode ? '#333' : '#cbd5e1';
  };

  const daysRemaining = (launchDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = typeof launchDate === 'string' && launchDate.length === 10 ? new Date(launchDate + 'T00:00:00') : new Date(launchDate);
    return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  };

  const fmt = (n) => n >= 1000000
    ? `$${(n / 1000000).toFixed(1)}M`
    : n >= 1000 ? `$${(n / 1000).toFixed(0)}K`
    : `$${n.toLocaleString()}`;

  const isMobile = windowWidth < 768;

  // Authenticated fetch — always sends JWT
  const authFetch = (url, options = {}) => {
    const token = _nomosToken || sessionRef.current?.access_token || session?.access_token;
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  };

  const toggleNotifications = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening) {
      const unread = notifications.filter(n => !n.read);
      if (unread.length > 0) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        authFetch(`${API}/notifications/mark-read`, { method: 'PATCH' }).catch(() => {});
      }
    }
  };

  const openAuthModal = (tab = 'signin') => { setAuthTab(tab); setAuthError(''); setAuthResetMode(false); setAuthResetSent(false); setAuthModalOpen(true); setSignupType('member'); setAuthCompanyName(''); setCreatedCompanyCode(''); };

  const enterConsole = () => {
    setAuthModalOpen(false); setLandingMode(false); setShowTeamDirectory(false); sessionStorage.setItem('nomos_entered', 'true');
    window.history.pushState({ page: 'programs' }, '');
    if (localStorage.getItem('nomos_new_admin') === '1') setShowWelcome(true);
    // Note: showWelcome initializes from localStorage on mount, so this handles the case
    // where enterConsole fires before the initial render has read the flag
  };

  const handleSignIn = async () => {
    setAuthError(''); setAuthSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
    if (error) { setAuthError(error.message); setAuthSubmitting(false); return; }
    // After successful password login, check if passkey not yet registered
    if (passkeyAvailable) {
      try {
        const { data: keys } = await supabase.auth.passkey.list();
        if (!keys?.length) setPasskeyBanner(true);
      } catch (_) {}
    }
    enterConsole();
    setAuthSubmitting(false);
  };

  const sendMagicLink = async () => {
    setAuthError(''); setAuthSubmitting(true);
    if (!authEmail.trim()) { setAuthError('Please enter your work email.'); setAuthSubmitting(false); return; }
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail.trim(),
      options: { emailRedirectTo: 'https://app.nomoslaunch.com', shouldCreateUser: true }
    });
    if (error) { setAuthError(error.message); setAuthSubmitting(false); return; }
    setMagicLinkSent(true);
    setAuthSubmitting(false);
  };

  const signInWithPasskey = async () => {
    setPasskeyLoading(true); setPasskeyError('');
    try {
      const { data: opts, error: startErr } = await supabase.auth.passkey.startAuthentication();
      if (startErr) throw startErr;
      const credential = await navigator.credentials.get({ publicKey: opts.publicKey, mediation: 'required' });
      const { error: verifyErr } = await supabase.auth.passkey.verifyAuthentication({ challengeId: opts.challenge_id, credential });
      if (verifyErr) throw verifyErr;
      enterConsole();
    } catch(e) {
      if (e?.name !== 'NotAllowedError') setPasskeyError('Biometric sign-in failed. Use your email and password instead.');
    } finally { setPasskeyLoading(false); }
  };

  const registerPasskey = async () => {
    setPasskeyRegistering(true); setPasskeyError('');
    try {
      const { data: opts, error: startErr } = await supabase.auth.passkey.startRegistration();
      if (startErr) throw startErr;
      const credential = await navigator.credentials.create({ publicKey: opts.publicKey });
      const { error: verifyErr } = await supabase.auth.passkey.verifyRegistration({ challengeId: opts.challenge_id, credential });
      if (verifyErr) throw verifyErr;
      localStorage.setItem('nomosPasskey', '1');
      setPasskeyBanner(false);
    } catch(e) {
      if (e?.name !== 'NotAllowedError') setPasskeyError('Could not save passkey. You can set it up later in Settings.');
      setPasskeyBanner(false);
    } finally { setPasskeyRegistering(false); }
  };

  const handlePasswordReset = async () => {
    setAuthError(''); setAuthSubmitting(true);
    if (!authEmail.trim()) { setAuthError('Please enter your email address.'); setAuthSubmitting(false); return; }
    const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
      redirectTo: 'https://app.nomoslaunch.com',
    });
    if (error) { setAuthError(error.message); setAuthSubmitting(false); return; }
    setAuthResetSent(true);
    setAuthSubmitting(false);
  };

  const handleSignUp = async () => {
    setAuthError(''); setAuthSubmitting(true);
    if (!authName.trim()) { setAuthError('Please enter your full name.'); setAuthSubmitting(false); return; }

    let companyCode = authCode;

    if (signupType === 'admin') {
      if (!authCompanyName.trim()) { setAuthError('Please enter your company name.'); setAuthSubmitting(false); return; }
      const companyRes = await fetch(`${API}/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: authCompanyName }),
      });
      const companyData = await companyRes.json();
      if (!companyRes.ok) { setAuthError(companyData.error || 'Failed to create company.'); setAuthSubmitting(false); return; }
      companyCode = companyData.code;
    } else {
      if (!authCode.trim()) { setAuthError('Please enter your company code.'); setAuthSubmitting(false); return; }
      if (!authLane) { setAuthError('Please select your team.'); setAuthSubmitting(false); return; }
    }

    const { data, error } = await supabase.auth.signUp({ email: authEmail, password: authPassword, options: { data: { full_name: authName } } });
    if (error) { setAuthError(error.message); setAuthSubmitting(false); return; }
    if (data.session) {
      const profileBody = { company_code: companyCode, full_name: authName };
      if (signupType === 'admin') profileBody.role = 'admin';
      else profileBody.lane = authLane;
      const res = await fetch(`${API}/auth/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.session.access_token}` },
        body: JSON.stringify(profileBody),
      });
      const result = await res.json();
      if (!res.ok) { await supabase.auth.signOut(); setAuthError(result.error || 'Setup failed.'); setAuthSubmitting(false); return; }
      setAuthCompany(result.company);
      if (signupType === 'admin') {
        setCreatedCompanyCode(companyCode);
        localStorage.setItem('nomos_new_admin', '1');
      } else {
        enterConsole();
      }
    } else {
      setAuthError('Check your email to confirm your account, then sign in.');
    }
    setAuthSubmitting(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null); sessionRef.current = null;
    setPrograms([]); setSelectedProgram(null); setAuthCompany(null);
    setLandingMode(true); sessionStorage.removeItem('nomos_entered');
  };

  const gates = [1, 2, 3, 4, 5, 6];
  const gateNames = ['Program Inception', 'Concept Review', 'Eng Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
  const lanes = ['design', 'finance', 'operations', 'installation', 'engineering'];

  const t = {
    bg:           darkMode ? '#0a0a0a' : '#faf9f7',
    surface:      darkMode ? '#111111' : '#ffffff',
    surfaceAlt:   darkMode ? '#0a0a0a' : '#f5f7fa',
    text:         darkMode ? '#ffffff' : '#1c1917',
    muted:        darkMode ? '#888888' : '#44403c',
    dim:          darkMode ? '#666666' : '#57534e',
    subLabel:     darkMode ? '#444444' : '#6b6560',
    border:       darkMode ? '#333333' : '#cbd5e1',
    borderDim:    darkMode ? '#222222' : '#e2e8f0',
    borderDimmer: darkMode ? '#1a1a1a' : '#edf0f4',
    accent:       darkMode ? '#22d3ee' : '#0891b2',
    idleText:     darkMode ? '#2a2a2a' : '#94a3b8',
    cascadeText:  darkMode ? '#cccccc' : '#334155',
    toggleBg:     darkMode ? '#1a1a1a' : '#e2e8f0',
    toggleColor:  darkMode ? '#888888' : '#475569',
    askInputBg:   darkMode ? '#111111' : '#ffffff',
    askBtnBg:     darkMode ? '#ffffff' : '#0f172a',
    askBtnColor:  darkMode ? '#000000' : '#ffffff',
    askBtnDisBg:  darkMode ? '#222222' : '#e2e8f0',
  };

  const s = {
    container:    { backgroundColor: t.bg, minHeight: '100vh', padding: isMobile ? '16px 12px' : '40px', fontFamily: "'Roboto Mono', monospace", color: t.text, },
    header:       { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '20px' : '40px', flexWrap: 'wrap' },
    logo:         { color: t.accent, fontSize: isMobile ? '22px' : '28px', margin: 0, letterSpacing: '4px' },
    tagline:      { color: t.dim, fontSize: isMobile ? '10px' : '12px', letterSpacing: '2px' },
    back:         { color: t.accent, fontSize: '11px', letterSpacing: '2px', cursor: 'pointer' },
    sectionTitle: { color: t.dim, fontSize: '12px', fontWeight: '500', letterSpacing: '2px', marginBottom: '20px' },
    grid:         { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' },
    card:         { backgroundColor: t.surface, border: '1px solid', borderRadius: '8px', padding: '16px', cursor: 'pointer' },
    cardStatus:   { fontSize: '10px', letterSpacing: '2px', marginBottom: '8px' },
    cardTitle:    { fontSize: '16px', margin: '0 0 10px 0', color: t.text, fontFamily: "'Inter', sans-serif", fontWeight: '600' },
    cardMeta:     { fontSize: '12px', color: t.muted, marginBottom: '4px' },
    loading:      { backgroundColor: t.bg, color: t.accent, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto Mono', monospace", fontSize: '18px' },
    programTitle: { fontSize: isMobile ? '20px' : '26px', margin: '0 0 4px 0', fontFamily: "'Inter', sans-serif", fontWeight: '700', letterSpacing: '-0.3px' },
    metaRow:      { display: 'flex', gap: isMobile ? '12px' : '24px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' },
    badge:        { fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold' },
    metaItem:     { fontSize: '12px', color: t.muted },
    tabs:         { display: 'flex', gap: '6px', marginBottom: '20px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', flexWrap: 'nowrap', paddingBottom: isMobile ? '8px' : '4px' },
    tab:          { backgroundColor: 'transparent', border: `1px solid ${t.border}`, color: t.dim, padding: isMobile ? '7px 10px' : '8px 16px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace", fontSize: isMobile ? '10px' : '11px', letterSpacing: isMobile ? '1px' : '2px', flexShrink: 0 },
    activeTab:    { borderColor: t.accent, color: t.accent },
    tableWrapper: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '40px' },
    tableHint:    { color: t.subLabel, fontSize: '10px', letterSpacing: '2px', marginBottom: '8px' },
    table:        { width: '100%', borderCollapse: 'collapse' },
    th:           { color: t.dim, fontSize: '11px', letterSpacing: '1px', padding: '8px 8px', textAlign: 'left', borderBottom: `1px solid ${t.border}` },
    gateName:     { color: t.subLabel, fontSize: '10px', fontWeight: 'normal' },
    laneCell:     { color: t.muted, fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', padding: '10px 8px', borderBottom: `1px solid ${t.borderDim}` },
    gateCell:     { border: '1px solid', borderRadius: '4px', padding: '6px', minWidth: isMobile ? '70px' : '90px', verticalAlign: 'top', cursor: 'pointer' },
    consequences: { maxWidth: '900px' },
    consRow:      { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '10px' : '16px', marginBottom: '24px' },
    consCard:     { backgroundColor: t.surface, padding: isMobile ? '12px' : '16px', borderRadius: '8px', border: `1px solid ${t.borderDim}` },
    consLabel:    { color: t.dim, fontSize: '9px', letterSpacing: '2px', marginBottom: '6px' },
    consValue:    { color: t.text, fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' },
    cascadeItem:  { backgroundColor: t.surface, padding: '14px', borderRadius: '8px', marginTop: '8px', border: `1px solid ${t.borderDim}`, borderLeft: '2px solid #ef4444' },
    overlay:      { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000 },
    modal:        { backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: isMobile ? '12px 12px 0 0' : '8px', padding: isMobile ? '20px 16px' : '32px', width: isMobile ? '100vw' : '520px', maxWidth: isMobile ? '100%' : '92vw', maxHeight: '88vh', overflowY: 'auto' },
    modalWide:    { backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: isMobile ? '12px 12px 0 0' : '8px', padding: isMobile ? '20px 16px' : '28px', width: isMobile ? '100vw' : '640px', maxWidth: isMobile ? '100%' : '94vw', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
    modalLane:    { color: t.accent, fontSize: '13px', letterSpacing: '2px', fontWeight: 'bold' },
    modalSub:     { color: t.subLabel, fontSize: '10px', letterSpacing: '1px', marginTop: '4px' },
    closeBtn:     { background: 'none', border: 'none', color: t.dim, fontSize: '20px', cursor: 'pointer', padding: '0 0 0 8px' },
    modalTabs:    { display: 'flex', borderBottom: `1px solid ${t.border}`, marginBottom: '20px' },
    modalTab:     { background: 'none', border: 'none', borderBottom: '2px solid transparent', color: t.dim, padding: isMobile ? '8px 12px' : '8px 20px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: isMobile ? '1px' : '2px', cursor: 'pointer', marginBottom: '-1px' },
    modalTabActive: { borderBottomColor: t.accent, color: t.accent },
    fieldGroup:   { marginBottom: '16px', flex: 1 },
    fieldRow:     { display: 'flex', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' },
    label:        { display: 'block', color: t.dim, fontSize: '11px', fontWeight: '500', letterSpacing: '1.5px', marginBottom: '6px' },
    select:       { width: '100%', backgroundColor: t.surfaceAlt, border: `1px solid ${t.border}`, color: t.text, padding: '10px 8px', fontFamily: "'Roboto Mono', monospace", fontSize: '14px', borderRadius: '4px' },
    input:        { width: '100%', backgroundColor: t.surfaceAlt, border: `1px solid ${t.border}`, color: t.text, padding: '10px 8px', fontFamily: "'Roboto Mono', monospace", fontSize: '14px', borderRadius: '4px', boxSizing: 'border-box' },
    textarea:     { width: '100%', backgroundColor: t.surfaceAlt, border: `1px solid ${t.border}`, color: t.text, padding: '10px 8px', fontFamily: "'Roboto Mono', monospace", fontSize: '13px', borderRadius: '4px', minHeight: '80px', resize: 'vertical', boxSizing: 'border-box' },
    saveBtn:      { width: '100%', backgroundColor: t.accent, border: 'none', color: '#000', padding: '14px', fontFamily: "'Roboto Mono', monospace", fontSize: '13px', letterSpacing: '2px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px', marginTop: '8px' },
    ghostBtn:     { background: 'none', border: `1px solid ${t.border}`, color: t.dim, padding: '8px 14px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', borderRadius: '4px' },
    toggleBtn:    { marginLeft: 'auto', background: t.toggleBg, border: `1px solid ${t.border}`, color: t.toggleColor, padding: '6px 10px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '1px', cursor: 'pointer', borderRadius: '4px' },
    msProgress:   { fontSize: '10px', color: t.dim, letterSpacing: '1px', marginBottom: '12px' },
    msItem:       { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 0', borderBottom: `1px solid ${t.borderDimmer}` },
    msCheck:      { marginTop: '3px', accentColor: t.accent, width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' },
    msDelete:     { background: 'none', border: 'none', color: t.subLabel, fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0, marginTop: '1px' },
    msAddRow:     { display: 'flex', gap: '8px', marginTop: '16px' },
    aiBox:        { background: t.surfaceAlt, border: `1px solid ${t.border}`, padding: '14px', borderRadius: '4px', color: t.cascadeText, fontSize: '12px', lineHeight: '1.7', fontFamily: "'Roboto Mono', monospace", marginTop: '12px', whiteSpace: 'pre-wrap' },
    aiLabel:      { color: t.dim, fontSize: '9px', letterSpacing: '2px', marginBottom: '6px' },
    alertBanner:  { backgroundColor: darkMode ? '#1a0a00' : '#fff7ed', border: `1px solid #f59e0b`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px' },
    alertHard:    { backgroundColor: darkMode ? '#1a0000' : '#fef2f2', borderColor: '#ef4444' },
    slideOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 900 },
    slidePanel:   { position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100vw' : '500px', backgroundColor: darkMode ? '#0d0d0d' : '#ffffff', borderLeft: `1px solid ${t.border}`, zIndex: 901, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
    panelSec:     { padding: '20px 24px', borderBottom: `1px solid ${t.borderDim}` },
  };

  // Bound helpers — stable module-level components receive context via props
  const micP = { activeFieldId: voiceFieldId, onStart: startVoiceFor, dimColor: t.borderDim, subColor: t.subLabel };
  const micWideP = { activeFieldId: voiceFieldId, onStart: startVoiceFor, surfaceAlt: t.surfaceAlt, border: t.border, dim: t.dim };

  // Auth modal (rendered over landing page or app)
  const authInp = { width: '100%', padding: '12px 14px', backgroundColor: darkMode ? '#1a1a1a' : '#ffffff', border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontSize: '15px', fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' };
  const authLbl = { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px', color: t.dim, marginBottom: '6px', fontFamily: "'Roboto Mono', monospace" };

  if ((landingMode || !session) && !demoMode) return (
    <div style={{ backgroundColor: t.bg, minHeight: '100vh', fontFamily: "'Roboto Mono', monospace", color: t.text }}>
      {authModalOpen && (
        <>
          <div onClick={() => setAuthModalOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '100%', maxWidth: '420px', backgroundColor: t.surface, borderRadius: '12px', padding: '36px', zIndex: 2001, boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: "'Inter', sans-serif", color: t.text }}>nomos.</div>
              <button onClick={() => setAuthModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '20px', lineHeight: 1 }}>×</button>
            </div>
            {!authResetMode && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, marginBottom: '24px' }}>
                {['signin', 'signup'].map(tab => (
                  <button key={tab} onClick={() => { setAuthTab(tab); setAuthError(''); }} style={{ flex: 1, background: 'none', border: 'none', borderBottom: authTab === tab ? `2px solid ${t.accent}` : '2px solid transparent', marginBottom: '-1px', padding: '10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", color: authTab === tab ? t.accent : t.dim }}>
                    {tab === 'signin' ? 'SIGN IN' : 'CREATE ACCOUNT'}
                  </button>
                ))}
              </div>
            )}
            {authResetMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ fontSize: '13px', color: t.dim, lineHeight: 1.6 }}>Enter your work email and we'll send you a link to reset your password.</div>
                {authResetSent ? (
                  <div style={{ padding: '14px', backgroundColor: darkMode ? '#052e16' : '#f0fdf4', border: '1px solid #16a34a', borderRadius: '6px', fontSize: '13px', color: '#16a34a', lineHeight: 1.5 }}>
                    Check your email for a reset link.
                  </div>
                ) : (
                  <>
                    <div><label style={authLbl}>WORK EMAIL</label>
                      <input style={authInp} type="email" placeholder="you@company.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePasswordReset()} /></div>
                    {authError && <div style={{ fontSize: '13px', color: '#ef4444', lineHeight: 1.4 }}>{authError}</div>}
                    <button onClick={handlePasswordReset} disabled={authSubmitting}
                      style={{ width: '100%', padding: '13px', backgroundColor: t.accent, border: 'none', borderRadius: '6px', color: '#000', fontSize: '12px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: authSubmitting ? 'not-allowed' : 'pointer', opacity: authSubmitting ? 0.6 : 1 }}>
                      {authSubmitting ? 'SENDING...' : 'SEND RESET LINK'}
                    </button>
                  </>
                )}
                <button onClick={() => { setAuthResetMode(false); setAuthResetSent(false); setAuthError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '12px', textDecoration: 'underline', fontFamily: "'Roboto Mono', monospace" }}>
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Admin invite-code reveal screen */}
                {createdCompanyCode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '13px', color: t.muted, marginBottom: '16px', lineHeight: 1.6 }}>Your company is set up. Share this code with your team so they can join.</div>
                      <div style={{ backgroundColor: darkMode ? '#1a1a1a' : '#f1f5f9', border: `2px solid ${t.accent}`, borderRadius: '8px', padding: '20px', marginBottom: '8px' }}>
                        <div style={{ fontSize: '11px', letterSpacing: '2px', color: t.dim, marginBottom: '8px' }}>YOUR COMPANY CODE</div>
                        <div style={{ fontSize: '28px', letterSpacing: '6px', fontWeight: '700', color: t.accent }}>{createdCompanyCode}</div>
                      </div>
                      <div style={{ fontSize: '11px', color: t.subLabel }}>You can find this code anytime in Settings</div>
                    </div>
                    <button onClick={enterConsole} style={{ width: '100%', padding: '13px', backgroundColor: t.accent, border: 'none', borderRadius: '6px', color: '#000', fontSize: '12px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer', marginTop: '4px' }}>
                      CONTINUE TO NOMOS →
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Path selector for signup */}
                    {authTab === 'signup' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {[
                          { key: 'admin', label: 'Set up my company', sub: "I'm launching" },
                          { key: 'member', label: 'Join my team', sub: 'I have a code' },
                        ].map(({ key, label, sub }) => (
                          <button key={key} type="button" onClick={() => { setSignupType(key); setAuthError(''); }} style={{
                            padding: '12px 10px', textAlign: 'center', cursor: 'pointer', borderRadius: '8px',
                            border: `2px solid ${signupType === key ? t.accent : t.border}`,
                            backgroundColor: signupType === key ? (darkMode ? '#0e3a42' : '#ecfeff') : 'transparent',
                            fontFamily: "'Roboto Mono', monospace",
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: signupType === key ? t.accent : t.text, letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</div>
                            <div style={{ fontSize: '10px', color: t.dim }}>{sub}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Name field */}
                    {authTab === 'signup' && (
                      <div><label style={authLbl}>FULL NAME</label>
                        <input style={authInp} placeholder="Jane Smith" value={authName} onChange={e => setAuthName(e.target.value)} /></div>
                    )}

                    {/* Admin: company name */}
                    {authTab === 'signup' && signupType === 'admin' && (
                      <div><label style={authLbl}>COMPANY NAME</label>
                        <input style={authInp} placeholder="Acme Inc." value={authCompanyName} onChange={e => setAuthCompanyName(e.target.value)} />
                        <div style={{ fontSize: '11px', color: t.subLabel, marginTop: '5px' }}>Nomos will generate a team invite code</div>
                      </div>
                    )}

                    {/* Google sign-in */}
                    <button
                      onClick={() => supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } })}
                      style={{ width: '100%', padding: '13px', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '12px', fontWeight: '600', fontFamily: "'Roboto Mono', monospace", color: '#374151', letterSpacing: '0.5px' }}>
                      <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      </svg>
                      SIGN IN WITH GOOGLE
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '1px', backgroundColor: t.borderDim }} />
                      <span style={{ fontSize: '10px', color: t.muted, fontFamily: "'Roboto Mono', monospace" }}>OR</span>
                      <div style={{ flex: 1, height: '1px', backgroundColor: t.borderDim }} />
                    </div>

                    <div><label style={authLbl}>WORK EMAIL</label>
                      <input style={authInp} type="email" placeholder="you@company.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && (authTab === 'signin' ? handleSignIn() : handleSignUp())} /></div>
                    <div>
                      <label style={authLbl}>PASSWORD</label>
                      <input style={authInp} type="password" placeholder="••••••••" value={authPassword} onChange={e => setAuthPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && (authTab === 'signin' ? handleSignIn() : handleSignUp())} />
                      {authTab === 'signin' && (
                        <button onClick={() => { setAuthResetMode(true); setAuthResetSent(false); setAuthError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '11px', fontFamily: "'Roboto Mono', monospace", marginTop: '6px', padding: 0, textDecoration: 'underline' }}>
                          Forgot password?
                        </button>
                      )}
                    </div>

                    {/* Member: company code */}
                    {authTab === 'signup' && signupType === 'member' && (
                      <div><label style={authLbl}>COMPANY CODE</label>
                        <input style={{ ...authInp, letterSpacing: '2px' }} placeholder="APPLE-BVU0" value={authCode} onChange={e => setAuthCode(e.target.value.toUpperCase())} />
                        <div style={{ fontSize: '11px', color: t.subLabel, marginTop: '5px' }}>Provided by your launch lead</div>
                      </div>
                    )}

                    {/* Member: team selector */}
                    {authTab === 'signup' && signupType === 'member' && (
                      <div>
                        <label style={authLbl}>YOUR TEAM</label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {['design', 'finance', 'operations', 'installation', 'engineering'].map(l => {
                            const laneColors = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
                            const sel = authLane === l;
                            const col = laneColors[l];
                            return (
                              <button key={l} onClick={() => setAuthLane(l)} type="button" style={{
                                padding: '6px 10px', fontSize: '10px', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace",
                                fontWeight: sel ? '700' : '500', cursor: 'pointer', borderRadius: '4px',
                                border: `1px solid ${sel ? col : t.border}`,
                                backgroundColor: sel ? col + '25' : 'transparent',
                                color: sel ? col : t.muted,
                              }}>
                                {l === 'operations' ? 'OPS' : l === 'installation' ? 'INSTALL' : l === 'engineering' ? 'ENG' : l.toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: '11px', color: t.subLabel, marginTop: '5px' }}>Your functional team in this launch</div>
                      </div>
                    )}

                    {authError && <div style={{ fontSize: '13px', color: '#ef4444', lineHeight: 1.4 }}>{authError}</div>}
                    {passkeyError && <div style={{ fontSize: '12px', color: '#f59e0b', lineHeight: 1.4 }}>{passkeyError}</div>}
                    <button onClick={authTab === 'signin' ? handleSignIn : handleSignUp} disabled={authSubmitting}
                      style={{ width: '100%', padding: '13px', backgroundColor: t.accent, border: 'none', borderRadius: '6px', color: '#000', fontSize: '12px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: authSubmitting ? 'not-allowed' : 'pointer', opacity: authSubmitting ? 0.6 : 1, marginTop: '4px' }}>
                      {authSubmitting ? 'PLEASE WAIT...' : authTab === 'signin' ? 'SIGN IN' : signupType === 'admin' ? 'SET UP COMPANY' : 'JOIN TEAM'}
                    </button>
                    {authTab === 'signup' && (
                      <div style={{ fontSize: '10px', color: t.dim, textAlign: 'center', lineHeight: 1.6 }}>
                        By creating an account you agree to our{' '}
                        <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: t.accent, textDecoration: 'underline' }}>Terms of Service</a>
                        {' '}and{' '}
                        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: t.accent, textDecoration: 'underline' }}>Privacy Policy</a>.
                      </div>
                    )}
                    {/* Magic link + passkey options — sign-in tab only */}
                    {authTab === 'signin' && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0' }}>
                          <div style={{ flex: 1, height: '1px', backgroundColor: t.borderDim }} />
                          <span style={{ fontSize: '10px', color: t.muted, fontFamily: "'Roboto Mono', monospace" }}>OR</span>
                          <div style={{ flex: 1, height: '1px', backgroundColor: t.borderDim }} />
                        </div>
                        {magicLinkSent ? (
                          <div style={{ padding: '14px', backgroundColor: darkMode ? '#052e16' : '#f0fdf4', border: '1px solid #16a34a', borderRadius: '8px', fontSize: '13px', color: '#16a34a', lineHeight: 1.5, textAlign: 'center' }}>
                            Check your inbox — a sign-in link is on its way to <strong>{authEmail}</strong>
                          </div>
                        ) : magicLinkMode ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '12px', color: t.muted, lineHeight: 1.5 }}>We'll email a one-click sign-in link — no password needed. Works with any company email.</div>
                            <button onClick={sendMagicLink} disabled={authSubmitting || !authEmail.trim()}
                              style={{ width: '100%', padding: '13px', backgroundColor: 'transparent', border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontSize: '12px', fontWeight: '600', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", cursor: authSubmitting ? 'not-allowed' : 'pointer', opacity: authSubmitting || !authEmail.trim() ? 0.5 : 1 }}>
                              {authSubmitting ? 'SENDING...' : 'SEND SIGN-IN LINK →'}
                            </button>
                            <button onClick={() => setMagicLinkMode(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '11px', fontFamily: "'Roboto Mono', monospace", textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                              Use password instead
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => { setMagicLinkMode(true); setAuthError(''); }}
                            style={{ width: '100%', padding: '13px', backgroundColor: 'transparent', border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontSize: '12px', fontWeight: '600', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer' }}>
                            SIGN IN WITHOUT A PASSWORD →
                          </button>
                        )}
                        {passkeyAvailable && localStorage.getItem('nomosPasskey') === '1' && !magicLinkMode && !magicLinkSent && (
                          <button onClick={signInWithPasskey} disabled={passkeyLoading}
                            style={{ width: '100%', padding: '13px', backgroundColor: 'transparent', border: `1px solid ${t.border}`, borderRadius: '6px', color: t.text, fontSize: '12px', fontWeight: '600', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", cursor: passkeyLoading ? 'not-allowed' : 'pointer', opacity: passkeyLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>⬡</span>
                            {passkeyLoading ? 'VERIFYING...' : 'USE FINGERPRINT / FACE ID'}
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Passkey setup banner — shown after first password sign-in on a passkey-capable device */}
      {passkeyBanner && session && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 3000, backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '18px 22px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', maxWidth: '380px', width: 'calc(100% - 48px)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <div style={{ fontSize: '28px', lineHeight: 1 }}>⬡</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: t.text, marginBottom: '4px' }}>Sign in faster next time</div>
              <div style={{ fontSize: '12px', color: t.muted, lineHeight: 1.5, marginBottom: '12px' }}>Save your fingerprint or Face ID so you never need a password again on this device.</div>
              {passkeyError && <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '8px' }}>{passkeyError}</div>}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={registerPasskey} disabled={passkeyRegistering}
                  style={{ flex: 1, padding: '9px', backgroundColor: t.accent, border: 'none', borderRadius: '6px', color: '#000', fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", cursor: passkeyRegistering ? 'not-allowed' : 'pointer', opacity: passkeyRegistering ? 0.6 : 1 }}>
                  {passkeyRegistering ? 'SAVING...' : 'SAVE PASSKEY'}
                </button>
                <button onClick={() => { setPasskeyBanner(false); localStorage.setItem('nomosPasskeyDismissed', '1'); }}
                  style={{ padding: '9px 14px', backgroundColor: 'transparent', border: `1px solid ${t.border}`, borderRadius: '6px', color: t.dim, fontSize: '11px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer' }}>
                  NOT NOW
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NAV */}
      <div style={{ borderBottom: `1px solid ${t.borderDim}`, padding: isMobile ? '0 20px' : '0 60px', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: t.bg, zIndex: 100, height: '56px' }}>
        <div style={{ color: t.accent, fontSize: isMobile ? '16px' : '20px', letterSpacing: '5px', fontWeight: 'bold', flexShrink: 0 }}>NOMOS</div>
        <div style={{ flex: 1 }} />
        {!isMobile && !session && (
          <button style={{ ...s.ghostBtn, fontSize: '10px', marginRight: '8px' }} onClick={enterAndExploreDemo}>EXPLORE DEMO</button>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          <ThemeToggle darkMode={darkMode} onToggle={() => setDarkMode(d => !d)} style={s.toggleBtn} />
          {session ? (
            <>
              <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: isMobile ? '7px 14px' : '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: isMobile ? '10px' : '11px', letterSpacing: '1.5px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px' }} onClick={enterConsole}>
                MY PROGRAMS →
              </button>
              <div data-avatar-menu style={{ position: 'relative' }}>
                <button onClick={() => setAvatarMenuOpen(o => !o)} style={{ width: '30px', height: '30px', borderRadius: '50%', overflow: 'hidden', border: `1.5px solid ${t.accent}60`, backgroundColor: t.accent + '15', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {myAvatarUrl
                    ? <img src={myAvatarUrl} alt="me" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: '10px', fontWeight: '700', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>{(myFullName || session.user?.email || '?').split(/[\s@]/)[0]?.[0]?.toUpperCase()}</span>}
                </button>
                {avatarMenuOpen && (
                  <div style={{ position: 'absolute', top: '38px', right: 0, backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', minWidth: '160px', zIndex: 999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                    <button onClick={() => { setAvatarMenuOpen(false); setMyProfileOpen(true); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: t.text, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', textAlign: 'left' }}>MY PROFILE</button>
                    <div style={{ height: '1px', backgroundColor: t.borderDim }} />
                    <button onClick={() => { setAvatarMenuOpen(false); handleSignOut(); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#ef4444', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', textAlign: 'left' }}>SIGN OUT</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button style={{ ...s.ghostBtn, fontSize: isMobile ? '10px' : '10px', padding: isMobile ? '7px 12px' : '8px 14px' }} onClick={() => openAuthModal('signin')}>SIGN IN</button>
              <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: isMobile ? '7px 14px' : '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: isMobile ? '10px' : '11px', letterSpacing: '1.5px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px' }} onClick={() => openAuthModal('signup')}>
                {isMobile ? 'GET STARTED' : 'GET STARTED FREE'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── HERO ── */}
      <div style={{ padding: isMobile ? '56px 20px 48px' : '90px 60px', maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? '44px' : '80px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: t.accent + '15', border: `1px solid ${t.accent}40`, borderRadius: '20px', padding: '5px 14px', marginBottom: '24px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: t.accent }} />
            <span style={{ color: t.accent, fontSize: '10px', letterSpacing: '3px', fontWeight: '700', fontFamily: "'Roboto Mono', monospace" }}>FOR PROGRAM MANAGERS</span>
          </div>
          <h1 style={{ fontSize: isMobile ? '32px' : '52px', fontWeight: '800', lineHeight: 1.1, margin: '0 0 20px 0', color: t.text, letterSpacing: '-1px', fontFamily: "'Inter', sans-serif" }}>
            Your launch is running<br />
            <span style={{ color: t.accent }}>in five different inboxes.</span>
          </h1>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: t.muted, lineHeight: 1.8, margin: '0 0 32px 0', maxWidth: '440px', fontFamily: "'Inter', sans-serif" }}>
            Nomos replaces the spreadsheets, status decks, and email threads with one platform where every team works, signs off, and sees the consequences of every decision.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {session ? (
              <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '14px 28px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', letterSpacing: '2px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '6px' }} onClick={enterConsole}>
                GO TO MY PROGRAMS →
              </button>
            ) : (
              <>
                <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '14px 28px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', letterSpacing: '2px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '6px', boxShadow: '0 4px 20px rgba(13,148,136,0.35)' }} onClick={() => openAuthModal('signup')}>
                  GET STARTED FREE →
                </button>
                <button style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${t.border}`, color: t.dim, padding: '14px 22px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', borderRadius: '6px', background: 'none' }} onClick={enterAndExploreDemo}>
                  EXPLORE DEMO
                </button>
              </>
            )}
          </div>
          {!session && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ color: t.subLabel, fontSize: '12px', fontFamily: "'Inter', sans-serif" }}>Free to start</span>
              <span style={{ color: t.borderDim }}>·</span>
              <span style={{ color: t.subLabel, fontSize: '12px', fontFamily: "'Inter', sans-serif" }}>No credit card</span>
            </div>
          )}
        </div>

        {/* Hero product mockup */}
        <div style={{ flex: 1, maxWidth: '520px', width: '100%' }}>
          <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: darkMode ? '0 24px 60px rgba(0,0,0,0.5)' : '0 24px 60px rgba(0,0,0,0.1)' }}>
            <div style={{ backgroundColor: darkMode ? '#0a0c14' : '#f1f5f9', padding: '10px 16px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '5px' }}>
                {['#ef4444', '#f59e0b', '#10b981'].map(c => <div key={c} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: c + '70' }} />)}
              </div>
              <div style={{ flex: 1, backgroundColor: t.borderDim + '80', borderRadius: '4px', height: '18px', display: 'flex', alignItems: 'center', padding: '0 10px' }}>
                <span style={{ fontSize: '9px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace" }}>app.nomoslaunch.com · SDS Global Rollout</span>
              </div>
            </div>
            <div style={{ padding: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '3px' }}>SDS Global Rollout</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                    <span style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>AT RISK · Gate 3 of 6</span>
                  </div>
                </div>
                <div style={{ backgroundColor: '#ef444412', border: '1px solid #ef444430', borderRadius: '6px', padding: '6px 10px', textAlign: 'right' }}>
                  <div style={{ fontSize: '8px', color: '#ef4444', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>EXPOSURE</div>
                  <div style={{ fontSize: '18px', color: '#ef4444', fontWeight: '800', fontFamily: "'Inter', sans-serif" }}>$5.2M</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(4, 1fr)', gap: '3px', marginBottom: '12px' }}>
                <div />
                {['G1', 'G2', 'G3', 'G4'].map(g => (
                  <div key={g} style={{ color: t.subLabel, textAlign: 'center', fontSize: '8px', padding: '2px', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>{g}</div>
                ))}
                {[
                  { lane: 'DESIGN',  statuses: ['complete', 'complete', 'on_track', 'idle'], color: '#a78bfa' },
                  { lane: 'FINANCE', statuses: ['complete', 'complete', 'blocked', 'idle'],  color: '#10b981' },
                  { lane: 'OPS',     statuses: ['complete', 'on_track', 'blocked', 'idle'],  color: '#f59e0b' },
                  { lane: 'INSTALL', statuses: ['complete', 'on_track', 'idle', 'idle'],     color: '#22d3ee' },
                  { lane: 'ENG',     statuses: ['complete', 'at_risk', 'idle', 'idle'],      color: '#3b82f6' },
                ].map(({ lane, statuses, color }) => [
                  <div key={lane + '_lbl'} style={{ color, fontSize: '7px', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>{lane}</div>,
                  ...statuses.map((st, i) => {
                    const c = st === 'complete' ? '#10b981' : st === 'on_track' ? '#10b981' : st === 'blocked' ? '#ef4444' : st === 'at_risk' ? '#f59e0b' : t.borderDim + '40';
                    const lbl = st === 'idle' ? '' : st === 'complete' || st === 'on_track' ? '✓' : st === 'blocked' ? '✕' : '!';
                    return <div key={lane + i} style={{ backgroundColor: c + '18', border: `1px solid ${c}50`, borderRadius: '3px', padding: '5px 2px', textAlign: 'center', fontSize: '8px', color: c, fontWeight: 'bold' }}>{lbl}</div>;
                  }),
                ])}
              </div>
              <div style={{ backgroundColor: '#ef444410', border: '1px solid #ef444428', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '8px', color: '#ef4444', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace', marginBottom: '4px'" }}>CASCADE ALERT · +45 DAY SLIP</div>
                <div style={{ fontSize: '10px', color: t.muted, fontFamily: "'Inter', sans-serif" }}>FINANCE G3 blocked → ENG G3 at risk → OPS G4 queued</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PM PAIN POINTS ── */}
      <div style={{ borderTop: `1px solid ${t.borderDim}`, borderBottom: `1px solid ${t.borderDim}`, backgroundColor: darkMode ? '#080a0f' : '#fafafa', padding: isMobile ? '36px 20px' : '40px 60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '24px' : '48px' }}>
          {[
            { pain: 'Status updates that require a meeting to compile.', fix: 'Nomos keeps every gate live — no manual updates, no prep decks.' },
            { pain: 'Critical decisions buried in email threads nobody can find.', fix: 'Every sign-off is logged, attributed, and searchable — forever.' },
            { pain: 'Finding out about a problem after it has become a delay.', fix: 'Nomos alerts the moment a gate slips and shows the financial impact instantly.' },
          ].map(({ pain, fix }, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderLeft: `2px solid ${t.accent}30`, paddingLeft: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>{pain}</div>
              <div style={{ fontSize: '13px', color: t.accent, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>{fix}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── THREE FEATURES ── */}
      <div style={{ padding: isMobile ? '60px 20px' : '90px 60px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '56px' }}>
          <h2 style={{ fontSize: isMobile ? '26px' : '38px', fontWeight: '800', color: t.text, margin: '0 0 14px', fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px' }}>Everything a program manager needs.</h2>
          <p style={{ fontSize: '15px', color: t.muted, margin: 0, fontFamily: "'Inter', sans-serif", lineHeight: 1.7, maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>Three functions. One platform. No more hunting across five tools.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
          {[
            {
              color: t.accent,
              tag: 'ELIMINATE SILOS',
              title: 'One place for every team.',
              body: 'Design, Finance, Operations, Engineering — all working in the same platform, tracking the same gates, seeing the same status. No more chasing updates across Slack and email.',
              bullets: ['Real-time gate status across all lanes', 'Each team sees only what is relevant to them', 'Sign-off required before anything advances'],
            },
            {
              color: '#f59e0b',
              tag: 'INSTANT OUTPUTS',
              title: 'The report is always ready.',
              body: 'Exec summary, timeline, gate status report — generated in seconds. Walk into any leadership meeting with a live, accurate picture of your launch. No prep required.',
              bullets: ['Exec summary with AI narrative — one click', 'Gantt timeline always current, never stale', 'Export to PDF for any stakeholder, any time'],
            },
            {
              color: '#a78bfa',
              tag: 'AI INTELLIGENCE',
              title: 'Catch mistakes before they ship.',
              body: 'Nomos monitors your program and alerts you before a problem becomes a delay. Ask any question about your launch in plain English and get a direct answer.',
              bullets: ['Predictive slip analysis across all lanes', 'Financial exposure calculated in real time', 'Ask Nomos anything — it knows your program'],
            },
          ].map(({ color, tag, title, body, bullets }) => (
            <div key={tag} style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'inline-block', backgroundColor: color + '18', border: `1px solid ${color}35`, borderRadius: '4px', padding: '3px 10px', alignSelf: 'flex-start' }}>
                <span style={{ color, fontSize: '9px', letterSpacing: '2.5px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>{tag}</span>
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: t.text, margin: 0, fontFamily: "'Inter', sans-serif", lineHeight: 1.3 }}>{title}</h3>
              <p style={{ fontSize: '13px', color: t.muted, margin: 0, fontFamily: "'Inter', sans-serif", lineHeight: 1.7 }}>{body}</p>
              <div style={{ borderTop: `1px solid ${t.borderDim}`, paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {bullets.map(b => (
                  <div key={b} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{ color, fontSize: '10px', marginTop: '2px', flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: '12px', color: t.subLabel, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── BABEL PROBLEM ── */}
      <div style={{ backgroundColor: darkMode ? '#080a0f' : '#f8f9fb', padding: isMobile ? '60px 20px' : '90px 60px', borderTop: `1px solid ${t.borderDim}` }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '4px', marginBottom: '16px', fontFamily: "'Roboto Mono', monospace" }}>WHY LAUNCHES FAIL</div>
          <h2 style={{ fontSize: isMobile ? '26px' : '40px', fontWeight: '800', lineHeight: 1.2, color: t.text, margin: '0 0 16px', fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px' }}>Every team speaks a different language.</h2>
          <p style={{ color: t.muted, fontSize: '15px', lineHeight: 1.8, margin: '0 auto 44px', maxWidth: '520px', fontFamily: "'Inter', sans-serif" }}>
            Design talks about form factor. Finance talks about COGS. Operations talks about lead times. Nobody is wrong — but without a shared platform, these conversations never connect until it is too late.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', marginBottom: '40px', textAlign: 'left' }}>
            {[
              { team: 'DESIGN',     color: '#a78bfa', words: ['Materials & CMF', 'Prototype fidelity', 'Aesthetic tolerances', 'Customer experience', 'Form factor'] },
              { team: 'FINANCE',    color: '#f59e0b', words: ['COGS targets', 'Stranded assets', 'Budget variance', 'Retrofit cost per location', 'Revenue at risk'] },
              { team: 'OPERATIONS', color: '#22d3ee', words: ['Supplier readiness', 'Lead time', 'Install schedule', 'SKU complexity', 'Fixture logistics'] },
            ].map(({ team, color, words }) => (
              <div key={team} style={{ backgroundColor: t.surface, border: `1px solid ${color}20`, borderTop: `3px solid ${color}`, borderRadius: '8px', padding: '22px 18px' }}>
                <div style={{ color, fontSize: '10px', letterSpacing: '2px', fontWeight: '700', marginBottom: '14px', fontFamily: "'Roboto Mono', monospace" }}>{team}</div>
                {words.map(w => (
                  <div key={w} style={{ color: t.muted, fontSize: '13px', marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center', fontFamily: "'Inter', sans-serif" }}>
                    <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                    {w}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', backgroundColor: t.accent + '12', border: `1px solid ${t.accent}30`, borderRadius: '8px', padding: '14px 24px' }}>
            <div style={{ width: '26px', height: '26px', backgroundColor: t.accent, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>N</div>
            <span style={{ color: t.accent, fontSize: isMobile ? '14px' : '16px', fontWeight: '700', fontFamily: "'Inter', sans-serif" }}>Nomos is the translation layer.</span>
          </div>
        </div>
      </div>

      {/* ── INDUSTRIES ── */}
      <div style={{ padding: isMobile ? '28px 20px' : '36px 60px', borderTop: `1px solid ${t.borderDim}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '14px', alignItems: 'center' }}>
          <div style={{ color: t.subLabel, fontSize: '9px', letterSpacing: '3px', flexShrink: 0, fontFamily: "'Roboto Mono', monospace" }}>BUILT FOR</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['RETAIL', 'CONSUMER ELECTRONICS', 'PHARMA', 'AUTOMOTIVE', 'FOOTWEAR', 'MEDICAL DEVICES', 'CONSUMER GOODS'].map(ind => (
              <span key={ind} style={{ color: t.dim, fontSize: '10px', letterSpacing: '1.5px', border: `1px solid ${t.borderDim}`, padding: '5px 12px', borderRadius: '20px', fontFamily: "'Roboto Mono', monospace" }}>{ind}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── BOTTOM CTA ── */}
      <div style={{ padding: isMobile ? '72px 20px' : '100px 60px', textAlign: 'center', borderTop: `1px solid ${t.borderDim}`, backgroundColor: darkMode ? '#080a0f' : '#f8f9fb' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? '28px' : '44px', fontWeight: '800', lineHeight: 1.15, color: t.text, margin: '0 0 16px', fontFamily: "'Inter', sans-serif", letterSpacing: '-1px' }}>
            Run a tighter launch.<br /><span style={{ color: t.accent }}>Starting today.</span>
          </h2>
          <p style={{ color: t.muted, fontSize: '15px', lineHeight: 1.7, margin: '0 auto 36px', fontFamily: "'Inter', sans-serif" }}>
            Set up your program, invite your team, and have a live gate dashboard in under 10 minutes.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            {session ? (
              <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '15px 36px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', letterSpacing: '2.5px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '6px', boxShadow: '0 4px 20px rgba(13,148,136,0.35)' }} onClick={enterConsole}>
                GO TO MY PROGRAMS →
              </button>
            ) : (
              <>
                <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '15px 36px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', letterSpacing: '2.5px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '6px', boxShadow: '0 4px 20px rgba(13,148,136,0.35)' }} onClick={() => openAuthModal('signup')}>
                  GET STARTED FREE →
                </button>
                <button style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${t.border}`, color: t.dim, padding: '15px 24px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', borderRadius: '6px', background: 'none' }} onClick={enterAndExploreDemo}>
                  EXPLORE DEMO
                </button>
              </>
            )}
          </div>
          {!session && <div style={{ color: t.subLabel, fontSize: '12px', fontFamily: "'Inter', sans-serif" }}>Free to start · No credit card required</div>}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: isMobile ? '24px 20px' : '32px 60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', backgroundColor: darkMode ? '#060810' : '#f8f9fb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '24px', height: '24px', backgroundColor: t.accent, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color: '#000' }}>N</div>
          <span style={{ color: t.accent, fontSize: '14px', letterSpacing: '4px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>NOMOS</span>
        </div>
        <div style={{ color: t.subLabel, fontSize: '11px', fontFamily: "'Inter', sans-serif" }}>The Launch Operating System</div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: t.subLabel, fontSize: '11px', textDecoration: 'none', fontFamily: "'Inter', sans-serif" }}>Privacy</a>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: t.subLabel, fontSize: '11px', textDecoration: 'none', fontFamily: "'Inter', sans-serif" }}>Terms</a>
          <div style={{ color: t.dim, fontSize: '11px', fontFamily: "'Inter', sans-serif" }}>© 2026 Nomos, Inc.</div>
        </div>
      </div>

    </div>
  );

  if (loading) return <div style={s.loading}>Loading Nomos...</div>;

  // ── PROGRAM DETAIL VIEW ────────────────────────────────────────────────────
  if (selectedProgram) {
    const getGateStatus = (lane, gate) =>
      selectedProgram.gates?.find(g => g.lane === lane && g.gate_number === gate) || null;

    const completedCount = milestones.filter(m => m.completed).length;

    const hardAlerts  = (consequences?.dependency_alerts || []).filter(a => a.severity === 'hard');
    const softAlerts  = (consequences?.dependency_alerts || []).filter(a => a.severity === 'soft');
    const fm = consequences?.financial_model || {};

    // Parse date-only strings (YYYY-MM-DD) as local time, not UTC, to avoid 1-day shift in negative UTC offsets
    const parseLocalDate = (str) => str ? new Date(str.length === 10 ? str + 'T00:00:00' : str) : null;

    // Timeline helpers
    const plannedLaunch = parseLocalDate(selectedProgram.launch_date);
    const fallbackLaunch = plannedLaunch || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
    const programStart = (() => {
      const raw = selectedProgram.program_start_date
        ? parseLocalDate(selectedProgram.program_start_date)
        : new Date(fallbackLaunch.getTime() - 180 * 24 * 60 * 60 * 1000);
      const todayCheck = new Date(); todayCheck.setHours(0,0,0,0);
      if (plannedLaunch && raw >= todayCheck && (plannedLaunch - todayCheck) > 60 * 24 * 60 * 60 * 1000) {
        return new Date(plannedLaunch.getTime() - 180 * 24 * 60 * 60 * 1000);
      }
      return raw;
    })();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const drDays = daysRemaining(selectedProgram.launch_date);
    const programIdx = programs.findIndex(p => p.id === selectedProgram.id);
    const programCode = `NMS-${String(Math.max(programIdx + 1, 1)).padStart(3, '0')}`;

    return (
      <div style={{ backgroundColor: t.bg, minHeight: '100vh', fontFamily: "'Roboto Mono', monospace", color: t.text }}>

        {/* ── USERNAME PROMPT ── */}
        {userNamePrompt && (
          <>
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000 }} onClick={() => setUserNamePrompt(false)} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '28px', zIndex: 1001, width: '320px' }}>
              <div style={{ fontSize: '11px', color: t.accent, letterSpacing: '2px', marginBottom: '8px' }}>SIGN-OFF AUTHORITY</div>
              <div style={{ fontSize: '13px', color: t.text, fontWeight: '600', marginBottom: '6px', lineHeight: 1.5 }}>Who is approving this work?</div>
              <div style={{ fontSize: '12px', color: t.muted, marginBottom: '16px', lineHeight: 1.6 }}>Enter the name of the lane lead or launch lead. This records who gave final approval — separate from the DRI who executed the task.</div>
              <input
                autoFocus
                placeholder="Your name"
                defaultValue={currentUser}
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    const name = e.target.value.trim();
                    setCurrentUser(name); localStorage.setItem('nomos_user', name);
                    setUserNamePrompt(false);
                    if (pendingSignOff) { signOffTask(pendingSignOff.milestoneId, pendingSignOff.gateId); setPendingSignOff(null); }
                  }
                }}
                style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '13px', boxSizing: 'border-box', marginBottom: '12px' }}
              />
              <button
                onClick={e => {
                  const input = e.target.previousSibling;
                  if (input.value.trim()) {
                    const name = input.value.trim();
                    setCurrentUser(name); localStorage.setItem('nomos_user', name);
                    setUserNamePrompt(false);
                    if (pendingSignOff) { signOffTask(pendingSignOff.milestoneId, pendingSignOff.gateId); setPendingSignOff(null); }
                  }
                }}
                style={{ width: '100%', padding: '8px', backgroundColor: t.accent, color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px' }}>
                SAVE & CONTINUE
              </button>
            </div>
          </>
        )}

        {/* ── LANE VIEW PANEL (slide from right) ── */}
        {laneViewOpen && selectedProgram && (
          <>
            {userRole !== 'member' && <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200 }} onClick={() => setLaneViewOpen(false)} />}
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: (isMobile || userRole === 'member') ? '100%' : '680px', backgroundColor: t.surface, borderLeft: `1px solid ${t.border}`, zIndex: 201, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>

              {/* Header */}
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>LANE VIEW · {selectedProgram.name}</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.2px' }}>{(laneViewLane || '').toUpperCase()}</div>
                    {!laneViewLoading && (laneViewData.gates || []).length > 0 && (() => {
                      const allMs = Object.values(laneViewData.milestones).flat();
                      const done = allMs.filter(m => m.completed).length;
                      return <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '4px' }}>{done} of {allMs.length} tasks signed off across 6 gates</div>;
                    })()}
                    {/* Team lead + DRIs from brief */}
                    {(teamLeads[laneViewLane] || (laneDris[laneViewLane] || []).length > 0) && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                        {teamLeads[laneViewLane] && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: t.accent + '15', border: `1px solid ${t.accent}40`, borderRadius: '20px', padding: '3px 9px' }}>
                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: t.accent + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: t.accent, fontWeight: 'bold', flexShrink: 0 }}>
                              {teamLeads[laneViewLane].slice(0, 2).toUpperCase()}
                            </div>
                            <span style={{ fontSize: '10px', color: t.accent }}>{teamLeads[laneViewLane]}</span>
                            <span style={{ fontSize: '8px', color: t.accent + '80', letterSpacing: '1px' }}>LEAD</span>
                          </div>
                        )}
                        {(laneDris[laneViewLane] || []).map(dri => (
                          <div key={dri} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '20px', padding: '3px 9px' }}>
                            <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: t.borderDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: t.subLabel, fontWeight: 'bold', flexShrink: 0 }}>
                              {dri.slice(0, 2).toUpperCase()}
                            </div>
                            <span style={{ fontSize: '10px', color: t.text }}>{dri}</span>
                            <span style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>DRI</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setLaneViewOpen(false)} style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '20px', padding: '0', lineHeight: 1 }}>✕</button>
                </div>
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                {laneViewLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: t.subLabel, fontSize: '11px', letterSpacing: '2px' }}>LOADING…</div>
                ) : (laneViewData.gates || []).length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{ color: t.subLabel, fontSize: '11px', letterSpacing: '2px', marginBottom: '12px' }}>NO GATE DATA</div>
                    <button onClick={() => openLaneView(laneViewLane)} style={{ fontSize: '10px', color: t.accent, background: 'none', border: `1px solid ${t.accent}50`, borderRadius: '4px', padding: '6px 16px', cursor: 'pointer', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>RETRY</button>
                  </div>
                ) : (
                  (laneViewData.gates || []).map((gate, gIdx) => {
                    const gateNames = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
                    const isExpanded = expandedGateInLane === gate.id;
                    const milestones = laneViewData.milestones[gate.id] || [];
                    const doneCount = milestones.filter(m => m.completed).length;
                    // Always derive % from in-memory milestones — DB value can lag
                    const livePct = milestones.length ? Math.round(doneCount / milestones.length * 100) : gate.completion_percent;
                    const statusColor = gate.status === 'complete' ? '#10b981' : gate.status === 'blocked' ? '#ef4444' : gate.status === 'at_risk' ? '#f59e0b' : gate.status === 'on_track' ? '#22d3ee' : t.borderDim;

                    return (
                      <div key={gate.id} style={{ borderBottom: `1px solid ${t.borderDimmer}` }}>
                        {/* Gate header — click to expand/collapse */}
                        <div
                          onClick={() => { setExpandedGateInLane(isExpanded ? null : gate.id); if (!isExpanded) { milestones.forEach(m => loadTaskResources(m.id)); } }}
                          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 24px', cursor: 'pointer', backgroundColor: isExpanded ? (t.bg + '80') : 'transparent' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '10px', color: t.dim, letterSpacing: '1px', fontWeight: '600' }}>G{gate.gate_number}</span>
                              <span style={{ fontSize: '13px', color: t.text, fontWeight: 'bold', letterSpacing: '0.3px' }}>{gateNames[gate.gate_number - 1]}</span>
                              {gate.status !== 'idle' && <span style={{ fontSize: '9px', color: statusColor, letterSpacing: '1px', border: `1px solid ${statusColor}40`, padding: '1px 5px', borderRadius: '3px' }}>{gate.status.replace('_', ' ').toUpperCase()}</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: t.muted, marginTop: '3px' }}>{doneCount}/{milestones.length} tasks complete · {livePct}%</div>
                          </div>
                          <div style={{ fontSize: '14px', color: t.dim, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</div>
                        </div>

                        {/* Task list */}
                        {isExpanded && (
                          <div style={{ paddingBottom: '8px' }}>
                            {milestones.map((ms, mIdx) => {
                              const isTaskExpanded = expandedTaskId === ms.id;
                              const resources = Array.isArray(taskResources[ms.id]) ? taskResources[ms.id] : [];
                              const signedOff = !!(ms.signed_off_by);
                              const driDone = ms.completed && !signedOff;
                              // circle: empty = not started, amber = DRI done awaiting review, green = lead approved
                              const circleColor = signedOff ? '#10b981' : driDone ? '#f59e0b' : t.borderDim;
                              const circleBg = signedOff ? '#10b981' : driDone ? '#f59e0b22' : 'transparent';
                              const borderColor = signedOff ? '#10b98130' : driDone ? '#f59e0b30' : t.borderDim;

                              return (
                                <div key={ms.id} style={{ margin: '0 16px 6px', backgroundColor: t.surface, border: `1px solid ${borderColor}`, borderRadius: '6px', overflow: 'hidden' }}>
                                  {/* Task row */}
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', cursor: 'pointer' }}
                                    onClick={() => { setExpandedTaskId(isTaskExpanded ? null : ms.id); if (!isTaskExpanded) loadTaskResources(ms.id); }}>
                                    {/* Circle — DRI clicks to mark work complete; does NOT sign off */}
                                    <div
                                      onClick={e => {
                                        e.stopPropagation();
                                        if (!signedOff) updateTaskField(ms.id, gate.id, 'completed', !ms.completed);
                                      }}
                                      title={signedOff ? 'Approved by lead' : ms.completed ? 'DRI marked complete — click to undo' : 'Click to mark your work complete'}
                                      style={{ width: '20px', height: '20px', borderRadius: '50%', border: `2px solid ${circleColor}`, backgroundColor: circleBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '0px', cursor: signedOff ? 'default' : 'pointer', transition: 'all 0.15s' }}>
                                      {signedOff && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>✓</span>}
                                      {driDone && <span style={{ color: '#f59e0b', fontSize: '10px', fontWeight: 'bold' }}>·</span>}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '13px', color: signedOff ? t.dim : t.text, textDecoration: signedOff ? 'line-through' : 'none', lineHeight: 1.4 }}>{ms.text}</div>
                                      <div style={{ display: 'flex', gap: '12px', marginTop: '3px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {ms.dri && <span style={{ fontSize: '10px', color: t.accent, letterSpacing: '0.5px' }}>DRI: {ms.dri}</span>}
                                        {driDone && <span style={{ fontSize: '10px', color: '#f59e0b', letterSpacing: '0.5px' }}>PENDING REVIEW</span>}
                                        {signedOff && <span style={{ fontSize: '10px', color: '#10b981' }}>✓ {ms.signed_off_by}{ms.signed_off_at ? ` · ${new Date(ms.signed_off_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</span>}
                                        {resources.length > 0 && <span style={{ fontSize: '10px', color: t.dim }}>{resources.length} resource{resources.length > 1 ? 's' : ''}</span>}
                                        {ms.ai_generated && <span style={{ fontSize: '8px', color: t.accent, letterSpacing: '1.5px', border: `1px solid ${t.accent}40`, borderRadius: '3px', padding: '1px 5px' }}>AI</span>}
                                      </div>
                                    </div>
                                    <div style={{ fontSize: '12px', color: t.subLabel, transform: isTaskExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>›</div>
                                  </div>

                                  {/* Expanded task detail */}
                                  {isTaskExpanded && (
                                    <div style={{ borderTop: `1px solid ${t.borderDimmer}`, padding: '12px' }}>
                                      {/* Description */}
                                      {ms.description && (
                                        <div style={{ fontSize: '12px', color: t.muted, lineHeight: 1.6, marginBottom: '12px', padding: '8px 10px', backgroundColor: darkMode ? '#111' : '#f8fafc', borderRadius: '4px', borderLeft: `2px solid ${t.accent}` }}>
                                          {ms.description}
                                        </div>
                                      )}

                                      {/* DRI */}
                                      {(() => {
                                        const lDris = laneDris[gate.lane] || [];
                                        const memberNames = teamMembers.map(m => m.full_name).filter(Boolean);
                                        const allSuggestions = [...new Set([...lDris, ...memberNames])];
                                        return (
                                          <div style={{ marginBottom: '10px' }}>
                                            <div style={{ fontSize: '10px', color: t.dim, letterSpacing: '1px', marginBottom: '4px' }}>
                                              DRI — DIRECTLY RESPONSIBLE
                                              {allSuggestions.length > 0 && <span style={{ color: t.accent, marginLeft: '6px', fontSize: '9px' }}>searchable</span>}
                                            </div>
                                            <DriTypeahead
                                              value={ms.dri || ''}
                                              onChange={name => {
                                                updateTaskField(ms.id, gate.id, 'dri', name);
                                                const match = programMembers.find(pm => pm.profiles?.full_name === name);
                                                updateTaskField(ms.id, gate.id, 'dri_user_id', match ? match.user_id : null);
                                              }}
                                              suggestions={allSuggestions}
                                              darkMode={darkMode}
                                            />
                                          </div>
                                        );
                                      })()}


                                      {/* Notes */}
                                      <div style={{ marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                          <div style={{ fontSize: '10px', color: t.dim, letterSpacing: '1px' }}>NOTES & CONTEXT</div>
                                          {savedNoteId === ms.id && <span style={{ fontSize: '9px', color: '#10b981', letterSpacing: '1px' }}>SAVED ✓</span>}
                                        </div>
                                        <textarea
                                          placeholder="Capture decisions, rationale, details, history…"
                                          defaultValue={ms.notes || ''}
                                          onBlur={e => {
                                            if (e.target.value !== ms.notes) {
                                              updateTaskField(ms.id, gate.id, 'notes', e.target.value);
                                              setSavedNoteId(ms.id);
                                              setTimeout(() => setSavedNoteId(null), 2000);
                                            }
                                          }}
                                          rows={3}
                                          style={{ width: '100%', padding: '6px 8px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '11px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
                                        />
                                      </div>

                                      {/* Resources */}
                                      <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontSize: '10px', color: t.dim, letterSpacing: '1px', marginBottom: '6px' }}>SUPPORTING DOCUMENTS & LINKS</div>
                                        {resources.map(r => (
                                          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', padding: '5px 8px', backgroundColor: t.card, borderRadius: '4px', border: `1px solid ${t.borderDimmer}` }}>
                                            <span style={{ fontSize: '11px' }}>🔗</span>
                                            <a href={r.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: '11px', color: t.accent, textDecoration: 'none' }}>{r.label}</a>
                                            <button onClick={() => deleteTaskResource(r.id, ms.id)} style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '12px', padding: '0 2px' }}>✕</button>
                                          </div>
                                        ))}
                                        {addingResourceTo === ms.id ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', backgroundColor: t.card, borderRadius: '4px', border: `1px solid ${t.border}` }}>
                                            <input placeholder="Label (e.g. Pricing Model, CAD File)" value={newResourceLabel} onChange={e => setNewResourceLabel(e.target.value)}
                                              style={{ padding: '5px 8px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '3px', color: t.text, fontSize: '11px' }} />
                                            <input placeholder="URL (Google Drive, Figma, Dropbox…)" value={newResourceUrl} onChange={e => setNewResourceUrl(e.target.value)}
                                              onKeyDown={e => e.key === 'Enter' && addTaskResource(ms.id)}
                                              style={{ padding: '5px 8px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '3px', color: t.text, fontSize: '11px' }} />
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                              <button onClick={() => addTaskResource(ms.id)} style={{ flex: 1, padding: '5px', backgroundColor: t.accent, color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.5px' }}>ADD</button>
                                              <button onClick={() => { setAddingResourceTo(null); setNewResourceLabel(''); setNewResourceUrl(''); }} style={{ padding: '5px 10px', backgroundColor: 'transparent', color: t.subLabel, border: `1px solid ${t.border}`, borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>CANCEL</button>
                                            </div>
                                          </div>
                                        ) : (
                                          <button onClick={() => setAddingResourceTo(ms.id)} style={{ fontSize: '10px', color: t.subLabel, background: 'none', border: `1px dashed ${t.borderDim}`, borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.5px' }}>+ Add link or document</button>
                                        )}
                                      </div>

                                      {/* Sign-off */}
                                      {!demoMode && <div style={{ borderTop: `1px solid ${t.borderDimmer}`, paddingTop: '12px' }}>
                                        {signedOff ? (
                                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                                            <div>
                                              <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>✓ Approved by {ms.signed_off_by}</div>
                                              {ms.signed_off_at && <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '2px' }}>{new Date(ms.signed_off_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                                              {ms.approval_note && <div style={{ fontSize: '11px', color: t.muted, marginTop: '6px', fontStyle: 'italic' }}>"{ms.approval_note}"</div>}
                                            </div>
                                            <button onClick={() => unsignOffTask(ms.id, gate.id)} style={{ fontSize: '9px', color: t.subLabel, background: 'none', border: `1px solid ${t.border}`, borderRadius: '3px', padding: '3px 8px', cursor: 'pointer', letterSpacing: '0.5px', flexShrink: 0 }}>UNDO</button>
                                          </div>
                                        ) : pendingApproval?.milestoneId === ms.id ? (
                                          <div style={{ backgroundColor: darkMode ? '#0a1a0a' : '#f0fdf4', border: '1px solid #10b98140', borderRadius: '6px', padding: '12px' }}>
                                            <div style={{ fontSize: '10px', color: '#10b981', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>CONFIRM APPROVAL</div>
                                            <div style={{ fontSize: '10px', color: t.subLabel, marginBottom: '8px' }}>
                                              {currentUser || 'You'} · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <textarea
                                              placeholder="Approval note (optional) — e.g. Reviewed against spec, assets delivered"
                                              value={approvalNote}
                                              onChange={e => setApprovalNote(e.target.value)}
                                              rows={2}
                                              style={{ width: '100%', padding: '6px 8px', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '11px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5, marginBottom: '8px' }}
                                            />
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                              <button
                                                onClick={() => { signOffTask(ms.id, gate.id, approvalNote); setPendingApproval(null); setApprovalNote(''); }}
                                                style={{ flex: 1, padding: '7px', backgroundColor: '#10b981', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>
                                                CONFIRM
                                              </button>
                                              <button
                                                onClick={() => { setPendingApproval(null); setApprovalNote(''); }}
                                                style={{ padding: '7px 14px', backgroundColor: 'transparent', color: t.subLabel, border: `1px solid ${t.border}`, borderRadius: '4px', cursor: 'pointer', fontSize: '10px' }}>
                                                CANCEL
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <button onClick={() => setPendingApproval({ milestoneId: ms.id, gateId: gate.id })}
                                            style={{ padding: '7px 18px', backgroundColor: '#10b98115', color: '#10b981', border: '1px solid #10b98140', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1px' }}>
                                            APPROVE{currentUser ? ` AS ${currentUser.toUpperCase()}` : ' TASK'}
                                          </button>
                                        )}
                                      </div>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '12px 24px', borderTop: `1px solid ${t.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: '10px', color: t.subLabel }}>
                  {currentUser ? `Approving as ${currentUser}` : 'No approver set'}
                  {teamLeads[laneViewLane] && currentUser === teamLeads[laneViewLane] && (
                    <span style={{ marginLeft: '6px', fontSize: '9px', color: t.accent, letterSpacing: '0.5px' }}>· TEAM LEAD</span>
                  )}
                  <button onClick={() => setUserNamePrompt(true)} style={{ marginLeft: '8px', fontSize: '9px', color: t.accent, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.5px', padding: 0 }}>
                    {currentUser ? 'CHANGE' : 'SET NAME'}
                  </button>
                </div>
                <button onClick={() => setLaneViewOpen(false)} style={{ fontSize: '10px', color: t.subLabel, background: 'none', border: `1px solid ${t.border}`, borderRadius: '4px', padding: '5px 14px', cursor: 'pointer', letterSpacing: '1px' }}>CLOSE</button>
              </div>
            </div>
          </>
        )}

        {/* ── TEAM WORKSPACE (full page overlay) ── */}
        {workspaceView && selectedProgram && (() => {
          const laneColorMap = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
          const customPalette = ['#ec4899', '#84cc16', '#f97316', '#14b8a6', '#a855f7', '#64748b'];
          const defaultLanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const isDefault = defaultLanes.includes(workspaceView);
          const allCustomKeys = Object.keys({ ...teamLeads }).filter(k => !defaultLanes.includes(k));
          const customIdx = allCustomKeys.indexOf(workspaceView);
          const color = laneColorMap[workspaceView] || customPalette[customIdx >= 0 ? customIdx % customPalette.length : 0];
          const laneGates = isDefault ? (laneViewData.gates || []).filter(g => g.lane === workspaceView).sort((a, b) => a.gate_number - b.gate_number) : [];
          const gateNamesArr = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
          const displayName = workspaceView.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const tw = (selectedProgram.team_workspace || {})[workspaceView] || {};
          const teamDocs = tw.documents || [];
          const lead = teamLeads[workspaceView] || '';
          const dris = laneDris[workspaceView] || [];
          const unreadCount = mailboxUnread?.count || 0;
          const tabs = [
            { id: 'overview', label: 'OVERVIEW' },
            ...(isDefault ? [{ id: 'gates', label: 'GATES' }] : []),
            { id: 'mailbox', label: unreadCount > 0 ? `MAILBOX (${unreadCount})` : 'MAILBOX' },
            { id: 'notes', label: 'NOTES' },
            { id: 'documents', label: 'DOCUMENTS' },
            { id: 'team', label: 'TEAM' },
          ];
          return (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: t.bg, zIndex: 500, overflowY: 'auto', fontFamily: "'Roboto Mono', monospace" }}>

              {/* Sticky header */}
              <div style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: t.surface, borderBottom: `1px solid ${t.borderDim}`, padding: isMobile ? '0 16px' : '0 40px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                  <button onClick={() => setWorkspaceView(null)} style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', fontSize: '11px', letterSpacing: '1.5px', padding: 0, display: 'flex', alignItems: 'center', gap: '5px', fontFamily: "'Roboto Mono', monospace" }}>
                    ← BACK
                  </button>
                  {!isMobile && <>
                    <div style={{ width: '1px', height: '16px', backgroundColor: t.borderDim }} />
                    <div style={{ width: '20px', height: '20px', backgroundColor: t.accent, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>N</div>
                    <span style={{ fontSize: '10px', letterSpacing: '3px', color: t.accent, fontWeight: 'bold' }}>NOMOS</span>
                  </>}
                </div>
                <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedProgram.name}</div>
                <ThemeToggle darkMode={darkMode} onToggle={() => setDarkMode(d => !d)} style={s.toggleBtn} />
              </div>

              <div style={{ maxWidth: '960px', margin: '0 auto', padding: isMobile ? '28px 16px' : '44px 48px' }}>

                {/* Identity bar */}
                <div style={{ marginBottom: '32px', paddingBottom: '28px', borderBottom: `1px solid ${t.borderDim}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                    <span style={{ fontSize: '9px', color: color, letterSpacing: '3px', fontWeight: '700' }}>WORKSPACE · {(authCompany?.name || '').toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: isMobile ? '28px' : '40px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px', lineHeight: 1.1, marginBottom: '6px' }}>
                    {displayName}
                  </div>
                  <div style={{ fontSize: '13px', color: t.muted }}>
                    {selectedProgram.name} · Gate {selectedProgram.current_gate} of 6
                    {lead && <span style={{ color: t.subLabel }}> · Led by {lead}</span>}
                  </div>
                </div>

                {/* Gate status chips — default lanes only */}
                {isDefault && laneGates.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {laneGates.map(gs => {
                      const sColor = gs.status === 'complete' ? '#10b981' : gs.status === 'blocked' ? '#ef4444' : gs.status === 'at_risk' ? '#f59e0b' : gs.status === 'on_track' ? color : t.borderDim;
                      const isCurrent = gs.gate_number === selectedProgram.current_gate;
                      return (
                        <button key={gs.id} onClick={() => { setWorkspaceTab('gates'); setExpandedGateInLane(gs.id); }}
                          style={{ flexShrink: 0, padding: '8px 14px', borderRadius: '6px', border: `1px solid ${isCurrent ? color : sColor + '50'}`, backgroundColor: isCurrent ? color + '15' : 'transparent', cursor: 'pointer', background: isCurrent ? color + '15' : 'transparent', textAlign: 'left' }}>
                          <div style={{ fontSize: '8px', color: isCurrent ? color : t.subLabel, letterSpacing: '1.5px', marginBottom: '3px', fontFamily: "'Roboto Mono', monospace" }}>G{gs.gate_number}</div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: sColor, letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>{gs.status.toUpperCase().replace('_', ' ')}</div>
                          <div style={{ width: '40px', height: '2px', backgroundColor: t.borderDim, borderRadius: '1px', marginTop: '5px' }}>
                            <div style={{ height: '100%', width: `${gs.completion_percent}%`, backgroundColor: color, borderRadius: '1px' }} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: `1px solid ${t.borderDim}`, marginBottom: '28px', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex' }}>
                    {tabs.map(tab => (
                      <button key={tab.id} onClick={() => setWorkspaceTab(tab.id)} style={{
                        padding: '10px 20px', background: 'none', border: 'none', marginBottom: '-1px',
                        borderBottom: workspaceTab === tab.id ? `2px solid ${color}` : '2px solid transparent',
                        fontSize: '10px', letterSpacing: '2px', fontWeight: workspaceTab === tab.id ? '700' : '500',
                        color: workspaceTab === tab.id ? color : t.dim, cursor: 'pointer', fontFamily: "'Roboto Mono', monospace",
                      }}>{tab.label}</button>
                    ))}
                  </div>
                  <button
                    onClick={() => setWorkspaceView(null)}
                    style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '5px 14px', fontSize: '9px', color: t.dim, letterSpacing: '1.5px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace", marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    ← FULL PROGRAM VIEW
                  </button>
                </div>

                {/* ── GATES TAB ── */}
                {workspaceTab === 'gates' && isDefault && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {laneViewLoading ? (
                      <div style={{ color: t.subLabel, fontSize: '12px', padding: '20px 0' }}>Loading gates...</div>
                    ) : laneGates.length === 0 ? (
                      <div style={{ color: t.subLabel, fontSize: '12px', padding: '20px 0' }}>No gate data for this lane yet.</div>
                    ) : laneGates.map(gs => {
                      const isExp = expandedGateInLane === gs.id;
                      const sColor = gs.status === 'complete' ? '#10b981' : gs.status === 'blocked' ? '#ef4444' : gs.status === 'at_risk' ? '#f59e0b' : gs.status === 'on_track' ? color : t.dim;
                      const ms = laneViewData.milestones[gs.id] || [];
                      const doneMs = ms.filter(m => m.completed).length;
                      return (
                        <div key={gs.id} style={{ backgroundColor: t.surface, border: `1px solid ${isExp ? color + '50' : t.borderDim}`, borderRadius: '10px', overflow: 'hidden' }}>
                          {/* Gate row */}
                          <div onClick={() => setExpandedGateInLane(isExp ? null : gs.id)}
                            style={{ padding: '18px 22px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: isExp ? color + '20' : t.surfaceAlt, border: `1px solid ${isExp ? color + '40' : t.borderDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color: isExp ? color : t.muted, flexShrink: 0 }}>
                                G{gs.gate_number}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", lineHeight: 1.3 }}>{gateNamesArr[gs.gate_number - 1]}</div>
                                <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '3px', letterSpacing: '0.5px' }}>
                                  {ms.length > 0 ? `${doneMs}/${ms.length} tasks done` : 'No tasks loaded'}
                                  {gs.planned_close_date ? ` · Due ${gs.planned_close_date.slice(5)}` : ''}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '9px', fontWeight: '700', color: sColor, letterSpacing: '1.5px', marginBottom: '5px' }}>{gs.status.toUpperCase().replace('_', ' ')}</div>
                                <div style={{ width: '72px', height: '3px', backgroundColor: t.borderDim, borderRadius: '2px' }}>
                                  <div style={{ height: '100%', width: `${gs.completion_percent}%`, backgroundColor: color, borderRadius: '2px' }} />
                                </div>
                                <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px' }}>{gs.completion_percent}%</div>
                              </div>
                              <span style={{ color: t.subLabel, fontSize: '11px' }}>{isExp ? '▲' : '▼'}</span>
                            </div>
                          </div>

                          {/* Expanded content */}
                          {isExp && (
                            <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: '20px 22px' }}>
                              {/* Milestones */}
                              {ms.length > 0 ? (
                                <div style={{ marginBottom: gs.notes ? '20px' : 0 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>TASKS · {ms.filter(m => m.completed).length}/{ms.length} COMPLETE</div>
                                  </div>
                                  {ms.map(m => {
                                    const isTaskExpanded = expandedWorkspaceTask === m.id;
                                    return (
                                    <div key={m.id} style={{ borderBottom: `1px solid ${t.borderDimmer}` }}>
                                      {/* Main task row */}
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0' }}>
                                        {/* Clickable checkbox */}
                                        <div
                                          onClick={() => toggleWorkspaceMilestone(m, gs.id)}
                                          style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${m.completed ? '#10b981' : t.border}`, backgroundColor: m.completed ? '#10b98120' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px', cursor: 'pointer' }}>
                                          {m.completed && <span style={{ color: '#10b981', fontSize: '12px', lineHeight: 1, fontWeight: 'bold' }}>✓</span>}
                                        </div>
                                        {/* Task content */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div
                                            onClick={() => {
                                              if (isTaskExpanded) { setExpandedWorkspaceTask(null); }
                                              else { setExpandedWorkspaceTask(m.id); setWorkspaceTaskNote(m.notes || ''); }
                                            }}
                                            style={{ fontSize: '13px', color: m.completed ? t.subLabel : t.text, textDecoration: m.completed ? 'line-through' : 'none', lineHeight: 1.5, marginBottom: '4px', cursor: 'pointer' }}>
                                            {m.text}
                                          </div>
                                          {/* Note toggle link */}
                                          {!isTaskExpanded ? (
                                            <div style={{ marginBottom: '4px' }}>
                                              {m.notes ? (
                                                <div
                                                  onClick={() => { setExpandedWorkspaceTask(m.id); setWorkspaceTaskNote(m.notes || ''); }}
                                                  style={{ fontSize: '11px', color: t.muted, lineHeight: 1.6, borderLeft: `2px solid ${color}50`, paddingLeft: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                                                  {m.notes}
                                                  <span style={{ color: color, marginLeft: '6px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace" }}>edit ✎</span>
                                                </div>
                                              ) : (
                                                <button
                                                  onClick={() => { setExpandedWorkspaceTask(m.id); setWorkspaceTaskNote(''); }}
                                                  style={{ background: 'none', border: 'none', color: t.subLabel, fontSize: '11px', cursor: 'pointer', padding: '0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                  <span style={{ fontSize: '12px' }}>+</span> add note
                                                </button>
                                              )}
                                            </div>
                                          ) : null}
                                          {/* Sign-off status */}
                                          {m.signed_off_by ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: color + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color, fontWeight: 'bold' }}>{m.signed_off_by.slice(0, 2).toUpperCase()}</div>
                                                <span style={{ fontSize: '10px', color: t.dim }}>Signed off by <strong>{m.signed_off_by}</strong></span>
                                              </div>
                                              {!demoMode && <button onClick={() => unsignOffTask(m.id, gs.id)} style={{ fontSize: '9px', color: t.subLabel, background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '2px 6px', cursor: 'pointer' }}>UNDO</button>}
                                            </div>
                                          ) : m.completed && !demoMode ? (
                                            <button
                                              onClick={() => { if (currentUser) signOffTask(m.id, gs.id); else { setPendingSignOff({ milestoneId: m.id, gateId: gs.id }); setUserNamePrompt(true); } }}
                                              style={{ fontSize: '9px', color, backgroundColor: color + '15', border: `1px solid ${color}40`, borderRadius: '4px', padding: '3px 9px', cursor: 'pointer', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", fontWeight: '600', marginTop: '4px' }}>
                                              + SIGN OFF
                                            </button>
                                          ) : null}
                                        </div>
                                        {/* DRI tag */}
                                        {m.dri && <div style={{ fontSize: '9px', color: t.subLabel, backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '2px 9px', flexShrink: 0, alignSelf: 'flex-start', marginTop: '2px' }}>{m.dri}</div>}
                                      </div>
                                      {/* Inline notes + attachments editor (expanded) */}
                                      {isTaskExpanded && (() => {
                                        const resources = taskResources[m.id];
                                        if (resources === undefined) loadTaskResources(m.id);
                                        const docs = resources || [];
                                        const addLink = () => {
                                          if (!wsResUrl.trim()) return;
                                          authFetch(`${API}/milestones/${m.id}/resources`, {
                                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ label: wsResLabel.trim() || wsResUrl.trim(), url: wsResUrl.trim(), resource_type: 'link' }),
                                          }).then(r => r.json()).then(res => {
                                            setTaskResources(prev => ({ ...prev, [m.id]: [...(prev[m.id] || []), res] }));
                                            setWsResLabel(''); setWsResUrl('');
                                          });
                                        };
                                        const uploadFile = (file) => {
                                          setWsResUploading(true);
                                          const fd = new FormData();
                                          fd.append('file', file);
                                          fd.append('label', file.name);
                                          authFetch(`${API}/milestones/${m.id}/upload-doc`, { method: 'POST', body: fd })
                                            .then(r => r.json())
                                            .then(res => { setTaskResources(prev => ({ ...prev, [m.id]: [...(prev[m.id] || []), res] })); })
                                            .finally(() => setWsResUploading(false));
                                        };
                                        const removeResource = (resId) => {
                                          authFetch(`${API}/resources/${resId}`, { method: 'DELETE' });
                                          setTaskResources(prev => ({ ...prev, [m.id]: (prev[m.id] || []).filter(r => r.id !== resId) }));
                                        };
                                        return (
                                          <div style={{ paddingLeft: '32px', paddingBottom: '16px' }}>
                                            {/* Notes */}
                                            <div style={{ fontSize: '9px', color: color, letterSpacing: '2px', marginBottom: '7px', fontWeight: '700' }}>TASK NOTES</div>
                                            <textarea
                                              autoFocus
                                              value={workspaceTaskNote}
                                              onChange={e => setWorkspaceTaskNote(e.target.value)}
                                              placeholder="Add notes, context, decisions, or blockers specific to this task..."
                                              style={{ width: '100%', minHeight: '90px', padding: '10px 12px', backgroundColor: t.bg, border: `1px solid ${color}40`, borderRadius: '6px', color: t.text, fontSize: '12px', lineHeight: '1.6', fontFamily: "'Inter', sans-serif", resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                                            />
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '20px' }}>
                                              <button
                                                onClick={() => {
                                                  updateMilestoneField(m.id, 'notes', workspaceTaskNote);
                                                  setLaneViewData(prev => ({
                                                    ...prev,
                                                    milestones: { ...prev.milestones, [gs.id]: (prev.milestones[gs.id] || []).map(x => x.id === m.id ? { ...x, notes: workspaceTaskNote } : x) },
                                                  }));
                                                  setExpandedWorkspaceTask(null);
                                                }}
                                                style={{ backgroundColor: color, border: 'none', color: '#fff', padding: '6px 16px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Roboto Mono', monospace" }}>
                                                SAVE NOTE
                                              </button>
                                              <button onClick={() => setExpandedWorkspaceTask(null)} style={{ background: 'none', border: `1px solid ${t.borderDim}`, color: t.dim, padding: '6px 12px', fontSize: '10px', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Roboto Mono', monospace" }}>
                                                CANCEL
                                              </button>
                                            </div>

                                            {/* Attachments */}
                                            <div style={{ borderTop: `1px solid ${t.borderDim}`, paddingTop: '14px' }}>
                                              <div style={{ fontSize: '9px', color: color, letterSpacing: '2px', marginBottom: '10px', fontWeight: '700' }}>SUPPORTING DOCUMENTS</div>

                                              {/* Existing attachments */}
                                              {docs.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                                  {docs.map(doc => (
                                                    <div key={doc.id} style={{ backgroundColor: t.bg, border: `1px solid ${t.borderDim}`, borderRadius: '6px', overflow: 'hidden' }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px' }}>
                                                        <span style={{ fontSize: '14px', flexShrink: 0 }}>{doc.resource_type === 'file' ? '📎' : '🔗'}</span>
                                                        <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '12px', color: t.accent, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label}</a>
                                                        <button onClick={() => removeResource(doc.id)} style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '14px', padding: '0 2px', opacity: 0.6, flexShrink: 0 }}>✕</button>
                                                      </div>
                                                      {doc.ai_summary && (
                                                        <div style={{ borderTop: `1px solid ${color}20`, padding: '10px 12px', backgroundColor: `${color}06` }}>
                                                          <div style={{ fontSize: '8px', color: color, letterSpacing: '2px', fontWeight: '700', marginBottom: '6px' }}>AI ANALYSIS</div>
                                                          <div style={{ fontSize: '11px', color: t.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{doc.ai_summary}</div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}

                                              {/* Upload file */}
                                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', border: `1px dashed ${t.border}`, borderRadius: '6px', cursor: 'pointer', marginBottom: '4px', color: t.dim, fontSize: '11px' }}>
                                                <span style={{ fontSize: '16px' }}>📎</span>
                                                {wsResUploading ? 'Uploading...' : 'Upload a file — PDF, AI, PSD, DWG, STL, PNG, XLSX, PPTX (up to 100 MB)'}
                                                <input type="file" accept=".pdf,.ai,.eps,.psd,.indd,.xd,.fig,.dwg,.dxf,.stl,.obj,.step,.stp,.iges,.png,.jpg,.jpeg,.gif,.tiff,.svg,.xlsx,.xls,.csv,.pptx,.ppt,.docx,.doc,.zip" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} />
                                              </label>
                                              <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '0.5px', marginBottom: '8px', paddingLeft: '2px' }}>Adobe · CAD · Office · Images · Archives</div>

                                              {/* Paste a link */}
                                              <div style={{ display: 'flex', gap: '6px' }}>
                                                <input placeholder="Label (optional)" value={wsResLabel} onChange={e => setWsResLabel(e.target.value)}
                                                  style={{ flex: '0 0 120px', padding: '6px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '11px', fontFamily: 'inherit', outline: 'none' }} />
                                                <input placeholder="Paste a URL (Google Doc, Figma, etc.)" value={wsResUrl} onChange={e => setWsResUrl(e.target.value)}
                                                  onKeyDown={e => e.key === 'Enter' && addLink()}
                                                  style={{ flex: 1, padding: '6px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '11px', fontFamily: 'inherit', outline: 'none' }} />
                                                <button onClick={addLink} disabled={!wsResUrl.trim()} style={{ backgroundColor: !wsResUrl.trim() ? t.borderDim : color, border: 'none', color: !wsResUrl.trim() ? t.subLabel : '#fff', padding: '6px 14px', fontSize: '10px', fontWeight: 'bold', cursor: !wsResUrl.trim() ? 'not-allowed' : 'pointer', borderRadius: '4px', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", flexShrink: 0 }}>ADD</button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ fontSize: '12px', color: t.subLabel, marginBottom: gs.notes ? '20px' : 0 }}>Open the gate panel to seed and manage tasks.</div>
                              )}
                              {/* Gate notes */}
                              {gs.notes && (
                                <div style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '14px 16px' }}>
                                  <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>GATE NOTES</div>
                                  <div style={{ fontSize: '13px', color: t.muted, lineHeight: 1.7 }}>{gs.notes}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── NOTES TAB ── */}
                {workspaceTab === 'notes' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '4px' }}>{displayName} Team Notes</div>
                        <div style={{ fontSize: '11px', color: t.subLabel }}>Capture strategy, decisions, constraints, and context for this team.</div>
                      </div>
                      <button onClick={saveWorkspaceNotes} disabled={workspaceNotesSaving} style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: 'bold', cursor: workspaceNotesSaving ? 'not-allowed' : 'pointer', borderRadius: '4px', opacity: workspaceNotesSaving ? 0.6 : 1, flexShrink: 0 }}>
                        {workspaceNotesSaving ? 'SAVING...' : 'SAVE'}
                      </button>
                    </div>
                    <textarea
                      value={workspaceNotes}
                      onChange={e => setWorkspaceNotes(e.target.value)}
                      placeholder={`Write ${displayName.toLowerCase()} team notes here...\n\nIdeas for what to capture:\n• Design principles and constraints\n• Key decisions and rationale\n• Open questions\n• Dependencies on other teams\n• Risks and mitigation approaches`}
                      style={{ width: '100%', minHeight: '420px', padding: '18px', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.text, fontSize: '13px', lineHeight: '1.8', fontFamily: "'Inter', sans-serif", resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>
                )}

                {/* ── DOCUMENTS TAB ── */}
                {workspaceTab === 'documents' && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '4px' }}>{displayName} Documents</div>
                      <div style={{ fontSize: '11px', color: t.subLabel }}>Link to briefs, specs, mockups, approvals, and reference materials.</div>
                    </div>

                    {/* Document list */}
                    {teamDocs.length > 0 && (
                      <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {teamDocs.map((doc, idx) => (
                          <div key={doc.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px' }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: color + '20', border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>📄</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: t.text, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label}</div>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: t.accent, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }} onClick={e => e.stopPropagation()}>{doc.url}</a>
                            </div>
                            <button onClick={() => { const updated = teamDocs.filter((_, i) => i !== idx); saveWorkspaceDocs(updated); }} style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '16px', padding: '4px', opacity: 0.6, flexShrink: 0 }} title="Remove">✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add new document */}
                    <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px' }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px' }}>ADD DOCUMENT</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '10px', color: t.dim, letterSpacing: '1px', marginBottom: '5px' }}>LABEL</label>
                          <input placeholder="e.g. Design Brief v2, Figma mockups, Approval email" value={newDocLabel} onChange={e => setNewDocLabel(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '13px', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", outline: 'none' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '10px', color: t.dim, letterSpacing: '1px', marginBottom: '5px' }}>URL</label>
                          <input placeholder="https://..." value={newDocUrl} onChange={e => setNewDocUrl(e.target.value)} type="url"
                            onKeyDown={e => {
                              if (e.key === 'Enter' && newDocLabel.trim() && newDocUrl.trim()) {
                                const updated = [...teamDocs, { id: Date.now().toString(), label: newDocLabel.trim(), url: newDocUrl.trim() }];
                                saveWorkspaceDocs(updated);
                                setNewDocLabel(''); setNewDocUrl('');
                              }
                            }}
                            style={{ width: '100%', padding: '9px 12px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontSize: '13px', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", outline: 'none' }} />
                        </div>
                        <button
                          disabled={!newDocLabel.trim() || !newDocUrl.trim() || workspaceDocsSaving}
                          onClick={() => {
                            if (!newDocLabel.trim() || !newDocUrl.trim()) return;
                            const updated = [...teamDocs, { id: Date.now().toString(), label: newDocLabel.trim(), url: newDocUrl.trim() }];
                            saveWorkspaceDocs(updated);
                            setNewDocLabel(''); setNewDocUrl('');
                          }}
                          style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '10px 20px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: 'bold', cursor: (!newDocLabel.trim() || !newDocUrl.trim()) ? 'not-allowed' : 'pointer', borderRadius: '4px', opacity: (!newDocLabel.trim() || !newDocUrl.trim()) ? 0.5 : 1, alignSelf: 'flex-start' }}>
                          {workspaceDocsSaving ? 'SAVING...' : 'ADD DOCUMENT'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── TEAM TAB ── */}
                {workspaceTab === 'mailbox' && (() => {
                  const myId = session?.user?.id;
                  const inbox = mailboxMessages.filter(m => m.to_user_id === myId);
                  const sent = mailboxMessages.filter(m => m.from_user_id === myId);
                  const otherMembers = teamMembers.filter(m => m.id !== myId);
                  const formatTime = (ts) => { const d = new Date(ts); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
                  const laneColorMap2 = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };

                  if (selectedMessage) {
                    const isInbound = selectedMessage.to_user_id === myId;
                    const fromColor = laneColorMap2[selectedMessage.from_lane] || t.accent;
                    return (
                      <div style={{ maxWidth: '640px' }}>
                        <button onClick={() => setSelectedMessage(null)} style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', fontSize: '11px', letterSpacing: '1px', padding: '0 0 20px', fontFamily: "'Roboto Mono', monospace', display: 'flex', alignItems: 'center', gap: '5px" }}>← BACK TO MAILBOX</button>
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '24px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: t.text, marginBottom: '6px' }}>{selectedMessage.subject || '(no subject)'}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '9px', color: fromColor, letterSpacing: '2px', fontWeight: '700', border: `1px solid ${fromColor}40`, borderRadius: '3px', padding: '2px 6px' }}>{selectedMessage.from_lane.toUpperCase()}</span>
                                <span style={{ fontSize: '11px', color: t.muted }}>{selectedMessage.from_name}</span>
                                <span style={{ fontSize: '10px', color: t.dim }}>→</span>
                                <span style={{ fontSize: '9px', color: laneColorMap2[selectedMessage.to_lane] || t.accent, letterSpacing: '2px', fontWeight: '700' }}>{selectedMessage.to_lane.toUpperCase()}</span>
                              </div>
                            </div>
                            <div style={{ fontSize: '10px', color: t.dim, flexShrink: 0 }}>{formatTime(selectedMessage.created_at)}</div>
                          </div>
                          <div style={{ borderTop: `1px solid ${t.borderDim}`, paddingTop: '16px', fontSize: '13px', color: t.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selectedMessage.body}</div>
                          {isInbound && (
                            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${t.borderDim}` }}>
                              <button onClick={() => { setComposeTo(selectedMessage.from_user_id); setComposeSubject(`Re: ${selectedMessage.subject || ''}`); setComposeOpen(true); setSelectedMessage(null); }}
                                style={{ backgroundColor: color, border: 'none', color: '#000', padding: '8px 20px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '4px' }}>
                                REPLY TO {selectedMessage.from_lane.toUpperCase()}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div style={{ maxWidth: '640px' }}>
                      {/* Compose button */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif" }}>
                          {displayName} Mailbox
                          {mailboxLoading && <span style={{ fontSize: '10px', color: t.muted, marginLeft: '10px' }}>Loading...</span>}
                        </div>
                        <button onClick={() => { setComposeOpen(true); setComposeTo(''); setComposeSubject(''); setComposeBody(''); }}
                          style={{ backgroundColor: color, border: 'none', color: '#000', padding: '7px 16px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '4px' }}>
                          + COMPOSE
                        </button>
                      </div>

                      {/* Compose modal */}
                      {composeOpen && (
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${color}50`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                          <div style={{ fontSize: '9px', color: color, letterSpacing: '2px', marginBottom: '14px', fontWeight: '700' }}>NEW MESSAGE FROM {workspaceView.toUpperCase()}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                            <div>
                              <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>TO</div>
                              {(() => {
                                // Build candidate list: real accounts first, then team leads + DRIs from brief
                                const namedLeads = Object.entries(teamLeads || {}).map(([lane, name]) => ({ id: `lead:${lane}`, label: `${name} · ${lane.toUpperCase()} LEAD` }));
                                const namedDris = Object.entries(laneDris || {}).flatMap(([lane, dris]) => (dris || []).map(d => ({ id: `dri:${lane}:${d}`, label: `${d} · ${lane.toUpperCase()}` })));
                                const namedOptions = [...namedLeads, ...namedDris].filter((v, i, a) => a.findIndex(x => x.label === v.label) === i);
                                const hasOptions = otherMembers.length > 0 || namedOptions.length > 0;
                                if (hasOptions) return (
                                  <select value={composeTo} onChange={e => setComposeTo(e.target.value)} style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: composeTo ? t.text : t.dim, fontFamily: "'Roboto Mono', monospace", fontSize: '11px' }}>
                                    <option value="">Select person...</option>
                                    {otherMembers.length > 0 && <optgroup label="NOMOS MEMBERS">{otherMembers.map(m => <option key={m.id} value={m.id}>{m.full_name}{m.lane ? ` · ${m.lane.toUpperCase()}` : ''}</option>)}</optgroup>}
                                    {namedOptions.length > 0 && <optgroup label="TEAM">{namedOptions.map(o => <option key={o.id} value={o.label.split(' · ')[0]}>{o.label}</option>)}</optgroup>}
                                  </select>
                                );
                                return <input value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="Type a name or lane..." style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '11px', boxSizing: 'border-box' }} />;
                              })()}
                            </div>
                            <div>
                              <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>SUBJECT</div>
                              <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Optional subject line" style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '11px', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>MESSAGE</div>
                              <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={5} placeholder={`Write your note to ${otherMembers.find(m => m.id === composeTo)?.full_name || '...'}`} style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '11px', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={sendMessage} disabled={composeSending || !composeTo.trim() || !composeBody.trim()}
                              style={{ backgroundColor: color, border: 'none', color: '#000', padding: '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '2px', fontWeight: '700', cursor: composeSending || !composeTo.trim() || !composeBody.trim() ? 'not-allowed' : 'pointer', borderRadius: '4px', opacity: !composeTo.trim() || !composeBody.trim() ? 0.5 : 1 }}>
                              {composeSending ? 'SENDING...' : 'SEND'}
                            </button>
                            <button onClick={saveDraft} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '8px 14px', fontSize: '9px', color: t.dim, letterSpacing: '1px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>SAVE DRAFT</button>
                            <button onClick={() => { setComposeOpen(false); setEditingDraftId(null); }} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '8px 14px', fontSize: '9px', color: t.dim, letterSpacing: '1px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>CANCEL</button>
                          </div>
                        </div>
                      )}

                      {/* Mailbox section tabs */}
                      {(() => {
                        const programDrafts = drafts.filter(d => d.programId === selectedProgram?.id);
                        const msgBtnStyle = (id) => ({ background: 'none', border: 'none', padding: '6px 0', fontSize: '9px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer', color: mailboxSection === id ? t.text : t.dim, borderBottom: mailboxSection === id ? `2px solid ${color}` : '2px solid transparent', marginRight: '20px' });
                        return (
                          <div>
                            <div style={{ display: 'flex', borderBottom: `1px solid ${t.borderDim}`, marginBottom: '16px' }}>
                              <button style={msgBtnStyle('inbox')} onClick={() => setMailboxSection('inbox')}>INBOX {inbox.length > 0 && `· ${inbox.length}`}</button>
                              <button style={msgBtnStyle('sent')} onClick={() => setMailboxSection('sent')}>SENT {sent.length > 0 && `· ${sent.length}`}</button>
                              <button style={msgBtnStyle('drafts')} onClick={() => setMailboxSection('drafts')}>DRAFTS {programDrafts.length > 0 && `· ${programDrafts.length}`}</button>
                            </div>

                            {mailboxSection === 'inbox' && (inbox.length === 0 ? (
                              <div style={{ fontSize: '12px', color: t.muted, padding: '20px', textAlign: 'center', backgroundColor: t.surface, borderRadius: '8px', border: `1px solid ${t.borderDim}` }}>No messages yet</div>
                            ) : inbox.map(msg => {
                              const isUnread = !msg.read;
                              const fromColor = laneColorMap2[msg.from_lane] || t.accent;
                              return (
                                <div key={msg.id} onClick={() => setSelectedMessage(msg)} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${isUnread ? color + '50' : t.borderDim}`, borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', transition: 'opacity 0.15s' }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                                  {isUnread && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: color, flexShrink: 0, marginTop: '5px' }} />}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '8px', color: fromColor, letterSpacing: '2px', fontWeight: '700', border: `1px solid ${fromColor}40`, borderRadius: '3px', padding: '1px 5px' }}>{msg.from_lane.toUpperCase()}</span>
                                        <span style={{ fontSize: '11px', color: t.muted }}>{msg.from_name}</span>
                                      </div>
                                      <span style={{ fontSize: '9px', color: t.dim, flexShrink: 0 }}>{formatTime(msg.created_at)}</span>
                                    </div>
                                    {msg.subject && <div style={{ fontSize: '12px', fontWeight: isUnread ? '700' : '500', color: t.text, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.subject}</div>}
                                    <div style={{ fontSize: '11px', color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.body}</div>
                                  </div>
                                </div>
                              );
                            }))}

                            {mailboxSection === 'sent' && (sent.length === 0 ? (
                              <div style={{ fontSize: '12px', color: t.muted, padding: '20px', textAlign: 'center', backgroundColor: t.surface, borderRadius: '8px', border: `1px solid ${t.borderDim}` }}>No sent messages</div>
                            ) : sent.map(msg => {
                              const toColor = laneColorMap2[msg.to_lane] || t.accent;
                            return (
                              <div key={msg.id} onClick={() => setSelectedMessage(msg)} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ fontSize: '9px', color: t.dim }}>TO</span>
                                      <span style={{ fontSize: '8px', color: toColor, letterSpacing: '2px', fontWeight: '700', border: `1px solid ${toColor}40`, borderRadius: '3px', padding: '1px 5px' }}>{msg.to_lane.toUpperCase()}</span>
                                    </div>
                                    <span style={{ fontSize: '9px', color: t.dim, flexShrink: 0 }}>{formatTime(msg.created_at)}</span>
                                  </div>
                                  {msg.subject && <div style={{ fontSize: '12px', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.subject}</div>}
                                  <div style={{ fontSize: '11px', color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.body}</div>
                                </div>
                              </div>
                            );
                          }))}

                            {mailboxSection === 'drafts' && (programDrafts.length === 0 ? (
                              <div style={{ fontSize: '12px', color: t.muted, padding: '20px', textAlign: 'center', backgroundColor: t.surface, borderRadius: '8px', border: `1px solid ${t.borderDim}` }}>No drafts saved</div>
                            ) : programDrafts.map(draft => (
                              <div key={draft.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', marginBottom: '8px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
                                    <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '1px 5px' }}>DRAFT</span>
                                    <span style={{ fontSize: '9px', color: t.dim, flexShrink: 0 }}>{new Date(draft.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  </div>
                                  {draft.subject && <div style={{ fontSize: '12px', color: t.text, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.subject}</div>}
                                  <div style={{ fontSize: '11px', color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.body}</div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                  <button onClick={() => openDraft(draft)} style={{ background: 'none', border: `1px solid ${color}50`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '9px', color: color, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>EDIT</button>
                                  <button onClick={() => deleteDraft(draft.id)} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>DELETE</button>
                                </div>
                              </div>
                            )))}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {workspaceTab === 'overview' && (() => {
                  const allGates = laneViewData.gates || [];
                  const myGates = allGates.filter(g => g.lane === workspaceView).sort((a, b) => a.gate_number - b.gate_number);
                  const gateNamesOv = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
                  const launchDate = selectedProgram.launch_date ? new Date(selectedProgram.launch_date) : null;
                  const today = new Date();
                  const daysToLaunch = launchDate ? Math.ceil((launchDate - today) / 86400000) : null;
                  const allStatusGates = allGates.filter(g => g.gate_number === selectedProgram.current_gate);
                  const blockedCount = allStatusGates.filter(g => g.status === 'blocked').length;
                  const atRiskCount = allStatusGates.filter(g => g.status === 'at_risk').length;
                  const onTrackCount = allStatusGates.filter(g => g.status === 'on_track').length;
                  const completeCount = allStatusGates.filter(g => g.status === 'complete').length;
                  const overallScore = selectedProgram.nomos_score || (blockedCount === 0 && atRiskCount === 0 ? 85 : blockedCount > 0 ? 42 : 65);
                  const scoreColor = overallScore >= 75 ? '#10b981' : overallScore >= 50 ? '#f59e0b' : '#ef4444';
                  const scoreLabel = overallScore >= 75 ? 'HEALTHY' : overallScore >= 50 ? 'AT RISK' : 'CRITICAL';
                  return (
                    <div style={{ maxWidth: '640px' }}>
                      {/* Program summary header */}
                      <div style={{ marginBottom: '28px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '4px' }}>Program Overview</div>
                        <div style={{ fontSize: '11px', color: t.subLabel }}>Read-only summary — all live data from Nomos.</div>
                      </div>

                      {/* Score + date row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${scoreColor}30`, borderRadius: '8px', padding: '16px 20px' }}>
                          <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>NOMOS SCORE</div>
                          <div style={{ fontSize: '32px', fontWeight: '900', color: scoreColor, lineHeight: 1, fontFamily: "'Roboto Mono', monospace" }}>{overallScore}</div>
                          <div style={{ fontSize: '9px', color: scoreColor, letterSpacing: '2px', fontWeight: '700', marginTop: '6px' }}>{scoreLabel}</div>
                        </div>
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '16px 20px' }}>
                          <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>LAUNCH DATE</div>
                          {launchDate ? <>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: t.text, fontFamily: "'Roboto Mono', monospace" }}>{launchDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                            <div style={{ fontSize: '9px', color: daysToLaunch < 60 ? '#f59e0b' : t.muted, letterSpacing: '1px', marginTop: '6px' }}>
                              {daysToLaunch > 0 ? `${daysToLaunch} days away` : daysToLaunch === 0 ? 'TODAY' : `${Math.abs(daysToLaunch)} days ago`}
                            </div>
                          </> : <div style={{ fontSize: '13px', color: t.subLabel, fontStyle: 'italic' }}>Not set</div>}
                        </div>
                      </div>

                      {/* Current gate + phase */}
                      <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '12px' }}>CURRENT PHASE · GATE {selectedProgram.current_gate} OF 6</div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '12px' }}>{gateNamesOv[selectedProgram.current_gate - 1] || 'Unknown'}</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {blockedCount > 0 && <span style={{ fontSize: '9px', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '3px', padding: '2px 8px', letterSpacing: '1.5px' }}>{blockedCount} BLOCKED</span>}
                          {atRiskCount > 0 && <span style={{ fontSize: '9px', color: '#f59e0b', border: '1px solid #f59e0b40', borderRadius: '3px', padding: '2px 8px', letterSpacing: '1.5px' }}>{atRiskCount} AT RISK</span>}
                          {onTrackCount > 0 && <span style={{ fontSize: '9px', color: color, border: `1px solid ${color}40`, borderRadius: '3px', padding: '2px 8px', letterSpacing: '1.5px' }}>{onTrackCount} ON TRACK</span>}
                          {completeCount > 0 && <span style={{ fontSize: '9px', color: '#10b981', border: '1px solid #10b98140', borderRadius: '3px', padding: '2px 8px', letterSpacing: '1.5px' }}>{completeCount} COMPLETE</span>}
                        </div>
                      </div>

                      {/* My lane gates */}
                      {myGates.length > 0 && (
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${color}30`, borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
                          <div style={{ fontSize: '8px', color: color, letterSpacing: '2px', fontWeight: '700', marginBottom: '14px' }}>YOUR LANE · {displayName.toUpperCase()}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {myGates.map(g => {
                              const sc = g.status === 'complete' ? '#10b981' : g.status === 'blocked' ? '#ef4444' : g.status === 'at_risk' ? '#f59e0b' : g.status === 'on_track' ? color : t.dim;
                              const isCurr = g.gate_number === selectedProgram.current_gate;
                              return (
                                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', opacity: g.gate_number > selectedProgram.current_gate ? 0.4 : 1 }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: `2px solid ${isCurr ? color : sc + '60'}`, backgroundColor: isCurr ? color + '15' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: isCurr ? color : sc, flexShrink: 0 }}>G{g.gate_number}</div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                      <span style={{ fontSize: '11px', color: t.text, fontWeight: isCurr ? '700' : '400' }}>{gateNamesOv[g.gate_number - 1]}</span>
                                      <span style={{ fontSize: '9px', color: sc, letterSpacing: '1px', fontWeight: '700' }}>{g.completion_percent}%</span>
                                    </div>
                                    <div style={{ height: '3px', backgroundColor: t.borderDim, borderRadius: '2px' }}>
                                      <div style={{ height: '100%', width: `${g.completion_percent}%`, backgroundColor: sc, borderRadius: '2px', transition: 'width 0.3s' }} />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Financial snapshot */}
                      {(selectedProgram.num_locations || selectedProgram.budget_total) ? (
                        <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '16px 20px' }}>
                          <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '12px' }}>FINANCIAL SNAPSHOT</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                            {selectedProgram.num_locations > 0 && <div><div style={{ fontSize: '9px', color: t.dim, letterSpacing: '1px' }}>LOCATIONS</div><div style={{ fontSize: '16px', fontWeight: '700', color: t.text }}>{selectedProgram.num_locations.toLocaleString()}</div></div>}
                            {selectedProgram.budget_total > 0 && <div><div style={{ fontSize: '9px', color: t.dim, letterSpacing: '1px' }}>BUDGET</div><div style={{ fontSize: '16px', fontWeight: '700', color: t.text }}>${(selectedProgram.budget_total / 1000000).toFixed(1)}M</div></div>}
                            {selectedProgram.unit_cost > 0 && <div><div style={{ fontSize: '9px', color: t.dim, letterSpacing: '1px' }}>UNIT COST</div><div style={{ fontSize: '16px', fontWeight: '700', color: t.text }}>${selectedProgram.unit_cost.toLocaleString()}</div></div>}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                {workspaceTab === 'team' && (
                  <div style={{ maxWidth: '500px' }}>
                    <div style={{ marginBottom: '28px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '4px' }}>{displayName} Team</div>
                      <div style={{ fontSize: '11px', color: t.subLabel }}>Edit team lead and members in the TEAMS tab.</div>
                    </div>
                    {/* Lead */}
                    <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
                      <div style={{ fontSize: '8px', color: color, letterSpacing: '2px', fontWeight: '700', marginBottom: '12px' }}>TEAM LEAD</div>
                      {lead ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: color + '25', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color, flexShrink: 0 }}>{lead.slice(0, 2).toUpperCase()}</div>
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif" }}>{lead}</div>
                            <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '1px', marginTop: '2px' }}>TEAM LEAD</div>
                          </div>
                        </div>
                      ) : <div style={{ fontSize: '13px', color: t.subLabel, fontStyle: 'italic' }}>No lead assigned — go to TEAMS tab to add one.</div>}
                    </div>
                    {/* Members */}
                    <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px' }}>
                      <div style={{ fontSize: '8px', color: color, letterSpacing: '2px', fontWeight: '700', marginBottom: '12px' }}>MEMBERS · {dris.length}</div>
                      {dris.length === 0 ? (
                        <div style={{ fontSize: '13px', color: t.subLabel, fontStyle: 'italic' }}>No members added — go to TEAMS tab to add members.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {dris.map((dri, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color, flexShrink: 0 }}>{dri.slice(0, 2).toUpperCase()}</div>
                              <div style={{ fontSize: '13px', color: t.text }}>{dri}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          );
        })()}

        {/* ── GATE DETAIL PANEL (slide from right) ── */}
        {editingGate && (
          <>
            <div style={s.slideOverlay} onClick={() => setEditingGate(null)} />
            <div style={s.slidePanel}>

              {/* Panel header */}
              <div style={s.panelSec}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>
                    {programCode} · G{editingGate.gate_number} · {editingGate.lane.toUpperCase()}
                  </div>
                  <button style={s.closeBtn} onClick={() => setEditingGate(null)}>✕</button>
                </div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: t.text, marginBottom: '12px', lineHeight: 1.2, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.2px' }}>
                  {gateNames[editingGate.gate_number - 1]}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ backgroundColor: getStatusColor(editingGate.status) + '20', border: `1px solid ${getStatusColor(editingGate.status)}80`, borderRadius: '4px', padding: '4px 10px', fontSize: '10px', color: getStatusColor(editingGate.status), letterSpacing: '2px', fontWeight: 'bold' }}>
                    {editingGate.status.toUpperCase().replace('_', ' ')}
                  </div>
                  <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '1px' }}>{selectedProgram.name.toUpperCase()}</div>
                </div>

                {/* Team lead + DRIs from brief */}
                {(teamLeads[editingGate.lane] || (laneDris[editingGate.lane] || []).length > 0) && (
                  <div style={{ marginTop: '14px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {teamLeads[editingGate.lane] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: t.accent + '15', border: `1px solid ${t.accent}40`, borderRadius: '20px', padding: '4px 10px' }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: t.accent + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: t.accent, fontWeight: 'bold', flexShrink: 0 }}>
                          {teamLeads[editingGate.lane].slice(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontSize: '10px', color: t.accent, letterSpacing: '0.5px' }}>{teamLeads[editingGate.lane]}</span>
                        <span style={{ fontSize: '8px', color: t.accent + '80', letterSpacing: '1px' }}>LEAD</span>
                      </div>
                    )}
                    {(laneDris[editingGate.lane] || []).map(dri => (
                      <div key={dri} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '20px', padding: '4px 10px' }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: t.borderDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: t.subLabel, fontWeight: 'bold', flexShrink: 0 }}>
                          {dri.slice(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontSize: '10px', color: t.text, letterSpacing: '0.5px' }}>{dri}</span>
                        <span style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>DRI</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Gate stats */}
              <div style={s.panelSec}>
                {/* Completion % */}
                <div style={{ marginBottom: '22px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>PERCENTAGE COMPLETE</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '36px', fontWeight: 'bold', color: getStatusColor(editingGate.status) }}>{editingGate.completion_percent}%</span>
                    <span style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '1px' }}>OF GATE SCOPE</span>
                  </div>
                  <div style={{ height: '4px', backgroundColor: t.borderDimmer, borderRadius: '2px' }}>
                    <div style={{ height: '4px', width: `${editingGate.completion_percent}%`, backgroundColor: getStatusColor(editingGate.status), borderRadius: '2px' }} />
                  </div>
                </div>

                {/* Readiness Score */}
                {milestones.length > 0 && (() => {
                  const pendingDri = milestones.filter(m => !m.completed).length;
                  const pendingSignoff = milestones.filter(m => m.completed && !m.signed_off_by).length;
                  const score = Math.max(0, 100 - (pendingDri * 20) - (pendingSignoff * 10));
                  const scoreColor = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
                  const verdict = score >= 80 ? 'READY TO ADVANCE' : score >= 50 ? 'NEEDS ATTENTION' : 'NOT READY';
                  return (
                    <div style={{ marginBottom: '20px', backgroundColor: scoreColor + '0e', border: `1px solid ${scoreColor}30`, borderRadius: '6px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>READINESS SCORE</div>
                        <div style={{ fontSize: '10px', color: scoreColor, fontWeight: 'bold', letterSpacing: '1.5px' }}>{verdict}</div>
                        {pendingDri > 0 && <div style={{ fontSize: '10px', color: t.dim, marginTop: '2px' }}>{pendingDri} task{pendingDri > 1 ? 's' : ''} not completed</div>}
                        {pendingSignoff > 0 && <div style={{ fontSize: '10px', color: t.dim }}>{pendingSignoff} awaiting lead sign-off</div>}
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: scoreColor }}>{score}</div>
                    </div>
                  );
                })()}

                {/* Owner (first milestone with a DRI) */}
                {milestones.find(m => m.dri) && (() => {
                  const owner = milestones.find(m => m.dri).dri;
                  return (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>OWNER</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '10px 14px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: t.accent + '25', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: t.accent, fontWeight: 'bold', flexShrink: 0 }}>
                          {owner.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', color: t.text, fontWeight: 'bold' }}>{owner}</div>
                          <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px', marginTop: '2px' }}>{editingGate.lane.toUpperCase()} TEAM</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Last update */}
                {editingGate.notes && !editingGate.notes.includes('[NOMOS]') && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>LAST UPDATE</div>
                      {editingGate.updated_at && (
                        <div style={{ fontSize: '9px', color: t.subLabel }}>
                          {new Date(editingGate.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: t.cascadeText, lineHeight: 1.6 }}>{editingGate.notes}</div>
                  </div>
                )}

                {/* Next step (first incomplete milestone) */}
                {milestones.find(m => !m.completed) && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>NEXT STEP</div>
                    <div style={{ backgroundColor: t.accent + '10', border: `1px solid ${t.accent}30`, borderRadius: '6px', padding: '12px 14px', fontSize: '12px', color: t.text, lineHeight: 1.6 }}>
                      {milestones.find(m => !m.completed).text}
                    </div>
                  </div>
                )}

                {/* Blocker callout */}
                {editingGate.status === 'blocked' && editingGate.notes && !editingGate.notes.includes('[NOMOS]') && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '9px', color: '#ef4444', letterSpacing: '2px', marginBottom: '8px' }}>BLOCKER</div>
                    <div style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430', borderRadius: '6px', padding: '12px 14px', fontSize: '12px', color: '#ef4444', lineHeight: 1.6 }}>
                      {editingGate.notes}
                    </div>
                  </div>
                )}

                {/* Days delayed */}
                {editingGate.days_delayed > 0 && (
                  <div>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '8px' }}>DAYS DELAYED</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444' }}>+{editingGate.days_delayed}d</span>
                      <span style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '1px' }}>VS. PLAN</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div style={{ padding: '0 24px 32px', flex: 1 }}>
                <div style={{ ...s.modalTabs, marginTop: '4px' }}>
                  {['milestones', 'actions', 'details', 'ai'].map(tab => (
                    <button key={tab}
                      style={{ ...s.modalTab, ...(gateModalTab === tab ? s.modalTabActive : {}) }}
                      onClick={() => {
                        setGateModalTab(tab);
                        if (tab === 'actions' && selectedProgram) fetchProgramLaneActions(selectedProgram.id);
                      }}>
                      {tab === 'milestones' ? 'MILESTONE TASKS' : tab === 'actions' ? 'RECOMMENDED ACTIONS' : tab.toUpperCase()}
                    </button>
                  ))}
                </div>

              {/* MILESTONES TAB */}
              {gateModalTab === 'milestones' && (
                <div>
                  {(demoMode || userRole === 'executive') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: t.accent + '12', border: `1px solid ${t.accent}30`, borderRadius: '6px', padding: '8px 12px', marginBottom: '14px' }}>
                      <span style={{ fontSize: '10px', color: t.accent, letterSpacing: '1.5px', fontWeight: '700' }}>{demoMode ? 'DEMO' : 'EXECUTIVE'} — READ ONLY</span>
                      <span style={{ fontSize: '10px', color: t.dim }}>{demoMode ? 'Sign in to manage your own launches.' : 'Executive access is view-only.'}</span>
                    </div>
                  )}
                  {milestonesLoading ? (
                    <div style={{ color: t.dim, fontSize: '12px' }}>Loading milestone tasks...</div>
                  ) : (
                    <>
                      <div style={s.msProgress}>{completedCount} / {milestones.length} COMPLETE</div>
                      {milestones.map(m => (
                        <div key={m.id} style={s.msItem}>
                          <input type="checkbox" style={{ ...s.msCheck, opacity: (demoMode || userRole === 'executive') ? 0.5 : 1, cursor: (demoMode || userRole === 'executive') ? 'not-allowed' : 'pointer' }}
                            checked={m.completed} onChange={() => !(demoMode || userRole === 'executive') && toggleMilestone(m)} disabled={demoMode || userRole === 'executive'} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: m.completed ? t.subLabel : t.text, textDecoration: m.completed ? 'line-through' : 'none', lineHeight: '1.5' }}>
                              {m.text}
                            </div>
                            <div style={{ display: 'flex', gap: '12px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ color: t.subLabel, fontSize: '9px', letterSpacing: '1px', flexShrink: 0 }}>DRI</div>
                              {(() => {
                                const laneMembers = programMembers.filter(pm => pm.lane === (editingGate?.lane));
                                const hasProgramMembers = laneMembers.length > 0;
                                const currentDriUser = laneMembers.find(pm => pm.user_id === m.dri_user_id);
                                if (hasProgramMembers) {
                                  return (
                                    <select
                                      value={m.dri_user_id || ''}
                                      onChange={e => {
                                        const pm = laneMembers.find(p => p.user_id === e.target.value);
                                        updateMilestoneField(m.id, 'dri', pm?.profiles?.full_name || '');
                                        updateMilestoneField(m.id, 'dri_user_id', e.target.value || null);
                                      }}
                                      style={{ ...s.input, padding: '3px 6px', fontSize: '11px', flex: 1, minWidth: '80px' }}>
                                      <option value="">Unassigned</option>
                                      {laneMembers.map(pm => <option key={pm.user_id} value={pm.user_id}>{pm.profiles?.full_name}{pm.role === 'lead' ? ' (Lead)' : ''}</option>)}
                                    </select>
                                  );
                                }
                                return (
                                  <input
                                    style={{ ...s.input, padding: '3px 6px', fontSize: '11px', flex: 1, minWidth: '80px' }}
                                    placeholder="Name or role"
                                    defaultValue={m.dri || ''}
                                    onBlur={e => { if (e.target.value !== (m.dri || '')) updateMilestoneField(m.id, 'dri', e.target.value); }}
                                  />
                                );
                              })()}
                              <div style={{ color: t.subLabel, fontSize: '9px', letterSpacing: '1px', flexShrink: 0 }}>DUE</div>
                              <input
                                type="date"
                                style={{ ...s.input, padding: '3px 6px', fontSize: '11px', width: '130px', flexShrink: 0, color: m.due_date && new Date(m.due_date) < new Date() && !m.completed ? '#ef4444' : t.text }}
                                defaultValue={m.due_date ? m.due_date.split('T')[0] : ''}
                                onBlur={e => { if (e.target.value !== (m.due_date ? m.due_date.split('T')[0] : '')) updateMilestoneField(m.id, 'due_date', e.target.value || null); }}
                              />
                            </div>
                            {expandedMilestoneId === m.id ? (
                              <textarea
                                style={{ ...s.textarea, minHeight: '48px', marginTop: '6px', fontSize: '11px' }}
                                placeholder="Add notes..."
                                defaultValue={m.notes || ''}
                                onBlur={e => { updateMilestoneField(m.id, 'notes', e.target.value); setExpandedMilestoneId(null); }}
                                autoFocus
                              />
                            ) : (
                              <div
                                style={{ color: m.notes ? t.muted : t.subLabel, fontSize: '10px', marginTop: '4px', cursor: 'pointer' }}
                                onClick={() => setExpandedMilestoneId(m.id)}>
                                {m.notes || '+ add note'}
                              </div>
                            )}
                          </div>
                          <button style={s.msDelete} onClick={() => deleteMilestone(m.id)}>✕</button>
                        </div>
                      ))}
                      <div style={s.msAddRow}>
                        <input
                          style={{ ...s.input, flex: 1 }}
                          placeholder="Add a milestone task..."
                          value={newMilestoneText}
                          onChange={e => setNewMilestoneText(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addMilestone()}
                        />
                        <MicBtn fieldId="ms-add" value={newMilestoneText} onSet={setNewMilestoneText} {...micP} />
                        <button style={{ ...s.saveBtn, width: 'auto', padding: '8px 16px', marginTop: 0 }}
                          onClick={addMilestone}>ADD</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* DETAILS TAB */}
              {gateModalTab === 'details' && (
                <div>
                  <div style={s.fieldGroup}>
                    <label style={s.label}>STATUS</label>
                    <select style={s.select} value={editForm.status}
                      onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                      <option value="complete">COMPLETE</option>
                      <option value="ready">READY</option>
                      <option value="on_track">ON TRACK</option>
                      <option value="blocked">BLOCKED</option>
                      <option value="at_risk">AT RISK</option>
                      <option value="idle">IDLE</option>
                    </select>
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.label}>COMPLETION %</label>
                    <input style={s.input} type="number" min="0" max="100"
                      value={editForm.completion_percent}
                      onChange={e => setEditForm({ ...editForm, completion_percent: parseInt(e.target.value) })} />
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.label}>PLANNED CLOSE DATE</label>
                    <input style={s.input} type="date"
                      value={editForm.planned_close_date}
                      onChange={e => setEditForm({ ...editForm, planned_close_date: e.target.value })} />
                  </div>
                  <div style={s.fieldGroup}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ ...s.label, marginBottom: 0 }}>NOTES</label>
                      <MicBtn fieldId="gate-notes" value={editForm.notes} onSet={v => setEditForm(f => ({ ...f, notes: v }))} {...micP} />
                    </div>
                    <textarea style={s.textarea} value={editForm.notes}
                      onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                      placeholder="Describe status or blockers..." />
                  </div>
                  {(editForm.status === 'blocked' || editForm.status === 'at_risk') && (
                    <>
                      <div style={s.fieldGroup}>
                        <label style={s.label}>BLOCKER CATEGORY</label>
                        <select style={s.input} value={editForm.blocker_category}
                          onChange={e => setEditForm({ ...editForm, blocker_category: e.target.value })}>
                          <option value="">Select category...</option>
                          <option value="supplier_delay">Supplier Delay</option>
                          <option value="approval_pending">Approval Pending</option>
                          <option value="design_change">Design Change</option>
                          <option value="budget_issue">Budget Issue</option>
                          <option value="resource_constraint">Resource Constraint</option>
                          <option value="technical_issue">Technical Issue</option>
                          <option value="regulatory">Regulatory / Compliance</option>
                          <option value="tooling">Tooling / Manufacturing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div style={s.fieldRow}>
                        <div style={s.fieldGroup}>
                          <label style={s.label}>DAYS DELAYED</label>
                          <input style={s.input} type="number" min="0"
                            value={editForm.days_delayed}
                            onChange={e => setEditForm({ ...editForm, days_delayed: parseInt(e.target.value) })} />
                        </div>
                        <div style={s.fieldGroup}>
                          <label style={s.label}>COST IMPACT ($)</label>
                          <input style={s.input} type="number" min="0"
                            value={editForm.cost_impact}
                            onChange={e => setEditForm({ ...editForm, cost_impact: parseInt(e.target.value) })} />
                        </div>
                      </div>
                    </>
                  )}
                  {userRole !== 'executive' && (
                    <button style={{ ...s.saveBtn, opacity: saving ? 0.5 : 1 }} onClick={saveGate} disabled={saving}>
                      {saving ? 'SAVING...' : 'SAVE UPDATE'}
                    </button>
                  )}
                  {userRole === 'executive' && (
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', padding: '10px 0', textAlign: 'center', fontFamily: "'Roboto Mono', monospace" }}>READ-ONLY ACCESS</div>
                  )}
                </div>
              )}

              {/* RECOMMENDED ACTIONS TAB */}
              {gateModalTab === 'actions' && (() => {
                const gateActions = programLaneActions.filter(a => a.lane === editingGate?.lane && a.gate_number === editingGate?.gate_number);
                const laneColor = ({ design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' })[editingGate?.lane] || t.accent;
                return (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '10px', color: t.dim, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>RECOMMENDED ACTIONS</div>
                        <div style={{ fontSize: '12px', color: t.muted }}>Launch-specific actions for {editingGate?.lane?.toUpperCase()} at Gate {editingGate?.gate_number}</div>
                      </div>
                      {userRole === 'admin' && (
                        <button
                          disabled={generatingActions}
                          onClick={async () => {
                            setGeneratingActions(true);
                            try {
                              const r = await authFetch(`${API}/programs/${selectedProgram.id}/generate-actions`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ gate_number: editingGate.gate_number }),
                              });
                              const d = await r.json();
                              if (d.actions) { setProgramLaneActions(prev => { const filtered = prev.filter(a => !(a.gate_number === editingGate.gate_number && a.ai_generated && !a.completed)); return [...filtered, ...d.actions]; }); }
                            } finally { setGeneratingActions(false); }
                          }}
                          style={{ padding: '7px 14px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.accent}`, borderRadius: '4px', background: 'none', color: t.accent, cursor: generatingActions ? 'not-allowed' : 'pointer', opacity: generatingActions ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                          {generatingActions ? 'GENERATING...' : '✦ GENERATE ACTIONS'}
                        </button>
                      )}
                    </div>
                    {gateActions.length === 0 ? (
                      <div style={{ padding: '32px', textAlign: 'center', border: `1px dashed ${t.borderDim}`, borderRadius: '8px', color: t.muted, fontSize: '13px' }}>
                        {userRole === 'admin' ? 'Click Generate Actions to create launch-specific recommendations for all lanes at this gate.' : 'No recommended actions yet. Ask your launch admin to generate them.'}
                      </div>
                    ) : (
                      <div>
                        {gateActions.map(action => (
                          <div key={action.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${t.borderDim}`, opacity: action.completed ? 0.4 : 1 }}>
                            <button
                              onClick={() => { if (!action.completed) completeLaneAction(action.id); }}
                              style={{ flexShrink: 0, marginTop: '2px', width: '16px', height: '16px', borderRadius: '3px', border: `2px solid ${action.completed ? laneColor : laneColor + '60'}`, background: action.completed ? laneColor : 'none', cursor: action.completed ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '9px' }}
                            >{action.completed ? '✓' : ''}</button>
                            <div style={{ fontSize: '13px', color: t.text, lineHeight: 1.4, textDecoration: action.completed ? 'line-through' : 'none' }}>{action.text}</div>
                          </div>
                        ))}
                        <div style={{ fontSize: '10px', color: t.dim, marginTop: '12px', fontFamily: "'Roboto Mono', monospace" }}>Completed actions are logged to the program timeline</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* AI TAB */}
              {gateModalTab === 'ai' && (
                <div>
                  <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '2px', marginBottom: '16px' }}>
                    ASK ABOUT THIS GATE — {editingGate.lane.toUpperCase()} G{editingGate.gate_number}
                  </div>
                  <div style={s.msAddRow}>
                    <input
                      style={{ ...s.input, flex: 1 }}
                      placeholder='e.g. "What is missing?" or "Is this ready to advance?"'
                      value={gateAiQuestion}
                      onChange={e => setGateAiQuestion(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && askGateAi(false)}
                    />
                    <MicBtn fieldId="gate-ai-q" value={gateAiQuestion} onSet={setGateAiQuestion} {...micP} />
                    <button
                      style={{ ...s.saveBtn, width: 'auto', padding: '8px 16px', marginTop: 0, opacity: gateAiLoading ? 0.5 : 1 }}
                      onClick={() => askGateAi(false)} disabled={gateAiLoading}>
                      {gateAiLoading ? '...' : 'ASK'}
                    </button>
                  </div>
                  {gateAiLoading && !gateAiAnswer && (
                    <div style={{ ...s.aiLabel, marginTop: '16px' }}>REVIEWING GATE...</div>
                  )}
                  {gateAiAnswer && (
                    <div style={s.aiBox}>
                      <div style={s.aiLabel}>NOMOS GATE REVIEW</div>
                      {gateAiAnswer.replace(/\*\*/g, '')}
                    </div>
                  )}
                </div>
              )}
              </div>{/* /tabs container */}
            </div>{/* /slidePanel */}
          </>
        )}

        {/* ── DUPLICATE MODAL ── */}
        {duplicateOpen && (
          <div style={s.overlay} onClick={() => setDuplicateOpen(false)}>
            <div style={{ ...s.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <div style={s.modalLane}>DUPLICATE PROGRAM</div>
                <button style={s.closeBtn} onClick={() => setDuplicateOpen(false)}>✕</button>
              </div>
              <div style={{ fontSize: '13px', color: t.muted, marginBottom: '20px', lineHeight: 1.6 }}>
                Creates a copy of this program — all lanes, gate schedule, and milestone templates. Statuses reset to start fresh.
              </div>
              <label style={{ fontSize: '10px', letterSpacing: '1.5px', color: t.dim, display: 'block', marginBottom: '6px' }}>NEW PROGRAM NAME</label>
              <input
                style={s.input}
                value={duplicateName}
                onChange={e => setDuplicateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && duplicateProgram()}
                placeholder={`${selectedProgram.name} (Copy)`}
                autoFocus
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button style={{ ...s.ghostBtn, flex: 1 }} onClick={() => setDuplicateOpen(false)}>CANCEL</button>
                <button
                  style={{ ...s.saveBtn, flex: 2, marginTop: 0, opacity: duplicating ? 0.6 : 1 }}
                  onClick={duplicateProgram}
                  disabled={duplicating}
                >
                  {duplicating ? 'DUPLICATING...' : 'DUPLICATE →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── BLUEPRINT MODAL ── */}
        {blueprintModalOpen && (
          <div style={s.overlay} onClick={() => setBlueprintModalOpen(false)}>
            <div style={{ ...s.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <div style={{ fontSize: '10px', letterSpacing: '2px', color: t.accent, fontWeight: '800' }}>✦ GENERATE BLUEPRINT</div>
                <button style={s.closeBtn} onClick={() => setBlueprintModalOpen(false)}>✕</button>
              </div>
              <div style={{ fontSize: '13px', color: t.muted, marginBottom: '20px', lineHeight: 1.7 }}>
                Nomos will generate a complete milestone plan — 3 tasks per lane per gate, 90 tasks total — tailored to this program. Existing milestones are kept.
              </div>
              <label style={{ fontSize: '10px', letterSpacing: '1.5px', color: t.accent, display: 'block', marginBottom: '4px', fontWeight: '700' }}>MASS PRODUCTION DATE</label>
              <div style={{ fontSize: '10px', color: t.subLabel, marginBottom: '8px' }}>When manufacturing must complete. Nomos works backward from this date. Optional — falls back to launch date.</div>
              <input
                type="date"
                style={{ ...s.input, borderColor: t.accent + '50', backgroundColor: t.accent + '08' }}
                value={blueprintMpDate}
                onChange={e => setBlueprintMpDate(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button style={{ ...s.ghostBtn, flex: 1 }} onClick={() => setBlueprintModalOpen(false)}>CANCEL</button>
                <button style={{ ...s.saveBtn, flex: 2, marginTop: 0 }} onClick={generateBlueprintForProgram}>
                  GENERATE →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DATA IMPORT MODAL ── */}
        {importOpen && (
          <div style={s.modalOverlay} onClick={() => setImportOpen(false)}>
            <div style={{ ...s.modal, maxWidth: '560px', width: '95%', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: '#8b5cf6', fontWeight: '700', marginBottom: '3px' }}>NOMOS DATA IMPORT</div>
                  <div style={{ fontSize: '12px', color: t.muted }}>Paste any external data — email, update, report — and Nomos will extract what it means for your gates.</div>
                </div>
                <button style={s.closeBtn} onClick={() => setImportOpen(false)}>✕</button>
              </div>

              {!importSuggestions ? (
                <>
                  <textarea
                    style={{ ...s.input, height: '200px', resize: 'vertical', fontSize: '12px', lineHeight: 1.6 }}
                    placeholder="Paste anything here — a supplier email, a budget update, a team message, a status report..."
                    value={importText}
                    onChange={e => setImportText(e.target.value)}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                    <button style={{ ...s.ghostBtn, flex: 1 }} onClick={() => setImportOpen(false)}>CANCEL</button>
                    <button
                      style={{ ...s.saveBtn, flex: 2, marginTop: 0, backgroundColor: '#8b5cf6', opacity: importLoading || !importText.trim() ? 0.6 : 1 }}
                      disabled={importLoading || !importText.trim()}
                      onClick={() => {
                        setImportLoading(true);
                        authFetch(`${API}/programs/${selectedProgram.id}/import-data`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ text: importText }),
                        }).then(r => r.json()).then(d => { setImportSuggestions(d); setImportApplied({}); }).catch(() => {}).finally(() => setImportLoading(false));
                      }}
                    >
                      {importLoading ? 'ANALYZING...' : 'ANALYZE →'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '12px', color: t.muted, marginBottom: '16px', padding: '10px 12px', backgroundColor: t.bg, borderRadius: '6px', border: `1px solid ${t.borderDim}`, lineHeight: 1.5 }}>
                    {importSuggestions.summary}
                  </div>

                  {importSuggestions.suggestions.length === 0 ? (
                    <div style={{ fontSize: '13px', color: t.dim, textAlign: 'center', padding: '24px 0' }}>No gate updates found in this data.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ fontSize: '10px', letterSpacing: '1.5px', color: t.dim, fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>{importSuggestions.suggestions.length} SUGGESTED UPDATE{importSuggestions.suggestions.length !== 1 ? 'S' : ''}</div>
                      {importSuggestions.suggestions.map((s, i) => {
                        const applied = importApplied[i];
                        const applying = importApplying[i];
                        return (
                          <div key={i} style={{ padding: '12px 14px', border: `1px solid ${applied ? '#10b98140' : t.border}`, borderRadius: '8px', backgroundColor: applied ? (darkMode ? '#10b98110' : '#f0fdf4') : t.card }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: t.accent, backgroundColor: t.accent + '15', padding: '1px 6px', borderRadius: '3px', textTransform: 'uppercase' }}>{s.lane}</span>
                                  <span style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: t.dim }}>G{s.gate_number}</span>
                                  <span style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: '#8b5cf6', textTransform: 'uppercase' }}>{s.field}</span>
                                </div>
                                <div style={{ fontSize: '12px', color: t.text, marginBottom: '4px' }}>
                                  <span style={{ color: t.dim }}>{String(s.current_value || '—')}</span>
                                  <span style={{ color: t.dim, margin: '0 6px' }}>→</span>
                                  <span style={{ color: '#8b5cf6', fontWeight: '600' }}>{String(s.suggested_value)}</span>
                                </div>
                                <div style={{ fontSize: '11px', color: t.muted, lineHeight: 1.4 }}>{s.reason}</div>
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                {applied ? (
                                  <span style={{ fontSize: '10px', color: '#10b981', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>✓ APPLIED</span>
                                ) : !s.gate_id ? (
                                  <span style={{ fontSize: '10px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>NO MATCH</span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setImportApplying(p => ({ ...p, [i]: true }));
                                      authFetch(`${API}/gates/${s.gate_id}/apply-suggestion`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ field: s.field, value: s.suggested_value }),
                                      }).then(r => r.json()).then(d => {
                                        if (!d.error) { setImportApplied(p => ({ ...p, [i]: true })); refreshProgram(selectedProgram.id); }
                                      }).catch(() => {}).finally(() => setImportApplying(p => ({ ...p, [i]: false })));
                                    }}
                                    disabled={applying}
                                    style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: '#8b5cf6', background: 'none', border: `1px solid #8b5cf680`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', opacity: applying ? 0.6 : 1 }}
                                  >
                                    {applying ? '...' : 'APPLY'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                    <button style={{ ...s.ghostBtn, flex: 1 }} onClick={() => setImportSuggestions(null)}>← PASTE NEW</button>
                    <button style={{ ...s.ghostBtn, flex: 1 }} onClick={() => setImportOpen(false)}>DONE</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── PROGRAM SETTINGS MODAL ── */}
        {editingProgram && (
          <div style={s.overlay} onClick={() => { setEditingProgram(false); setTestEmailResult(null); }}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <div style={s.modalLane}>PROGRAM SETTINGS</div>
                <button style={s.closeBtn} onClick={() => { setEditingProgram(false); setTestEmailResult(null); }}>✕</button>
              </div>
              <div style={s.fieldRow}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>TOTAL LOCATIONS</label>
                  <input style={s.input} type="number"
                    value={programForm.num_locations ?? ''}
                    onChange={e => setProgramForm({ ...programForm, num_locations: parseInt(e.target.value) })} />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>ALREADY LAUNCHED</label>
                  <input style={s.input} type="number"
                    value={programForm.launched_locations ?? ''}
                    onChange={e => setProgramForm({ ...programForm, launched_locations: parseInt(e.target.value) })} />
                </div>
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>MARKETS (e.g. US, EU, UK, Japan)</label>
                <input style={s.input}
                  value={programForm.markets ?? ''}
                  onChange={e => setProgramForm({ ...programForm, markets: e.target.value })} />
              </div>
              <div style={s.fieldRow}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>UNIT COST ($)</label>
                  <input style={s.input} type="number"
                    value={programForm.unit_cost ?? ''}
                    onChange={e => setProgramForm({ ...programForm, unit_cost: parseFloat(e.target.value) })} />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>UNITS PER LOCATION</label>
                  <input style={s.input} type="number"
                    value={programForm.units_per_location ?? ''}
                    onChange={e => setProgramForm({ ...programForm, units_per_location: parseInt(e.target.value) })} />
                </div>
              </div>
              <div style={s.fieldRow}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>AVG DAILY REVENUE / LOCATION ($)</label>
                  <input style={s.input} type="number"
                    value={programForm.avg_daily_revenue_per_location ?? ''}
                    onChange={e => setProgramForm({ ...programForm, avg_daily_revenue_per_location: parseFloat(e.target.value) })} />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>EXPECTED SALES UPLIFT (%)</label>
                  <input style={s.input} type="number"
                    value={programForm.expected_uplift_percent ?? ''}
                    onChange={e => setProgramForm({ ...programForm, expected_uplift_percent: parseFloat(e.target.value) })} />
                </div>
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>RETROFIT COST / LOCATION ($)</label>
                <input style={s.input} type="number"
                  value={programForm.retrofit_cost_per_location ?? ''}
                  onChange={e => setProgramForm({ ...programForm, retrofit_cost_per_location: parseFloat(e.target.value) })} />
              </div>
              <button style={s.saveBtn} onClick={saveProgram}>SAVE SETTINGS</button>
              {/* Passkey setup in settings */}
              {passkeyAvailable && (
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${t.borderDim}` }}>
                  <div style={{ ...s.label, marginBottom: '6px' }}>BIOMETRIC SIGN-IN</div>
                  <div style={{ fontSize: '10px', color: t.subLabel, marginBottom: '10px', lineHeight: 1.5 }}>
                    {localStorage.getItem('nomosPasskey') === '1'
                      ? 'Fingerprint / Face ID is set up on this device.'
                      : 'Skip the password — sign in with your fingerprint or Face ID next time.'}
                  </div>
                  {localStorage.getItem('nomosPasskey') !== '1' && (
                    <button style={{ ...s.ghostBtn, fontSize: '10px', opacity: passkeyRegistering ? 0.5 : 1 }}
                      onClick={registerPasskey} disabled={passkeyRegistering}>
                      {passkeyRegistering ? 'SAVING...' : 'SET UP FINGERPRINT / FACE ID'}
                    </button>
                  )}
                  {passkeyError && <div style={{ marginTop: '6px', fontSize: '11px', color: '#f59e0b' }}>{passkeyError}</div>}
                </div>
              )}
              {userRole === 'admin' && (
                <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${t.borderDim}` }}>
                  <div style={{ ...s.label, marginBottom: '10px' }}>EMAIL ALERTS</div>
                  <button
                    style={{ ...s.ghostBtn, fontSize: '10px', opacity: testEmailSending ? 0.5 : 1 }}
                    onClick={sendTestEmail}
                    disabled={testEmailSending}
                  >
                    {testEmailSending ? 'SENDING...' : 'SEND TEST EMAIL'}
                  </button>
                  {testEmailResult && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: testEmailResult.ok ? t.accent : '#ff6b6b' }}>
                      {testEmailResult.ok ? '✓ ' : '✗ '}{testEmailResult.msg}
                    </div>
                  )}
                  <div style={{ marginTop: '8px', fontSize: '10px', color: t.subLabel, lineHeight: 1.5 }}>
                    Alerts fire when gates are blocked or complete. Requires RESEND_API_KEY in Railway environment variables.
                  </div>
                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${t.borderDim}` }}>
                    <div style={{ ...s.label, marginBottom: '6px' }}>SLACK / TEAMS WEBHOOK</div>
                    <div style={{ fontSize: '10px', color: t.subLabel, marginBottom: '8px', lineHeight: 1.5 }}>
                      Paste an Incoming Webhook URL to post gate alerts to Slack or Microsoft Teams.
                    </div>
                    <input
                      style={{ ...s.input, marginBottom: '8px' }}
                      placeholder="https://hooks.slack.com/services/..."
                      value={slackWebhook}
                      onChange={e => setSlackWebhook(e.target.value)}
                    />
                    <button style={{ ...s.ghostBtn, fontSize: '10px', opacity: slackSaving ? 0.5 : 1 }} onClick={saveSlackWebhook} disabled={slackSaving}>
                      {slackSaving ? 'SAVING...' : 'SAVE WEBHOOK'}
                    </button>
                    {slackResult && <div style={{ marginTop: '8px', fontSize: '11px', color: slackResult.ok ? '#10b981' : '#ef4444' }}>{slackResult.ok ? '✓ ' : '✗ '}{slackResult.msg}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── HEADER ── */}
        {/* ── TOP NAV ── */}
        <div style={{ borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.surface, padding: isMobile ? '0 16px' : '0 48px', display: 'flex', alignItems: 'center', height: '52px', gap: '16px', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flexShrink: 0 }} onClick={goToLanding}>
            <div style={{ width: '26px', height: '26px', backgroundColor: t.accent, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: '#000' }}>N</div>
            <span style={{ color: t.text, fontSize: '13px', letterSpacing: '3px', fontWeight: 'bold' }}>NOMOS</span>
            {!isMobile && <span style={{ color: t.subLabel, fontSize: '9px', letterSpacing: '2px' }}>LAUNCH OS</span>}
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', flex: 1 }}>
              {['PROGRAMS', 'MY TASKS', 'TEAM STATUS', 'CONSEQUENCES', 'TIMELINE'].map(tab => {
                const isActive = tab === 'MY TASKS' ? showMyTasks : tab === 'TEAM STATUS' ? showTeamDirectory : false;
                return (
                  <div key={tab} style={{ padding: '0 16px', height: '52px', display: 'flex', alignItems: 'center', fontSize: '10px', letterSpacing: '2px', color: isActive ? t.accent : t.dim, cursor: 'pointer', borderBottom: `2px solid ${isActive ? t.accent : 'transparent'}`, whiteSpace: 'nowrap' }}
                    onClick={() => {
                      if (tab === 'PROGRAMS') { setSelectedProgram(null); setConsequences(null); setShowTeamDirectory(false); setShowMyTasks(false); }
                      else if (tab === 'MY TASKS') { setSelectedProgram(null); setConsequences(null); setShowTeamDirectory(false); setShowMyTasks(true); fetchMyTasks(); fetchMyLaneActions(); }
                      else if (tab === 'TEAM STATUS') { setSelectedProgram(null); setConsequences(null); setShowTeamDirectory(true); setShowMyTasks(false); }
                      else if (tab === 'CONSEQUENCES') { setShowTeamDirectory(false); setShowMyTasks(false); setActiveTab('consequences'); }
                      else if (tab === 'TIMELINE') { setShowTeamDirectory(false); setShowMyTasks(false); setActiveTab('timeline'); }
                    }}>
                    {tab}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginLeft: isMobile ? 'auto' : 0, display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', flexShrink: 0 }}>
            <span style={{ ...s.back, fontSize: '10px' }} onClick={() => { setSelectedProgram(null); setConsequences(null); }}>← BACK</span>
            {isMobile ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setProgramActionsOpen(v => !v)}
                  style={{ background: 'none', border: `1px solid ${programActionsOpen ? t.accent : t.border}`, borderRadius: '6px', color: programActionsOpen ? t.accent : t.dim, padding: '5px 10px', cursor: 'pointer', fontSize: '16px', lineHeight: 1, fontFamily: 'inherit' }}>
                  ⋯
                </button>
                {programActionsOpen && (
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 500, minWidth: '160px', overflow: 'hidden' }}>
                    {[
                      { label: 'EXPORT PDF', action: () => { exportGateReport(); setProgramActionsOpen(false); }, color: '#0891b2' },
                      ...(realUserRole === 'admin' ? [
                        { label: '✦ BLUEPRINT', action: () => { setBlueprintMpDate(''); setBlueprintModalOpen(true); setProgramActionsOpen(false); }, color: t.accent },
                        { label: 'IMPORT DATA', action: () => { setImportText(''); setImportSuggestions(null); setImportApplied({}); setImportOpen(true); setProgramActionsOpen(false); }, color: '#8b5cf6' },
                        { label: 'DUPLICATE', action: () => { setDuplicateName(`${selectedProgram.name} (Copy)`); setDuplicateOpen(true); setProgramActionsOpen(false); }, color: '#10b981' },
                        selectedProgram.status === 'complete'
                          ? { label: 'REOPEN', action: () => { markAsLaunched(true); setProgramActionsOpen(false); }, color: '#f59e0b' }
                          : { label: 'MARK LAUNCHED', action: () => { markAsLaunched(false); setProgramActionsOpen(false); }, color: '#10b981' },
                        { label: 'SETTINGS', action: () => { setProgramForm({ num_locations: selectedProgram.num_locations || 0, launched_locations: selectedProgram.launched_locations || 0, markets: selectedProgram.markets || '', unit_cost: selectedProgram.unit_cost || 0, units_per_location: selectedProgram.units_per_location || 2, avg_daily_revenue_per_location: selectedProgram.avg_daily_revenue_per_location || 0, expected_uplift_percent: selectedProgram.expected_uplift_percent || 0, retrofit_cost_per_location: selectedProgram.retrofit_cost_per_location || 750 }); setEditingProgram(true); setProgramActionsOpen(false); }, color: t.dim },
                        { label: 'DELETE', action: () => { deleteProgram(); setProgramActionsOpen(false); }, color: '#ef4444' },
                      ] : []),
                    ].map(({ label, action, color }) => (
                      <button key={label} onClick={action} style={{ display: 'block', width: '100%', background: 'none', border: 'none', borderBottom: `1px solid ${t.borderDim}`, padding: '12px 16px', textAlign: 'left', cursor: 'pointer', fontSize: '10px', letterSpacing: '1.5px', color, fontFamily: "'Roboto Mono', monospace", fontWeight: '600' }}>
                        {label}
                      </button>
                    ))}
                    {realUserRole === 'admin' && !demoMode && (
                      <>
                        <div style={{ padding: '6px 16px', fontSize: '8px', color: t.subLabel, letterSpacing: '2px', borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.bg }}>PREVIEW AS MEMBER</div>
                        {[['', 'ADMIN VIEW'], ['design', 'DESIGN'], ['finance', 'FINANCE'], ['operations', 'OPS'], ['installation', 'INSTALL'], ['engineering', 'ENGINEERING']].map(([val, lbl]) => (
                          <button key={val} onClick={() => { setPreviewLane(val || null); setProgramActionsOpen(false); }}
                            style={{ display: 'block', width: '100%', background: previewLane === (val || null) ? '#f59e0b15' : 'none', border: 'none', borderBottom: `1px solid ${t.borderDim}`, padding: '10px 16px', textAlign: 'left', cursor: 'pointer', fontSize: '10px', letterSpacing: '1.5px', color: previewLane === (val || null) ? '#f59e0b' : t.dim, fontFamily: "'Roboto Mono', monospace", fontWeight: previewLane === (val || null) ? '700' : '400' }}>
                            {val ? `PREVIEW: ${lbl}` : lbl}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '6px' }}>
                {userRole === 'admin' && <button style={{ ...s.ghostBtn, color: '#f59e0b', borderColor: '#f59e0b80', fontWeight: '800' }} disabled={generatingActions} onClick={async () => { setGeneratingActions(true); try { const r = await authFetch(`${API}/programs/${selectedProgram.id}/generate-actions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gate_number: selectedProgram.current_gate || 1 }) }); const d = await r.json(); if (d.actions) { setProgramLaneActions(d.actions); fetchMyLaneActions(); } } finally { setGeneratingActions(false); } }} title="Generate AI action recommendations for all lanes at current gate">{generatingActions ? 'GENERATING...' : '✦ ACTIONS'}</button>}
                {userRole === 'admin' && <button style={{ ...s.ghostBtn, color: t.accent, borderColor: t.accent + '80', fontWeight: '800' }} onClick={() => { setBlueprintMpDate(''); setBlueprintModalOpen(true); }} title="Generate AI milestone blueprint for all lanes and gates">✦ BLUEPRINT</button>}
                {userRole === 'admin' && <button style={{ ...s.ghostBtn, color: '#8b5cf6', borderColor: '#8b5cf680' }} onClick={() => { setImportText(''); setImportSuggestions(null); setImportApplied({}); setImportOpen(true); }} title="Import data from external sources">IMPORT</button>}
                <button style={{ ...s.ghostBtn, color: '#0891b2', borderColor: '#0891b280' }} onClick={exportGateReport} title="Export gate status report as PDF">EXPORT</button>
                {userRole === 'admin' && <button style={{ ...s.ghostBtn, color: '#10b981', borderColor: '#10b98180' }} onClick={() => { setDuplicateName(`${selectedProgram.name} (Copy)`); setDuplicateOpen(true); }} title="Duplicate this program">DUPLICATE</button>}
                {userRole === 'admin' && (
                  selectedProgram.status === 'complete'
                    ? <button style={{ ...s.ghostBtn, color: '#f59e0b', borderColor: '#f59e0b80' }} onClick={() => markAsLaunched(true)} title="Reopen as active">REOPEN</button>
                    : <button style={{ ...s.ghostBtn, color: '#10b981', borderColor: '#10b98180' }} onClick={() => markAsLaunched(false)} title="Mark as launched and archive">MARK LAUNCHED</button>
                )}
                {userRole === 'admin' && <button style={s.ghostBtn} onClick={() => { setProgramForm({ num_locations: selectedProgram.num_locations || 0, launched_locations: selectedProgram.launched_locations || 0, markets: selectedProgram.markets || '', unit_cost: selectedProgram.unit_cost || 0, units_per_location: selectedProgram.units_per_location || 2, avg_daily_revenue_per_location: selectedProgram.avg_daily_revenue_per_location || 0, expected_uplift_percent: selectedProgram.expected_uplift_percent || 0, retrofit_cost_per_location: selectedProgram.retrofit_cost_per_location || 750 }); setEditingProgram(true); }}>SETTINGS</button>}
                {userRole === 'admin' && <button style={{ ...s.ghostBtn, color: '#ef4444', borderColor: '#ef4444' }} onClick={deleteProgram}>DELETE</button>}
              </div>
            )}
            {realUserRole === 'admin' && !demoMode && !isMobile && (
              <select
                value={previewLane || ''}
                onChange={e => setPreviewLane(e.target.value || null)}
                style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '600', background: previewLane ? '#f59e0b' : 'none', border: `1px solid ${previewLane ? '#f59e0b' : t.borderDim}`, borderRadius: '4px', color: previewLane ? '#000' : t.dim, padding: '4px 8px', cursor: 'pointer', outline: 'none' }}>
                <option value="">ADMIN VIEW</option>
                <option value="design">PREVIEW: DESIGN</option>
                <option value="finance">PREVIEW: FINANCE</option>
                <option value="operations">PREVIEW: OPS</option>
                <option value="installation">PREVIEW: INSTALL</option>
                <option value="engineering">PREVIEW: ENG</option>
              </select>
            )}
            {(session || demoMode) && <NotificationBell notifications={notifications} open={notifOpen} onToggle={toggleNotifications} darkMode={darkMode} />}
            <ThemeToggle darkMode={darkMode} onToggle={() => setDarkMode(d => !d)} style={s.toggleBtn} />
          </div>
        </div>

        {/* ── PERSISTENT STAT BAR ── */}
        {(() => {
          const allGatesStat = selectedProgram.gates || [];
          const blockedGatesStat = allGatesStat.filter(g => g.status === 'blocked');
          const atRiskGatesStat  = allGatesStat.filter(g => g.status === 'at_risk');
          const completedGates = selectedProgram.current_gate - 1;
          let score = 100;
          score -= blockedGatesStat.length * 12;
          score -= atRiskGatesStat.length * 6;
          score -= hardAlerts.length * 5;
          score -= softAlerts.length * 2;
          if (drDays < 30 && selectedProgram.current_gate < 4) score -= 15;
          else if (drDays < 60 && selectedProgram.current_gate < 3) score -= 8;
          score += Math.min(completedGates * 3, 15);
          score = Math.max(0, Math.min(100, score));
          const scoreColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
          const scoreLabel = score >= 75 ? 'HEALTHY' : score >= 50 ? 'AT RISK' : 'CRITICAL';
          // 3-col on mobile: row1 = T-MINUS|GATE|SCORE, row2 = BUDGET|LAUNCH (span 1.5 each)
          const cols = isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)';
          const cells = [
            { label: 'T-MINUS',      value: drDays > 0 ? `${drDays}d` : 'PAST DUE', color: drDays < 60 ? '#ef4444' : t.accent },
            { label: 'GATE',         value: selectedProgram.current_gate ? `G${selectedProgram.current_gate}` : 'G1', color: t.accent },
            { label: 'SCORE',        value: `${score}/100`, color: scoreColor, sub: scoreLabel },
            { label: 'BUDGET',       value: `$${((selectedProgram.budget_total||0)/1000000).toFixed(1)}M`, color: t.text },
            { label: 'LAUNCH DATE',  value: selectedProgram.launch_date || '—', color: t.text },
          ];
          return (
            <div style={{ borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.surface, display: 'grid', gridTemplateColumns: cols }}>
              {cells.map(({ label, value, color, sub }, i) => (
                <div key={label} style={{ padding: isMobile ? '10px 12px' : '16px 28px', borderLeft: i > 0 ? `1px solid ${t.borderDim}` : 'none', borderBottom: isMobile && i < 3 ? `1px solid ${t.borderDim}` : 'none' }}>
                  <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: isMobile ? '0.5px' : '2px', marginBottom: '4px' }}>{label}</div>
                  <div style={{ fontSize: isMobile ? '15px' : '22px', fontWeight: '800', color, fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>{value}</div>
                  {sub && <div style={{ fontSize: '8px', color, letterSpacing: '1.5px', marginTop: '3px', fontWeight: '700' }}>{sub}</div>}
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── PROGRAM TITLE ── */}
        <div style={{ padding: isMobile ? '20px 16px 0' : '28px 48px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            {selectedProgram.product_image_url && (
              <img
                src={selectedProgram.product_image_url}
                alt="Product"
                style={{ width: isMobile ? '52px' : '68px', height: isMobile ? '52px' : '68px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${t.borderDim}`, flexShrink: 0, marginTop: '4px' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>{programCode}</div>
                {selectedProgram.markets && <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>· {selectedProgram.markets}</div>}
              </div>
              <h2 style={{ ...s.programTitle, margin: '0 0 20px 0' }}>{selectedProgram.name}</h2>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ padding: isMobile ? '16px 16px' : '24px 48px' }}>

        {/* Preview banner inside program view */}
        {previewLane && (
          <div style={{ backgroundColor: darkMode ? '#2d1f00' : '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '10px', color: '#f59e0b', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>PREVIEW MODE</div>
              <div style={{ fontSize: '12px', color: darkMode ? '#fcd34d' : '#92400e' }}>
                Viewing as <strong>{previewLane.charAt(0).toUpperCase() + previewLane.slice(1)} team member</strong>
              </div>
            </div>
            <button onClick={() => setPreviewLane(null)} style={{ background: 'none', border: '1px solid #f59e0b80', borderRadius: '4px', color: '#f59e0b', padding: '5px 12px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', cursor: 'pointer', fontWeight: '700', flexShrink: 0 }}>
              EXIT PREVIEW ✕
            </button>
          </div>
        )}

        {/* ── DEPENDENCY ALERT BADGES ── */}
        {(hardAlerts.length > 0 || softAlerts.length > 0) && (
          <div style={{ marginBottom: '20px' }}>

            {/* Badge row */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {hardAlerts.length > 0 && (
                <button
                  onClick={() => setAlertOpen(v => v === 'hard' ? null : 'hard')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: alertOpen === 'hard' ? '#ef444418' : darkMode ? '#1a0000' : '#fef2f2', border: '1px solid #ef444450', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0 }} />
                  <span style={{ fontSize: '10px', color: '#ef4444', letterSpacing: '2px', fontWeight: 'bold' }}>BLOCKED</span>
                  <span style={{ backgroundColor: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '1px 7px', letterSpacing: '0' }}>{hardAlerts.length}</span>
                  <span style={{ color: '#ef444480', fontSize: '11px', marginLeft: '2px' }}>{alertOpen === 'hard' ? '▲' : '▼'}</span>
                </button>
              )}
              {softAlerts.length > 0 && (
                <button
                  onClick={() => setAlertOpen(v => v === 'soft' ? null : 'soft')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: alertOpen === 'soft' ? '#f59e0b18' : darkMode ? '#1a0a00' : '#fff7ed', border: '1px solid #f59e0b50', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0 }} />
                  <span style={{ fontSize: '10px', color: '#f59e0b', letterSpacing: '2px', fontWeight: 'bold' }}>AT RISK</span>
                  <span style={{ backgroundColor: '#f59e0b', color: '#000', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '1px 7px', letterSpacing: '0' }}>{softAlerts.length}</span>
                  <span style={{ color: '#f59e0b80', fontSize: '11px', marginLeft: '2px' }}>{alertOpen === 'soft' ? '▲' : '▼'}</span>
                </button>
              )}
            </div>

            {/* Dropdown memo — BLOCKED */}
            {alertOpen === 'hard' && (
              <div style={{ marginTop: '8px', backgroundColor: darkMode ? '#110000' : '#fff5f5', border: '1px solid #ef444430', borderRadius: '6px', padding: '16px 18px' }}>
                <div style={{ color: '#ef4444', fontSize: '9px', letterSpacing: '3px', fontWeight: 'bold', marginBottom: '12px' }}>
                  BLOCKED — {hardAlerts.length} HARD DEPENDENCY {hardAlerts.length === 1 ? 'VIOLATION' : 'VIOLATIONS'}
                </div>
                {hardAlerts.map((a, i) => (
                  <div key={i} style={{ fontSize: '11px', color: t.cascadeText, marginBottom: '6px', display: 'flex', gap: '8px' }}>
                    <span style={{ color: '#ef4444', flexShrink: 0 }}>→</span>
                    <span>{a.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Dropdown memo — AT RISK */}
            {alertOpen === 'soft' && (
              <div style={{ marginTop: '8px', backgroundColor: darkMode ? '#110800' : '#fffbf0', border: '1px solid #f59e0b30', borderRadius: '6px', padding: '16px 18px' }}>
                <div style={{ color: '#f59e0b', fontSize: '9px', letterSpacing: '3px', fontWeight: 'bold', marginBottom: '12px' }}>
                  AT RISK — {softAlerts.length} DEPENDENCY {softAlerts.length === 1 ? 'WARNING' : 'WARNINGS'}
                </div>
                {softAlerts.map((a, i) => (
                  <div key={i} style={{ fontSize: '11px', color: t.cascadeText, marginBottom: '6px', display: 'flex', gap: '8px' }}>
                    <span style={{ color: '#f59e0b', flexShrink: 0 }}>→</span>
                    <span>{a.message}</span>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

        {/* ── LAUNCHED BANNER ── */}
        {selectedProgram.status === 'complete' && (
          <div style={{ backgroundColor: darkMode ? '#052e16' : '#f0fdf4', border: '2px solid #10b981', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '20px' }}>✓</div>
              <div>
                <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#10b981', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '3px' }}>PROGRAM LAUNCHED</div>
                <div style={{ fontSize: '12px', color: darkMode ? '#a7f3d0' : '#065f46' }}>This program has been marked as launched and moved to your launch archive.</div>
              </div>
            </div>
            {userRole === 'admin' && (
              <button onClick={() => markAsLaunched(true)} style={{ background: 'none', border: '1px solid #10b98170', color: '#10b981', padding: '8px 16px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '6px', flexShrink: 0 }}>
                REOPEN →
              </button>
            )}
          </div>
        )}

        {/* ── GATE ADVANCE BANNER ── */}
        {userRole === 'admin' && (() => {
          const cg = selectedProgram.current_gate || 1;
          if (cg >= 6) return null;
          const lanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const currentGateStatuses = (selectedProgram.gates || []).filter(g => g.gate_number === cg);
          const allComplete = lanes.every(l => currentGateStatuses.find(g => g.lane === l)?.status === 'complete');
          if (!allComplete) return null;
          const gateNames = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
          const advanceGate = () => {
            authFetch(`${API}/programs/${selectedProgram.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ current_gate: cg + 1 }),
            }).then(() => refreshProgram(selectedProgram.id));
          };
          return (
            <div style={{ backgroundColor: darkMode ? '#052e16' : '#f0fdf4', border: '1px solid #10b981', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#10b981', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '3px' }}>ALL LANES COMPLETE — GATE {cg} CLOSED</div>
                  <div style={{ fontSize: '12px', color: darkMode ? '#a7f3d0' : '#065f46' }}>Program is ready to advance to <strong>G{cg + 1} · {gateNames[cg]}</strong></div>
                </div>
              </div>
              <button
                onClick={advanceGate}
                style={{ backgroundColor: '#10b981', border: 'none', color: '#fff', padding: '10px 20px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '1.5px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px', flexShrink: 0 }}
              >
                ADVANCE TO G{cg + 1} →
              </button>
            </div>
          );
        })()}

        {/* ── NOMOS FEEDBACK ── */}
        {selectedProgram && (feedbackLoading || nomosFeedback.length > 0 || (!feedbackLoading && nomosFeedback.length === 0 && feedbackOpen)) && (() => {
          const sevColor = { critical: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };
          const sevBg = { critical: darkMode ? '#ef444415' : '#fef2f2', warning: darkMode ? '#f59e0b15' : '#fffbeb', info: darkMode ? '#3b82f615' : '#eff6ff' };
          const topSev = nomosFeedback.find(f => f.severity === 'critical') ? 'critical' : nomosFeedback.find(f => f.severity === 'warning') ? 'warning' : 'info';
          return (
            <div style={{ margin: '0 0 12px 0', border: `1px solid ${feedbackLoading ? t.borderDim : sevColor[topSev] + '40'}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: t.card }}>
              <button
                onClick={() => setFeedbackOpen(o => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', gap: '10px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: feedbackLoading ? t.borderDim : sevColor[topSev], flexShrink: 0 }} />
                  <span style={{ fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: feedbackLoading ? t.dim : sevColor[topSev], fontWeight: '700' }}>
                    NOMOS FEEDBACK
                  </span>
                  {!feedbackLoading && nomosFeedback.length > 0 && (
                    <span style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                      · {nomosFeedback.length} item{nomosFeedback.length !== 1 ? 's' : ''}
                      {nomosFeedback.filter(f => f.severity === 'critical').length > 0 && ` · ${nomosFeedback.filter(f => f.severity === 'critical').length} critical`}
                    </span>
                  )}
                  {feedbackLoading && <span style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>· ANALYZING...</span>}
                </div>
                <span style={{ fontSize: '12px', color: t.dim, transform: feedbackOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>
              </button>
              {feedbackOpen && !feedbackLoading && nomosFeedback.length === 0 && (
                <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: '14px', fontSize: '12px', color: feedbackError ? '#ef4444' : t.dim, textAlign: 'center' }}>
                  {feedbackError ? `Error: ${feedbackError}` : 'No concerns detected — program looks healthy at this stage.'}
                  <button onClick={() => { setNomosFeedback([]); setFeedbackError(null); setFeedbackLoading(true); const url = demoMode ? `${API}/demo/programs/${selectedProgram.id}/feedback` : `${API}/programs/${selectedProgram.id}/feedback`; const f = demoMode ? fetch(url, { method: 'POST' }) : authFetch(url, { method: 'POST' }); f.then(r => r.json()).then(d => { if (d.feedback && d.feedback.length > 0) setNomosFeedback(d.feedback); else if (d.error) setFeedbackError(d.error); }).catch(e => setFeedbackError(e.message)).finally(() => setFeedbackLoading(false)); }} style={{ marginLeft: '10px', fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: t.dim, background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '3px 8px', cursor: 'pointer' }}>RETRY</button>
                </div>
              )}
              {feedbackOpen && !feedbackLoading && nomosFeedback.length > 0 && (
                <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {nomosFeedback.map((f, i) => (
                    <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px 12px', backgroundColor: sevBg[f.severity] || sevBg.info, borderRadius: '6px', border: `1px solid ${(sevColor[f.severity] || sevColor.info) + '30'}` }}>
                      <div style={{ flexShrink: 0, marginTop: '2px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sevColor[f.severity] || sevColor.info }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: sevColor[f.severity] || sevColor.info, fontWeight: '700', textTransform: 'uppercase' }}>{f.severity}</span>
                          <span style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: t.accent, backgroundColor: t.accent + '15', padding: '1px 6px', borderRadius: '3px', textTransform: 'uppercase' }}>{f.lane}</span>
                          <span style={{ fontSize: '12px', color: t.text, fontWeight: '600' }}>{f.title}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: t.subLabel, lineHeight: 1.5, marginBottom: '6px' }}>{f.insight}</div>
                        <div style={{ fontSize: '11px', color: sevColor[f.severity] || sevColor.info, fontWeight: '500' }}>→ {f.action}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                    <button
                      onClick={() => { setNomosFeedback([]); setFeedbackLoading(true); authFetch(`${API}/programs/${selectedProgram.id}/feedback`, { method: 'POST' }).then(r => r.json()).then(d => { if (d.feedback) setNomosFeedback(d.feedback); }).catch(() => {}).finally(() => setFeedbackLoading(false)); }}
                      style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: t.dim, background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '3px 10px', cursor: 'pointer' }}
                    >
                      REFRESH
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── SINCE LAST VISIT ── */}
        {visitDiff.length > 0 && !visitDiffDismissed && (() => {
          const statusLabel = s => s.replace('_', ' ').toUpperCase();
          const statusColor = s => s === 'blocked' ? '#ef4444' : s === 'at_risk' ? '#f59e0b' : s === 'complete' ? '#10b981' : t.dim;
          return (
            <div style={{ marginBottom: '12px', border: `1px solid ${t.accent}30`, borderRadius: '8px', backgroundColor: darkMode ? `${t.accent}08` : `${t.accent}05`, padding: '10px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', fontWeight: '700', marginBottom: '8px' }}>
                  SINCE YOUR LAST VISIT — {visitDiff.length} CHANGE{visitDiff.length !== 1 ? 'S' : ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {visitDiff.map(({ lane, gate, from, to }) => (
                    <div key={`${lane}-${gate}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '4px 8px' }}>
                      <span style={{ fontSize: '8px', color: t.accent, fontWeight: '700', letterSpacing: '1px' }}>{lane.toUpperCase()} G{gate}</span>
                      <span style={{ fontSize: '8px', color: statusColor(from) }}>{statusLabel(from)}</span>
                      <span style={{ fontSize: '8px', color: t.dim }}>→</span>
                      <span style={{ fontSize: '8px', color: statusColor(to), fontWeight: '700' }}>{statusLabel(to)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => setVisitDiffDismissed(true)} style={{ background: 'none', border: 'none', color: t.dim, cursor: 'pointer', fontSize: '14px', padding: '0', lineHeight: 1, flexShrink: 0 }}>✕</button>
            </div>
          );
        })()}

        {/* ── TODAY'S FOCUS ── */}
        {selectedProgram && (() => {
          const allGates = selectedProgram.gates || [];
          const currentGate = selectedProgram.current_gate || 1;
          const today = new Date();
          const items = [];

          // 1. BLOCKED at or one gate ahead of current
          allGates.filter(g => g.status === 'blocked' && g.gate_number <= currentGate + 1)
            .forEach(g => items.push({
              key: `blocked-${g.lane}-${g.gate_number}`,
              severity: 'critical', color: '#ef4444',
              lane: g.lane, gate: g.gate_number,
              issue: (g.notes || '').replace(/\[NOMOS[^\]]*\][^\n]*/g, '').trim().slice(0, 90) || 'Gate is BLOCKED — requires immediate attention',
              action: () => { setActiveTab('gates'); openEdit(g); },
              actionLabel: 'OPEN GATE',
            }));

          // 2. Forward violations (will miss dependency at current pace)
          (consequences?.forward_violations || []).forEach(v => {
            items.push({
              key: `fwd-${v.requiredLane}-${v.requiredGate}`,
              severity: 'warning', color: '#f59e0b',
              lane: v.requiredLane, gate: v.requiredGate,
              issue: `On pace to miss ${v.dependentLane.charAt(0).toUpperCase() + v.dependentLane.slice(1)} G${v.dependentGate} dependency — projected ${Math.round(v.projectedReqPct || 0)}% vs ${v.minCompletion}% required`,
              action: () => setActiveTab('consequences'),
              actionLabel: 'VIEW CASCADE',
            });
          });

          // 3. AT_RISK with overdue or imminent close date (within 5 days)
          allGates.filter(g => g.status === 'at_risk' && g.planned_close_date).forEach(g => {
            const daysToClose = Math.ceil((new Date(g.planned_close_date + 'T00:00:00') - today) / 86400000);
            if (daysToClose < 5) items.push({
              key: `close-${g.lane}-${g.gate_number}`,
              severity: daysToClose < 0 ? 'critical' : 'warning',
              color: daysToClose < 0 ? '#ef4444' : '#f59e0b',
              lane: g.lane, gate: g.gate_number,
              issue: daysToClose < 0
                ? `Gate close was ${Math.abs(daysToClose)}d ago — still AT RISK at ${g.completion_percent}%`
                : `Gate close in ${daysToClose}d — AT RISK at ${g.completion_percent}%`,
              action: () => { setActiveTab('gates'); openEdit(g); },
              actionLabel: 'REVIEW',
            });
          });

          // Dedupe by lane+gate, sort critical first, cap at 3
          const seen = new Set();
          const order = { critical: 0, warning: 1, info: 2 };
          const focusItems = items
            .sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3))
            .filter(item => {
              const k = `${item.lane}-${item.gate}`;
              if (seen.has(k)) return false;
              seen.add(k); return true;
            })
            .slice(0, 3);

          if (!focusItems.length) return null;
          const headerColor = focusItems[0].severity === 'critical' ? '#ef4444' : '#f59e0b';

          return (
            <div style={{ marginBottom: '16px', border: `1px solid ${headerColor}30`, borderRadius: '8px', overflow: 'hidden', backgroundColor: darkMode ? `${headerColor}08` : `${headerColor}05` }}>
              <div
                onClick={() => setFocusCollapsed(c => !c)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 16px', cursor: 'pointer', borderBottom: focusCollapsed ? 'none' : `1px solid ${headerColor}20` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: headerColor }} />
                  <span style={{ fontSize: '9px', color: headerColor, letterSpacing: '2px', fontWeight: '700' }}>TODAY'S FOCUS</span>
                  <span style={{ fontSize: '9px', color: headerColor, backgroundColor: `${headerColor}20`, padding: '1px 7px', borderRadius: '10px', fontFamily: "'Roboto Mono', monospace" }}>{focusItems.length}</span>
                </div>
                <span style={{ fontSize: '9px', color: t.dim }}>{focusCollapsed ? '▼' : '▲'}</span>
              </div>
              {!focusCollapsed && (
                <div>
                  {focusItems.map((item, idx) => {
                    const solKey = `${item.lane}-${item.gate}`;
                    const sol = focusSolutions[solKey];
                    const isLast = idx === focusItems.length - 1;
                    return (
                      <div key={item.key} style={{ borderBottom: isLast && !sol ? 'none' : `1px solid ${t.borderDim}` }}>
                        {/* Issue row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 16px' }}>
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
                          <span style={{ fontSize: '8px', color: item.color, backgroundColor: `${item.color}18`, padding: '2px 6px', borderRadius: '3px', letterSpacing: '1px', fontWeight: '700', flexShrink: 0 }}>{item.lane.toUpperCase()}</span>
                          <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '1px', flexShrink: 0 }}>G{item.gate}</span>
                          <div style={{ flex: 1, fontSize: '11px', color: t.text, lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'nowrap' : 'normal' }}>{item.issue}</div>
                          <button
                            onClick={e => { e.stopPropagation(); item.action(); }}
                            style={{ flexShrink: 0, fontSize: '8px', color: item.color, backgroundColor: `${item.color}15`, border: `1px solid ${item.color}40`, borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", whiteSpace: 'nowrap' }}>
                            {item.actionLabel}
                          </button>
                        </div>
                        {/* AI solution row */}
                        {focusSolutionsLoading && !sol && (
                          <div style={{ padding: '6px 16px 10px', fontSize: '9px', color: t.subLabel, fontStyle: 'italic', borderBottom: isLast ? 'none' : `1px solid ${t.borderDim}` }}>
                            Nomos is predicting resolution path...
                          </div>
                        )}
                        {sol && (
                          <div style={{ padding: '8px 16px 12px', backgroundColor: darkMode ? '#0d0d0d' : '#f8f8f8', borderBottom: isLast ? 'none' : `1px solid ${t.borderDim}` }}>
                            <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', marginBottom: '5px', fontWeight: '700' }}>NOMOS PREDICTS →</div>
                            <div style={{ fontSize: '11px', color: t.text, lineHeight: 1.5, marginBottom: '6px' }}>{sol.action}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                              <span style={{ fontSize: '9px', color: '#10b981', fontFamily: "'Roboto Mono', monospace" }}>⏱ {sol.timeEstimate}</span>
                              <span style={{ fontSize: '9px', color: t.subLabel, lineHeight: 1.4 }}>{sol.basis}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── TABS ── */}
        {(userRole === 'member' && userLane) ? (
          <div>
            {/* Scope toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '12px' }}>
              {[{ id: 'lane', label: `MY LANE` }, { id: 'full', label: 'FULL PROGRAM' }].map(opt => (
                <button key={opt.id}
                  onClick={() => {
                    setMemberScopeMode(opt.id);
                    setActiveTab(opt.id === 'lane' ? 'my_work' : 'overview');
                  }}
                  style={{ padding: '5px 14px', fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer', borderRadius: '4px', border: memberScopeMode === opt.id ? `1px solid ${t.accent}` : `1px solid ${t.borderDim}`, background: memberScopeMode === opt.id ? `${t.accent}18` : 'none', color: memberScopeMode === opt.id ? t.accent : t.dim, transition: 'all 0.15s' }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Lane-focused tabs */}
            {memberScopeMode === 'lane' && (
              <div style={s.tabs}>
                {[
                  { id: 'my_work', label: 'MY WORK' },
                  { id: 'launch_status', label: 'LAUNCH STATUS' },
                  { id: 'timeline', label: 'TIMELINE' },
                  { id: 'team_contacts', label: 'TEAM' },
                  { id: 'ask', label: 'ASK NOMOS' },
                  { id: 'brief', label: 'BRIEF' },
                ].map(({ id, label }) => (
                  <button key={id}
                    style={{ ...s.tab, ...(activeTab === id ? s.activeTab : {}) }}
                    onClick={() => setActiveTab(id)}>
                    {isMobile && label === 'ASK NOMOS' ? 'ASK' : label}
                  </button>
                ))}
              </div>
            )}
            {/* Full program tabs (read-only subset) */}
            {memberScopeMode === 'full' && (
              <div style={s.tabs}>
                {[
                  { id: 'overview', label: 'OVERVIEW' },
                  { id: 'teams', label: 'TEAMS' },
                  { id: 'gates', label: 'READINESS' },
                  { id: 'timeline', label: 'TIMELINE' },
                ].map(({ id, label }) => (
                  <button key={id}
                    style={{ ...s.tab, ...(activeTab === id ? s.activeTab : {}) }}
                    onClick={() => setActiveTab(id)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={s.tabs}>
            {['overview', 'teams', 'gates', 'timeline', 'consequences', 'ask', 'exec', 'adaptive', 'activity', 'brief'].map(tab => {
              const fullLabel = tab === 'ask' ? 'ASK NOMOS' : tab === 'exec' ? 'EXEC SUMMARY' : tab === 'gates' ? 'LAUNCH READINESS' : tab === 'consequences' ? 'CONSEQUENCES' : tab.toUpperCase();
              const shortLabel = tab === 'ask' ? 'ASK' : tab === 'exec' ? 'EXEC' : tab === 'gates' ? 'READINESS' : tab === 'consequences' ? 'RISK' : tab === 'adaptive' ? 'PREDICT' : tab === 'activity' ? 'LOG' : tab.toUpperCase();
              return (
                <button key={tab}
                  style={{ ...s.tab, ...(activeTab === tab ? s.activeTab : {}) }}
                  onClick={() => setActiveTab(tab)}>
                  {isMobile ? shortLabel : fullLabel}
                </button>
              );
            })}
          </div>
        )}

        {/* ── MEMBER: MY WORK TAB ── */}
        {(userRole === 'member' && userLane) && activeTab === 'my_work' && (() => {
          const laneColorMap = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
          const color = laneColorMap[userLane] || t.accent;
          const laneGates = (laneViewData.gates || []).filter(g => g.lane === userLane).sort((a, b) => a.gate_number - b.gate_number);
          const gateNamesArr = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
          const displayName = userLane.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return (
            <div style={{ maxWidth: '860px' }}>
              {/* Header */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color }} />
                  <span style={{ fontSize: '9px', color, letterSpacing: '3px', fontWeight: '700' }}>{displayName.toUpperCase()} LANE</span>
                </div>
                <div style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: '800', color: t.text, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px', lineHeight: 1.1, marginBottom: '6px' }}>
                  My Work
                </div>
                <div style={{ fontSize: '12px', color: t.muted }}>
                  {selectedProgram.name} · Gate {selectedProgram.current_gate} of 6
                </div>
              </div>

              {laneViewLoading ? (
                <div style={{ color: t.subLabel, fontSize: '11px', letterSpacing: '2px', padding: '40px 0', textAlign: 'center' }}>LOADING YOUR TASKS…</div>
              ) : laneGates.length === 0 ? (
                <div style={{ color: t.subLabel, fontSize: '12px', padding: '40px 0', textAlign: 'center' }}>No gate data for your lane yet. Ask your admin to set up your lane.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {laneGates.map(gs => {
                    const isExp = expandedGateInLane === gs.id;
                    const isCurrent = gs.gate_number === selectedProgram.current_gate;
                    const sColor = gs.status === 'complete' ? '#10b981' : gs.status === 'blocked' ? '#ef4444' : gs.status === 'at_risk' ? '#f59e0b' : gs.status === 'on_track' ? color : t.dim;
                    const ms = laneViewData.milestones[gs.id] || [];
                    const doneMs = ms.filter(m => m.completed).length;
                    const livePct = ms.length ? Math.round(doneMs / ms.length * 100) : gs.completion_percent;
                    return (
                      <div key={gs.id} style={{ backgroundColor: t.surface, border: `1px solid ${isExp ? color + '50' : isCurrent ? color + '30' : t.borderDim}`, borderRadius: '10px', overflow: 'hidden' }}>
                        {/* Gate header */}
                        <div onClick={() => setExpandedGateInLane(isExp ? null : gs.id)}
                          style={{ padding: '18px 22px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', backgroundColor: isCurrent && !isExp ? color + '08' : 'transparent' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: isExp ? color + '20' : isCurrent ? color + '15' : t.surfaceAlt, border: `1px solid ${isExp ? color + '40' : isCurrent ? color + '30' : t.borderDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color: isExp || isCurrent ? color : t.muted, flexShrink: 0, fontFamily: "'Roboto Mono', monospace" }}>
                              G{gs.gate_number}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif", lineHeight: 1.3 }}>{gateNamesArr[gs.gate_number - 1]}</div>
                                {isCurrent && <span style={{ fontSize: '8px', color, letterSpacing: '1.5px', border: `1px solid ${color}40`, padding: '2px 6px', borderRadius: '3px', fontFamily: "'Roboto Mono', monospace" }}>CURRENT</span>}
                              </div>
                              <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '3px', letterSpacing: '0.5px' }}>
                                {ms.length > 0 ? `${doneMs}/${ms.length} tasks done` : 'No tasks yet'}
                                {gs.planned_close_date ? ` · Due ${gs.planned_close_date.slice(5)}` : ''}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: sColor, letterSpacing: '1.5px', marginBottom: '5px', fontFamily: "'Roboto Mono', monospace" }}>{gs.status.toUpperCase().replace('_', ' ')}</div>
                              <div style={{ width: '72px', height: '3px', backgroundColor: t.borderDim, borderRadius: '2px' }}>
                                <div style={{ height: '100%', width: `${livePct}%`, backgroundColor: color, borderRadius: '2px' }} />
                              </div>
                              <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px', fontFamily: "'Roboto Mono', monospace" }}>{livePct}%</div>
                            </div>
                            <span style={{ color: t.subLabel, fontSize: '11px' }}>{isExp ? '▲' : '▼'}</span>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExp && (
                          <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: '20px 22px' }}>
                            {ms.length === 0 ? (
                              <div style={{ color: t.subLabel, fontSize: '12px', fontStyle: 'italic', textAlign: 'center', padding: '16px 0' }}>
                                No tasks yet — ask your admin to add milestones to this gate.
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px', fontFamily: "'Roboto Mono', monospace" }}>TASKS · {doneMs}/{ms.length} COMPLETE</div>
                                {/* Progress bar */}
                                <div style={{ height: '4px', backgroundColor: t.borderDim, borderRadius: '2px', marginBottom: '18px' }}>
                                  <div style={{ height: '100%', width: `${livePct}%`, backgroundColor: color, borderRadius: '2px', transition: 'width 0.3s ease' }} />
                                </div>
                                {ms.map(m => (
                                  <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${t.borderDimmer}` }}>
                                    {/* Checkbox */}
                                    <div
                                      onClick={() => updateTaskField(m.id, gs.id, 'completed', !m.completed)}
                                      style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${m.completed ? '#10b981' : t.border}`, backgroundColor: m.completed ? '#10b98120' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px', cursor: 'pointer' }}>
                                      {m.completed && <span style={{ color: '#10b981', fontSize: '12px', lineHeight: 1, fontWeight: 'bold' }}>✓</span>}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '13px', color: m.completed ? t.subLabel : t.text, textDecoration: m.completed ? 'line-through' : 'none', lineHeight: 1.5, marginBottom: '4px' }}>
                                        {m.text}
                                      </div>
                                      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                                        {m.dri && <span style={{ fontSize: '10px', color: t.accent, letterSpacing: '0.5px', fontFamily: "'Roboto Mono', monospace" }}>DRI: {m.dri}</span>}
                                        {m.due_date && <span style={{ fontSize: '10px', color: t.dim, letterSpacing: '0.5px', fontFamily: "'Roboto Mono', monospace" }}>Due {m.due_date.slice(0, 10)}</span>}
                                        {m.notes && <span style={{ fontSize: '10px', color: t.subLabel, fontStyle: 'italic', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.notes}</span>}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── MEMBER: LAUNCH STATUS TAB ── */}
        {(userRole === 'member' && userLane) && activeTab === 'launch_status' && (() => {
          const allGatesSrc = selectedProgram.gates || [];
          const currentGateNum = selectedProgram.current_gate || 1;
          const laneColorMap = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
          const stdLanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const launchDate = selectedProgram.launch_date ? new Date(selectedProgram.launch_date) : null;
          const daysToLaunch = launchDate ? Math.round((launchDate - today) / 86400000) : null;

          const laneStatuses = stdLanes.map(lane => {
            const gs = allGatesSrc.find(g => g.lane === lane && g.gate_number === currentGateNum);
            const status = gs?.status || 'idle';
            const pct = gs?.completion_percent || 0;
            const dotColor = status === 'complete' || status === 'on_track' ? '#10b981' : status === 'at_risk' ? '#f59e0b' : status === 'blocked' ? '#ef4444' : t.borderDim;
            const statusLabel = status === 'complete' ? 'COMPLETE' : status === 'on_track' ? 'ON TRACK' : status === 'at_risk' ? 'AT RISK' : status === 'blocked' ? 'BLOCKED' : '—';
            return { lane, status, pct, dotColor, statusLabel, color: laneColorMap[lane] };
          });

          // Gate progression
          const gateNamesArr = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

          return (
            <div style={{ maxWidth: '700px' }}>
              {/* T-minus hero */}
              <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '12px', padding: isMobile ? '24px 20px' : '32px 36px', marginBottom: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '3px', marginBottom: '12px', fontFamily: "'Roboto Mono', monospace" }}>DAYS TO LAUNCH</div>
                <div style={{ fontSize: isMobile ? '56px' : '80px', fontWeight: '900', color: daysToLaunch !== null && daysToLaunch < 0 ? '#ef4444' : daysToLaunch !== null && daysToLaunch < 30 ? '#f59e0b' : t.accent, fontFamily: "'Roboto Mono', monospace", letterSpacing: '-3px', lineHeight: 1 }}>
                  {daysToLaunch !== null ? (daysToLaunch < 0 ? `-${Math.abs(daysToLaunch)}` : `T-${daysToLaunch}`) : '—'}
                </div>
                {launchDate && <div style={{ fontSize: '12px', color: t.muted, marginTop: '10px' }}>{launchDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>}
              </div>

              {/* Gate progression */}
              <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '20px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '14px', fontFamily: "'Roboto Mono', monospace" }}>GATE PROGRESSION</div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5, 6].map(n => {
                    const isDone = n < currentGateNum;
                    const isActive = n === currentGateNum;
                    const blockColor = isDone ? '#10b981' : isActive ? t.accent : t.borderDim;
                    return (
                      <div key={n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '100%', height: '8px', borderRadius: '3px', backgroundColor: blockColor, transition: 'background-color 0.3s', position: 'relative' }}>
                          {isActive && <div style={{ position: 'absolute', inset: 0, borderRadius: '3px', backgroundColor: t.accent, opacity: 0.4, animation: 'pulse 2s infinite' }} />}
                        </div>
                        <div style={{ fontSize: '8px', color: isActive ? t.accent : isDone ? '#10b981' : t.dim, fontWeight: isActive ? '700' : '400', fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.5px' }}>G{n}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '10px', fontFamily: "'Roboto Mono', monospace" }}>
                  Currently in G{currentGateNum}: {gateNamesArr[currentGateNum - 1]}
                </div>
              </div>

              {/* Per-lane status grid */}
              <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '16px', fontFamily: "'Roboto Mono', monospace" }}>ALL TEAMS · GATE {currentGateNum} STATUS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {laneStatuses.map(r => (
                    <div key={r.lane} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px', borderRadius: '8px', backgroundColor: r.lane === userLane ? r.color + '10' : 'transparent', border: r.lane === userLane ? `1px solid ${r.color}30` : `1px solid ${t.borderDimmer}` }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: r.dotColor, flexShrink: 0 }} />
                      <div style={{ flex: 1, fontSize: '12px', color: t.text, textTransform: 'capitalize', fontFamily: "'Inter', sans-serif", fontWeight: r.lane === userLane ? '600' : '400' }}>
                        {r.lane}
                        {r.lane === userLane && <span style={{ fontSize: '8px', color: r.color, marginLeft: '6px', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>YOU</span>}
                      </div>
                      <div style={{ fontSize: '9px', color: r.dotColor, fontWeight: '700', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace', minWidth: '70px', textAlign: 'right" }}>{r.statusLabel}</div>
                      <div style={{ fontSize: '10px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace", minWidth: '36px', textAlign: 'right' }}>{r.pct}%</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ fontSize: '10px', color: t.dim, textAlign: 'center', letterSpacing: '0.5px', fontStyle: 'italic' }}>
                For task details, see MY WORK
              </div>
            </div>
          );
        })()}

        {/* ── MEMBER: TIMELINE TAB ── */}
        {(userRole === 'member' && userLane) && activeTab === 'timeline' && (() => {
          const allGatesSrc = selectedProgram.gates || [];
          const currentGateNum = selectedProgram.current_gate || 1;
          const launchDate = selectedProgram.launch_date ? new Date(selectedProgram.launch_date) : null;
          const daysToLaunch = launchDate ? Math.round((launchDate - today) / 86400000) : null;
          const gateNamesArr = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

          // For each gate number, get the planned_close_date from any available lane (prefer design)
          const gateSchedule = [1, 2, 3, 4, 5, 6].map(n => {
            const designGate = allGatesSrc.find(g => g.gate_number === n && g.lane === 'design');
            const anyGate = allGatesSrc.find(g => g.gate_number === n);
            const ref = designGate || anyGate;
            const isDone = n < currentGateNum;
            const isActive = n === currentGateNum;
            const closeDate = ref?.planned_close_date ? new Date(ref.planned_close_date) : null;
            const daysUntilClose = closeDate ? Math.round((closeDate - today) / 86400000) : null;
            return { n, name: gateNamesArr[n - 1], closeDate, daysUntilClose, isDone, isActive };
          });

          return (
            <div style={{ maxWidth: '680px' }}>
              <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '24px', fontFamily: "'Roboto Mono', monospace" }}>PROGRAM TIMELINE — READ ONLY</div>

              {/* Launch countdown */}
              {launchDate && daysToLaunch !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>TARGET LAUNCH DATE</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: t.text, fontFamily: "'Roboto Mono', monospace" }}>{launchDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>T-MINUS</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: daysToLaunch < 0 ? '#ef4444' : daysToLaunch < 30 ? '#f59e0b' : t.accent, fontFamily: "'Roboto Mono', monospace", letterSpacing: '-1px', lineHeight: 1 }}>
                      {daysToLaunch < 0 ? `-${Math.abs(daysToLaunch)}d` : `${daysToLaunch}d`}
                    </div>
                  </div>
                </div>
              )}

              {/* Gate list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {gateSchedule.map(({ n, name, closeDate, daysUntilClose, isDone, isActive }) => {
                  const statusColor = isDone ? '#10b981' : isActive ? t.accent : t.dim;
                  const statusLabel = isDone ? 'COMPLETE' : isActive ? 'ACTIVE' : 'UPCOMING';
                  return (
                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 18px', backgroundColor: isActive ? t.surface : 'transparent', border: `1px solid ${isActive ? t.accent + '40' : t.borderDimmer}`, borderRadius: '8px', opacity: isDone ? 0.7 : 1 }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '7px', backgroundColor: isDone ? '#10b98120' : isActive ? t.accent + '20' : t.surfaceAlt, border: `1px solid ${isDone ? '#10b98140' : isActive ? t.accent + '50' : t.borderDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', color: statusColor, flexShrink: 0, fontFamily: "'Roboto Mono', monospace" }}>
                        {isDone ? '✓' : `G${n}`}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: isActive ? '700' : '500', color: isActive ? t.text : t.muted, fontFamily: "'Inter', sans-serif" }}>{name}</div>
                        {closeDate && (
                          <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '2px', fontFamily: "'Roboto Mono', monospace" }}>
                            {isDone ? 'Closed' : 'Target close'} {closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {!isDone && daysUntilClose !== null && <span style={{ color: daysUntilClose < 7 ? '#ef4444' : daysUntilClose < 21 ? '#f59e0b' : t.dim, marginLeft: '8px' }}>({daysUntilClose < 0 ? `${Math.abs(daysUntilClose)}d overdue` : `${daysUntilClose}d away`})</span>}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '1.5px', color: statusColor, fontFamily: "'Roboto Mono', monospace", flexShrink: 0 }}>{statusLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── MEMBER: TEAM CONTACTS TAB ── */}
        {(userRole === 'member' && userLane) && activeTab === 'team_contacts' && (() => {
          const laneColorMap2 = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
          const laneOrder = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const myColor = laneColorMap2[userLane] || t.accent;
          const currentGateNum = selectedProgram.current_gate || 1;
          const myId = session?.user?.id;
          const inbox = mailboxMessages.filter(m => m.to_user_id === myId);
          const sent  = mailboxMessages.filter(m => m.from_user_id === myId);

          // Build full contact list: real accounts + named leads/DRIs
          const accountMembers = teamMembers.filter(m => m.id !== myId);
          const allContacts = laneOrder.flatMap(lane => {
            const lead = teamLeads[lane];
            const dris = (laneDris[lane] || []).filter(Boolean);
            const accts = accountMembers.filter(m => m.lane === lane);
            // Gate DRI from current gate for this lane
            const gDri = (selectedProgram.gates || []).find(g => g.lane === lane && g.gate_number === currentGateNum)?.dri_user_id;
            const gDriName = gDri ? teamMembers.find(m => m.id === gDri)?.full_name : null;
            return [
              ...accts.map(m => ({ id: m.id, name: m.full_name, lane, isAccount: true, isLead: m.role === 'lead' || m.role === 'admin', isDri: m.id === gDri })),
              ...(lead && !accts.some(m => m.full_name === lead) ? [{ id: `lead:${lane}`, name: lead, lane, isAccount: false, isLead: true, isDri: false }] : []),
              ...dris.filter(d => !accts.some(m => m.full_name === d) && d !== lead).map(d => ({ id: `dri:${lane}:${d}`, name: d, lane, isAccount: false, isLead: false, isDri: false })),
            ];
          });

          return (
            <div style={{ maxWidth: '800px' }}>

              {/* Compose message */}
              {composeOpen && (
                <div style={{ backgroundColor: t.surface, border: `1px solid ${myColor}50`, borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
                  <div style={{ fontSize: '9px', color: myColor, letterSpacing: '2px', marginBottom: '14px', fontWeight: '700' }}>NEW MESSAGE FROM {userLane.toUpperCase()}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                    <div>
                      <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>TO</div>
                      <select value={composeTo} onChange={e => setComposeTo(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: composeTo ? t.text : t.dim, fontFamily: "'Roboto Mono', monospace", fontSize: '11px' }}>
                        <option value="">Select person...</option>
                        {accountMembers.map(m => <option key={m.id} value={m.id}>{m.full_name}{m.lane ? ` · ${m.lane.toUpperCase()}` : ''}</option>)}
                        {Object.entries(teamLeads || {}).filter(([, name]) => name && !accountMembers.some(m => m.full_name === name)).map(([lane, name]) => (
                          <option key={`lead:${lane}`} value={name}>{name} · {lane.toUpperCase()} LEAD</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>SUBJECT</div>
                      <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Optional subject" style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '11px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px' }}>MESSAGE</div>
                      <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={4} placeholder="Write your message..." style={{ width: '100%', padding: '8px 10px', backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text, fontFamily: "'Roboto Mono', monospace", fontSize: '11px', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={sendMessage} disabled={composeSending || !composeTo.trim() || !composeBody.trim()}
                      style={{ backgroundColor: myColor, border: 'none', color: '#000', padding: '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '4px', opacity: !composeTo.trim() || !composeBody.trim() ? 0.5 : 1 }}>
                      {composeSending ? 'SENDING...' : 'SEND'}
                    </button>
                    <button onClick={saveDraft} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '8px 14px', fontSize: '9px', color: t.dim, letterSpacing: '1px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>SAVE DRAFT</button>
                    <button onClick={() => { setComposeOpen(false); setComposeTo(''); setComposeSubject(''); setComposeBody(''); setEditingDraftId(null); }}
                      style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '8px 14px', fontSize: '9px', color: t.dim, letterSpacing: '1px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace" }}>CANCEL</button>
                  </div>
                </div>
              )}

              {/* Team directory */}
              <div style={{ marginBottom: '36px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '10px', letterSpacing: '3px', color: t.dim }}>LAUNCH TEAM — {allContacts.length} PEOPLE</div>
                  <button onClick={() => { setComposeOpen(true); setComposeTo(''); setComposeSubject(''); setComposeBody(''); }}
                    style={{ backgroundColor: myColor, border: 'none', color: '#000', padding: '7px 14px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', fontWeight: '700', cursor: 'pointer', borderRadius: '4px' }}>
                    + NEW MESSAGE
                  </button>
                </div>

                {laneOrder.map(lane => {
                  const laneContacts = allContacts.filter(c => c.lane === lane);
                  if (laneContacts.length === 0) return null;
                  const lc = laneColorMap2[lane] || t.accent;
                  const isMyLane = lane === userLane;
                  return (
                    <div key={lane} style={{ marginBottom: '16px', border: `1px solid ${isMyLane ? lc + '40' : t.borderDim}`, borderLeft: isMyLane ? `3px solid ${lc}` : `1px solid ${t.borderDim}`, borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ padding: '10px 16px', backgroundColor: isMyLane ? lc + '0a' : t.surface, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: lc }} />
                        <span style={{ fontSize: '9px', color: lc, letterSpacing: '2px', fontWeight: '700' }}>{lane.toUpperCase()}</span>
                        {isMyLane && <span style={{ fontSize: '8px', color: lc, backgroundColor: lc + '20', padding: '1px 6px', borderRadius: '3px', letterSpacing: '1px' }}>YOUR LANE</span>}
                      </div>
                      {laneContacts.map(contact => {
                        const initials = contact.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                        return (
                          <div key={contact.id} style={{ padding: '12px 16px', borderTop: `1px solid ${t.borderDim}`, display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: t.bg }}>
                            <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: lc + '20', border: `1px solid ${lc}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: lc, flexShrink: 0 }}>{initials}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: t.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {contact.name}
                                {contact.isLead && <span style={{ fontSize: '7px', color: lc, backgroundColor: lc + '15', padding: '1px 5px', borderRadius: '3px', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>LEAD</span>}
                              </div>
                              <div style={{ fontSize: '10px', color: t.muted, marginTop: '2px' }}>
                                {contact.isAccount ? `G${currentGateNum} ${lane} team` : `${lane} DRI`}
                              </div>
                            </div>
                            {contact.id !== myId && contact.isAccount && (
                              <button onClick={() => { setComposeTo(contact.id); setComposeSubject(''); setComposeBody(''); setComposeOpen(true); }}
                                style={{ background: 'none', border: `1px solid ${lc}50`, borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '9px', color: lc, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', flexShrink: 0 }}>
                                MESSAGE
                              </button>
                            )}
                            {(!contact.isAccount) && (
                              <button onClick={() => { setComposeTo(contact.name); setComposeSubject(''); setComposeBody(''); setComposeOpen(true); }}
                                style={{ background: 'none', border: `1px solid ${lc}50`, borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '9px', color: lc, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', flexShrink: 0 }}>
                                MESSAGE
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Messages — tabbed inbox / sent / drafts */}
              {(() => {
                const programDrafts2 = drafts.filter(d => d.programId === selectedProgram?.id);
                const msgBtnStyle2 = (id) => ({ background: 'none', border: 'none', padding: '6px 0', fontSize: '9px', fontWeight: '700', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", cursor: 'pointer', color: mailboxSection === id ? t.text : t.dim, borderBottom: mailboxSection === id ? `2px solid ${myColor}` : '2px solid transparent', marginRight: '20px' });
                return (
                  <div>
                    <div style={{ display: 'flex', borderBottom: `1px solid ${t.borderDim}`, marginBottom: '16px' }}>
                      <button style={msgBtnStyle2('inbox')} onClick={() => setMailboxSection('inbox')}>INBOX {inbox.length > 0 && `· ${inbox.length}`}</button>
                      <button style={msgBtnStyle2('sent')} onClick={() => setMailboxSection('sent')}>SENT {sent.length > 0 && `· ${sent.length}`}</button>
                      <button style={msgBtnStyle2('drafts')} onClick={() => setMailboxSection('drafts')}>DRAFTS {programDrafts2.length > 0 && `· ${programDrafts2.length}`}</button>
                    </div>

                    {selectedMessage ? (
                      <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px' }}>
                        <button onClick={() => setSelectedMessage(null)} style={{ background: 'none', border: 'none', color: t.accent, cursor: 'pointer', fontSize: '11px', letterSpacing: '1px', padding: '0 0 16px', fontFamily: "'Roboto Mono', monospace" }}>← BACK</button>
                        <div style={{ fontSize: '10px', color: laneColorMap2[selectedMessage.from_lane] || t.accent, letterSpacing: '2px', fontWeight: '700', marginBottom: '8px' }}>{selectedMessage.from_lane?.toUpperCase()} · {selectedMessage.from_name}</div>
                        {selectedMessage.subject && <div style={{ fontSize: '14px', fontWeight: '600', color: t.text, marginBottom: '12px' }}>{selectedMessage.subject}</div>}
                        <div style={{ fontSize: '13px', color: t.muted, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selectedMessage.body}</div>
                        <button onClick={() => { setComposeTo(selectedMessage.from_user_id || selectedMessage.from_name); setComposeSubject(`Re: ${selectedMessage.subject || ''}`); setComposeOpen(true); setSelectedMessage(null); }}
                          style={{ marginTop: '16px', background: 'none', border: `1px solid ${myColor}`, borderRadius: '4px', padding: '7px 14px', cursor: 'pointer', fontSize: '9px', color: myColor, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px' }}>
                          REPLY →
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {mailboxSection === 'inbox' && (inbox.length === 0 ? (
                          <div style={{ fontSize: '12px', color: t.subLabel, textAlign: 'center', padding: '32px 0', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>No messages yet.</div>
                        ) : inbox.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(msg => {
                          const laneColor = laneColorMap2[msg.from_lane] || t.accent;
                          return (
                            <div key={msg.id} onClick={() => setSelectedMessage(msg)}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${!msg.read ? myColor + '50' : t.borderDim}`, borderRadius: '8px', cursor: 'pointer' }}>
                              {!msg.read && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: myColor, flexShrink: 0, marginTop: '5px' }} />}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px', gap: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '8px', color: laneColor, letterSpacing: '2px', fontWeight: '700', border: `1px solid ${laneColor}40`, borderRadius: '3px', padding: '1px 5px' }}>{(msg.from_lane || '').toUpperCase()}</span>
                                    <span style={{ fontSize: '11px', color: t.muted }}>{msg.from_name}</span>
                                  </div>
                                  <span style={{ fontSize: '9px', color: t.dim, flexShrink: 0 }}>{new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                </div>
                                <div style={{ fontSize: '12px', color: t.text, fontWeight: !msg.read ? '600' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.subject || msg.body?.slice(0, 60) || '(no subject)'}</div>
                              </div>
                            </div>
                          );
                        }))}

                        {mailboxSection === 'sent' && (sent.length === 0 ? (
                          <div style={{ fontSize: '12px', color: t.subLabel, textAlign: 'center', padding: '32px 0', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>No sent messages.</div>
                        ) : sent.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(msg => {
                          const laneColor = laneColorMap2[msg.to_lane] || t.accent;
                          return (
                            <div key={msg.id} onClick={() => setSelectedMessage(msg)}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', cursor: 'pointer', opacity: 0.85 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px', gap: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '9px', color: t.dim }}>TO</span>
                                    <span style={{ fontSize: '8px', color: laneColor, letterSpacing: '2px', fontWeight: '700', border: `1px solid ${laneColor}40`, borderRadius: '3px', padding: '1px 5px' }}>{(msg.to_lane || '').toUpperCase()}</span>
                                    <span style={{ fontSize: '11px', color: t.muted }}>{msg.to_name}</span>
                                  </div>
                                  <span style={{ fontSize: '9px', color: t.dim, flexShrink: 0 }}>{new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                </div>
                                <div style={{ fontSize: '12px', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.subject || msg.body?.slice(0, 60) || '(no subject)'}</div>
                              </div>
                            </div>
                          );
                        }))}

                        {mailboxSection === 'drafts' && (programDrafts2.length === 0 ? (
                          <div style={{ fontSize: '12px', color: t.subLabel, textAlign: 'center', padding: '32px 0', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>No drafts saved.</div>
                        ) : programDrafts2.map(draft => (
                          <div key={draft.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '1px 5px' }}>DRAFT</span>
                                <span style={{ fontSize: '9px', color: t.dim }}>{new Date(draft.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                              </div>
                              <div style={{ fontSize: '12px', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.subject || draft.body?.slice(0, 60) || '(no subject)'}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                              <button onClick={() => openDraft(draft)} style={{ background: 'none', border: `1px solid ${myColor}50`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '9px', color: myColor, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>EDIT</button>
                              <button onClick={() => deleteDraft(draft.id)} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>DELETE</button>
                            </div>
                          </div>
                        )))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (() => {
          const allGates = selectedProgram.gates || [];
          const allLanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const allGateNums = [1, 2, 3, 4, 5, 6];
          const gateNames = ['Program Inception', 'Concept Review', 'Eng Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
          const blockedGates = allGates.filter(g => g.status === 'blocked');
          const atRiskGates = allGates.filter(g => g.status === 'at_risk');
          const completeGates = allGates.filter(g => g.status === 'complete');
          const overallPct = allGates.length ? Math.round(allGates.reduce((s, g) => s + (g.completion_percent || 0), 0) / allGates.length) : 0;

          // Health score — uses all blocked/at-risk gates + cascade alerts
          const blockedDeduct  = blockedGates.length * 12;
          const atRiskDeduct   = atRiskGates.length * 6;
          const hardDepsDeduct = hardAlerts.length * 5;
          const softDepsDeduct = softAlerts.length * 2;
          const timeDeduct     = (drDays < 30 && selectedProgram.current_gate < 4) ? 15
                               : (drDays < 60 && selectedProgram.current_gate < 3) ? 8 : 0;
          const gateBonus      = Math.min((selectedProgram.current_gate - 1) * 3, 15);
          let score = 100 - blockedDeduct - atRiskDeduct - hardDepsDeduct - softDepsDeduct - timeDeduct + gateBonus;
          score = Math.max(0, Math.min(100, score));
          const scoreColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
          const scoreLabel = score >= 75 ? 'HEALTHY' : score >= 50 ? 'AT RISK' : 'CRITICAL';

          const slipDays = consequences?.projected_slip_days || 0;
          const projectedLaunch = slipDays > 0
            ? new Date(new Date(selectedProgram.launch_date).getTime() + slipDays * 86400000).toISOString().split('T')[0]
            : selectedProgram.launch_date;
          const fm = consequences?.financial_model || {};

          const contextQs = [
            blockedGates.length > 0
              ? `How do I unblock ${blockedGates[0].lane.toUpperCase()} Gate ${blockedGates[0].gate_number}?`
              : "What's the biggest risk to our launch date?",
            slipDays > 14 ? `We're projected to slip ${slipDays} days — what can we recover?` : "Which lane is furthest behind?",
            "What should I escalate today?",
            "Who has the most open tasks?",
            "Are we on track for mass production?",
            "Summarize this program for an exec in 3 sentences.",
          ];

          return (
            <div style={{ maxWidth: '1100px' }}>

              {/* ── HERO ROW ── */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>

                {/* Health score */}
                <div style={{ backgroundColor: t.surface, border: `1px solid ${scoreColor}30`, borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '24px 28px', display: 'flex', alignItems: 'center', gap: '20px', cursor: 'pointer' }} onClick={() => setScoreBreakdownOpen(o => !o)}>
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>NOMOS SCORE</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', justifyContent: 'center' }}>
                        <div style={{ fontSize: isMobile ? '44px' : '54px', fontWeight: '900', color: scoreColor, lineHeight: 1, fontFamily: "'Roboto Mono', monospace" }}>{score}</div>
                        <div style={{ fontSize: '14px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>/100</div>
                      </div>
                      <div style={{ fontSize: '9px', color: scoreColor, letterSpacing: '3px', fontWeight: '700', marginTop: '6px', backgroundColor: `${scoreColor}18`, padding: '3px 8px', borderRadius: '3px' }}>{scoreLabel}</div>
                      <div style={{ fontSize: '8px', color: t.subLabel, marginTop: '8px', letterSpacing: '0.5px' }}>{scoreBreakdownOpen ? '▲ hide math' : '▼ show math'}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '10px' }}>PROGRAM HEALTH</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {[
                          { label: 'BLOCKED', val: blockedGates.length, color: '#ef4444', tab: 'gates' },
                          { label: 'HARD DEPS', val: hardAlerts.length, color: '#ef4444', tab: 'consequences' },
                          { label: 'AT RISK', val: softAlerts.length, color: '#f59e0b', tab: 'consequences' },
                          { label: 'COMPLETE', val: completeGates.length, color: '#10b981', tab: 'gates' },
                        ].map(({ label, val, color, tab }) => (
                          <div key={label}
                            onClick={() => val > 0 && (setActiveTab(tab), setScoreBreakdownOpen(false))}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: val > 0 ? 'pointer' : 'default', borderRadius: '4px', padding: '2px 4px', margin: '0 -4px', transition: 'background 0.15s' }}
                            onMouseEnter={e => { if (val > 0) e.currentTarget.style.backgroundColor = darkMode ? '#ffffff08' : '#00000006'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: val > 0 ? color : t.borderDim, flexShrink: 0 }} />
                            <span style={{ fontSize: '9px', color: val > 0 ? color : t.subLabel, letterSpacing: '1.5px', width: '64px' }}>{label}</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: val > 0 ? color : t.subLabel }}>{val}</span>
                            {val > 0 && <span style={{ fontSize: '7px', color: t.subLabel, marginLeft: '4px' }}>→</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Score breakdown math */}
                  {scoreBreakdownOpen && (
                    <div style={{ borderTop: `1px solid ${t.borderDim}`, padding: '16px 28px', backgroundColor: darkMode ? '#0d0d0d' : '#f8f9fa' }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '12px' }}>HOW THIS SCORE IS CALCULATED</div>
                      <div style={{ fontFamily: "'Roboto Mono', monospace", display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[
                          { label: 'Starting score', val: '+100', color: t.text },
                          ...(blockedDeduct > 0 ? [{ label: `${blockedGates.length} blocked gate${blockedGates.length !== 1 ? 's' : ''} × 12 pts`, val: `−${blockedDeduct}`, color: '#ef4444', tab: 'gates' }] : []),
                          ...(atRiskDeduct > 0 ? [{ label: `${atRiskGates.length} at-risk gate${atRiskGates.length !== 1 ? 's' : ''} × 6 pts`, val: `−${atRiskDeduct}`, color: '#f59e0b', tab: 'gates' }] : []),
                          ...(hardDepsDeduct > 0 ? [{ label: `${hardAlerts.length} hard dependency violation${hardAlerts.length !== 1 ? 's' : ''} × 5 pts`, val: `−${hardDepsDeduct}`, color: '#ef4444', tab: 'consequences' }] : []),
                          ...(softDepsDeduct > 0 ? [{ label: `${softAlerts.length} at-risk dependency warning${softAlerts.length !== 1 ? 's' : ''} × 2 pts`, val: `−${softDepsDeduct}`, color: '#f59e0b', tab: 'consequences' }] : []),
                          ...(timeDeduct > 0 ? [{ label: `Timeline pressure (${drDays}d to launch at gate ${selectedProgram.current_gate})`, val: `−${timeDeduct}`, color: '#ef4444', tab: 'timeline' }] : []),
                          ...(gateBonus > 0 ? [{ label: `Gate progress bonus (gate ${selectedProgram.current_gate} of 6)`, val: `+${gateBonus}`, color: '#10b981', tab: 'timeline' }] : []),
                        ].map(({ label, val, color, tab }, i, arr) => (
                          <div key={i}
                            onClick={() => tab && (setActiveTab(tab), setScoreBreakdownOpen(false))}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: i === arr.length - 1 ? '10px' : '0', borderBottom: i === arr.length - 1 ? `1px solid ${t.borderDim}` : 'none', cursor: tab ? 'pointer' : 'default', borderRadius: '4px', padding: '4px 6px', margin: '0 -6px', transition: 'background 0.15s' }}
                            onMouseEnter={e => { if (tab) e.currentTarget.style.backgroundColor = darkMode ? '#ffffff08' : '#00000006'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                            <span style={{ fontSize: '10px', color: t.muted }}>{label}{tab && <span style={{ marginLeft: '6px', fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>→ {tab.toUpperCase()}</span>}</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color }}>{val}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: t.text, letterSpacing: '1px' }}>NOMOS SCORE</span>
                          <span style={{ fontSize: '16px', fontWeight: '900', color: scoreColor }}>{score}/100</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Key metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {[
                    { label: 'T-MINUS', value: drDays > 0 ? `${drDays}d` : 'PAST DUE', color: drDays < 60 ? '#ef4444' : t.accent },
                    { label: 'CURRENT GATE', value: `G${selectedProgram.current_gate || 1} — ${gateNames[(selectedProgram.current_gate || 1) - 1]?.split(' ')[0]}`, color: t.accent },
                    { label: 'OVERALL PROGRESS', value: `${overallPct}%`, color: t.text },
                    { label: slipDays > 0 ? 'SLIP' : 'ON SCHEDULE', value: slipDays > 0 ? `+${slipDays}d` : '✓', color: slipDays > 0 ? '#ef4444' : '#10b981' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '14px 16px' }}>
                      <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>{label}</div>
                      <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: '800', color, fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── GATE MAP + SITUATION ── */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap: '16px', marginBottom: '24px' }}>

                {/* Gate map */}
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px', overflowX: 'auto' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px' }}>GATE MAP — ALL LANES</div>
                  <div style={{ display: 'grid', gridTemplateColumns: `80px repeat(6, 1fr)`, gap: '4px', minWidth: '320px' }}>
                    <div />
                    {allGateNums.map((g, i) => (
                      <div key={g} style={{ textAlign: 'center', fontSize: '8px', color: g === selectedProgram.current_gate ? t.accent : t.subLabel, letterSpacing: '1px', fontWeight: g === selectedProgram.current_gate ? '700' : 'normal', paddingBottom: '4px' }}>
                        G{g}
                      </div>
                    ))}
                    {allLanes.map(lane => (
                      <Fragment key={lane}>
                        <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '1px', display: 'flex', alignItems: 'center', paddingRight: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lane.slice(0, 5).toUpperCase()}
                        </div>
                        {allGateNums.map(g => {
                          const gs = allGates.find(x => x.lane === lane && x.gate_number === g);
                          const st = gs?.status || 'idle';
                          const color = st === 'blocked' ? '#ef4444' : st === 'at_risk' ? '#f59e0b' : st === 'complete' ? '#10b981' : st === 'on_track' ? '#3b82f6' : t.borderDim;
                          const isActive = g === selectedProgram.current_gate;
                          return (
                            <div
                              key={g}
                              onClick={() => { if (gs) { openEdit(gs); } }}
                              title={`${lane} G${g} — ${st.replace('_', ' ')}`}
                              style={{ borderRadius: '4px', height: '28px', backgroundColor: color + (st === 'idle' ? '15' : '25'), border: `${isActive ? 2 : 1}px solid ${isActive ? color : color + '60'}`, cursor: gs ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {st === 'complete' && <span style={{ fontSize: '8px', color }}>✓</span>}
                              {st === 'blocked' && <span style={{ fontSize: '7px', color, fontWeight: '700' }}>!</span>}
                              {st === 'at_risk' && <span style={{ fontSize: '7px', color, fontWeight: '700' }}>~</span>}
                              {st === 'on_track' && <span style={{ fontSize: '7px', color }}>{gs?.completion_percent || 0}%</span>}
                            </div>
                          );
                        })}
                      </Fragment>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                    {[['#10b981', 'COMPLETE'], ['#3b82f6', 'ON TRACK'], ['#f59e0b', 'AT RISK'], ['#ef4444', 'BLOCKED']].map(([c, l]) => (
                      <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: c + '40', border: `1px solid ${c}` }} />
                        <span style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Situation */}
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px' }}>SITUATION</div>
                  {blockedGates.length === 0 && atRiskGates.length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                      <span style={{ fontSize: '11px', color: '#10b981' }}>All lanes on track</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {blockedGates.slice(0, 5).map(g => (
                        <div key={g.id} onClick={() => openEdit(g)} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0, marginTop: '4px' }} />
                          <div>
                            <div style={{ fontSize: '9px', color: '#ef4444', letterSpacing: '1.5px', fontWeight: '700' }}>{g.lane.toUpperCase()} G{g.gate_number}</div>
                            <div style={{ fontSize: '10px', color: t.cascadeText, lineHeight: 1.4 }}>{g.notes?.replace('[NOMOS]', '').trim() || 'Blocked — tap to open'}</div>
                          </div>
                        </div>
                      ))}
                      {atRiskGates.slice(0, 3).map(g => (
                        <div key={g.id} onClick={() => openEdit(g)} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', backgroundColor: '#f59e0b10', border: '1px solid #f59e0b30' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0, marginTop: '4px' }} />
                          <div>
                            <div style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '1.5px', fontWeight: '700' }}>{g.lane.toUpperCase()} G{g.gate_number}</div>
                            <div style={{ fontSize: '10px', color: t.cascadeText, lineHeight: 1.4 }}>{g.notes?.replace('[NOMOS]', '').trim() || 'At risk — tap to open'}</div>
                          </div>
                        </div>
                      ))}
                      {(blockedGates.length > 5 || atRiskGates.length > 3) && (
                        <button onClick={() => setActiveTab('gates')} style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', padding: '6px 12px', color: t.dim, fontSize: '9px', letterSpacing: '1.5px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          +{(blockedGates.length - 5) + (atRiskGates.length - 3)} MORE → VIEW GATES
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── TEAM READINESS ── */}
              <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '24px' }}>
                <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '16px' }}>TEAM READINESS — GATE {selectedProgram.current_gate}</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '12px' }}>
                  {allLanes.map(lane => {
                    const laneGate = allGates.find(g => g.lane === lane && g.gate_number === selectedProgram.current_gate);
                    const st = laneGate?.status || 'idle';
                    const pct = laneGate?.completion_percent || 0;
                    const color = st === 'blocked' ? '#ef4444' : st === 'at_risk' ? '#f59e0b' : st === 'complete' ? '#10b981' : st === 'on_track' ? '#3b82f6' : t.borderDim;
                    const laneColors = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
                    const lead = teamLeads[lane];
                    return (
                      <div key={lane} onClick={() => openEdit(laneGate)} style={{ cursor: laneGate ? 'pointer' : 'default', padding: '12px 14px', backgroundColor: t.bg, border: `1px solid ${color}40`, borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '8px', color: laneColors[lane], letterSpacing: '1.5px', fontWeight: '700' }}>{lane.toUpperCase()}</span>
                          {st !== 'idle' && (
                            <span style={{ fontSize: '7px', color, letterSpacing: '1px', fontWeight: '700' }}>
                              {st === 'complete' ? '✓ DONE' : st === 'blocked' ? 'BLOCKED' : st === 'at_risk' ? 'AT RISK' : `${pct}%`}
                            </span>
                          )}
                        </div>
                        <div style={{ height: '3px', backgroundColor: t.borderDim, borderRadius: '2px', marginBottom: '8px' }}>
                          <div style={{ height: '3px', width: `${pct}%`, backgroundColor: color, borderRadius: '2px', transition: 'width 0.4s ease' }} />
                        </div>
                        <div style={{ fontSize: '9px', color: t.subLabel, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead || 'No lead set'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── FINANCIAL + TIMELINE ── */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>

                {/* Financial exposure */}
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '16px' }}>FINANCIAL EXPOSURE</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { label: 'BUDGET', value: `$${((selectedProgram.budget_total || 0) / 1000000).toFixed(1)}M`, color: t.text },
                      { label: 'COST IMPACT', value: fm.stranded_asset_cost ? `$${(fm.stranded_asset_cost / 1000).toFixed(0)}K` : '—', color: fm.stranded_asset_cost > 0 ? '#ef4444' : t.subLabel },
                      { label: 'REVENUE AT RISK', value: fm.revenue_at_risk ? `$${(fm.revenue_at_risk / 1000000).toFixed(1)}M` : '—', color: fm.revenue_at_risk > 0 ? '#f59e0b' : t.subLabel },
                      { label: 'RETROFIT COST', value: fm.retrofit_cost ? `$${(fm.retrofit_cost / 1000).toFixed(0)}K` : '—', color: fm.retrofit_cost > 0 ? '#f59e0b' : t.subLabel },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `1px solid ${t.borderDim}`, paddingBottom: '8px' }}>
                        <span style={{ fontSize: '9px', color: t.dim, letterSpacing: '1.5px' }}>{label}</span>
                        <span style={{ fontSize: '15px', fontWeight: '700', color, fontFamily: "'Roboto Mono', monospace" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '16px' }}>TIMELINE</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { label: 'PLANNED LAUNCH', value: selectedProgram.launch_date || '—', color: t.text },
                      { label: 'PROJECTED LAUNCH', value: projectedLaunch || '—', color: slipDays > 0 ? '#ef4444' : '#10b981' },
                      { label: 'SLIP', value: slipDays > 0 ? `+${slipDays} days` : 'On schedule', color: slipDays > 0 ? '#ef4444' : '#10b981' },
                      { label: 'ACTIVE CASCADES', value: hardAlerts.length, color: hardAlerts.length > 0 ? '#8b5cf6' : t.subLabel },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `1px solid ${t.borderDim}`, paddingBottom: '8px' }}>
                        <span style={{ fontSize: '9px', color: t.dim, letterSpacing: '1.5px' }}>{label}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color, fontFamily: "'Roboto Mono', monospace" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── RECENT ACTIVITY ── */}
              {notifications.filter(n => n.program_id === selectedProgram.id).length > 0 && (
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>RECENT ACTIVITY</div>
                    <button onClick={() => setActiveTab('activity')} style={{ background: 'none', border: 'none', fontSize: '9px', color: t.accent, cursor: 'pointer', letterSpacing: '1px', fontFamily: 'inherit' }}>VIEW ALL →</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {notifications.filter(n => n.program_id === selectedProgram.id).slice(0, 5).map(n => {
                      const ec = { blocked: '#ef4444', complete: '#10b981', at_risk: '#f59e0b', cascade: '#8b5cf6' }[n.event_type?.toLowerCase()] || t.dim;
                      const dt = new Date(n.created_at);
                      const diff = Date.now() - dt.getTime();
                      const mins = Math.floor(diff / 60000);
                      const timeAgo = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
                      return (
                        <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: t.bg, borderRadius: '6px', border: `1px solid ${t.borderDim}` }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: ec, flexShrink: 0 }} />
                          <span style={{ fontSize: '8px', color: ec, letterSpacing: '1.5px', fontWeight: '700', width: '60px', flexShrink: 0 }}>{(n.event_type || '').toUpperCase()}</span>
                          <span style={{ fontSize: '11px', color: t.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</span>
                          <span style={{ fontSize: '9px', color: t.subLabel, flexShrink: 0 }}>{timeAgo}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── ASK NOMOS — INTEGRATED ── */}
              <div style={{ backgroundColor: darkMode ? '#0d0f1a' : '#f0f4ff', border: `1px solid ${t.accent}30`, borderRadius: '10px', padding: '20px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ width: '28px', height: '28px', backgroundColor: t.accent, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>N</div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: t.accent, letterSpacing: '2px' }}>ASK NOMOS</div>
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '1px' }}>AI-powered answers about this program</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  {contextQs.map(q => (
                    <button key={q} onClick={() => { askNomos(q); setQuestion(q); }} disabled={aiLoading}
                      style={{ background: 'none', border: `1px solid ${t.accent}40`, borderRadius: '20px', padding: '5px 12px', fontSize: '10px', color: t.accent, cursor: aiLoading ? 'default' : 'pointer', fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.3px', opacity: aiLoading ? 0.5 : 1 }}>
                      {q}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    style={{ flex: 1, background: t.askInputBg, border: `1px solid ${t.accent}40`, color: t.text, padding: '10px 14px', fontSize: '12px', fontFamily: "'Roboto Mono', monospace", outline: 'none', borderRadius: '4px' }}
                    placeholder="Ask anything about this launch…"
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && askNomos()}
                  />
                  <button
                    style={{ background: aiLoading ? t.askBtnDisBg : t.accent, color: '#000', border: 'none', padding: '10px 20px', fontSize: '11px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace", cursor: aiLoading ? 'default' : 'pointer', letterSpacing: '1px', borderRadius: '4px' }}
                    onClick={() => askNomos()} disabled={aiLoading}>
                    {aiLoading ? '...' : 'ASK'}
                  </button>
                </div>
                {aiLoading && <div style={{ color: t.subLabel, fontSize: '11px', letterSpacing: '1px', marginTop: '10px' }}>Nomos is thinking...</div>}
                {aiAnswer && (
                  <div style={{ ...s.aiBox, marginTop: '14px', border: `1px solid ${t.accent}30` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={s.aiLabel}>NOMOS</div>
                      <button onClick={() => { setAiAnswer(''); setQuestion(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.subLabel, fontSize: '11px', fontFamily: "'Roboto Mono', monospace" }}>clear</button>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: '13px' }}>{aiAnswer.replace(/\*\*/g, '')}</div>
                  </div>
                )}
              </div>

            </div>
          );
        })()}

        {/* ── BRIEF TAB ── */}
        {activeTab === 'brief' && (
          <div style={{ maxWidth: '1100px' }}>

            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ color: t.text, fontSize: '14px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '4px' }}>LAUNCH BRIEF</div>
                <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '2px' }}>THE FOUNDING DOCUMENT — WHAT WE ARE BUILDING AND WHY</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {userRole === 'admin' && !briefEditMode && (
                  <>
                    <button style={{ ...s.ghostBtn, opacity: briefDraftLoading ? 0.5 : 1, whiteSpace: 'nowrap' }} onClick={draftBrief} disabled={briefDraftLoading}>
                      {briefDraftLoading ? 'DRAFTING...' : 'AI DRAFT'}
                    </button>
                    {/* Share brief toggle */}
                    {selectedProgram.sharing_enabled ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${t.border}`, borderRadius: '6px', padding: '4px 8px' }}>
                        <input
                          readOnly
                          value={shareUrl || `https://app.nomoslaunch.com/?share=${selectedProgram.share_token}`}
                          style={{ background: 'none', border: 'none', color: t.accent, fontSize: '9px', fontFamily: "'Roboto Mono', monospace", width: '200px', outline: 'none', letterSpacing: '0.5px' }}
                          onClick={e => e.target.select()}
                        />
                        <button onClick={() => { navigator.clipboard.writeText(shareUrl || `https://app.nomoslaunch.com/?share=${selectedProgram.share_token}`); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '12px', padding: '2px' }} title="Copy">⎘</button>
                        <button
                          onClick={async () => {
                            setShareLoading(true);
                            try {
                              await authFetch(`${API}/programs/${selectedProgram.id}/unshare`, { method: 'POST' });
                              setSelectedProgram(prev => ({ ...prev, sharing_enabled: false }));
                              setShareUrl(null);
                            } finally { setShareLoading(false); }
                          }}
                          disabled={shareLoading}
                          style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '3px', cursor: 'pointer', color: t.dim, fontSize: '9px', fontFamily: "'Roboto Mono', monospace", padding: '3px 8px', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                          DISABLE
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={async () => {
                          setShareLoading(true);
                          try {
                            const r = await authFetch(`${API}/programs/${selectedProgram.id}/share`, { method: 'POST' });
                            const d = await r.json();
                            setShareUrl(d.shareUrl);
                            setSelectedProgram(prev => ({ ...prev, sharing_enabled: true, share_token: d.shareUrl.split('?share=')[1] }));
                          } finally { setShareLoading(false); }
                        }}
                        disabled={shareLoading}
                        style={{ padding: '7px 14px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.borderDim}`, borderRadius: '4px', background: 'none', color: t.dim, cursor: shareLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: shareLoading ? 0.5 : 1 }}>
                        {shareLoading ? 'SHARING...' : 'SHARE BRIEF →'}
                      </button>
                    )}
                    <button onClick={() => { setShareGroupOpen(o => !o); setShareGroupResult(null); }} style={{ padding: '7px 14px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.borderDim}`, borderRadius: '4px', background: 'none', color: t.dim, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      SEND TO GROUP
                    </button>
                    <button onClick={() => setBriefEditMode(true)} style={{ padding: '7px 16px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.accent}`, borderRadius: '4px', background: t.accent, color: '#000', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      EDIT BRIEF
                    </button>
                  </>
                )}
                {userRole === 'admin' && briefEditMode && (
                  <button onClick={() => setBriefEditMode(false)} style={{ padding: '7px 16px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.accent}`, borderRadius: '4px', background: 'none', color: t.accent, cursor: 'pointer' }}>
                    ✓ DONE EDITING
                  </button>
                )}
                {userRole !== 'admin' && (
                  <button onClick={() => { setBriefRequestOpen(true); setBriefRequestText(''); }} style={{ padding: '7px 16px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.borderDim}`, borderRadius: '4px', background: 'none', color: t.dim, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    REQUEST EDIT
                  </button>
                )}
              </div>
            </div>

            {/* Group share panel */}
            {shareGroupOpen && userRole === 'admin' && (
              <div style={{ background: t.surfaceAlt, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '16px 20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>SEND BRIEF TO GROUP</div>
                <ShareGroupInput
                  teamMembers={teamMembers}
                  selected={shareGroupEmails}
                  onChange={setShareGroupEmails}
                  darkMode={darkMode}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    disabled={shareGroupSending || shareGroupEmails.length === 0}
                    onClick={async () => {
                      setShareGroupSending(true);
                      setShareGroupResult(null);
                      try {
                        const r = await authFetch(`${API}/programs/${selectedProgram.id}/share-group`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: shareGroupEmails }) });
                        const d = await r.json();
                        if (d.ok) {
                          setShareGroupResult({ success: true, count: d.sent });
                          setShareGroupEmails([]);
                          if (d.shareUrl) { setShareUrl(d.shareUrl); setSelectedProgram(prev => ({ ...prev, sharing_enabled: true, share_token: d.shareUrl.split('?share=')[1] })); }
                        } else {
                          setShareGroupResult({ success: false, msg: d.error });
                        }
                      } catch { setShareGroupResult({ success: false, msg: 'Failed to send' }); }
                      finally { setShareGroupSending(false); }
                    }}
                    style={{ padding: '7px 16px', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '700', border: `1px solid ${t.accent}`, borderRadius: '4px', background: shareGroupSending ? 'none' : t.accent, color: shareGroupSending ? t.accent : '#000', cursor: (shareGroupSending || shareGroupEmails.length === 0) ? 'not-allowed' : 'pointer', opacity: (shareGroupEmails.length === 0 || shareGroupSending) ? 0.5 : 1 }}>
                    {shareGroupSending ? 'SENDING...' : `SEND →${shareGroupEmails.length > 0 ? ` (${shareGroupEmails.length})` : ''}`}
                  </button>
                  {shareGroupResult && (
                    <span style={{ fontSize: '11px', fontFamily: "'Roboto Mono', monospace", color: shareGroupResult.success ? '#10b981' : '#ef4444' }}>
                      {shareGroupResult.success ? `✓ Sent to ${shareGroupResult.count} recipient${shareGroupResult.count !== 1 ? 's' : ''}` : shareGroupResult.msg}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Snapshot bar — editable + lockable (edit mode only) */}
            {briefEditMode && (() => {
              const GEO_MARKETS =['USA', 'Canada', 'LATAM', 'EMEA', 'UK', 'India', 'China', 'Japan', 'SEA', 'ANZ', 'KR', 'MEA', 'GCC', 'Brazil', 'Mexico'];
              const selectedMarkets = (selectedProgram.markets || '').split(',').map(m => m.trim()).filter(Boolean);
              const LockBtn = ({ field }) => (
                <button
                  title={lockedFields[field] ? 'Click to unlock' : 'Click to lock'}
                  onClick={e => { e.stopPropagation(); toggleLock(field); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '10px', color: lockedFields[field] ? t.accent : t.borderDim, lineHeight: 1 }}>
                  {lockedFields[field] ? '▣' : '▢'}
                </button>
              );
              return (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>

                  {/* MARKETS */}
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${marketsOpen ? t.accent : t.borderDim}`, borderRadius: '6px', padding: '12px', cursor: lockedFields.markets ? 'default' : 'pointer', minHeight: '60px' }}
                      onClick={() => !lockedFields.markets && setMarketsOpen(o => !o)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ color: t.dim, fontSize: '9px', letterSpacing: '2px' }}>MARKETS</div>
                        <LockBtn field="markets" />
                      </div>
                      {selectedMarkets.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {selectedMarkets.map(m => (
                            <span key={m} style={{ backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, borderRadius: '3px', padding: '2px 6px', fontSize: '9px', color: t.accent, fontFamily: "'Roboto Mono', monospace", fontWeight: 'bold', letterSpacing: '0.5px' }}>{m}</span>
                          ))}
                          {!lockedFields.markets && <span style={{ fontSize: '10px', color: t.subLabel }}>+</span>}
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: t.subLabel }}>{lockedFields.markets ? '—' : 'Click to add markets'}</div>
                      )}
                    </div>

                    {marketsOpen && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setMarketsOpen(false)} />
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '10px', zIndex: 200, boxShadow: '0 8px 24px #00000030', minWidth: '200px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                            {GEO_MARKETS.map(m => {
                              const active = selectedMarkets.includes(m);
                              return (
                                <button key={m}
                                  onClick={() => toggleMarket(m)}
                                  style={{ background: active ? t.accent + '20' : 'none', border: `1px solid ${active ? t.accent : t.borderDim}`, borderRadius: '4px', padding: '4px 8px', fontSize: '10px', color: active ? t.accent : t.muted, cursor: 'pointer', fontFamily: "'Roboto Mono', monospace", fontWeight: active ? 'bold' : 'normal', letterSpacing: '0.5px' }}>
                                  {active ? '✓ ' : ''}{m}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', borderTop: `1px solid ${t.borderDimmer}`, paddingTop: '8px' }}>
                            <input
                              style={{ ...s.input, flex: 1, fontSize: '11px', padding: '5px 8px' }}
                              placeholder="Add custom market..."
                              value={customMarket}
                              onChange={e => setCustomMarket(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { addCustomMarket(); } e.stopPropagation(); }}
                              onClick={e => e.stopPropagation()}
                            />
                            <button style={{ ...s.ghostBtn, padding: '5px 10px', fontSize: '10px' }} onClick={e => { e.stopPropagation(); addCustomMarket(); }}>ADD</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* LOCATIONS */}
                  <div
                    style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${briefInlineEdit === 'locations' ? t.accent : t.borderDim}`, borderRadius: '6px', padding: '12px', cursor: lockedFields.locations ? 'default' : 'pointer', minHeight: '60px' }}
                    onClick={() => { if (!lockedFields.locations && briefInlineEdit !== 'locations') { setBriefInlineEdit('locations'); setBriefInlineVal(String(selectedProgram.num_locations || 0)); } }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div style={{ color: t.dim, fontSize: '9px', letterSpacing: '2px' }}>LOCATIONS</div>
                      <LockBtn field="locations" />
                    </div>
                    {briefInlineEdit === 'locations' ? (
                      <input
                        type="number"
                        autoFocus
                        style={{ ...s.input, fontSize: '14px', fontWeight: 'bold', padding: '2px 4px', margin: 0 }}
                        value={briefInlineVal}
                        onChange={e => setBriefInlineVal(e.target.value)}
                        onBlur={() => { patchProgram({ num_locations: parseInt(briefInlineVal) || 0 }); setBriefInlineEdit(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <div style={{ color: t.text, fontSize: '14px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>
                        {(selectedProgram.num_locations || 0).toLocaleString()}
                        {!lockedFields.locations && <span style={{ fontSize: '9px', color: t.subLabel, marginLeft: '6px' }}>edit</span>}
                      </div>
                    )}
                  </div>

                  {/* LAUNCH DATE */}
                  <div
                    style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${briefInlineEdit === 'launch' ? t.accent : t.borderDim}`, borderRadius: '6px', padding: '12px', cursor: lockedFields.launch ? 'default' : 'pointer', minHeight: '60px' }}
                    onClick={() => { if (!lockedFields.launch && briefInlineEdit !== 'launch') { setBriefInlineEdit('launch'); setBriefInlineVal(selectedProgram.launch_date || ''); } }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div style={{ color: t.dim, fontSize: '9px', letterSpacing: '2px' }}>LAUNCH</div>
                      <LockBtn field="launch" />
                    </div>
                    {briefInlineEdit === 'launch' ? (
                      <input
                        type="date"
                        autoFocus
                        style={{ ...s.input, fontSize: '13px', fontWeight: 'bold', padding: '2px 4px', margin: 0 }}
                        value={briefInlineVal}
                        onChange={e => setBriefInlineVal(e.target.value)}
                        onBlur={() => { if (briefInlineVal) patchProgram({ launch_date: briefInlineVal }); setBriefInlineEdit(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <div style={{ color: t.text, fontSize: '14px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>
                        {selectedProgram.launch_date || '—'}
                        {!lockedFields.launch && <span style={{ fontSize: '9px', color: t.subLabel, marginLeft: '6px' }}>edit</span>}
                      </div>
                    )}
                  </div>

                  {/* BUDGET */}
                  <div style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '12px', minHeight: '60px' }}>
                    <div style={{ color: t.dim, fontSize: '9px', letterSpacing: '2px', marginBottom: '6px' }}>BUDGET</div>
                    <div style={{ color: t.text, fontSize: '14px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>
                      ${((selectedProgram.budget_total || 0) / 1000000).toFixed(1)}M
                    </div>
                    <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '2px' }}>Edit in Settings</div>
                  </div>

                </div>
              );
            })()}

            {/* Brief — memo view (default) or edit sections */}
            <div style={{ width: '100%' }}>

                {!briefEditMode ? (
                  /* ── LIVING DOCUMENT VIEW ── */
                  (() => {
                    const BRIEF_GATE_COLORS = ['#6366f1', '#8b5cf6', '#0d9488', '#f59e0b', '#ef4444', '#10b981'];
                    const BRIEF_GATE_NAMES = ['INCEPTION', 'CONCEPT', 'ENG. REVIEW', 'PROTO 1', 'PROTO 2', 'MASS PROD'];
                    const allGates = selectedProgram.gates || [];
                    // Build per-gate timeline from gate_statuses (one entry per gate number, using the first lane's planned_close_date)
                    const timelineGates = [1,2,3,4,5,6].map(n => {
                      const g = allGates.find(gs => gs.gate_number === n);
                      return { gate: n, date: g?.planned_close_date || null };
                    });
                    const hasTimeline = timelineGates.some(g => g.date);
                    const gate1Briefings = allGates.filter(g => g.gate_number === 1 && g.notes && g.notes.trim() && !g.notes.startsWith('[NOMOS]'));
                    const hasContent = briefForm.what_is_it || briefForm.why_it_matters || briefForm.end_vision || gate1Briefings.length > 0;

                    const printBriefDoc = () => {
                      const gc = ['#6366f1','#8b5cf6','#0d9488','#f59e0b','#ef4444','#10b981'];
                      const gn = ['INCEPTION','CONCEPT','ENG. REVIEW','PROTO 1','PROTO 2','MASS PROD'];
                      const timelineHtml = hasTimeline ? timelineGates.map((g, i) => `
                        <div style="display:flex;flex-direction:column;align-items:center;width:16.6%;text-align:center;">
                          <div style="width:12px;height:12px;background:${gc[i]};transform:rotate(45deg);flex-shrink:0;"></div>
                          <div style="margin-top:10px;font-size:7px;letter-spacing:1px;color:${gc[i]};font-family:'Courier New',monospace;font-weight:700;">${gn[i]}</div>
                          <div style="font-size:9px;color:#64748b;font-family:'Courier New',monospace;margin-top:2px;">${g.date ? new Date(g.date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : ''}</div>
                        </div>`).join('') : '';
                      const briefingsHtml = gate1Briefings.map(g => `
                        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:14px 16px;break-inside:avoid;">
                          <div style="font-size:8px;color:#0d9488;letter-spacing:2px;font-family:'Courier New',monospace;font-weight:700;margin-bottom:8px;">${g.lane.toUpperCase()}</div>
                          <div style="font-size:12px;color:#374151;line-height:1.7;">${g.notes}</div>
                        </div>`).join('');
                      const extraSections = [
                        {label:'WHY IT MATTERS',value:briefForm.why_it_matters},
                        {label:'END VISION',value:briefForm.end_vision},
                        {label:'WHO EXPERIENCES IT',value:briefForm.target_customer},
                        {label:'SUCCESS CRITERIA',value:briefForm.success_criteria},
                      ].filter(f => f.value).map(f => `
                        <div style="margin-bottom:28px;">
                          <div style="font-size:9px;letter-spacing:2px;color:#0d9488;font-weight:700;margin-bottom:8px;font-family:'Courier New',monospace;">${f.label}</div>
                          <div style="font-size:13px;color:#374151;line-height:1.85;white-space:pre-wrap;">${f.value}</div>
                        </div>`).join('');
                      const inner = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#1a1a1a;padding:48px;max-width:860px;margin:0 auto;">
                        <div style="font-family:'Courier New',monospace;font-size:13px;font-weight:700;letter-spacing:3px;color:#0d9488;margin-bottom:32px;">NOMOS</div>
                        <div style="font-size:9px;letter-spacing:3px;color:#0d9488;font-family:'Courier New',monospace;margin-bottom:10px;">YOUR LAUNCH PLAN — LIVE DOCUMENT</div>
                        <div style="font-size:32px;font-weight:800;letter-spacing:-0.5px;margin-bottom:6px;">${selectedProgram.name}</div>
                        <div style="font-size:11px;color:#94a3b8;margin-bottom:36px;font-family:'Courier New',monospace;">Generated ${new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
                        ${briefForm.what_is_it ? `<div style="font-size:14px;color:#374151;line-height:1.8;margin-bottom:40px;padding-bottom:40px;border-bottom:1px solid #e2e8f0;">${briefForm.what_is_it}</div>` : ''}
                        ${hasTimeline ? `<div style="margin-bottom:40px;"><div style="font-size:9px;letter-spacing:3px;font-weight:700;margin-bottom:24px;font-family:'Courier New',monospace;">PHASE GATE TIMELINE</div><div style="position:relative;padding-bottom:24px;"><div style="position:absolute;top:6px;left:0;right:0;height:2px;background:#e2e8f0;"></div><div style="display:flex;justify-content:space-between;position:relative;">${timelineHtml}</div></div></div>` : ''}
                        ${briefingsHtml ? `<div style="margin-bottom:40px;padding-bottom:40px;border-bottom:1px solid #e2e8f0;"><div style="font-size:9px;letter-spacing:3px;font-weight:700;margin-bottom:16px;font-family:'Courier New',monospace;">TEAM BRIEFINGS</div><div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px;">${briefingsHtml}</div></div>` : ''}
                        ${extraSections}
                        <div style="margin-top:48px;padding-top:20px;border-top:1px solid #e2e8f0;font-size:9px;color:#94a3b8;display:flex;justify-content:space-between;font-family:'Courier New',monospace;letter-spacing:1px;"><span>NOMOS · THE LAUNCH OPERATING SYSTEM</span><span>app.nomoslaunch.com</span></div>
                      </div>`;
                      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${selectedProgram.name} — Launch Brief</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}</style></head><body>${inner}</body></html>`;
                      const w = window.open('', '_blank');
                      if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => { w.print(); }, 400); }
                    };

                    return (
                      <div>
                        {/* Program name + summary */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace" }}>YOUR LAUNCH PLAN — LIVE DOCUMENT</div>
                          {hasContent && (
                            <button onClick={printBriefDoc} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '4px', cursor: 'pointer', fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: t.dim, fontWeight: '700' }}>
                              ⬇ EXPORT PDF
                            </button>
                          )}
                        </div>
                        <div style={{ fontSize: isMobile ? '26px' : '40px', fontWeight: '800', color: t.text, lineHeight: 1.2, marginBottom: '20px' }}>{selectedProgram.name}</div>

                        {briefForm.what_is_it ? (
                          <div style={{ fontSize: '15px', color: t.dim, lineHeight: 1.8, marginBottom: '40px', paddingBottom: '40px', borderBottom: `1px solid ${t.border}` }}>{briefForm.what_is_it}</div>
                        ) : (
                          <div style={{ fontSize: '13px', color: t.dim, fontStyle: 'italic', marginBottom: '40px', paddingBottom: '40px', borderBottom: `1px solid ${t.border}` }}>No executive summary yet — click AI DRAFT to generate one, or EDIT BRIEF to write it.</div>
                        )}

                        {/* Phase Gate Timeline */}
                        {hasTimeline && (
                          <div style={{ marginBottom: '48px' }}>
                            <div style={{ fontSize: '9px', color: t.text, letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '32px' }}>PHASE GATE TIMELINE</div>
                            {!isMobile ? (
                              <div style={{ position: 'relative', paddingBottom: '28px' }}>
                                <div style={{ position: 'absolute', top: '10px', left: '0', right: '0', height: '2px', backgroundColor: t.border }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                                  {timelineGates.map((g, i) => (
                                    <div key={g.gate} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${100/6}%` }}>
                                      <div style={{ width: '16px', height: '16px', backgroundColor: BRIEF_GATE_COLORS[i], borderRadius: '3px', transform: 'rotate(45deg)', zIndex: 1, flexShrink: 0, border: `3px solid ${t.bg}`, boxSizing: 'border-box' }} />
                                      <div style={{ marginTop: '12px', fontSize: '7px', letterSpacing: '1px', color: BRIEF_GATE_COLORS[i], fontFamily: "'Roboto Mono', monospace", fontWeight: '700', textAlign: 'center' }}>{BRIEF_GATE_NAMES[i]}</div>
                                      <div style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace", textAlign: 'center', marginTop: '3px' }}>{g.date ? new Date(g.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {timelineGates.map((g, i) => (
                                  <div key={g.gate} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: BRIEF_GATE_COLORS[i], borderRadius: '2px', transform: 'rotate(45deg)', flexShrink: 0 }} />
                                      {i < 5 && <div style={{ width: '1px', height: '36px', backgroundColor: t.border, marginTop: '6px' }} />}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '8px', color: BRIEF_GATE_COLORS[i], fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '2px' }}>{BRIEF_GATE_NAMES[i]}</div>
                                      <div style={{ fontSize: '10px', color: t.dim, fontFamily: "'Roboto Mono', monospace" }}>{g.date ? new Date(g.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Team Briefings */}
                        {gate1Briefings.length > 0 && (
                          <div style={{ marginBottom: '48px', paddingBottom: '48px', borderBottom: `1px solid ${t.border}` }}>
                            <div style={{ fontSize: '9px', color: t.text, letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '20px' }}>TEAM BRIEFINGS</div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
                              {gate1Briefings.map(g => (
                                <div key={g.id} style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '16px 18px' }}>
                                  <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '10px' }}>{g.lane.toUpperCase()}</div>
                                  <div style={{ fontSize: '13px', color: t.dim, lineHeight: 1.7 }}>{g.notes}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Why it matters / End vision / Target customer */}
                        {[
                          { key: 'why_it_matters',   label: 'WHY IT MATTERS' },
                          { key: 'end_vision',        label: 'END VISION' },
                          { key: 'target_customer',   label: 'WHO EXPERIENCES IT' },
                          { key: 'success_criteria',  label: 'SUCCESS CRITERIA' },
                        ].map(f => briefForm[f.key] ? (
                          <div key={f.key} style={{ marginBottom: '36px' }}>
                            <div style={{ fontSize: '9px', letterSpacing: '2px', color: t.accent, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>{f.label}</div>
                            <div style={{ fontSize: '14px', color: t.dim, lineHeight: 1.85, whiteSpace: 'pre-wrap' }}>{briefForm[f.key]}</div>
                          </div>
                        ) : null)}

                        {/* Product images */}
                        {programImages.length > 0 && (
                          <div style={{ paddingTop: '32px', borderTop: `1px solid ${t.border}` }}>
                            <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", marginBottom: '12px' }}>PRODUCT IMAGES</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                              {programImages.map(img => (
                                <img key={img.id} src={img.url} alt="Product" onError={e => { e.target.style.display = 'none'; }}
                                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${t.border}` }} />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Empty state */}
                        {!hasContent && (
                          <div style={{ padding: '48px 0', textAlign: 'center' }}>
                            <div style={{ fontSize: '13px', color: t.dim, marginBottom: '20px' }}>No launch plan yet.</div>
                            <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '12px 32px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }} onClick={draftBrief}>
                              {briefDraftLoading ? 'GENERATING...' : 'GENERATE WITH AI →'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  /* ── EDIT SECTIONS ── */
                  <>
                    {[
                      { key: 'what_is_it',       label: 'WHAT IS IT',         hint: 'The physical product — what it is, what it does, what it replaces. Be concrete.' },
                      { key: 'end_vision',       label: 'END VISION',         hint: 'What does a completed location look like? What does someone see when they walk in?' },
                      { key: 'why_it_matters',   label: 'WHY IT MATTERS',     hint: 'The business case — what does this program unlock? What problem does it solve?' },
                      { key: 'target_customer',  label: 'WHO EXPERIENCES IT', hint: 'The end customer or user and what their experience will be.' },
                      { key: 'success_criteria', label: 'SUCCESS CRITERIA',   hint: 'Specific, measurable — how will we know at launch that this program succeeded?' },
                    ].map(({ key, label, hint }) => (
                      <div key={key} style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', color: t.accent, letterSpacing: '2px', fontSize: '11px', fontWeight: '600' }}>{label}</label>
                        <div style={{ fontSize: '11px', color: t.subLabel, lineHeight: 1.4, marginBottom: '6px' }}>{hint}</div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                          <textarea
                            style={{ ...s.textarea, minHeight: '76px', lineHeight: '1.6', fontSize: '13px', flex: 1 }}
                            value={briefForm[key]}
                            onChange={e => setBriefForm(prev => ({ ...prev, [key]: e.target.value }))}
                            onBlur={e => { const updated = { ...briefForm, [key]: e.target.value }; saveBrief(updated); }}
                            placeholder={hint}
                          />
                          <MicBtn fieldId={`brief-${key}`} value={briefForm[key]} onSet={v => { const updated = { ...briefForm, [key]: v }; setBriefForm(updated); saveBrief(updated); }} {...micP} />
                        </div>
                      </div>
                    ))}

                    {/* Product images — edit mode */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '10px' }}>PRODUCT IMAGES</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
                        {programImages.map(img => (
                          <div key={img.id} style={{ position: 'relative', flexShrink: 0 }}>
                            <img src={img.url} alt="Product"
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: img.url === briefForm.product_image_url ? `2px solid ${t.accent}` : `1px solid ${t.borderDim}`, display: 'block', cursor: 'pointer' }}
                              onError={e => { e.target.style.display = 'none'; }}
                              onClick={() => setBriefForm(prev => ({ ...prev, product_image_url: img.url }))}
                              title="Click to set as featured image"
                            />
                            <button onClick={() => deleteImage(img.id)} style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ff6b6b', border: 'none', borderRadius: '50%', width: '18px', height: '18px', color: '#fff', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
                          </div>
                        ))}
                        {userRole === 'admin' && (
                          <label style={{ width: '80px', height: '80px', borderRadius: '6px', border: `2px dashed ${t.borderDim}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: imageUploading ? 'not-allowed' : 'pointer', flexShrink: 0, gap: '4px', backgroundColor: t.surfaceAlt || t.surface }}>
                            <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={imageUploading}
                              onChange={e => { Array.from(e.target.files).forEach(f => uploadProductImage(f)); e.target.value = ''; }} />
                            {imageUploading ? <span style={{ fontSize: '9px', color: t.subLabel, textAlign: 'center', letterSpacing: '1px' }}>UPLOADING...</span>
                              : <><span style={{ fontSize: '20px', color: t.subLabel, lineHeight: 1 }}>+</span><span style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>ADD PHOTO</span></>}
                          </label>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                      <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>Auto-saves on blur</div>
                      <div style={{ fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', color: briefSaving ? t.dim : briefSaved ? '#10b981' : 'transparent', transition: 'color 0.3s' }}>
                        {briefSaving ? 'SAVING...' : '✓ SAVED'}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── PROGRAM MILESTONE TIMELINE moved to TIMELINE tab ── */}
              {false && <div style={{ order: 1 }}>
              {/* ── PROGRAM MILESTONE TIMELINE ── */}
              {(() => {
                const allGates = selectedProgram.gates || [];
                const startDate = selectedProgram.program_start_date ? new Date(selectedProgram.program_start_date + 'T00:00:00') : null;
                const endDate = selectedProgram.launch_date ? new Date(selectedProgram.launch_date + 'T00:00:00') : null;
                const hasTimeline = startDate && endDate && endDate > startDate;

                const today = new Date(); today.setHours(0,0,0,0);
                // Pixel-based scrollable layout — only computed when dates exist
                const totalMs = hasTimeline ? endDate - startDate : 0;
                const pxPerMonth = 120;
                const totalMonths = hasTimeline ? Math.max(8, Math.ceil(totalMs / (30.44 * 24 * 3600 * 1000))) : 0;
                const totalWidth = totalMonths * pxPerMonth;
                const toPx = (d) => hasTimeline ? ((d - startDate) / totalMs) * totalWidth : 0;
                const todayInRange = hasTimeline && today >= startDate && today <= endDate;
                const todayX = todayInRange ? toPx(today) : null;
                const daysToStart = hasTimeline ? Math.ceil((startDate - today) / 86400000) : 0;
                const daysToEnd = hasTimeline ? Math.ceil((endDate - today) / 86400000) : 0;

                const GATE_NAMES = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
                const GATE_SHORT = ['Inception', 'Concept', 'Eng Review', 'P1', 'P2', 'Mass Prod'];
                const PRIORITY = { blocked: 5, at_risk: 4, on_track: 3, complete: 2, idle: 1 };

                const STATUS_COLOR = { complete: '#10b981', on_track: '#0d9488', at_risk: '#f59e0b', blocked: '#ef4444', idle: '#94a3b8' };

                // Build milestones and axis only when timeline dates exist
                const milestones = hasTimeline ? [1,2,3,4,5,6].map(gNum => {
                  const designGate = allGates.find(g => g.gate_number === gNum && g.lane === 'design');
                  const closeDate = designGate?.planned_close_date
                    ? new Date(designGate.planned_close_date + 'T00:00:00')
                    : new Date(startDate.getTime() + (gNum / 6) * totalMs);
                  const laneGates = allGates.filter(g => g.gate_number === gNum);
                  const worstStatus = laneGates.reduce((worst, g) => (PRIORITY[g.status] || 0) > (PRIORITY[worst] || 0) ? g.status : worst, 'idle');
                  const avgPct = laneGates.length ? Math.round(laneGates.reduce((s, g) => s + (g.completion_percent || 0), 0) / laneGates.length) : 0;
                  return { gNum, closeDate, status: worstStatus, avgPct, px: toPx(closeDate) };
                }) : [];

                const axisItems = [];
                if (hasTimeline) {
                  let cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                  let lastYear = null;
                  while (cur <= endDate) {
                    const year = cur.getFullYear();
                    axisItems.push({ px: toPx(cur), month: cur.toLocaleDateString('en-US', { month: 'short' }), year: year !== lastYear ? year : null });
                    lastYear = year;
                    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
                  }
                }

                const buildBriefInnerHtml = () => {
                  const briefFields = [
                    { label: 'WHAT IT IS', value: briefForm.what_is_it },
                    { label: 'WHY IT MATTERS', value: briefForm.why_it_matters },
                    { label: 'END VISION', value: briefForm.end_vision },
                    { label: 'FOR WHO', value: briefForm.target_customer },
                  ].filter(f => f.value);
                  const milestonesHtml = milestones.map(m => {
                    const color = STATUS_COLOR[m.status];
                    const fmt = m.closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    return `<div style="display:flex;align-items:center;gap:16px;padding:10px 0;border-bottom:1px solid #f1f5f9;">
                      <div style="width:32px;height:32px;border-radius:50%;background:${color}20;border:2px solid ${color};display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;color:${color};">G${m.gNum}</div>
                      <div style="flex:1;"><div style="font-size:13px;font-weight:700;color:#1a1a1a;">${GATE_NAMES[m.gNum-1]}</div><div style="font-size:11px;color:#64748b;margin-top:2px;">${fmt}</div></div>
                      <div style="font-size:10px;font-weight:700;letter-spacing:1px;color:${color};background:${color}15;padding:3px 10px;border-radius:20px;">${m.status.replace('_',' ').toUpperCase()}</div>
                    </div>`;
                  }).join('');
                  return `
                    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#1a1a1a;padding:48px;max-width:860px;margin:0 auto;">
                      <div style="font-family:'Courier New',monospace;font-size:14px;font-weight:700;letter-spacing:3px;color:#0d9488;margin-bottom:32px;">NOMOS</div>
                      <div style="font-size:28px;font-weight:800;letter-spacing:-0.5px;margin-bottom:4px;">${selectedProgram.name}</div>
                      <div style="font-size:12px;color:#64748b;margin-bottom:40px;">Program Brief · ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin-bottom:40px;">
                        <div><div style="font-size:9px;letter-spacing:1.5px;color:#94a3b8;font-weight:600;margin-bottom:4px;">MARKETS</div><div style="font-size:14px;font-weight:700;color:#1a1a1a;">${selectedProgram.markets || '—'}</div></div>
                        <div><div style="font-size:9px;letter-spacing:1.5px;color:#94a3b8;font-weight:600;margin-bottom:4px;">LOCATIONS</div><div style="font-size:14px;font-weight:700;color:#1a1a1a;">${(selectedProgram.num_locations || 0).toLocaleString()}</div></div>
                        <div><div style="font-size:9px;letter-spacing:1.5px;color:#94a3b8;font-weight:600;margin-bottom:4px;">LAUNCH DATE</div><div style="font-size:14px;font-weight:700;color:#1a1a1a;">${selectedProgram.launch_date || '—'}</div></div>
                        <div><div style="font-size:9px;letter-spacing:1.5px;color:#94a3b8;font-weight:600;margin-bottom:4px;">BUDGET</div><div style="font-size:14px;font-weight:700;color:#1a1a1a;">$${((selectedProgram.budget_total || 0)/1e6).toFixed(1)}M</div></div>
                      </div>
                      ${briefFields.map(f => `<div style="margin-bottom:32px;"><div style="font-size:9px;letter-spacing:2px;color:#0d9488;font-weight:700;margin-bottom:8px;">${f.label}</div><div style="font-size:13px;color:#374151;line-height:1.8;">${f.value}</div></div>`).join('')}
                      <div style="height:1px;background:#e2e8f0;margin:40px 0;"></div>
                      <div style="font-size:11px;font-weight:700;letter-spacing:2px;color:#1a1a1a;margin-bottom:20px;">PROGRAM MILESTONE TIMELINE</div>
                      ${milestonesHtml}
                      <div style="margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;display:flex;justify-content:space-between;">
                        <span>NOMOS · THE LAUNCH OPERATING SYSTEM</span><span>app.nomoslaunch.com</span>
                      </div>
                    </div>`;
                };

                const exportBriefTimeline = () => {
                  const inner = buildBriefInnerHtml();
                  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${selectedProgram.name} — Program Brief</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{background:#fff;}</style></head><body>${inner}</body></html>`;
                  const w = window.open('', '_blank');
                  w.document.write(html);
                  w.document.close();
                };

                const printBriefAsPdf = () => {
                  const inner = buildBriefInnerHtml();
                  const container = document.createElement('div');
                  container.id = '__nomos_brief_print__';
                  container.innerHTML = inner;
                  // Cover the screen — must be VISIBLE when window.print() fires or Safari won't capture it
                  container.style.cssText = 'position:fixed;inset:0;overflow:auto;background:#fff;z-index:99999;padding:0;';
                  document.body.appendChild(container);
                  const style = document.createElement('style');
                  style.id = '__nomos_brief_print_style__';
                  style.textContent = `@media print{body>*:not(#__nomos_brief_print__){display:none!important;}}`;
                  document.head.appendChild(style);
                  const cleanup = () => {
                    document.getElementById('__nomos_brief_print__')?.remove();
                    document.getElementById('__nomos_brief_print_style__')?.remove();
                    window.removeEventListener('afterprint', cleanup);
                  };
                  window.addEventListener('afterprint', cleanup);
                  // Two rAFs so the overlay fully paints before print dialog opens
                  requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
                };

                return (
                  <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: `1px solid ${t.borderDim}` }}>

                    {/* Section header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', color: t.text, marginBottom: '3px' }}>PROGRAM MILESTONE TIMELINE</div>
                        <div style={{ fontSize: '11px', color: t.dim }}>{selectedProgram.program_start_date} → {selectedProgram.launch_date}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {todayInRange && (
                          <button onClick={() => { const el = document.getElementById('prog-timeline-scroll'); if (el) el.scrollTo({ left: todayX - el.clientWidth / 2, behavior: 'smooth' }); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px', background: '#ef444415', border: '1px solid #ef444440', borderRadius: '6px', cursor: 'pointer', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: '#ef4444', fontWeight: '700' }}>
                            ● JUMP TO TODAY
                          </button>
                        )}
                        <button onClick={printBriefAsPdf} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: t.accent, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: '#000', fontWeight: '700' }}>
                          ⬇ CREATE PDF
                        </button>
                        <button onClick={exportBriefTimeline} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '6px', cursor: 'pointer', fontSize: '10px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', color: t.dim, fontWeight: '700' }}>
                          ↗ EXPORT BRIEF
                        </button>
                      </div>
                    </div>

                    {/* Context banner when today is outside the program window */}
                    {hasTimeline && !todayInRange && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', marginBottom: '16px', backgroundColor: darkMode ? '#1e293b' : '#f8fafc', border: `1px solid ${t.borderDim}`, borderRadius: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0 }} />
                        <div style={{ fontSize: '12px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.5px' }}>
                          {daysToStart > 0
                            ? `Today is ${daysToStart} day${daysToStart !== 1 ? 's' : ''} before program start · ${startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                            : `Program ended ${Math.abs(daysToEnd)} day${Math.abs(daysToEnd) !== 1 ? 's' : ''} ago`}
                        </div>
                      </div>
                    )}

                    {/* Chart card — only when timeline dates are set */}
                    {!hasTimeline && (
                      <div style={{ padding: '24px', backgroundColor: darkMode ? t.surface : '#f8fafc', border: `1px solid ${t.borderDim}`, borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: t.dim, letterSpacing: '1px' }}>Set a program start date and launch date to see the milestone timeline.</div>
                      </div>
                    )}
                    {hasTimeline && <div
                      id="prog-timeline-scroll"
                      ref={el => { if (el && !el.dataset.scrolled) { el.dataset.scrolled = '1'; if (todayX !== null) el.scrollLeft = Math.max(0, todayX - el.clientWidth / 2); } }}
                      style={{ backgroundColor: darkMode ? t.surface : '#ffffff', border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '28px 24px 40px', overflowX: 'auto' }}
                    >
                      <div style={{ width: `${totalWidth + 60}px`, position: 'relative' }}>

                        {/* Year labels + TODAY badge when in range */}
                        <div style={{ position: 'relative', height: '26px', marginBottom: '0' }}>
                          {axisItems.filter(a => a.year).map((a, i) => (
                            <div key={i} style={{ position: 'absolute', left: `${a.px}px`, fontSize: '14px', fontWeight: '800', color: darkMode ? t.text : '#0f172a', letterSpacing: '-0.5px' }}>{a.year}</div>
                          ))}
                          {todayInRange && todayX !== null && (
                            <div style={{ position: 'absolute', left: `${todayX}px`, transform: 'translateX(-50%)', background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '800', letterSpacing: '1px', padding: '2px 6px', borderRadius: '3px', whiteSpace: 'nowrap' }}>TODAY</div>
                          )}
                        </div>

                        {/* Month labels */}
                        <div style={{ position: 'relative', height: '20px', marginBottom: '16px', borderBottom: `1px solid ${darkMode ? t.borderDim : '#e2e8f0'}` }}>
                          {axisItems.map((a, i) => (
                            <div key={i} style={{ position: 'absolute', left: `${a.px}px`, fontSize: '10px', color: darkMode ? t.subLabel : '#94a3b8', whiteSpace: 'nowrap' }}>{a.month}</div>
                          ))}
                        </div>

                        {/* Single program row */}
                        <div style={{ position: 'relative', height: '80px' }}>

                          {/* Vertical month grid lines */}
                          {axisItems.map((a, i) => (
                            <div key={i} style={{ position: 'absolute', left: `${a.px}px`, top: 0, bottom: '-16px', width: '1px', backgroundColor: darkMode ? t.borderDimmer : '#f1f5f9' }} />
                          ))}

                          {/* Today line — only when today falls within the program window */}
                          {todayInRange && todayX !== null && (
                            <div style={{ position: 'absolute', left: `${todayX}px`, top: '-8px', bottom: '-16px', width: '2px', zIndex: 10, backgroundColor: '#ef4444', boxShadow: '0 0 8px #ef444460' }} />
                          )}

                          {/* Baseline track (program start → launch) */}
                          <div style={{ position: 'absolute', left: '0px', right: '0px', top: '26px', height: '4px', backgroundColor: darkMode ? '#334155' : '#e2e8f0', borderRadius: '2px' }} />

                          {/* Colored segment bars between gate milestones */}
                          {milestones.map((m, i) => {
                            const segStart = i === 0 ? 0 : milestones[i-1].px;
                            const color = STATUS_COLOR[m.status];
                            return (
                              <div key={m.gNum} style={{ position: 'absolute', left: `${segStart}px`, width: `${Math.max(0, m.px - segStart)}px`, top: '26px', height: '4px', backgroundColor: color, borderRadius: i === 0 ? '2px 0 0 2px' : i === 5 ? '0 2px 2px 0' : '0', zIndex: 1 }} />
                            );
                          })}

                          {/* Program start dot */}
                          <div style={{ position: 'absolute', left: '0px', top: '19px', transform: 'translateX(-50%)', zIndex: 5 }}>
                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: darkMode ? t.surface : '#fff', border: `2px solid ${darkMode ? '#94a3b8' : '#475569'}` }} />
                            <div style={{ fontSize: '9px', fontWeight: '700', color: darkMode ? t.subLabel : '#475569', marginTop: '10px', whiteSpace: 'nowrap', textAlign: 'center' }}>START</div>
                            <div style={{ fontSize: '8px', color: darkMode ? t.dim : '#94a3b8', whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedProgram.program_start_date}</div>
                          </div>

                          {/* Gate diamonds */}
                          {(() => {
                            const launchPx = toPx(endDate);
                            return milestones.map((m, i) => {
                              const color = STATUS_COLOR[m.status];
                              const fmt = m.closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              const isComplete = m.status === 'complete';
                              const labelAbove = i % 2 === 0;
                              // If this gate is within 14 days of launch, merge it with the launch dot
                              const isMergedWithLaunch = i === milestones.length - 1 && Math.abs(launchPx - m.px) < pxPerMonth * 0.5;
                              if (isMergedWithLaunch) return null;
                              return (
                                <div key={m.gNum} style={{ position: 'absolute', left: `${m.px}px`, top: '19px', transform: 'translateX(-50%)', zIndex: 5 }}>
                                  <div style={{ width: '18px', height: '18px', backgroundColor: isComplete ? color : (darkMode ? t.surface : '#fff'), border: `2px solid ${color}`, transform: 'rotate(45deg)', margin: '0 auto', boxShadow: `0 0 0 3px ${color}20` }} />
                                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', ...(labelAbove ? { bottom: '26px' } : { top: '26px' }), textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '700', color, letterSpacing: '0.3px' }}>{GATE_SHORT[m.gNum - 1]}</div>
                                    <div style={{ fontSize: '9px', color: darkMode ? t.dim : '#64748b', marginTop: '1px' }}>{fmt}</div>
                                  </div>
                                  <div style={{ position: 'absolute', left: '50%', width: '1px', backgroundColor: color + '50', ...(labelAbove ? { top: '-16px', height: '16px' } : { top: '18px', height: '16px' }) }} />
                                </div>
                              );
                            });
                          })()}

                          {/* Launch dot — always at track end */}
                          <div style={{ position: 'absolute', left: `${totalWidth}px`, top: '19px', transform: 'translateX(-50%)', zIndex: 5 }}>
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#0d9488', border: '2px solid #0d9488', boxShadow: '0 0 0 5px #0d948825', marginLeft: '-2px', marginTop: '-2px' }} />
                            <div style={{ fontSize: '10px', fontWeight: '800', color: '#0d9488', marginTop: '10px', whiteSpace: 'nowrap', textAlign: 'center', letterSpacing: '0.5px' }}>LAUNCH</div>
                            <div style={{ fontSize: '8px', color: darkMode ? t.dim : '#94a3b8', whiteSpace: 'nowrap', textAlign: 'center' }}>{selectedProgram.launch_date}</div>
                          </div>

                        </div>
                      </div>
                    </div>}
                  </div>
                );
              })()}
              </div>}

          </div>
        )}

        {/* REQUEST EDIT modal */}
        {briefRequestOpen && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div style={{ backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '32px', maxWidth: '460px', width: '100%' }}>
              <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '3px', marginBottom: '10px', fontFamily: "'Roboto Mono', monospace" }}>REQUEST BRIEF EDIT</div>
              <div style={{ fontSize: '17px', fontWeight: '700', color: t.text, marginBottom: '8px' }}>What needs to change?</div>
              <div style={{ fontSize: '13px', color: t.muted, marginBottom: '20px', lineHeight: 1.6 }}>
                Describe what you'd like updated in the brief. Your request will be sent to the program admin.
              </div>
              <textarea
                style={{ ...s.textarea, minHeight: '100px', width: '100%', fontSize: '13px', marginBottom: '16px' }}
                placeholder="e.g. The success criteria needs to be updated to reflect the new regional targets..."
                value={briefRequestText}
                onChange={e => setBriefRequestText(e.target.value)}
                autoFocus
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setBriefRequestOpen(false)} style={{ flex: 1, padding: '10px', background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '6px', color: t.dim, cursor: 'pointer', fontSize: '11px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                  CANCEL
                </button>
                <button
                  disabled={!briefRequestText.trim() || briefRequestSending}
                  onClick={() => {
                    if (!briefRequestText.trim()) return;
                    setBriefRequestSending(true);
                    authFetch(`${API}/programs/${selectedProgram.id}/messages`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        to_name: 'Admin',
                        subject: `Brief edit request — ${selectedProgram.name}`,
                        body: `${myFullName || 'A team member'} (${userLane || 'no lane'}) is requesting an update to the Launch Brief:\n\n"${briefRequestText.trim()}"`,
                        from_lane: userLane || '',
                      }),
                    })
                      .then(() => { setBriefRequestOpen(false); setBriefRequestText(''); alert('Request sent to your admin.'); })
                      .catch(() => alert('Failed to send — try again.'))
                      .finally(() => setBriefRequestSending(false));
                  }}
                  style={{ flex: 1, padding: '10px', background: t.accent, border: 'none', borderRadius: '6px', color: '#000', cursor: briefRequestSending || !briefRequestText.trim() ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '700', opacity: briefRequestSending || !briefRequestText.trim() ? 0.5 : 1 }}>
                  {briefRequestSending ? 'SENDING...' : 'SEND REQUEST'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TEAMS TAB ── */}
        {activeTab === 'teams' && (() => {
          const defaultLanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const laneColorMap = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
          const customPalette = ['#ec4899', '#84cc16', '#f97316', '#14b8a6', '#a855f7', '#64748b', '#d97706', '#7c3aed'];
          const customTeamKeys = Object.keys({ ...teamLeads, ...Object.fromEntries(Object.entries(laneDris)) })
            .filter(k => !defaultLanes.includes(k) && k);
          const allTeams = [...defaultLanes, ...customTeamKeys];
          const gateNamesArr = ['Program Inception', 'Concept Review', 'Eng Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

          const addCustomTeam = () => {
            const raw = newCustomTeamName.trim();
            if (!raw) return;
            const key = raw.toLowerCase().replace(/\s+/g, '_');
            if (!teamLeads[key]) setTeamLeads(prev => ({ ...prev, [key]: '' }));
            if (!laneDris[key]) setLaneDris(prev => ({ ...prev, [key]: [] }));
            setNewCustomTeamName('');
            setAddingCustomTeam(false);
          };

          return (
            <div style={{ maxWidth: '1100px' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.2px', marginBottom: '6px' }}>Team Workspaces</div>
                  <div style={{ fontSize: '11px', color: t.subLabel, letterSpacing: '1px' }}>Add registered team members to each lane. Changes save instantly and feed the program grid, timeline, and consequences engine.</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
                {allTeams.map((team, teamIdx) => {
                  const isDefault = defaultLanes.includes(team);
                  const color = laneColorMap[team] || customPalette[(teamIdx - defaultLanes.length) % customPalette.length];
                  const lead = teamLeads[team] || '';
                  const dris = laneDris[team] || [];
                  const laneGates = isDefault ? (selectedProgram.gates || []).filter(g => g.lane === team) : [];
                  const currentGs = laneGates.find(g => g.gate_number === selectedProgram.current_gate);
                  const completedCount = laneGates.filter(g => g.status === 'complete').length;
                  const blockedCount = laneGates.filter(g => g.status === 'blocked').length;
                  const status = currentGs?.status || 'idle';
                  const statusColor = status === 'complete' ? '#10b981' : status === 'blocked' ? '#ef4444' : status === 'at_risk' ? '#f59e0b' : status === 'on_track' ? '#22d3ee' : t.borderDim;
                  const pct = currentGs?.completion_percent || 0;
                  const displayName = team.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                  return (
                    <div key={team} style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      {/* Header */}
                      <div style={{ backgroundColor: color + '15', borderBottom: `1px solid ${color}30`, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 'bold', color, letterSpacing: '2px' }}>{displayName.toUpperCase()}</div>
                          {isDefault && <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px', letterSpacing: '1px' }}>G{selectedProgram.current_gate} · {gateNamesArr[(selectedProgram.current_gate || 1) - 1]}</div>}
                          {!isDefault && <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px', letterSpacing: '1px' }}>CUSTOM TEAM</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isDefault && (
                            <div style={{ fontSize: '9px', fontWeight: 'bold', color: statusColor, letterSpacing: '1px', backgroundColor: statusColor + '15', border: `1px solid ${statusColor}40`, borderRadius: '4px', padding: '3px 8px' }}>
                              {status.toUpperCase().replace('_', ' ')}
                            </div>
                          )}
                          {!isDefault && (
                            <button
                              title="Remove team"
                              onClick={() => {
                                setTeamLeads(prev => { const n = { ...prev }; delete n[team]; return n; });
                                setLaneDris(prev => { const n = { ...prev }; delete n[team]; return n; });
                              }}
                              style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '14px', padding: '2px 4px', lineHeight: 1, opacity: 0.6 }}>✕</button>
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* Progress bar (default lanes only) */}
                        {isDefault && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>CURRENT GATE PROGRESS</div>
                              <div style={{ fontSize: '9px', color, fontWeight: 'bold' }}>{pct}%</div>
                            </div>
                            <div style={{ height: '4px', backgroundColor: t.borderDim, borderRadius: '2px' }}>
                              <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '2px', transition: 'width 0.3s' }} />
                            </div>
                          </div>
                        )}

                        {/* Team members — user-ID-backed */}
                        {(() => {
                          const laneMembers = programMembers.filter(m => m.lane === team);
                          const laneLead = laneMembers.find(m => m.role === 'lead');
                          const laneRegMembers = laneMembers.filter(m => m.role === 'member');
                          const assignedIds = new Set(laneMembers.map(m => m.user_id));
                          const available = teamMembers.filter(m => !assignedIds.has(m.id));

                          const MemberChip = ({ pm, isLead }) => {
                            const profile = pm.profiles || {};
                            const initials = (profile.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                            const tmProfile = teamMembers.find(m => m.id === pm.user_id);
                            const pres = getPresence(tmProfile?.last_active_at);
                            const pColor = presenceColor[pres];
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', backgroundColor: t.bg, border: `1px solid ${isLead ? color + '50' : t.borderDim}`, borderRadius: '6px', marginBottom: '6px' }}>
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden', backgroundColor: color + '20', border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color }}>
                                    {profile.avatar_url ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                                  </div>
                                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: '9px', height: '9px', borderRadius: '50%', backgroundColor: pColor, border: `2px solid ${t.bg}` }} title={presenceLabel[pres]} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '12px', fontWeight: isLead ? '700' : '500', color: t.text }}>{profile.full_name}</div>
                                  {profile.title && <div style={{ fontSize: '9px', color: t.muted }}>{profile.title}</div>}
                                </div>
                                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                  {isLead && <span style={{ fontSize: '7px', color, letterSpacing: '1px', border: `1px solid ${color}40`, borderRadius: '3px', padding: '2px 5px' }}>LEAD</span>}
                                  {!isLead && <button onClick={() => promoteProgramMember(pm.id)} title="Make lead" style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '2px 6px', fontSize: '8px', color: t.muted, cursor: 'pointer', letterSpacing: '0.5px' }}>↑ LEAD</button>}
                                  <button onClick={() => removeProgramMember(pm.id)} style={{ background: 'none', border: 'none', color: t.muted, cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1, opacity: 0.6 }}>×</button>
                                </div>
                              </div>
                            );
                          };

                          return (
                            <div>
                              <div style={{ fontSize: '8px', color, letterSpacing: '1.5px', marginBottom: '8px', fontWeight: '700' }}>TEAM · {laneMembers.length} {laneMembers.length === 1 ? 'person' : 'people'}</div>
                              {laneLead && <MemberChip pm={laneLead} isLead={true} />}
                              {laneRegMembers.map(pm => <MemberChip key={pm.id} pm={pm} isLead={false} />)}
                              {available.length > 0 && (
                                <select
                                  value=""
                                  onChange={e => { if (e.target.value) { const role = laneMembers.length === 0 ? 'lead' : 'member'; addProgramMember(e.target.value, team, role); } }}
                                  style={{ width: '100%', padding: '7px 10px', backgroundColor: t.bg, border: `1px dashed ${t.border}`, borderRadius: '4px', color: t.muted, fontSize: '11px', boxSizing: 'border-box', fontFamily: 'inherit', marginTop: '4px', cursor: 'pointer' }}>
                                  <option value="">+ Add team member...</option>
                                  {available.map(m => <option key={m.id} value={m.id}>{m.full_name}{m.title ? ` · ${m.title}` : ''}</option>)}
                                </select>
                              )}
                              {available.length === 0 && laneMembers.length === 0 && (
                                <div style={{ fontSize: '11px', color: t.muted, padding: '8px 0', fontStyle: 'italic' }}>No registered users available — invite your team first.</div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Gate summary (default lanes) */}
                        {isDefault && (
                          <div style={{ display: 'flex', gap: '12px', fontSize: '9px', letterSpacing: '1px' }}>
                            <span style={{ color: '#10b981' }}>{completedCount} GATES DONE</span>
                            {blockedCount > 0 && <span style={{ color: '#ef4444' }}>{blockedCount} BLOCKED</span>}
                          </div>
                        )}
                      </div>

                      {/* Workspace button (default lanes only) */}
                      {isDefault && (
                        <div style={{ padding: '12px 18px', borderTop: `1px solid ${t.borderDim}` }}>
                          <button
                            onClick={() => openWorkspace(team)}
                            style={{ width: '100%', backgroundColor: color, border: 'none', color: '#fff', padding: '10px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '2px', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Roboto Mono', monospace", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            OPEN {displayName.toUpperCase()} WORKSPACE →
                            {(mailboxUnread?.count || 0) > 0 && workspaceView === team && (
                              <span style={{ backgroundColor: '#fff', color: color, borderRadius: '10px', padding: '1px 7px', fontSize: '9px', fontWeight: '900', lineHeight: 1.6 }}>{mailboxUnread.count}</span>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add Team card */}
                {addingCustomTeam ? (
                  <div style={{ backgroundColor: t.surface, border: `2px dashed ${t.border}`, borderRadius: '8px', padding: '24px 18px', display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'center' }}>
                    <div style={{ fontSize: '10px', color: t.accent, letterSpacing: '2px', fontWeight: '700' }}>NEW TEAM</div>
                    <input
                      autoFocus
                      placeholder="Team name (e.g. Legal, Marketing)"
                      value={newCustomTeamName}
                      onChange={e => setNewCustomTeamName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addCustomTeam(); if (e.key === 'Escape') { setAddingCustomTeam(false); setNewCustomTeamName(''); } }}
                      style={{ width: '100%', padding: '9px 12px', backgroundColor: t.bg, border: `1px solid ${t.accent}`, borderRadius: '4px', color: t.text, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={addCustomTeam} style={{ flex: 1, backgroundColor: t.accent, border: 'none', color: '#000', padding: '8px', fontSize: '10px', fontWeight: 'bold', letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Roboto Mono', monospace" }}>ADD</button>
                      <button onClick={() => { setAddingCustomTeam(false); setNewCustomTeamName(''); }} style={{ flex: 1, background: 'none', border: `1px solid ${t.border}`, color: t.dim, padding: '8px', fontSize: '10px', letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Roboto Mono', monospace" }}>CANCEL</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingCustomTeam(true)}
                    style={{ backgroundColor: 'transparent', border: `2px dashed ${t.border}`, borderRadius: '8px', padding: '40px 18px', color: t.dim, fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', fontFamily: "'Roboto Mono', monospace", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}>
                    + ADD TEAM
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── GATES TAB ── */}
        {activeTab === 'gates' && (
          <div>
          {/* Sub-tab switcher */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${t.borderDim}`, paddingBottom: '0' }}>
            {[{ id: 'current', label: 'CURRENT MILESTONE' }, { id: 'full', label: 'FULL LAUNCH' }].map(st => (
              <button key={st.id} onClick={() => setReadinessSubTab(st.id)} style={{ background: 'none', border: 'none', borderBottom: readinessSubTab === st.id ? `2px solid ${t.accent}` : '2px solid transparent', padding: '8px 16px', cursor: 'pointer', fontSize: '10px', letterSpacing: '2px', color: readinessSubTab === st.id ? t.accent : t.dim, fontWeight: readinessSubTab === st.id ? '700' : '400', fontFamily: 'inherit', marginBottom: '-1px', transition: 'all 0.15s' }}>{st.label}</button>
            ))}
          </div>

          {/* ── CURRENT MILESTONE sub-tab ── */}
          {readinessSubTab === 'current' && (() => {
            const currentGateNum = selectedProgram.current_gate || 1;
            const gateName = gateNames[currentGateNum - 1] || `Gate ${currentGateNum}`;
            const criticalPath = consequences?.critical_path;
            return (
              <div>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '14px' }}>CLICK A LANE TO OPEN MILESTONE TASKS</div>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '200px', padding: '10px 16px', textAlign: 'left', borderBottom: `1px solid ${t.borderDim}`, fontSize: '9px', color: t.subLabel, letterSpacing: '2px', fontWeight: '600' }}>SWIM LANE</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', borderBottom: `2px solid ${t.accent}`, backgroundColor: t.accent + '08' }}>
                        <div style={{ fontSize: '8px', letterSpacing: '1.5px', fontWeight: '700', color: t.accent, marginBottom: '3px' }}>GATE {currentGateNum}</div>
                        <div style={{ fontSize: '11px', color: t.accent, fontWeight: '600' }}>{gateName}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lanes.map(lane => {
                      const gs = (selectedProgram.gates || []).find(g => g.lane === lane && g.gate_number === currentGateNum);
                      const isCritical = criticalPath === lane;
                      const isIdle = !gs || gs.status === 'idle';
                      const isDone = gs?.status === 'complete';
                      const color = gs && !isIdle ? getStatusColor(gs.status) : t.borderDim;
                      const laneOverallPct = gs?.completion_percent || 0;
                      const dotColor = gs
                        ? gs.status === 'blocked' ? '#ef4444'
                          : gs.status === 'at_risk' ? '#f59e0b'
                          : gs.status === 'complete' || gs.status === 'on_track' ? '#10b981'
                          : t.accent
                        : t.borderDim;
                      const pace = gs && !isIdle ? getGatePace(gs, selectedProgram.gates, selectedProgram) : null;
                      const paceBadge = pace && pace.status !== 'on_pace' ? (pace.status === 'overdue' ? `${pace.daysLate}D LATE` : (pace.daysLate ? `~${pace.daysLate}D SLIP` : 'BEHIND')) : null;
                      const paceColor = pace?.status === 'overdue' ? '#ef4444' : '#f59e0b';
                      return (
                        <tr key={lane}>
                          <td style={{ padding: '14px 16px', verticalAlign: 'middle', borderBottom: `1px solid ${t.borderDim}`, borderLeft: isCritical ? `3px solid #f59e0b` : `3px solid transparent`, backgroundColor: isCritical ? '#f59e0b06' : 'transparent', cursor: 'pointer' }}
                            onClick={() => demoMode ? openLaneView(lane) : openWorkspace(lane, selectedProgram.id)}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0, marginTop: '4px' }} />
                              <div>
                                <div style={{ fontSize: '13px', color: t.text, fontWeight: '600' }}>{lane.charAt(0).toUpperCase() + lane.slice(1)}</div>
                                <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '2px', letterSpacing: '1px' }}>{laneOverallPct}% <span style={{ opacity: 0.6 }}>G{currentGateNum}</span></div>
                                {isCritical && <div style={{ fontSize: '8px', color: '#f59e0b', letterSpacing: '1px', marginTop: '5px', border: '1px solid #f59e0b40', borderRadius: '3px', padding: '1px 5px', display: 'inline-block' }}>CRITICAL PATH</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '8px 16px', borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.accent + '05', verticalAlign: 'top', cursor: isIdle ? 'default' : 'pointer' }}
                            onClick={() => { if (!isIdle && gs) openEdit(gs); else if (isIdle && gs) openEdit(gs); }}>
                            {isIdle ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px', border: `1px dashed ${t.accent}50`, borderRadius: '4px' }}>
                                <span style={{ fontSize: '9px', color: t.accent, letterSpacing: '1.5px', opacity: 0.7 }}>NOT STARTED</span>
                              </div>
                            ) : (
                              <div>
                                <div style={{ position: 'relative', height: '42px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${color}35`, backgroundColor: darkMode ? color + '0f' : color + '0a' }}>
                                  <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${gs.completion_percent}%`, backgroundColor: color + '2a', transition: 'width 0.4s ease' }} />
                                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%', padding: '0 9px' }}>
                                    <span style={{ fontSize: '10px', color, fontWeight: '700', letterSpacing: '0.3px' }}>{isDone ? '✓ DONE' : gs.status.toUpperCase().replace('_', ' ')}</span>
                                    <span style={{ fontSize: '10px', color, fontWeight: '500' }}>{gs.completion_percent}%</span>
                                  </div>
                                </div>
                                {paceBadge && <div style={{ fontSize: '8px', color: paceColor, padding: '3px 4px 0', letterSpacing: '0.5px', fontWeight: '700', fontFamily: "'Roboto Mono', monospace" }}>{paceBadge}</div>}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* ── FULL LAUNCH sub-tab ── */}
          {readinessSubTab === 'full' && <div>

            {/* ── Context header + timeline ── */}
            {(() => {
              const allGates = selectedProgram.gates || [];
              const criticalPath = consequences?.critical_path;
              const slipDays = consequences?.projected_slip_days || 0;
              const totalComplete = allGates.filter(g => g.status === 'complete').length;
              const projLaunch = consequences?.projected_launch || selectedProgram.launch_date;
              const completePct = allGates.length ? Math.round(totalComplete / allGates.length * 100) : 0;
              return (
                <>
                  {/* 4-column context strip */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1.6fr 1fr 1fr', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', marginBottom: '14px', overflow: 'hidden' }}>
                    {[
                      { label: 'PROGRAM', value: selectedProgram.name, color: t.text, mono: false },
                      { label: 'CURRENT GATE', value: `G${selectedProgram.current_gate} · ${gateNames[selectedProgram.current_gate - 1]}`, color: t.accent, mono: false },
                      { label: 'CRITICAL PATH', value: criticalPath ? `${criticalPath.charAt(0).toUpperCase() + criticalPath.slice(1)} lane` : 'None', color: criticalPath ? '#f59e0b' : '#10b981', mono: false },
                      { label: 'PROJECTED SLIP', value: slipDays > 0 ? `+${slipDays} days` : 'On Track', color: slipDays > 0 ? '#ef4444' : '#10b981', mono: true },
                    ].map((item, idx, arr) => (
                      <div key={item.label} style={{ padding: '16px 20px', borderRight: idx < arr.length - 1 ? `1px solid ${t.borderDim}` : 'none' }}>
                        <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>{item.label}</div>
                        <div style={{ fontSize: '13px', color: item.color, fontWeight: '600', fontFamily: item.mono ? "'Roboto Mono', monospace" : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Mini timeline bar */}
                  {selectedProgram.launch_date && (
                    <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '14px 20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>ORIGINAL TARGET</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ fontSize: '11px', color: t.text, letterSpacing: '1px', fontWeight: '500' }}>{selectedProgram.launch_date?.toUpperCase()}</span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>PROJECTED LAUNCH{slipDays > 0 ? ' (WITH DELAYS)' : ''}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                            <span style={{ fontSize: '11px', color: slipDays > 0 ? '#ef4444' : '#10b981', letterSpacing: '1px', fontWeight: '600' }}>{projLaunch?.toUpperCase()}</span>
                            {slipDays > 0 && <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block', flexShrink: 0 }} />}
                          </div>
                        </div>
                      </div>
                      <div style={{ position: 'relative', height: '4px', backgroundColor: t.borderDimmer, borderRadius: '2px' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, height: '4px', width: `${completePct}%`, backgroundColor: '#10b981', borderRadius: '2px' }} />
                        {slipDays > 0 && (
                          <div style={{ position: 'absolute', top: 0, right: 0, height: '4px', width: `${Math.min(25, slipDays / 180 * 100)}%`, backgroundColor: '#ef4444', borderRadius: '2px' }} />
                        )}
                      </div>
                      {slipDays > 0 && (
                        <div style={{ textAlign: 'right', marginTop: '5px', fontSize: '9px', color: '#ef4444', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>— SLIP {slipDays}D →</div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── Gate Grid ── */}
            {(() => {
              const currentGate = selectedProgram.current_gate || 1;
              const criticalPath = consequences?.critical_path;
              return (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '10px' }}>CLICK ANY GATE CELL TO OPEN PLANNING</div>
                  <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '160px', padding: '10px 16px', textAlign: 'left', borderBottom: `1px solid ${t.borderDim}`, fontSize: '9px', color: t.subLabel, letterSpacing: '2px', fontWeight: '600' }}>SWIM LANE</th>
                        {gates.map((g, i) => {
                          const isActive = g === currentGate;
                          return (
                            <th key={g} style={{
                              padding: '10px 8px',
                              textAlign: 'left',
                              borderBottom: isActive ? `2px solid ${t.accent}` : `1px solid ${t.borderDim}`,
                              backgroundColor: isActive ? t.accent + '08' : 'transparent',
                              minWidth: '110px',
                            }}>
                              <div style={{ fontSize: '8px', letterSpacing: '1.5px', fontWeight: '700', color: isActive ? t.accent : t.dim, marginBottom: '3px' }}>GATE {g}</div>
                              <div style={{ fontSize: '10px', color: isActive ? t.accent : t.subLabel, fontWeight: isActive ? '600' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '130px' }}>{gateNames[i]}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {lanes.map(lane => {
                        const allLaneGates = (selectedProgram.gates || []).filter(g => g.lane === lane);
                        const laneCurrentGs = getGateStatus(lane, currentGate);
                        const isCritical = criticalPath === lane;
                        const dotColor = laneCurrentGs
                          ? laneCurrentGs.status === 'blocked' ? '#ef4444'
                            : laneCurrentGs.status === 'at_risk' ? '#f59e0b'
                            : laneCurrentGs.status === 'complete' || laneCurrentGs.status === 'on_track' ? '#10b981'
                            : t.accent
                          : t.borderDim;
                        const laneCurrentGate = allLaneGates.find(g => g.gate_number === currentGate);
                        const laneOverallPct = laneCurrentGate ? (laneCurrentGate.completion_percent || 0) : 0;
                        return (
                          <tr key={lane}>
                            <td style={{
                              padding: '14px 16px',
                              verticalAlign: 'middle',
                              borderBottom: `1px solid ${t.borderDim}`,
                              borderLeft: isCritical ? `3px solid #f59e0b` : `3px solid transparent`,
                              backgroundColor: isCritical ? '#f59e0b06' : 'transparent',
                              cursor: 'pointer',
                            }} onClick={() => demoMode ? openLaneView(lane) : openWorkspace(lane, selectedProgram.id)}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0, marginTop: '4px' }} />
                                <div>
                                  <div style={{ fontSize: '13px', color: t.text, fontWeight: '600' }}>{lane.charAt(0).toUpperCase() + lane.slice(1)}</div>
                                  <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '2px', letterSpacing: '1px' }}>{laneOverallPct}% <span style={{ opacity: 0.6 }}>G{currentGate}</span></div>
                                  {isCritical && <div style={{ fontSize: '8px', color: '#f59e0b', letterSpacing: '1px', marginTop: '5px', border: '1px solid #f59e0b40', borderRadius: '3px', padding: '1px 5px', display: 'inline-block' }}>CRITICAL PATH</div>}
                                </div>
                              </div>
                            </td>
                            {gates.map(g => {
                              const gs = getGateStatus(lane, g);
                              const isActive = g === currentGate;
                              const isPastGate = g < currentGate;
                              const isFutureGate = g > currentGate;
                              const isIdle = !gs || gs.status === 'idle';
                              const isActiveIdle = isIdle && isActive;
                              const color = gs && !isIdle ? getStatusColor(gs.status) : t.borderDim;
                              const isDone = gs?.status === 'complete';
                              const isAutoCascade = gs?.notes?.includes('[NOMOS]');
                              const noteText = gs?.notes && !isAutoCascade && (gs.status === 'blocked' || gs.status === 'at_risk') ? gs.notes.slice(0, 45) : null;
                              const pace = gs && !isIdle ? getGatePace(gs, selectedProgram.gates, selectedProgram) : null;
                              const paceBadge = pace && pace.status !== 'on_pace' ? (pace.status === 'overdue' ? `${pace.daysLate}D LATE` : (pace.daysLate ? `~${pace.daysLate}D SLIP` : 'BEHIND')) : null;
                              const paceColor = pace?.status === 'overdue' ? '#ef4444' : '#f59e0b';
                              const hasOwner = teamLeads[lane] || (laneDris[lane] || []).length > 0;
                              const needsOwner = isActive && !hasOwner && gs && ['at_risk', 'blocked'].includes(gs.status);
                              const cellOpacity = isPastGate ? 0.5 : (isFutureGate && isIdle) ? 0.4 : 1;
                              const adaptivePred = adaptiveData?.predictions?.find(p => p.lane === lane && p.gate_number === g);
                              const showPredBadge = adaptivePred && !isDone && !isIdle && adaptivePred.slipRisk !== 'low';
                              return (
                                <td key={g} style={{
                                  padding: '8px 4px',
                                  borderBottom: `1px solid ${t.borderDim}`,
                                  backgroundColor: isActive ? t.accent + '05' : 'transparent',
                                  verticalAlign: 'top',
                                  cursor: (isIdle && !isActiveIdle) ? 'default' : 'pointer',
                                  opacity: cellOpacity,
                                  transition: 'opacity 0.2s',
                                }} onClick={() => { if (isActiveIdle && gs) openEdit(gs); else if (!isIdle && gs) openEdit(gs); }}>
                                  {isIdle && !isActiveIdle ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px', color: t.borderDim, fontSize: '18px', letterSpacing: '1px' }}>–</div>
                                  ) : isActiveIdle ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '42px', border: `1px dashed ${t.accent}50`, borderRadius: '4px' }}>
                                      <span style={{ fontSize: '9px', color: t.accent, letterSpacing: '1.5px', opacity: 0.7 }}>NOT STARTED</span>
                                    </div>
                                  ) : (
                                    <div>
                                      <div style={{
                                        position: 'relative', height: '42px', borderRadius: '4px', overflow: 'hidden',
                                        border: `1px solid ${color}35`, backgroundColor: darkMode ? color + '0f' : color + '0a',
                                      }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${gs.completion_percent}%`, backgroundColor: color + '2a', transition: 'width 0.4s ease' }} />
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%', padding: '0 9px' }}>
                                          <span style={{ fontSize: '10px', color, fontWeight: '700', letterSpacing: '0.3px' }}>
                                            {isDone ? '✓ DONE' : gs.status.toUpperCase().replace('_', ' ')}
                                          </span>
                                          <span style={{ fontSize: '10px', color, fontWeight: '500' }}>{gs.completion_percent}%</span>
                                        </div>
                                      </div>
                                      {noteText && <div style={{ fontSize: '9px', color: t.dim, padding: '3px 4px 0', lineHeight: '1.3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{noteText}</div>}
                                      {paceBadge && <div style={{ fontSize: '8px', color: paceColor, padding: '3px 4px 0', letterSpacing: '0.5px', fontWeight: '700', fontFamily: "'Roboto Mono', monospace" }}>{paceBadge}</div>}
                                      {isAutoCascade && <div style={{ fontSize: '8px', color: '#f59e0b', padding: '2px 4px 0', letterSpacing: '0.5px' }}>AUTO</div>}
                                      {needsOwner && <div style={{ fontSize: '8px', color: '#ef4444', padding: '2px 4px 0', letterSpacing: '0.5px', fontWeight: '700' }}>NO OWNER</div>}
                                      {showPredBadge && <div style={{ fontSize: '8px', color: adaptivePred.slipRisk === 'high' ? '#ef4444' : '#f59e0b', padding: '2px 4px 0', letterSpacing: '0.5px', fontWeight: '700', fontFamily: "'Roboto Mono', monospace" }}>⚡ {adaptivePred.slipRisk.toUpperCase()} SLIP RISK</div>}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* Team Accountability */}
            {(Object.keys(teamLeads).length > 0 || Object.keys(laneDris).length > 0) && (
              <div style={{ marginTop: '28px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '14px' }}>TEAM ACCOUNTABILITY — G{selectedProgram.current_gate}</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '10px' }}>
                  {lanes.map(lane => {
                    const lead = teamLeads[lane];
                    const dris = laneDris[lane] || [];
                    const curGate = (selectedProgram.gates || []).find(g => g.lane === lane && g.gate_number === selectedProgram.current_gate);
                    if (!lead && !dris.length) return null;
                    const isComplete = curGate?.status === 'complete';
                    const pct = curGate?.completion_percent || 0;
                    const statusColor = getStatusColor(curGate?.status || 'idle');
                    return (
                      <div key={lane} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontSize: '10px', letterSpacing: '1.5px', color: t.text, fontWeight: '600' }}>{lane.toUpperCase()}</span>
                          <span style={{ fontSize: '9px', color: statusColor, letterSpacing: '1px', fontWeight: 'bold' }}>{isComplete ? '✓ COMPLETE' : `${pct}%`}</span>
                        </div>
                        {lead && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: t.accent, fontWeight: 'bold', flexShrink: 0 }}>{lead.slice(0, 2).toUpperCase()}</div>
                            <div>
                              <div style={{ fontSize: '11px', color: t.text }}>{lead}</div>
                              <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '1px' }}>LEAD</div>
                            </div>
                          </div>
                        )}
                        {dris.map(dri => (
                          <div key={dri} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: t.subLabel, fontWeight: 'bold', flexShrink: 0 }}>{dri.slice(0, 2).toUpperCase()}</div>
                            <div>
                              <div style={{ fontSize: '11px', color: t.dim }}>{dri}</div>
                              <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px' }}>DRI</div>
                            </div>
                          </div>
                        ))}
                        {curGate && (
                          <div style={{ marginTop: '8px', height: '2px', backgroundColor: t.borderDimmer, borderRadius: '1px' }}>
                            <div style={{ height: '2px', width: `${pct}%`, backgroundColor: statusColor, borderRadius: '1px' }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>}
          </div>
        )}



        {/* ── TIMELINE TAB ── */}
        {activeTab === 'timeline' && !(userRole === 'member' && userLane) && (() => {
          const allGates = selectedProgram.gates || [];

          // ── Sub-tab switcher ──
          const timelineSwitcher = (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: `1px solid ${t.borderDim}`, paddingBottom: '0' }}>
              {[{ id: 'live', label: 'LIVE TIMELINE' }, { id: 'all', label: 'ALL PHASES' }].map(st => (
                <button key={st.id} onClick={() => setTimelineSubTab(st.id)} style={{ background: 'none', border: 'none', borderBottom: timelineSubTab === st.id ? `2px solid ${t.accent}` : '2px solid transparent', padding: '8px 16px', cursor: 'pointer', fontSize: '10px', letterSpacing: '2px', color: timelineSubTab === st.id ? t.accent : t.dim, fontWeight: timelineSubTab === st.id ? '700' : '400', fontFamily: 'inherit', marginBottom: '-1px', transition: 'all 0.15s' }}>{st.label}</button>
              ))}
            </div>
          );

          // ── LIVE TIMELINE ──
          if (timelineSubTab === 'live') {
            const currentGateNum = selectedProgram.current_gate || 1;
            const gateName = gateNames[currentGateNum - 1] || `Gate ${currentGateNum}`;
            const laneColors = { design: '#6366f1', finance: '#10b981', operations: '#f59e0b', installation: '#22d3ee', engineering: '#111111' };
            const statusConfig = {
              complete:  { label: 'COMPLETE',    bg: '#10b98115', color: '#10b981', border: '#10b98135' },
              on_track:  { label: 'ON TRACK',    bg: '#10b98112', color: '#10b981', border: '#10b98130' },
              at_risk:   { label: 'AT RISK',     bg: '#f59e0b12', color: '#f59e0b', border: '#f59e0b30' },
              blocked:   { label: 'BLOCKED',     bg: '#ef444412', color: '#ef4444', border: '#ef444430' },
              idle:      { label: 'NOT STARTED', bg: 'transparent', color: t.dim,   border: t.borderDim  },
            };
            const statusOrder = { blocked: 0, at_risk: 1, on_track: 2, complete: 3, idle: 4 };

            const laneRows = lanes.map(lane => {
              const gate = allGates.find(g => g.lane === lane && g.gate_number === currentGateNum);
              const status = gate?.status || 'idle';
              const pct = gate?.completion_percent || 0;
              const closeDate = gate?.planned_close_date ? new Date(gate.planned_close_date) : null;
              const daysUntil = closeDate ? Math.round((closeDate - today) / 86400000) : null;
              return { lane, status, pct, closeDate, daysUntil, color: laneColors[lane] || t.accent, cfg: statusConfig[status] || statusConfig.idle };
            }).sort((a, b) => (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4));

            const launchDate = selectedProgram.launch_date ? new Date(selectedProgram.launch_date) : null;
            const daysToLaunch = launchDate ? Math.round((launchDate - today) / 86400000) : null;

            const blockedCount  = laneRows.filter(r => r.status === 'blocked').length;
            const atRiskCount   = laneRows.filter(r => r.status === 'at_risk').length;
            const onTrackCount  = laneRows.filter(r => r.status === 'on_track').length;
            const completeCount = laneRows.filter(r => r.status === 'complete').length;

            const allTasks = lanes.flatMap(lane => {
              const gateStatus = currentGateMilestones.find(g => g.lane === lane);
              return (gateStatus?.gate_milestones || []).map(m => ({ ...m, lane, color: laneColors[lane] || t.accent }));
            });
            const upcoming = allTasks
              .filter(m => m.due_date && !m.completed)
              .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
              .slice(0, 6);

            return (
              <div style={{ maxWidth: '860px' }}>
                {timelineSwitcher}

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: t.text, letterSpacing: '-0.3px', marginBottom: '6px' }}>
                      G{currentGateNum} · {gateName}
                    </div>
                    <div style={{ fontSize: '11px', color: t.dim, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {blockedCount > 0  && <span style={{ color: '#ef4444', fontWeight: '700' }}>{blockedCount} BLOCKED</span>}
                      {atRiskCount > 0   && <span style={{ color: '#f59e0b', fontWeight: '700' }}>{atRiskCount} AT RISK</span>}
                      {onTrackCount > 0  && <span style={{ color: '#10b981' }}>{onTrackCount} on track</span>}
                      {completeCount > 0 && <span style={{ color: t.dim }}>{completeCount} complete</span>}
                      {blockedCount === 0 && atRiskCount === 0 && onTrackCount === 0 && completeCount === 0 && <span>No gate data yet</span>}
                    </div>
                  </div>
                  {launchDate && daysToLaunch !== null && (
                    <div style={{ textAlign: 'right', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: '12px 18px' }}>
                      <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '4px' }}>DAYS TO LAUNCH</div>
                      <div style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '-1px', color: daysToLaunch < 0 ? '#ef4444' : daysToLaunch < 30 ? '#f59e0b' : t.accent, lineHeight: 1 }}>
                        {daysToLaunch < 0 ? `-${Math.abs(daysToLaunch)}` : `T-${daysToLaunch}`}
                      </div>
                      <div style={{ fontSize: '9px', color: t.dim, marginTop: '4px' }}>
                        {launchDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Lane rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
                  {laneRows.map(r => (
                    <div key={r.lane} style={{
                      border: `1px solid ${r.status === 'blocked' ? '#ef444430' : r.status === 'at_risk' ? '#f59e0b30' : t.borderDim}`,
                      borderLeft: `4px solid ${r.color}`,
                      borderRadius: '8px',
                      padding: '14px 16px',
                      backgroundColor: r.status === 'blocked' ? '#ef444406' : r.status === 'at_risk' ? '#f59e0b04' : 'transparent',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        <div style={{ width: '9px', height: '9px', transform: 'rotate(45deg)', backgroundColor: r.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '13px', fontWeight: '700', color: t.text, textTransform: 'capitalize', flex: 1 }}>{r.lane}</span>
                        <span style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', padding: '3px 9px', borderRadius: '4px', backgroundColor: r.cfg.bg, color: r.cfg.color, border: `1px solid ${r.cfg.border}` }}>
                          {r.cfg.label}
                        </span>
                        {r.closeDate ? (
                          <span style={{ fontSize: '11px', color: r.daysUntil !== null && r.daysUntil < 0 ? '#ef4444' : r.daysUntil !== null && r.daysUntil < 7 ? '#f59e0b' : t.dim, whiteSpace: 'nowrap', fontWeight: r.daysUntil !== null && r.daysUntil < 7 ? '700' : '400' }}>
                            {r.daysUntil === null ? '' : r.daysUntil < 0 ? `${Math.abs(r.daysUntil)}d overdue` : r.daysUntil === 0 ? 'Due today' : `${r.daysUntil}d`}
                            {' · '}{r.closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        ) : (
                          <span style={{ fontSize: '9px', color: '#ef4444', fontWeight: '700' }}>NO DATE</span>
                        )}
                      </div>
                      <div style={{ height: '6px', backgroundColor: darkMode ? '#1e293b' : '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${r.pct}%`,
                          backgroundColor: r.status === 'complete' ? '#10b981' : r.status === 'blocked' ? '#ef4444' : r.status === 'at_risk' ? '#f59e0b' : r.color,
                          borderRadius: '3px',
                          transition: 'width 0.4s ease',
                          minWidth: r.pct > 0 ? '6px' : '0',
                        }} />
                      </div>
                      <div style={{ fontSize: '9px', color: t.dim, marginTop: '5px' }}>{r.pct}% complete</div>
                    </div>
                  ))}
                </div>

                {/* Upcoming milestones with dates */}
                {upcoming.length > 0 && (
                  <div>
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '12px', fontWeight: '600' }}>UPCOMING MILESTONES</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {upcoming.map(m => {
                        const dDate = new Date(m.due_date);
                        const daysAway = Math.round((dDate - today) / 86400000);
                        return (
                          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', border: `1px solid ${t.borderDim}`, borderLeft: `3px solid ${m.color}`, borderRadius: '6px' }}>
                            <div style={{ width: '7px', height: '7px', transform: 'rotate(45deg)', backgroundColor: m.color, flexShrink: 0 }} />
                            <span style={{ fontSize: '11px', color: t.text, fontWeight: '600', flex: 1 }}>{m.text}</span>
                            <span style={{ fontSize: '10px', color: t.dim, textTransform: 'capitalize' }}>{m.lane}</span>
                            {m.dri && <span style={{ fontSize: '10px', color: t.dim }}>· {m.dri}</span>}
                            <span style={{ fontSize: '10px', fontWeight: '700', color: daysAway < 0 ? '#ef4444' : daysAway < 7 ? '#f59e0b' : t.dim, whiteSpace: 'nowrap' }}>
                              {daysAway < 0 ? `${Math.abs(daysAway)}d late` : daysAway === 0 ? 'Today' : dDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // ── ALL PHASES (existing Gantt) ──
          const projectedLaunchDate = consequences?.projected_launch ? new Date(consequences.projected_launch) : (plannedLaunch || fallbackLaunch);
          // (timelineSubTab === 'all' guaranteed here)
          const slipDays = consequences?.projected_slip_days || 0;
          const totalCostImpact = consequences?.total_cost_impact || 0;

          // Timeline spans from program start to projected end + small buffer
          const rawEnd = new Date(Math.max(fallbackLaunch.getTime(), (projectedLaunchDate || fallbackLaunch).getTime()));
          const buffer = (rawEnd - programStart) * 0.06;
          const timelineEnd = new Date(rawEnd.getTime() + buffer);
          const totalSpanMs = timelineEnd - programStart;

          const toPct = (d) => Math.max(0, Math.min(100, ((typeof d === 'string' ? parseLocalDate(d) : new Date(d)) - programStart) / totalSpanMs * 100));
          const launchPct = toPct(plannedLaunch);
          const projectedPct = toPct(projectedLaunchDate);
          const todayLinePct = toPct(today);

          // Gate boundary positions from design lane planned_close_dates
          const gateBoundaries = allGates
            .filter(g => g.lane === 'design' && g.planned_close_date)
            .sort((a, b) => a.gate_number - b.gate_number)
            .map(g => ({ gate: g.gate_number, pct: toPct(g.planned_close_date) }));

          return (
            <div>
              {timelineSwitcher}
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '32px' }}>
                <div style={s.consCard}>
                  <div style={s.consLabel}>ORIGINAL LAUNCH</div>
                  <div style={{ ...s.consValue, fontSize: '15px' }}>{selectedProgram.launch_date}</div>
                </div>
                <div style={s.consCard}>
                  <div style={s.consLabel}>PROJECTED LAUNCH</div>
                  <div style={{ ...s.consValue, fontSize: '15px', color: slipDays > 0 ? '#ef4444' : '#10b981' }}>
                    {consequences?.projected_launch || selectedProgram.launch_date}
                  </div>
                </div>
                <div style={s.consCard}>
                  <div style={s.consLabel}>TOTAL SLIP</div>
                  <div style={{ ...s.consValue, fontSize: '22px', color: slipDays > 0 ? '#ef4444' : '#10b981' }}>
                    {slipDays > 0 ? `+${slipDays}d` : 'ON TRACK'}
                  </div>
                </div>
                <div style={s.consCard}>
                  <div style={s.consLabel}>COST VARIANCE</div>
                  <div style={{ ...s.consValue, fontSize: '22px', color: totalCostImpact > 0 ? '#f59e0b' : '#10b981' }}>
                    {totalCostImpact > 0 ? `+${fmt(totalCostImpact)}` : '$0'}
                  </div>
                </div>
              </div>

              {/* Situation bar */}
              {(() => {
                const delayedLanes = lanes
                  .map(lane => ({ lane, gates: allGates.filter(g => g.lane === lane) }))
                  .filter(({ gates }) => gates.some(g => g.days_delayed > 0 || g.status === 'blocked'))
                  .map(({ lane, gates }) => ({
                    lane,
                    totalDelay: gates.reduce((s, g) => s + (g.days_delayed || 0), 0),
                    totalCost: gates.reduce((s, g) => s + (g.cost_impact || 0), 0),
                    worstGate: gates.find(g => g.status === 'blocked') || gates.find(g => g.days_delayed > 0),
                  }));
                const daysLeft = Math.ceil((plannedLaunch - today) / (1000 * 60 * 60 * 24));
                const severity = slipDays > 0 ? '#ef4444' : '#10b981';
                return (
                  <div style={{ backgroundColor: severity + '10', border: `1px solid ${severity}30`, borderRadius: '6px', padding: '14px 16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: delayedLanes.length > 0 ? '10px' : 0 }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: severity, flexShrink: 0 }} />
                      <span style={{ fontSize: '10px', color: severity, letterSpacing: '2px', fontWeight: 'bold' }}>
                        {slipDays > 0
                          ? `SLIPPING +${slipDays}d — projected launch ${consequences?.projected_launch}`
                          : `ON TRACK — ${daysLeft > 0 ? `${daysLeft}d to planned launch` : 'launch date reached'}`
                        }
                      </span>
                    </div>
                    {delayedLanes.length > 0 && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                          {delayedLanes.map(({ lane, totalDelay, totalCost, worstGate }) => (
                            <div key={lane} style={{ fontSize: '11px', color: t.cascadeText, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{ color: '#ef4444', flexShrink: 0, fontWeight: 'bold' }}>→</span>
                              <span>
                                <span
                                  style={{ color: t.accent, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', fontWeight: 'bold' }}
                                  onClick={() => worstGate && openEdit(worstGate)}>
                                  {lane.toUpperCase()}
                                </span>
                                {totalDelay > 0 && ` — +${totalDelay}d delay`}
                                {totalCost > 0 && ` · ${fmt(totalCost)} cost impact`}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop: `1px solid ${severity}20`, paddingTop: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', flexShrink: 0, marginTop: '2px' }}>NEXT ACTION</span>
                          <span style={{ fontSize: '11px', color: t.text }}>
                            Resolve delay in{' '}
                            <span
                              style={{ color: t.accent, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                              onClick={() => delayedLanes[0]?.worstGate && openEdit(delayedLanes[0].worstGate)}>
                              {delayedLanes[0]?.lane.toUpperCase()}
                            </span>
                            {delayedLanes[0]?.totalDelay > 0 && ` to recover ${delayedLanes[0].totalDelay}d`}
                            {delayedLanes[0]?.totalCost > 0 && ` and prevent ${fmt(delayedLanes[0].totalCost)} in cost impact`}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Gantt chart — scrolls horizontally on mobile */}
              {(() => {
                // Build monthly tick marks across the timeline
                const monthTicks = [];
                const cur = new Date(programStart);
                cur.setDate(1);
                cur.setMonth(cur.getMonth() + 1);
                while (cur < timelineEnd) {
                  monthTicks.push(new Date(cur));
                  cur.setMonth(cur.getMonth() + 1);
                }
                const ROW_H = 80;
                const HEADER_H = 36;
                const statusOrder = { blocked: 0, at_risk: 1, idle: 2, on_track: 3, ready: 4, complete: 5 };

                return (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ minWidth: '600px' }}>

                  {/* HEADER ROW: title + legend */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', color: t.dim, letterSpacing: '3px', fontWeight: 'bold' }}>GANTT — PLANNED VS PROJECTED</div>
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {[
                        { color: '#10b981', label: 'COMPLETE' },
                        { color: '#22d3ee', label: 'ON TRACK' },
                        { color: '#f59e0b', label: 'AT RISK' },
                        { color: '#ef4444', label: 'BLOCKED' },
                      ].map(({ color, label }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ width: '12px', height: '9px', backgroundColor: color + '25', border: `1px solid ${color}70`, borderRadius: '2px' }} />
                          <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '1px' }}>{label}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '2px', height: '12px', backgroundColor: t.accent }} />
                        <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '1px' }}>TODAY</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex' }}>

                    {/* Lane labels column */}
                    <div style={{ width: '150px', flexShrink: 0 }}>
                      {/* Spacer for date axis */}
                      <div style={{ height: HEADER_H }} />
                      {lanes.map(lane => {
                        const lg = allGates.filter(g => g.lane === lane);
                        const worstStatus = lg.reduce((w, g) => (statusOrder[g.status] ?? 2) < (statusOrder[w] ?? 2) ? g.status : w, 'idle');
                        const totalDelay = lg.reduce((s, g) => s + (g.days_delayed || 0), 0);
                        const totalCost  = lg.reduce((s, g) => s + (g.cost_impact  || 0), 0);
                        const statusColor = getStatusColor(worstStatus);
                        return (
                          <div key={lane} style={{ height: ROW_H, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingRight: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
                              <span style={{ fontSize: '11px', color: t.muted, letterSpacing: '1px', fontWeight: worstStatus === 'blocked' || worstStatus === 'at_risk' ? 'bold' : 'normal' }}>
                                {lane.toUpperCase()}
                              </span>
                            </div>
                            {totalDelay > 0 && (
                              <div style={{ fontSize: '9px', color: '#ef4444', marginLeft: '14px', marginTop: '3px', letterSpacing: '0.5px' }}>+{totalDelay}d slip</div>
                            )}
                            {totalCost > 0 && (
                              <div style={{ fontSize: '9px', color: '#f59e0b', marginLeft: '14px', marginTop: '1px', letterSpacing: '0.5px' }}>{fmt(totalCost)}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Bar area */}
                    <div style={{ flex: 1, position: 'relative' }}>

                      {/* Vertical lines spanning full chart height — gate boundaries + launch + today */}
                      {gateBoundaries.map(gb => (
                        <div key={gb.gate} style={{ position: 'absolute', left: `${gb.pct}%`, top: 0, bottom: 0, width: '1px', borderLeft: `1px dashed ${t.borderDim}`, zIndex: 1, pointerEvents: 'none' }} />
                      ))}
                      <div style={{ position: 'absolute', left: `${launchPct}%`, top: 0, bottom: 0, width: '2px', backgroundColor: '#10b981', opacity: 0.4, zIndex: 2, pointerEvents: 'none' }} />
                      {slipDays > 0 && (
                        <div style={{ position: 'absolute', left: `${projectedPct}%`, top: 0, bottom: 0, width: '2px', backgroundColor: '#ef4444', opacity: 0.7, zIndex: 2, pointerEvents: 'none' }} />
                      )}
                      {todayLinePct > 0 && todayLinePct < 100 && (
                        <div style={{ position: 'absolute', left: `${todayLinePct}%`, top: 0, bottom: 0, width: '2px', backgroundColor: t.accent, zIndex: 3, pointerEvents: 'none', boxShadow: `0 0 8px ${t.accent}88` }} />
                      )}

                      {/* Date axis */}
                      <div style={{ height: HEADER_H, position: 'relative', borderBottom: `1px solid ${t.borderDim}`, marginBottom: '0' }}>
                        {/* Gate labels in header */}
                        {gateBoundaries.map(gb => (
                          <div key={gb.gate} style={{ position: 'absolute', left: `${gb.pct}%`, bottom: '6px', transform: 'translateX(-50%)', fontSize: '8px', color: t.subLabel, letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                            G{gb.gate}
                          </div>
                        ))}
                        {/* Month ticks */}
                        {monthTicks.map(d => {
                          const pct = toPct(d);
                          if (pct < 1 || pct > 99) return null;
                          const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).toUpperCase().replace(' ', " '");
                          return (
                            <div key={d.toISOString()} style={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, pointerEvents: 'none' }}>
                              <div style={{ position: 'absolute', top: 0, left: 0, width: '1px', height: '6px', backgroundColor: t.borderDim }} />
                              <div style={{ position: 'absolute', top: '8px', left: '3px', fontSize: '8px', color: t.subLabel, whiteSpace: 'nowrap', letterSpacing: '0.3px' }}>{label}</div>
                            </div>
                          );
                        })}
                        {/* TODAY label in header */}
                        {todayLinePct > 0 && todayLinePct < 100 && (
                          <div style={{ position: 'absolute', left: `${todayLinePct}%`, bottom: '18px', transform: 'translateX(-50%)', fontSize: '8px', color: t.accent, letterSpacing: '1px', whiteSpace: 'nowrap', fontWeight: 'bold' }}>TODAY</div>
                        )}
                        {/* PROJECTED label in header */}
                        {slipDays > 0 && (
                          <div style={{ position: 'absolute', left: `${projectedPct}%`, bottom: '6px', transform: 'translateX(-50%)', fontSize: '8px', color: '#ef4444', letterSpacing: '1px', whiteSpace: 'nowrap' }}>+{slipDays}d</div>
                        )}
                      </div>

                      {/* Lane bar rows — gate segment approach */}
                      {lanes.map(lane => {
                        const lg = allGates.filter(g => g.lane === lane).sort((a, b) => a.gate_number - b.gate_number);

                        // Build segment boundaries: gate N spans from prev gate's close → this gate's close
                        const getSegBounds = (gateNum) => {
                          const prev = lg.find(g => g.gate_number === gateNum - 1);
                          const cur  = lg.find(g => g.gate_number === gateNum);
                          const startPct = gateNum === 1
                            ? 0
                            : prev?.planned_close_date
                              ? toPct(prev.planned_close_date)
                              : ((gateNum - 1) / 6) * launchPct;
                          const endPct = cur?.planned_close_date
                            ? toPct(cur.planned_close_date)
                            : (gateNum / 6) * launchPct;
                          return { startPct, endPct, width: Math.max(0, endPct - startPct), gate: cur };
                        };

                        return (
                          <div key={lane} style={{ height: ROW_H, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${t.borderDimmer}` }}>
                            <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                              <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 0, right: 0, height: '38px' }}>
                                {[1,2,3,4,5,6].map(gateNum => {
                                  const { startPct, width, gate } = getSegBounds(gateNum);
                                  if (width <= 0) return null;
                                  const status = gate?.status || 'idle';
                                  const pct    = gate?.completion_percent || 0;
                                  const inWindow = todayLinePct >= startPct && todayLinePct < (startPct + width);
                                  const hasActual = gate?.completed_at;
                                  const actualRelPct = hasActual ? Math.min(100, Math.max(0, ((toPct(gate.completed_at) - startPct) / width) * 100)) : null;
                                  const isLate = hasActual && new Date(gate.completed_at) > new Date(gate.planned_close_date);

                                  let bg, border, fg;
                                  if (status === 'complete')       { bg = '#10b98118'; border = '#10b98180'; fg = '#10b981'; }
                                  else if (status === 'blocked')   { bg = '#ef444418'; border = '#ef444480'; fg = '#ef4444'; }
                                  else if (status === 'at_risk')   { bg = '#f59e0b18'; border = '#f59e0b80'; fg = '#f59e0b'; }
                                  else if (status === 'on_track')  { bg = '#22d3ee18'; border = '#22d3ee80'; fg = '#22d3ee'; }
                                  else if (inWindow)               { bg = t.accent + '14'; border = t.accent + '70'; fg = t.accent; }
                                  else                             { bg = t.borderDimmer; border = t.borderDim; fg = t.subLabel; }

                                  return (
                                    <div
                                      key={gateNum}
                                      onClick={() => gate && openEdit(gate)}
                                      title={gate ? `G${gateNum} — ${status.replace('_',' ')}${pct > 0 ? ` · ${pct}%` : ''}` : `G${gateNum}`}
                                      style={{
                                        position: 'absolute',
                                        left: `${startPct}%`,
                                        width: `calc(${width}% - 3px)`,
                                        height: '100%',
                                        backgroundColor: bg,
                                        border: `1px solid ${border}`,
                                        borderRadius: '4px',
                                        cursor: gate ? 'pointer' : 'default',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        paddingLeft: '6px',
                                        boxSizing: 'border-box',
                                        transition: 'background-color 0.15s',
                                      }}
                                    >
                                      {/* Completion fill at bottom */}
                                      {pct > 0 && (
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${pct}%`, height: '3px', backgroundColor: border }} />
                                      )}
                                      {/* Actual completion dot */}
                                      {hasActual && (
                                        <div
                                          title={`Closed ${gate.completed_at.split('T')[0]}${isLate ? ' (late)' : ' (on time)'}`}
                                          style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: isLate ? '#ef4444' : '#10b981', border: '1.5px solid white', zIndex: 5 }}
                                        />
                                      )}
                                      {/* Gate label */}
                                      <div style={{ fontSize: '9px', color: fg, fontWeight: 'bold', letterSpacing: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>G{gateNum}</div>
                                      {/* Status or % */}
                                      {pct > 0 ? (
                                        <div style={{ fontSize: '8px', color: fg, opacity: 0.85, marginTop: '2px', letterSpacing: '0.3px' }}>{pct}%</div>
                                      ) : status !== 'idle' ? (
                                        <div style={{ fontSize: '8px', color: fg, opacity: 0.85, marginTop: '2px', letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {status.replace('_',' ')}
                                        </div>
                                      ) : inWindow ? (
                                        <div style={{ fontSize: '8px', color: fg, opacity: 0.7, marginTop: '2px', letterSpacing: '0.3px' }}>active</div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                  </div>
                </div>
                </div>
                );
              })()}

              {/* Bottom legend */}
              <div style={{ marginTop: '28px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { color: t.accent,   label: 'TODAY / ACTIVE GATE',   type: 'line' },
                  { color: '#10b981',  label: 'PLANNED LAUNCH',        type: 'line' },
                  { color: '#10b981',  label: 'COMPLETE',              type: 'box' },
                  { color: '#22d3ee',  label: 'ON TRACK',              type: 'box' },
                  { color: '#f59e0b',  label: 'AT RISK',               type: 'box' },
                  { color: '#ef4444',  label: 'BLOCKED',               type: 'box' },
                  { color: t.borderDim, label: 'FUTURE',               type: 'box' },
                ].concat(slipDays > 0 ? [{ color: '#ef4444', label: `PROJECTED (+${slipDays}d)`, type: 'line' }] : []).map(({ color, label, type }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {type === 'line'
                      ? <div style={{ width: '18px', height: '2px', backgroundColor: color, opacity: color === t.borderDim ? 1 : 0.8 }} />
                      : <div style={{ width: '14px', height: '10px', backgroundColor: color + '25', border: `1px solid ${color}70`, borderRadius: '2px' }} />
                    }
                    <span style={{ fontSize: '8px', color: t.dim, letterSpacing: '1px' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── CONSEQUENCES TAB ── */}
        {activeTab === 'consequences' && consequences && (() => {
          const currentGateNum = selectedProgram.current_gate || 1;
          // Only show cascades relevant to the current horizon:
          // source must be at or before current gate, target within 2 gates ahead
          const rawCascadeMap  = consequences.cascade_map || [];
          const cascadeMap     = rawCascadeMap.filter(c =>
            c.sourceGate <= currentGateNum && c.targetGate <= currentGateNum + 2
          );
          const peakDelay      = consequences.peak_delay || 0;
          const activeCascades = cascadeMap.length;
          const statusOrder   = { blocked: 0, at_risk: 1, idle: 2, on_track: 3, ready: 4, complete: 5 };

          return (
          <div style={s.consequences}>

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '32px' }}>
              {[
                { label: 'PEAK DELAY',        value: peakDelay > 0 ? `+${peakDelay}d` : 'NONE',              sub: 'Largest single-gate slip',       color: peakDelay > 0 ? '#ef4444' : '#10b981' },
                { label: 'TOTAL COST CASCADE', value: fmt(consequences.total_cost_impact || 0),              sub: 'Across all affected gates',       color: consequences.total_cost_impact > 0 ? '#f59e0b' : '#10b981' },
                { label: 'ACTIVE CASCADES',   value: String(activeCascades),                                 sub: 'Live dependency violations',      color: activeCascades > 0 ? '#ef4444' : '#10b981' },
                { label: 'T-MINUS',           value: drDays > 0 ? `${drDays}d` : 'PAST DUE',               sub: `Launch ${selectedProgram.launch_date}`, color: drDays < 60 ? '#ef4444' : t.accent },
              ].map(({ label, value, sub, color }) => (
                <div key={label} style={s.consCard}>
                  <div style={s.consLabel}>{label}</div>
                  <div style={{ ...s.consValue, color, fontSize: isMobile ? '20px' : '24px' }}>{value}</div>
                  <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '6px' }}>{sub}</div>
                </div>
              ))}
            </div>

            {/* CASCADE MAP */}
            <div style={{ marginBottom: '36px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={s.consLabel}>CASCADE MAP</div>
                  <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px' }}>Showing G{currentGateNum}–G{Math.min(currentGateNum + 2, 6)} horizon · {rawCascadeMap.length - cascadeMap.length > 0 ? `${rawCascadeMap.length - cascadeMap.length} later-phase cascade${rawCascadeMap.length - cascadeMap.length > 1 ? 's' : ''} hidden` : 'All relevant cascades shown'}</div>
                </div>
                <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>SOURCE → TARGET</div>
              </div>

              {cascadeMap.length === 0 ? (
                <div style={{ backgroundColor: '#10b98110', border: '1px solid #10b98130', borderRadius: '8px', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                  <span style={{ fontSize: '12px', color: '#10b981' }}>No active dependency violations — all lanes clear</span>
                </div>
              ) : (
                cascadeMap.map((c, i) => {
                  const sevColor = c.severity === 'hard' ? '#ef4444' : '#f59e0b';
                  const sevLabel = c.severity === 'hard' ? 'HIGH' : 'MED';
                  const srcGate  = selectedProgram.gates?.find(g => g.lane === c.sourceLane && g.gate_number === c.sourceGate);
                  const tgtGate  = selectedProgram.gates?.find(g => g.lane === c.targetLane && g.gate_number === c.targetGate);
                  return (
                    <div key={i} style={{ backgroundColor: t.surface, border: `1px solid ${sevColor}25`, borderLeft: `3px solid ${sevColor}`, borderRadius: '8px', padding: '16px 18px', marginBottom: '10px' }}>

                      {/* Source → Target row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>

                        {/* Source gate box — the blocker */}
                        <div
                          title="Click to open gate"
                          style={{ backgroundColor: '#ef444412', border: '1px solid #ef444440', borderRadius: '6px', padding: '8px 14px', cursor: srcGate ? 'pointer' : 'default', flexShrink: 0, minWidth: '80px' }}
                          onClick={() => srcGate && openEdit(srcGate)}>
                          <div style={{ fontSize: '8px', color: '#ef444490', letterSpacing: '1px', marginBottom: '3px' }}>SOURCE · G{c.sourceGate}</div>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#ef4444' }}>{c.sourceLane.toUpperCase()}</div>
                        </div>

                        {/* Arrow */}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '32px', position: 'relative' }}>
                          <div style={{ flex: 1, height: '1px', backgroundColor: sevColor + '50' }} />
                          <div style={{ color: sevColor, fontSize: '10px', marginLeft: '-1px' }}>▶</div>
                        </div>

                        {/* Target gate box — the blocked */}
                        <div
                          title="Click to open gate"
                          style={{ backgroundColor: t.accent + '10', border: `1px solid ${t.accent}35`, borderRadius: '6px', padding: '8px 14px', cursor: tgtGate ? 'pointer' : 'default', flexShrink: 0, minWidth: '80px' }}
                          onClick={() => tgtGate && openEdit(tgtGate)}>
                          <div style={{ fontSize: '8px', color: t.accent + '80', letterSpacing: '1px', marginBottom: '3px' }}>TARGET · G{c.targetGate}</div>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: t.accent }}>{c.targetLane.toUpperCase()}</div>
                        </div>

                        {/* Badges */}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {c.slipDays > 0 && (
                            <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>+{c.slipDays}d</span>
                          )}
                          {c.costImpact > 0 && (
                            <span style={{ backgroundColor: '#f59e0b18', border: '1px solid #f59e0b40', borderRadius: '4px', padding: '3px 9px', fontSize: '11px', color: '#f59e0b', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>+{fmt(c.costImpact)}</span>
                          )}
                          <span style={{ backgroundColor: sevColor + '18', border: `1px solid ${sevColor}50`, borderRadius: '4px', padding: '3px 9px', fontSize: '9px', color: sevColor, fontWeight: 'bold', letterSpacing: '1px' }}>{sevLabel}</span>
                        </div>
                      </div>

                      {/* Reason */}
                      <div style={{ fontSize: '11px', color: t.muted, lineHeight: 1.6, borderTop: `1px solid ${t.borderDimmer}`, paddingTop: '10px', marginBottom: '10px' }}>{c.reason}</div>

                      {/* GET RESOLUTION */}
                      {(() => {
                        const key = `${c.sourceLane}-${c.sourceGate}-${c.targetLane}-${c.targetGate}`;
                        const res = resolutions[key];
                        const isLoading = resolutionLoading[key];
                        const isExpanded = expandedResolution === key;
                        const riskColor = { low: '#10b981', med: '#f59e0b', high: '#ef4444' };
                        return (
                          <div>
                            <button
                              style={{ ...s.ghostBtn, fontSize: '9px', padding: '4px 12px', color: t.accent, borderColor: t.accent + '50' }}
                              disabled={isLoading}
                              onClick={() => {
                                if (!res && !isLoading) fetchResolution(key, c);
                                else setExpandedResolution(isExpanded ? null : key);
                              }}>
                              {isLoading ? 'ANALYZING...' : isExpanded ? 'HIDE RESOLUTION ▲' : 'GET RESOLUTION ▼'}
                            </button>

                            {isExpanded && res && (
                              <div style={{ marginTop: '12px', padding: '14px 16px', backgroundColor: t.bg, border: `1px solid ${t.borderDim}`, borderRadius: '6px' }}>

                                {/* 3 Options */}
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
                                  {(res.options || []).map((opt, oi) => (
                                    <div key={oi} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '12px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ fontSize: '10px', color: t.text, fontWeight: 'bold' }}>
                                          {['①', '②', '③'][oi]} {opt.title}
                                        </div>
                                        <span style={{ fontSize: '8px', color: riskColor[opt.riskLevel] || t.muted, letterSpacing: '1px', fontWeight: 'bold', flexShrink: 0, marginLeft: '6px' }}>
                                          {(opt.riskLevel || 'MED').toUpperCase()}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: '11px', color: t.cascadeText, lineHeight: 1.5, marginBottom: '8px' }}>{opt.action}</div>
                                      <div style={{ fontSize: '9px', color: t.subLabel, marginBottom: '4px' }}>⏱ {opt.timeToResolve}</div>
                                      <div style={{ fontSize: '9px', color: '#f59e0b90', fontStyle: 'italic' }}>↔ {opt.tradeoff}</div>
                                    </div>
                                  ))}
                                </div>

                                {/* Nomos recommendation */}
                                {res.recommendation && (
                                  <div style={{ backgroundColor: t.accent + '10', border: `1px solid ${t.accent}30`, borderRadius: '6px', padding: '10px 14px', marginBottom: '10px' }}>
                                    <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', marginBottom: '5px' }}>NOMOS RECOMMENDS</div>
                                    <div style={{ fontSize: '11px', color: t.cascadeText, lineHeight: 1.5 }}>{res.recommendation}</div>
                                  </div>
                                )}

                                {/* Historical insight */}
                                {res.historicalInsight && (
                                  <div style={{ backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '10px 14px' }}>
                                    <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', marginBottom: '5px' }}>HISTORICAL INSIGHT</div>
                                    <div style={{ fontSize: '11px', color: t.muted, lineHeight: 1.5 }}>{res.historicalInsight}</div>
                                  </div>
                                )}

                              </div>
                            )}
                          </div>
                        );
                      })()}

                    </div>
                  );
                })
              )}
            </div>

            {/* FORWARD VIOLATIONS — projected future blockages */}
            {(() => {
              const fwdViolations = (consequences.forward_violations || []).filter(
                v => v.requiresGate <= currentGateNum + 2 && v.dependentGate <= currentGateNum + 2
              );
              if (fwdViolations.length === 0) return null;
              return (
                <div style={{ marginBottom: '36px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <div style={s.consLabel}>PROJECTED VIOLATIONS</div>
                      <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '3px' }}>Dependencies that will fail at current pace — no human input yet</div>
                    </div>
                    <div style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '2px', fontWeight: 'bold' }}>⚠ INCOMING</div>
                  </div>
                  {fwdViolations.map((v, i) => {
                    const reqGate = selectedProgram.gates?.find(g => g.lane === v.requiresLane && g.gate_number === v.requiresGate);
                    const depGate = selectedProgram.gates?.find(g => g.lane === v.dependentLane && g.gate_number === v.dependentGate);
                    const pctGap = v.requiredPct - v.projectedPct;
                    return (
                      <div key={i} style={{ backgroundColor: t.surface, border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '8px', padding: '14px 16px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                          <div
                            onClick={() => reqGate && openEdit(reqGate)}
                            style={{ backgroundColor: '#f59e0b12', border: '1px solid #f59e0b40', borderRadius: '6px', padding: '8px 14px', cursor: reqGate ? 'pointer' : 'default', flexShrink: 0, minWidth: '80px' }}>
                            <div style={{ fontSize: '8px', color: '#f59e0b90', letterSpacing: '1px', marginBottom: '3px' }}>SOURCE · G{v.requiresGate}</div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#f59e0b' }}>{v.requiresLane.toUpperCase()}</div>
                          </div>
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '32px' }}>
                            <div style={{ flex: 1, height: '1px', backgroundColor: '#f59e0b40', borderTop: '1px dashed #f59e0b40' }} />
                            <div style={{ color: '#f59e0b', fontSize: '10px' }}>▶</div>
                          </div>
                          <div
                            onClick={() => depGate && openEdit(depGate)}
                            style={{ backgroundColor: t.accent + '10', border: `1px solid ${t.accent}35`, borderRadius: '6px', padding: '8px 14px', cursor: depGate ? 'pointer' : 'default', flexShrink: 0, minWidth: '80px' }}>
                            <div style={{ fontSize: '8px', color: t.accent + '80', letterSpacing: '1px', marginBottom: '3px' }}>TARGET · G{v.dependentGate}</div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: t.accent }}>{v.dependentLane.toUpperCase()}</div>
                          </div>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 'bold' }}>{v.currentPct}% → {v.projectedPct}%</span>
                            <span style={{ backgroundColor: '#f59e0b18', border: '1px solid #f59e0b40', borderRadius: '4px', padding: '3px 9px', fontSize: '9px', color: '#f59e0b', letterSpacing: '1px', fontWeight: 'bold' }}>NEEDS {v.requiredPct}%</span>
                            <span style={{ backgroundColor: '#ef444415', border: '1px solid #ef444430', borderRadius: '4px', padding: '3px 9px', fontSize: '9px', color: '#ef4444', letterSpacing: '1px' }}>−{pctGap}pts short</span>
                          </div>
                        </div>
                        <div style={{ fontSize: '11px', color: t.muted, lineHeight: 1.6, borderTop: `1px solid ${t.borderDimmer}`, paddingTop: '10px' }}>
                          {v.message} <span style={{ color: t.subLabel }}>Close window: ~{v.daysUntilClose}d</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Impact by Team — card grid */}
            <div style={{ marginBottom: '36px' }}>
              <div style={{ ...s.consLabel, marginBottom: '14px' }}>IMPACT BY TEAM</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '10px' }}>
                {lanes.map(lane => {
                  const lg = selectedProgram.gates?.filter(g => g.lane === lane) || [];
                  const worstStatus = lg.reduce((w, g) => (statusOrder[g.status] ?? 2) < (statusOrder[w] ?? 2) ? g.status : w, 'idle');
                  const totalDelay  = lg.reduce((s, g) => s + (g.days_delayed || 0), 0);
                  const totalCost   = lg.reduce((s, g) => s + (g.cost_impact  || 0), 0);
                  const statusColor = getStatusColor(worstStatus);
                  const isClear     = totalCost === 0 && totalDelay === 0;
                  return (
                    <div key={lane}
                      onClick={() => setConsDetailPanel({ type: 'team', lane })}
                      style={{ backgroundColor: t.surface, border: `1px solid ${isClear ? t.borderDim : statusColor + '35'}`, borderTop: `3px solid ${isClear ? t.borderDim : statusColor}`, borderRadius: '6px', padding: '14px 12px', cursor: 'pointer', transition: 'opacity 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                      <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '10px' }}>{lane.toUpperCase()}</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 'bold', color: isClear ? '#10b981' : '#f59e0b', marginBottom: '4px', fontFamily: "'Roboto Mono', monospace" }}>
                        {isClear ? 'Clear' : fmt(totalCost)}
                      </div>
                      <div style={{ fontSize: '10px', color: isClear ? t.subLabel : '#ef4444', marginBottom: '8px' }}>
                        {isClear ? 'No impact' : totalDelay > 0 ? `+${totalDelay}d schedule slip` : 'Cost only'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '9px', color: statusColor, fontWeight: 'bold', letterSpacing: '1px' }}>{worstStatus.toUpperCase().replace('_', ' ')}</div>
                        <div style={{ fontSize: '9px', color: t.subLabel }}>↗</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Financial consequence model */}
            {(fm.total_consequence > 0 || selectedProgram.num_locations > 0) && (
              <div style={{ marginBottom: '32px' }}>
                <div style={s.consLabel}>FINANCIAL CONSEQUENCE MODEL</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px', marginTop: '12px' }}>
                  {[
                    { key: 'stranded_assets', label: 'STRANDED ASSETS', value: fm.stranded_assets || 0, color: fm.stranded_assets > 0 ? '#ef4444' : null, sub: `${fm.launched_locations || 0} locations × ${selectedProgram.units_per_location || 2} units × $${(selectedProgram.unit_cost || 0).toLocaleString()}` },
                    { key: 'retrofit_cost',   label: 'RETROFIT COST',   value: fm.retrofit_cost   || 0, color: fm.retrofit_cost   > 0 ? '#f59e0b' : null, sub: `${fm.launched_locations || 0} locations × $${(selectedProgram.retrofit_cost_per_location || 750).toLocaleString()}/loc` },
                    { key: 'revenue_impact',  label: 'REVENUE IMPACT',  value: fm.revenue_impact  || 0, color: fm.revenue_impact  > 0 ? '#f59e0b' : null, sub: `${consequences.projected_slip_days || 0}d slip × ${selectedProgram.expected_uplift_percent || 0}% uplift` },
                    { key: 'total',           label: 'TOTAL CONSEQUENCE', value: fm.total_consequence || 0, color: fm.total_consequence > 0 ? '#ef4444' : '#10b981', sub: fm.total_consequence > 0 ? 'preventable with on-time launch' : 'no active exposure', bold: true },
                  ].map(({ key, label, value, color, sub, bold }) => (
                    <div key={key}
                      onClick={() => setConsDetailPanel({ type: 'finance', metric: key })}
                      style={{ ...s.consCard, borderColor: color || t.borderDim, borderWidth: bold && value > 0 ? '2px' : '1px', cursor: 'pointer', transition: 'opacity 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                      <div style={s.consLabel}>{label}</div>
                      <div style={{ ...s.consValue, fontSize: '16px', color: color || t.muted }}>{fmt(value)}</div>
                      <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '4px' }}>{sub}</div>
                      <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '6px' }}>↗ details</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
          );
        })()}

        {/* ── ASK NOMOS TAB ── */}
        {activeTab === 'ask' && (
          <div style={{ padding: '24px 0', maxWidth: '700px' }}>

            {/* AI Inception Analysis — admins only */}
            {!(userRole === 'member' && userLane) && (
              <div style={{ marginBottom: '32px' }}>
                <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '2px', marginBottom: '12px' }}>
                  NOMOS PROGRAM ANALYSIS
                </div>
                <button
                  style={{ ...s.saveBtn, width: 'auto', padding: '10px 24px', marginTop: 0, opacity: inceptionLoading ? 0.5 : 1 }}
                  onClick={runInception} disabled={inceptionLoading}>
                  {inceptionLoading ? 'ANALYZING...' : 'RUN FULL PROGRAM ANALYSIS'}
                </button>
                <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '6px', letterSpacing: '1px' }}>
                  Certifications · Dependencies · Financial exposure · Immediate actions
                </div>
                {inceptionAnalysis && (
                  <div style={{ ...s.aiBox, marginTop: '16px' }}>
                    <div style={s.aiLabel}>NOMOS ANALYSIS</div>
                    {inceptionAnalysis.replace(/\*\*/g, '')}
                  </div>
                )}
              </div>
            )}

            {/* Ask anything */}
            <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '2px', marginBottom: '12px' }}>
              {(userRole === 'member' && userLane) ? `ASK NOMOS — ${userLane.toUpperCase()} FOCUSED` : 'ASK ANYTHING ABOUT THIS PROGRAM'}
            </div>

            {/* Suggested questions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
              {((userRole === 'member' && userLane) ? [
                `What's the status of ${userLane}'s tasks?`,
                `What is ${userLane} blocking on right now?`,
                `What does ${userLane} need to complete before the next gate?`,
                `Are there any dependencies other teams have on ${userLane}?`,
                `What should ${userLane} prioritize this week?`,
                `Who should ${userLane} follow up with today?`,
              ] : [
                "What's the biggest risk to our launch date?",
                "Which lane is furthest behind?",
                "What tasks are blocking us right now?",
                "Who has the most open tasks?",
                "Are we on track for mass production?",
                "What should I escalate today?",
              ]).map(q => (
                <button key={q} onClick={() => askNomos(q)} disabled={aiLoading}
                  style={{ background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '20px', padding: '5px 12px', fontSize: '10px', color: t.dim, cursor: aiLoading ? 'default' : 'pointer', fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.3px', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => { if (!aiLoading) e.target.style.borderColor = t.accent; e.target.style.color = t.accent; }}
                  onMouseLeave={e => { e.target.style.borderColor = t.borderDim; e.target.style.color = t.dim; }}>
                  {q}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                style={{ flex: 1, background: t.askInputBg, border: `1px solid ${t.border}`, color: t.text, padding: '10px 14px', fontSize: '13px', fontFamily: "'Roboto Mono', monospace", outline: 'none', borderRadius: '4px' }}
                placeholder={(userRole === 'member' && userLane) ? `Ask about ${userLane}'s work, blockers, or next steps…` : 'Ask Nomos anything about this launch…'}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && askNomos()}
              />
              <MicBtn fieldId="ask-nomos" value={question} onSet={setQuestion} {...micP} />
              <button
                style={{ background: aiLoading ? t.askBtnDisBg : t.askBtnBg, color: t.askBtnColor, border: 'none', padding: '10px 20px', fontSize: '12px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace", cursor: aiLoading ? 'default' : 'pointer', letterSpacing: '0.05em', borderRadius: '4px' }}
                onClick={() => askNomos()} disabled={aiLoading}>
                {aiLoading ? '...' : 'ASK'}
              </button>
            </div>
            {aiLoading && (
              <div style={{ color: t.subLabel, fontSize: '11px', letterSpacing: '1px', marginTop: '12px' }}>Nomos is thinking...</div>
            )}
            {aiAnswer && (
              <div style={{ ...s.aiBox, marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={s.aiLabel}>NOMOS</div>
                  <button onClick={() => { setAiAnswer(''); setQuestion(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.subLabel, fontSize: '11px', fontFamily: "'Roboto Mono', monospace" }}>clear</button>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{aiAnswer.replace(/\*\*/g, '')}</div>
              </div>
            )}
          </div>
        )}

        {/* ── EXEC SUMMARY TAB ── */}
        {activeTab === 'exec' && (() => {
          const allGates = selectedProgram.gates || [];
          const slipDays = consequences?.projected_slip_days || 0;
          const doneCount = allGates.filter(g => g.status === 'complete').length;
          const overallPct = allGates.length ? Math.round(allGates.reduce((s, g) => s + g.completion_percent, 0) / allGates.length) : 0;
          const statusOrder = { blocked: 0, at_risk: 1, idle: 2, on_track: 3, ready: 4, complete: 5 };
          const overallWorst = allGates.reduce((w, g) => (statusOrder[g.status] ?? 2) < (statusOrder[w] ?? 2) ? g.status : w, 'idle');
          const healthColor = getStatusColor(overallWorst);
          const upcoming = allGates
            .filter(g => g.planned_close_date && g.status !== 'complete')
            .sort((a, b) => new Date(a.planned_close_date) - new Date(b.planned_close_date))[0];
          const upcomingDays = upcoming ? Math.ceil((new Date(upcoming.planned_close_date) - today) / (1000 * 60 * 60 * 24)) : null;

          return (
            <div style={{ maxWidth: '900px' }}>

              {/* AI verbal summary */}
              <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '18px 20px', marginBottom: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: (execSummary || execSummaryLoading) ? '12px' : 0 }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px' }}>PROGRAM SUMMARY</div>
                  <button
                    style={{ ...s.ghostBtn, fontSize: '9px', padding: '4px 10px', opacity: execSummaryLoading ? 0.5 : 1 }}
                    onClick={generateExecSummary} disabled={execSummaryLoading}>
                    {execSummaryLoading ? 'GENERATING...' : execSummary ? 'REFRESH' : 'GENERATE'}
                  </button>
                </div>
                {execSummaryLoading && !execSummary && (
                  <div style={{ color: t.subLabel, fontSize: '12px', letterSpacing: '1px' }}>Generating summary...</div>
                )}
                {execSummaryError && !execSummaryLoading && (
                  <div style={{ fontSize: '11px', color: '#ef4444', letterSpacing: '0.5px' }}>{execSummaryError}</div>
                )}
                {execSummary && (
                  <div style={{ fontSize: '13px', color: t.cascadeText, lineHeight: 1.9 }}>{execSummary}</div>
                )}
              </div>

              {/* Risk Intelligence */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px' }}>RISK INTELLIGENCE</div>
                  <button style={{ ...s.ghostBtn, fontSize: '9px', padding: '4px 10px', opacity: riskIntelLoading ? 0.5 : 1 }} onClick={generateRiskIntel} disabled={riskIntelLoading}>
                    {riskIntelLoading ? 'ANALYZING...' : riskIntel.length ? 'REFRESH' : 'ANALYZE'}
                  </button>
                </div>
                {riskIntelLoading && !riskIntel.length && (
                  <div style={{ color: t.subLabel, fontSize: '12px', letterSpacing: '1px' }}>Analyzing risk patterns...</div>
                )}
                {riskIntel.map((risk, i) => {
                  const sev = risk.severity;
                  const sevColor = sev === 'high' ? '#ef4444' : sev === 'medium' ? '#f59e0b' : '#10b981';
                  return (
                    <div key={i} style={{ backgroundColor: t.surface, border: `1px solid ${sevColor}30`, borderLeft: `3px solid ${sevColor}`, borderRadius: '6px', padding: '14px 16px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '8px', fontWeight: '700', letterSpacing: '1.5px', color: sevColor, border: `1px solid ${sevColor}`, borderRadius: '3px', padding: '2px 6px' }}>{sev.toUpperCase()}</span>
                        {risk.lane && <span style={{ fontSize: '8px', letterSpacing: '1.5px', color: t.dim, border: `1px solid ${t.borderDim}`, borderRadius: '3px', padding: '2px 6px' }}>{risk.lane.toUpperCase()}</span>}
                        <span style={{ fontSize: '12px', fontWeight: '600', color: t.text, fontFamily: "'Inter', sans-serif" }}>{risk.title}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: t.cascadeText, lineHeight: 1.7, marginBottom: '6px' }}>{risk.description}</div>
                      <div style={{ fontSize: '11px', color: t.dim, lineHeight: 1.6 }}>
                        <span style={{ color: t.accent, letterSpacing: '1px', fontSize: '9px' }}>RECOMMENDATION: </span>{risk.recommendation}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recommended actions */}
              {execActions.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '12px' }}>RECOMMENDED ACTIONS</div>
                  {execActions.map((action, i) => (
                    <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${t.borderDimmer}` }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '9px', color: t.accent, fontWeight: 'bold' }}>
                        {i + 1}
                      </div>
                      <div style={{ fontSize: '12px', color: t.cascadeText, lineHeight: 1.7, paddingTop: '2px' }}>{action}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Program health bar */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px' }}>PROGRAM HEALTH</div>
                  <div style={{ fontSize: '11px', color: healthColor, letterSpacing: '2px', fontWeight: 'bold' }}>
                    {overallWorst.toUpperCase().replace('_', ' ')} — {overallPct}%
                  </div>
                </div>
                <div style={{ height: '10px', backgroundColor: t.borderDimmer, borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{ height: '10px', width: `${overallPct}%`, backgroundColor: healthColor, borderRadius: '5px', transition: 'width 0.4s ease' }} />
                </div>
              </div>

              {/* 4 key metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
                {[
                  { label: 'GATES COMPLETE',     value: `${doneCount}/${allGates.length}`,                                     color: t.text },
                  { label: 'TOTAL SLIP',          value: slipDays > 0 ? `+${slipDays}d` : 'ON TRACK',                         color: slipDays > 0 ? '#ef4444' : '#10b981' },
                  { label: 'FINANCIAL EXPOSURE',  value: fm.total_consequence > 0 ? fmt(fm.total_consequence) : '$0',         color: fm.total_consequence > 0 ? '#f59e0b' : '#10b981' },
                  { label: 'T-MINUS',             value: drDays > 0 ? `${drDays}d` : 'PAST DUE',                             color: drDays < 60 ? '#ef4444' : t.accent },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', marginBottom: '8px' }}>{label}</div>
                    <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 'bold', color, fontFamily: "'Roboto Mono', monospace" }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Team readiness rows */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '14px' }}>TEAM READINESS</div>
                {lanes.map(lane => {
                  const lg = allGates.filter(g => g.lane === lane);
                  const worstStatus = lg.reduce((w, g) => (statusOrder[g.status] ?? 2) < (statusOrder[w] ?? 2) ? g.status : w, 'idle');
                  const avgPct = lg.length ? Math.round(lg.reduce((s, g) => s + g.completion_percent, 0) / lg.length) : 0;
                  const totalDelay = lg.reduce((s, g) => s + (g.days_delayed || 0), 0);
                  const totalCost  = lg.reduce((s, g) => s + (g.cost_impact  || 0), 0);
                  const color = getStatusColor(worstStatus);
                  return (
                    <div key={lane} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: `1px solid ${t.borderDimmer}` }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                      <div style={{ width: isMobile ? '86px' : '110px', fontSize: '10px', color: t.muted, letterSpacing: '1px', flexShrink: 0 }}>{lane.toUpperCase()}</div>
                      <div style={{ flex: 1, height: '6px', backgroundColor: t.borderDimmer, borderRadius: '3px', minWidth: '40px' }}>
                        <div style={{ height: '6px', width: `${avgPct}%`, backgroundColor: color, borderRadius: '3px', transition: 'width 0.4s ease' }} />
                      </div>
                      <div style={{ width: '34px', fontSize: '10px', color: t.muted, textAlign: 'right', flexShrink: 0 }}>{avgPct}%</div>
                      <div style={{ width: isMobile ? '68px' : '90px', fontSize: '9px', color, fontWeight: 'bold', letterSpacing: '0.5px', flexShrink: 0 }}>{worstStatus.toUpperCase().replace('_', ' ')}</div>
                      {totalDelay > 0 && <div style={{ fontSize: '9px', color: '#ef4444', flexShrink: 0, whiteSpace: 'nowrap' }}>+{totalDelay}d</div>}
                      {totalCost  > 0 && <div style={{ fontSize: '9px', color: '#f59e0b', flexShrink: 0, whiteSpace: 'nowrap' }}>{fmt(totalCost)}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Top risks */}
              {(hardAlerts.length > 0 || softAlerts.length > 0) && (
                <div style={{ marginBottom: '28px' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '14px' }}>TOP RISKS</div>
                  {[...hardAlerts.slice(0, 2), ...softAlerts.slice(0, 1)].map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                      <span style={{ color: a.severity === 'hard' ? '#ef4444' : '#f59e0b', flexShrink: 0, fontWeight: 'bold' }}>→</span>
                      <span style={{ fontSize: '12px', color: t.cascadeText, lineHeight: 1.5 }}>{a.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Next gate deadline */}
              {upcoming && (
                <div style={{ marginBottom: '28px', backgroundColor: t.surfaceAlt, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '6px' }}>NEXT GATE DEADLINE</div>
                    <div style={{ fontSize: '13px', color: t.text, fontWeight: 'bold' }}>{upcoming.lane.toUpperCase()} G{upcoming.gate_number} — {gateNames[upcoming.gate_number - 1]}</div>
                    <div style={{ fontSize: '10px', color: t.muted, marginTop: '4px' }}>{upcoming.planned_close_date}</div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: upcomingDays !== null && upcomingDays < 14 ? '#ef4444' : t.accent, fontFamily: "'Roboto Mono', monospace" }}>
                    {upcomingDays !== null ? `${upcomingDays}d` : '—'}
                  </div>
                </div>
              )}

              {/* Financial exposure */}
              {fm.total_consequence > 0 && (
                <div>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '3px', marginBottom: '14px' }}>FINANCIAL EXPOSURE</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '10px' }}>
                    {[
                      { label: 'STRANDED ASSETS', value: fmt(fm.stranded_assets    || 0), color: '#ef4444' },
                      { label: 'RETROFIT COST',   value: fmt(fm.retrofit_cost      || 0), color: '#f59e0b' },
                      { label: 'REVENUE IMPACT',  value: fmt(fm.revenue_impact     || 0), color: '#f59e0b' },
                      { label: 'TOTAL EXPOSURE',  value: fmt(fm.total_consequence  || 0), color: '#ef4444' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ backgroundColor: color + '10', border: `1px solid ${color}30`, borderRadius: '6px', padding: '12px 14px' }}>
                        <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', marginBottom: '6px' }}>{label}</div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          );
        })()}

        {/* ── ADAPTIVE TAB ── */}
        {activeTab === 'adaptive' && (() => {
          const riskColor = r => r === 'high' ? '#ef4444' : r === 'medium' ? '#f59e0b' : '#10b981';
          const urgencyColor = u => u === 'high' ? '#ef4444' : u === 'medium' ? '#f59e0b' : '#22d3ee';
          const insightIcon = type => type === 'pattern' ? '⟳' : type === 'benchmark' ? '◈' : '✓';
          const dataQualityLabel = q => q === 'rich' ? { text: 'RICH DATA · Learning from your history', color: '#10b981' } : q === 'moderate' ? { text: 'MODERATE DATA · Building patterns', color: '#f59e0b' } : { text: 'SPARSE DATA · Using industry benchmarks', color: '#22d3ee' };
          const dql = adaptiveData ? dataQualityLabel(adaptiveData.dataQuality) : null;
          return (
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>ADAPTIVE · PREDICTIVE LAUNCH INTELLIGENCE</div>
                  <div style={{ fontSize: '13px', color: t.muted, lineHeight: 1.6, maxWidth: '540px' }}>
                    Learns from every launch to predict failures before they happen. Gets smarter as your program history grows.
                  </div>
                </div>
                <button
                  style={{ ...s.ghostBtn, fontSize: '9px', padding: '5px 12px', opacity: adaptiveLoading ? 0.5 : 1, flexShrink: 0 }}
                  onClick={generateAdaptive} disabled={adaptiveLoading}>
                  {adaptiveLoading ? 'ANALYZING...' : adaptiveData ? 'REFRESH' : 'RUN ANALYSIS'}
                </button>
              </div>

              {demoMode && (
                <div style={{ backgroundColor: t.accent + '12', border: `1px solid ${t.accent}30`, borderRadius: '8px', padding: '14px 18px', marginBottom: '24px', fontSize: '12px', color: t.dim }}>
                  Adaptive analysis requires an authenticated account with program history.
                </div>
              )}

              {!demoMode && !adaptiveData && !adaptiveLoading && !adaptiveError && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: t.subLabel }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px', opacity: 0.4 }}>⚡</div>
                  <div style={{ fontSize: '13px', letterSpacing: '1px', marginBottom: '8px' }}>No analysis yet</div>
                  <div style={{ fontSize: '11px', color: t.dim, marginBottom: '20px' }}>Click RUN ANALYSIS to generate predictive insights</div>
                  <button style={{ ...s.ghostBtn, margin: '0 auto' }} onClick={generateAdaptive}>RUN ANALYSIS →</button>
                </div>
              )}

              {adaptiveLoading && !adaptiveData && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: t.dim }}>
                  <div style={{ fontSize: '11px', letterSpacing: '2px', marginBottom: '8px' }}>ANALYZING LAUNCH PATTERNS...</div>
                  <div style={{ fontSize: '11px', color: t.subLabel }}>Reviewing gates, history, and industry data</div>
                </div>
              )}

              {adaptiveError && (
                <div style={{ backgroundColor: '#ef444415', border: '1px solid #ef444430', borderRadius: '8px', padding: '14px 18px', color: '#ef4444', fontSize: '12px', marginBottom: '20px' }}>
                  {adaptiveError}
                </div>
              )}

              {adaptiveData && (
                <div>
                  {/* Data quality + timestamp */}
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap' }}>
                    {dql && <span style={{ fontSize: '9px', color: dql.color, letterSpacing: '1.5px', border: `1px solid ${dql.color}40`, borderRadius: '3px', padding: '3px 8px', fontWeight: '700' }}>{dql.text}</span>}
                    {adaptiveData.analyzedAt && <span style={{ fontSize: '9px', color: t.dim }}>Analyzed {new Date(adaptiveData.analyzedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>}
                  </div>

                  {/* Empty state */}
                  {!adaptiveData.recommendation && !(adaptiveData.predictions || []).length && (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: t.subLabel }}>
                      <div style={{ fontSize: '12px', marginBottom: '8px' }}>Analysis returned no predictions.</div>
                      <div style={{ fontSize: '11px', color: t.dim, marginBottom: '16px' }}>Add tasks and due dates to your gates, then try again.</div>
                      <button style={{ ...s.ghostBtn, margin: '0 auto' }} onClick={generateAdaptive}>RETRY →</button>
                    </div>
                  )}

                  {/* Top recommendation */}
                  {adaptiveData.recommendation && (
                    <div style={{ backgroundColor: t.accent + '10', border: `1px solid ${t.accent}30`, borderRadius: '10px', padding: '16px 20px', marginBottom: '28px' }}>
                      <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', fontWeight: '700', marginBottom: '8px' }}>⚡ THIS WEEK'S PRIORITY</div>
                      <div style={{ fontSize: '13px', color: t.text, lineHeight: 1.7 }}>{adaptiveData.recommendation}</div>
                    </div>
                  )}

                  {/* Risk predictions — grouped by lane */}
                  {(adaptiveData.predictions || []).length > 0 && (() => {
                    const laneColorMap = { design: '#8b5cf6', finance: '#f59e0b', operations: '#06b6d4', installation: '#10b981', engineering: '#ef4444' };
                    // Group predictions by lane
                    const byLane = {};
                    for (const p of adaptiveData.predictions) {
                      if (!byLane[p.lane]) byLane[p.lane] = [];
                      byLane[p.lane].push(p);
                    }
                    // Sort lanes by worst risk confidence
                    const laneOrder = Object.keys(byLane).sort((a, b) => {
                      const worstA = Math.max(...byLane[a].map(p => p.confidence < 1 ? p.confidence * 100 : p.confidence));
                      const worstB = Math.max(...byLane[b].map(p => p.confidence < 1 ? p.confidence * 100 : p.confidence));
                      return worstB - worstA;
                    });
                    return (
                      <div style={{ marginBottom: '28px' }}>
                        <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px', fontWeight: '700' }}>SLIP RISK PREDICTIONS · BY LANE</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {laneOrder.map(lane => {
                            const laneColor = laneColorMap[lane] || t.accent;
                            const lanePreds = byLane[lane].sort((a, b) => a.gate_number - b.gate_number);
                            const worstRisk = lanePreds.reduce((w, p) => {
                              const order = { high: 2, medium: 1, low: 0 };
                              return order[p.slipRisk] > order[w] ? p.slipRisk : w;
                            }, 'low');
                            return (
                              <div key={lane} style={{ border: `1px solid ${laneColor}30`, borderRadius: '10px', overflow: 'hidden' }}>
                                {/* Lane header */}
                                <div style={{ backgroundColor: laneColor + '12', borderBottom: `1px solid ${laneColor}25`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: laneColor, flexShrink: 0 }} />
                                  <span style={{ fontSize: '11px', fontWeight: '700', color: laneColor, letterSpacing: '1.5px' }}>{lane.toUpperCase()}</span>
                                  <span style={{ fontSize: '8px', color: riskColor(worstRisk), border: `1px solid ${riskColor(worstRisk)}40`, borderRadius: '3px', padding: '1px 6px', letterSpacing: '1.5px', fontWeight: '700' }}>{worstRisk.toUpperCase()} RISK</span>
                                </div>
                                {/* Gate predictions within lane */}
                                <div style={{ padding: '0' }}>
                                  {lanePreds.map((p, pi) => {
                                    const rc = riskColor(p.slipRisk);
                                    const conf = p.confidence < 1 ? Math.round(p.confidence * 100) : Math.round(p.confidence);
                                    const drivers = p.taskDrivers || [];
                                    return (
                                      <div key={pi} style={{ padding: '16px 18px', borderBottom: pi < lanePreds.length - 1 ? `1px solid ${t.borderDim}` : 'none' }}>
                                        {/* Gate header */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                                          <span style={{ fontSize: '20px', fontWeight: '900', color: rc, fontFamily: "'Roboto Mono', monospace", lineHeight: 1 }}>{conf}%</span>
                                          <span style={{ fontSize: '10px', color: rc, fontWeight: '700', letterSpacing: '1px' }}>{p.slipRisk?.toUpperCase()}</span>
                                          <span style={{ fontSize: '10px', color: t.subLabel }}>Gate {p.gate_number}</span>
                                          {p.daysAtRisk > 0 && <span style={{ fontSize: '9px', color: t.dim, border: `1px solid ${t.border}`, borderRadius: '3px', padding: '1px 6px' }}>~{p.daysAtRisk}d at risk</span>}
                                        </div>
                                        {/* Root cause */}
                                        {p.rootCause && (
                                          <div style={{ marginBottom: '10px' }}>
                                            <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', fontWeight: '700', marginBottom: '4px' }}>ROOT CAUSE</div>
                                            <div style={{ fontSize: '12px', color: t.text, lineHeight: 1.6 }}>{p.rootCause}</div>
                                          </div>
                                        )}
                                        {/* Recommended action */}
                                        {p.recommendedAction && (
                                          <div style={{ marginBottom: drivers.length > 0 ? '10px' : 0 }}>
                                            <div style={{ fontSize: '8px', color: t.accent, letterSpacing: '2px', fontWeight: '700', marginBottom: '4px' }}>RECOMMENDED ACTION</div>
                                            <div style={{ fontSize: '12px', color: t.text, lineHeight: 1.6 }}>{p.recommendedAction}</div>
                                          </div>
                                        )}
                                        {/* Missing tasks */}
                                        {drivers.length > 0 && (
                                          <div>
                                            <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '2px', fontWeight: '700', marginBottom: '6px' }}>MISSING TASKS</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              {drivers.map((d, di) => (
                                                <div key={di} style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                                                  <span style={{ fontSize: '9px', color: rc, fontWeight: '700', flexShrink: 0 }}>▲</span>
                                                  <span style={{ fontSize: '11px', color: t.text }}>{d.task}</span>
                                                  <span style={{ fontSize: '10px', color: t.dim }}>— {d.issue}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Launch patterns / insights */}
                  {(adaptiveData.insights || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: '10px', color: t.subLabel, letterSpacing: '2px', marginBottom: '14px', fontWeight: '700' }}>LAUNCH PATTERNS</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                        {adaptiveData.insights.map((ins, i) => (
                          <div key={i} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '16px 18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: t.accent, letterSpacing: '1px' }}>{insightIcon(ins.type)} {ins.type?.toUpperCase()}</span>
                              <span style={{ fontSize: '9px', color: t.dim }}>{ins.affectedLane?.toUpperCase()} G{ins.affectedGate}</span>
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: t.text, marginBottom: '6px', lineHeight: 1.4 }}>{ins.title}</div>
                            <div style={{ fontSize: '11px', color: t.muted, lineHeight: 1.6 }}>{ins.body}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── ACTIVITY TAB ── */}
        {activeTab === 'activity' && (
          (() => {
          const allLanesActivity = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const filteredActivity = activityLaneFilter === 'all'
            ? programActivity
            : programActivity.filter(n => n.lane === activityLaneFilter);
          return (
          <div style={{ maxWidth: '800px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '10px', letterSpacing: '3px', color: t.dim }}>PROGRAM ACTIVITY LOG</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {['all', ...allLanesActivity].map(lane => (
                  <button key={lane} onClick={() => setActivityLaneFilter(lane)}
                    style={{ padding: '4px 10px', fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1.5px', fontWeight: '600', border: `1px solid ${activityLaneFilter === lane ? t.accent : t.borderDim}`, borderRadius: '4px', background: activityLaneFilter === lane ? t.accent + '18' : 'none', color: activityLaneFilter === lane ? t.accent : t.subLabel, cursor: 'pointer' }}>
                    {lane === 'all' ? 'ALL' : lane.slice(0, 4).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {activityLoading && <div style={{ color: t.dim, fontSize: '11px', padding: '32px 0', textAlign: 'center', fontFamily: "'Roboto Mono', monospace" }}>LOADING...</div>}
            {!activityLoading && filteredActivity.length === 0 && (
              <div style={{ color: t.subLabel, fontSize: '11px', padding: '40px 0', textAlign: 'center', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                {activityLaneFilter === 'all' ? 'No activity recorded yet. Gate status changes will appear here.' : `No activity for ${activityLaneFilter.toUpperCase()} lane yet.`}
              </div>
            )}
            {!activityLoading && filteredActivity.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {(() => {
                  const eventColor = { blocked: '#ef4444', complete: '#10b981', at_risk: '#f59e0b', cascade: '#8b5cf6' };
                  const eventLabel = { blocked: 'BLOCKED', complete: 'COMPLETE', at_risk: 'AT RISK', cascade: 'CASCADE' };
                  let lastDate = null;
                  return filteredActivity.map((n, i) => {
                    const dt = new Date(n.created_at);
                    const dateStr = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    const timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    const color = eventColor[n.event_type?.toLowerCase()] || t.dim;
                    const label = eventLabel[n.event_type?.toLowerCase()] || (n.event_type || 'UPDATE');
                    const showDate = dateStr !== lastDate;
                    lastDate = dateStr;
                    return (
                      <div key={n.id}>
                        {showDate && (
                          <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", padding: i === 0 ? '0 0 12px 0' : '20px 0 12px 0' }}>
                            {dateStr.toUpperCase()}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '14px', paddingBottom: '12px', borderBottom: `1px solid ${t.borderDim}`, marginBottom: '12px', alignItems: 'flex-start' }}>
                          <div style={{ flexShrink: 0, paddingTop: '3px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '3px' }}>
                              <span style={{ fontSize: '9px', letterSpacing: '2px', fontWeight: '700', color, fontFamily: "'Roboto Mono', monospace" }}>{label}</span>
                              {n.lane && <span style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace" }}>{n.lane.toUpperCase()} · G{n.gate_number}</span>}
                              <span style={{ fontSize: '9px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace", marginLeft: 'auto' }}>{timeStr}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: t.text, lineHeight: 1.5 }}>{n.message}</div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
          );
          })()
        )}

        {/* ── BOTTOM TAB BAR ── */}
        <div style={{ ...s.tabs, marginTop: '40px', borderTop: `1px solid ${t.borderDim}`, borderBottom: 'none', paddingTop: '12px' }}>
          {['overview', 'teams', 'gates', 'timeline', 'consequences', 'ask', 'exec', 'activity', 'brief'].map(tab => {
            const fullLabel = tab === 'ask' ? 'ASK NOMOS' : tab === 'exec' ? 'EXEC SUMMARY' : tab === 'gates' ? 'LAUNCH READINESS' : tab === 'consequences' ? 'CONSEQUENCES' : tab.toUpperCase();
            const shortLabel = tab === 'ask' ? 'ASK' : tab === 'exec' ? 'EXEC' : tab === 'gates' ? 'READINESS' : tab === 'consequences' ? 'RISK' : tab === 'activity' ? 'LOG' : tab.toUpperCase();
            return (
              <button key={tab}
                style={{ ...s.tab, ...(activeTab === tab ? s.activeTab : {}) }}
                onClick={() => { setActiveTab(tab); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                {isMobile ? shortLabel : fullLabel}
              </button>
            );
          })}
        </div>

        </div>{/* /main content */}

        {/* ── FLOATING ASK NOMOS BUTTON ── */}
        {!askPanelOpen && (
          <button
            onClick={() => setAskPanelOpen(true)}
            style={{ position: 'fixed', bottom: isMobile ? '20px' : '28px', right: isMobile ? '16px' : '28px', zIndex: 800, width: '52px', height: '52px', borderRadius: '50%', backgroundColor: t.accent, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(99,102,241,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1px' }}
            title="Ask Nomos">
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#000', lineHeight: 1 }}>N</span>
            <span style={{ fontSize: '6px', color: '#000', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>ASK</span>
          </button>
        )}

        {/* ── ASK NOMOS SLIDE PANEL ── */}
        {askPanelOpen && (
          <div style={{ position: 'fixed', bottom: 0, right: 0, top: isMobile ? 0 : 'auto', width: isMobile ? '100vw' : '420px', height: isMobile ? '100vh' : '580px', backgroundColor: darkMode ? '#0d0f1a' : '#ffffff', border: `1px solid ${t.accent}30`, borderRadius: isMobile ? 0 : '12px 12px 0 0', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)', zIndex: 800, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Panel header */}
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${t.borderDim}`, display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: darkMode ? '#0d0f1a' : '#f0f4ff', flexShrink: 0 }}>
              <div style={{ width: '26px', height: '26px', backgroundColor: t.accent, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>N</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: t.accent, letterSpacing: '2px' }}>ASK NOMOS</div>
                <div style={{ fontSize: '9px', color: t.dim }}>{selectedProgram.name}</div>
              </div>
              <button onClick={() => setAskPanelOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.subLabel, fontSize: '16px', padding: '4px' }}>✕</button>
            </div>
            {/* Suggested questions */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${t.borderDim}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {[
                  "What's the biggest risk right now?",
                  "Which lane is most behind?",
                  "What should I escalate today?",
                  "Summarize this launch for an exec.",
                  "What tasks are blocking us?",
                  "Who has the most open tasks?",
                ].map(q => (
                  <button key={q} onClick={() => { setQuestion(q); askNomos(q); }} disabled={aiLoading}
                    style={{ background: 'none', border: `1px solid ${t.accent}30`, borderRadius: '20px', padding: '4px 10px', fontSize: '9px', color: t.accent, cursor: aiLoading ? 'default' : 'pointer', fontFamily: "'Roboto Mono', monospace", opacity: aiLoading ? 0.5 : 1 }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
            {/* Answer area */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
              {!aiAnswer && !aiLoading && (
                <div style={{ color: t.subLabel, fontSize: '11px', textAlign: 'center', paddingTop: '32px', lineHeight: 1.7 }}>
                  Ask anything about this launch.<br />Nomos has full context on all gates, milestones, DRIs, and financial data.
                </div>
              )}
              {aiLoading && <div style={{ color: t.dim, fontSize: '11px', letterSpacing: '1px' }}>Nomos is thinking...</div>}
              {aiAnswer && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '2px', fontWeight: '700' }}>NOMOS</div>
                    <button onClick={() => { setAiAnswer(''); setQuestion(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.subLabel, fontSize: '10px', fontFamily: "'Roboto Mono', monospace" }}>clear</button>
                  </div>
                  <div style={{ fontSize: '12px', color: t.text, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{aiAnswer.replace(/\*\*/g, '')}</div>
                </div>
              )}
            </div>
            {/* Input */}
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${t.borderDim}`, flexShrink: 0, display: 'flex', gap: '8px', backgroundColor: darkMode ? '#0d0f1a' : '#ffffff' }}>
              <input
                style={{ flex: 1, background: t.askInputBg, border: `1px solid ${t.border}`, color: t.text, padding: '9px 12px', fontSize: '12px', fontFamily: "'Roboto Mono', monospace", outline: 'none', borderRadius: '4px' }}
                placeholder="Ask about this launch…"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && askNomos()}
                autoFocus
              />
              <button
                style={{ background: aiLoading ? t.borderDim : t.accent, color: '#000', border: 'none', padding: '9px 16px', fontSize: '11px', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace", cursor: aiLoading ? 'default' : 'pointer', borderRadius: '4px', flexShrink: 0 }}
                onClick={() => askNomos()} disabled={aiLoading}>
                {aiLoading ? '...' : 'ASK'}
              </button>
            </div>
          </div>
        )}

      </div>
    );
  }

  // ── PROGRAMS LIST ──────────────────────────────────────────────────────────
  const inFlight   = programs.filter(p => p.status !== 'complete').length;
  const atRiskCnt  = programs.filter(p => p.status === 'at_risk' || p.status === 'blocked').length;
  const totalBudget = programs.reduce((sum, p) => sum + (p.budget_total || 0), 0);
  const sysStatus  = programs.some(p => p.status === 'blocked') ? 'CRITICAL' : programs.some(p => p.status === 'at_risk') ? 'AT RISK' : 'SYSTEM NOMINAL';
  const sysColor   = sysStatus === 'CRITICAL' ? '#ef4444' : sysStatus === 'AT RISK' ? '#f59e0b' : '#10b981';

  // ── PUBLIC SHARE VIEW ──
  const shareToken = (() => { const p = new URLSearchParams(window.location.search); return p.get('share'); })();
  if (shareToken) return <PublicBriefView token={shareToken} />;

  return (
    <div style={{ backgroundColor: t.bg, minHeight: '100vh', fontFamily: "'Roboto Mono', monospace", color: t.text }}>

      {/* ── BLUEPRINT GENERATING OVERLAY ── */}
      {blueprintGenerating && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: t.bg, zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '32px', fontFamily: "'Roboto Mono', monospace" }}>
          <div style={{ width: '48px', height: '48px', backgroundColor: t.accent, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold', color: '#000' }}>N</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: t.accent, marginBottom: '12px' }}>BUILDING YOUR BLUEPRINT</div>
            <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', color: t.text, letterSpacing: '-0.5px', maxWidth: '400px', lineHeight: 1.3 }}>Nomos is generating your complete gate plan...</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', width: '100%' }}>
            {['CALCULATING GATE TIMELINE', 'MAPPING LANE DEPENDENCIES', 'GENERATING MILESTONE TASKS', 'SEEDING YOUR PLAN'].map((label, i) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: t.accent, flexShrink: 0, animation: `pulse ${1 + i * 0.3}s ease-in-out infinite` }} />
                <span style={{ fontSize: '10px', letterSpacing: '2px', color: t.subLabel }}>{label}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: t.dim, maxWidth: '360px', textAlign: 'center', lineHeight: 1.7 }}>
            Nomos will build your starting point. Your team builds the real plan.
          </div>
        </div>
      )}

      {/* ── FIRST RUN FLOW (full screen) ── */}
      {showFirstRun && (
        <FirstRunFlow
          onComplete={onFirstRunComplete}
          darkMode={darkMode}
          t={t}
          authFetch={authFetch}
          isMobile={isMobile}
          startVoiceFor={startVoiceFor}
          voiceFieldId={voiceFieldId}
        />
      )}

      {/* ── LAUNCH WIZARD (full screen) ── */}
      {creating && (() => {
        const gateNames = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];
        const gateDates = calcWizardGateDates(wizardData.program_start_date, wizardData.launch_date);
        const wSet = (field, val) => setWizardData(prev => ({ ...prev, [field]: val }));

        const slip30 = (() => {
          const locs = parseInt(wizardData.num_locations) || 0;
          const launched = parseInt(wizardData.launched_locations) || 0;
          const remaining = Math.max(0, locs - launched);
          const dailyRev = parseFloat(wizardData.avg_daily_revenue_per_location) || 0;
          const uplift = parseFloat(wizardData.expected_uplift_percent) || 0;
          return Math.round(remaining * dailyRev * (uplift / 100) * 30);
        })();


        return (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: t.bg, zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Roboto Mono', monospace" }}>

            {/* Header */}
            <div style={{ padding: '0 32px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.surface, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '24px', height: '24px', backgroundColor: t.accent, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color: '#000' }}>N</div>
                <span style={{ fontSize: '10px', letterSpacing: '3px', color: t.accent, fontWeight: 'bold' }}>USE NOMOS TO LAUNCH</span>
              </div>
              <button style={s.closeBtn} onClick={() => setCreating(false)}>✕</button>
            </div>

            {/* Progress bar */}
            <div style={{ padding: '0 32px', paddingTop: '20px', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                {[1, 2, 3, 4].map(n => (
                  <div key={n} style={{ flex: 1, height: '3px', borderRadius: '2px', backgroundColor: n <= wizardStep ? t.accent : t.borderDim, transition: 'background-color 0.3s' }} />
                ))}
              </div>
              <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px' }}>
                STEP {wizardStep} OF 4 — {['YOUR LAUNCH', 'THE BRIEF', 'THE NUMBERS', 'YOUR TIMELINE'][wizardStep - 1]}
              </div>
            </div>

            {/* Scrollable content — key resets scroll to top on each step change */}
            <div key={wizardStep} style={{ flex: 1, overflowY: 'auto', padding: '28px 32px 40px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '640px' }}>

                {/* ── STEP 1: DESCRIBE YOUR LAUNCH ── */}
                {wizardStep === 1 && (
                  <div>
                    <div style={{ marginBottom: '28px' }}>
                      <h2 style={{ fontSize: isMobile ? '26px' : '34px', fontWeight: 'bold', color: t.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>What are you launching?</h2>
                      <div style={{ fontSize: '13px', color: t.subLabel, lineHeight: 1.6 }}>Speak or type — Nomos will figure out the rest.</div>
                    </div>

                    {/* Voice + textarea */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px' }}>DESCRIBE YOUR LAUNCH</div>
                        <MicBtnWide fieldId="intake" value={intakeText} onSet={setIntakeText} {...micWideP} />
                      </div>
                      <textarea
                        style={{ ...s.textarea, minHeight: '140px', fontSize: '15px', lineHeight: 1.7 }}
                        placeholder="e.g. We're launching a new display fixture across 500 US retail stores by Q4 2026 with an $18M budget — replacing existing static signage with a digital display unit."
                        value={intakeText}
                        onChange={e => setIntakeText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && e.metaKey && runWizardIntake()}
                        autoFocus
                      />
                      <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '8px', lineHeight: 1.6 }}>
                        Include: what you're building · where · when · how many locations · budget
                      </div>
                    </div>

                    {/* AI analyze button */}
                    {!wizardIntakeAnalyzed && (
                      <button
                        style={{ ...s.saveBtn, width: '100%', fontSize: '13px', padding: '14px', opacity: intakeLoading || !intakeText.trim() ? 0.5 : 1 }}
                        onClick={runWizardIntake} disabled={intakeLoading || !intakeText.trim()}>
                        {intakeLoading ? 'NOMOS IS THINKING...' : 'ANALYZE LAUNCH →'}
                      </button>
                    )}

                    {/* Confirmed fields — shown after AI runs */}
                    {wizardIntakeAnalyzed && (
                      <div>
                        <div style={{ backgroundColor: t.accent + '12', border: `1px solid ${t.accent}30`, borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: t.accent, flexShrink: 0 }} />
                          <span style={{ fontSize: '10px', color: t.accent, letterSpacing: '1px' }}>NOMOS EXTRACTED YOUR LAUNCH — EDIT ANYTHING BELOW</span>
                        </div>
                        {[
                          { label: 'PROGRAM NAME', field: 'name', type: 'text', placeholder: 'e.g. SDS Global Rollout — Q4 2026' },
                          { label: 'GO-TO-MARKET DATE', field: 'launch_date', type: 'date', placeholder: '', hint: 'When this hits stores' },
                          { label: 'MASS PRODUCTION DATE', field: 'mass_production_date', type: 'date', placeholder: '', hint: 'When manufacturing must complete — Nomos works backward from this' },
                          { label: 'PROGRAM START DATE', field: 'program_start_date', type: 'date', placeholder: '' },
                          { label: 'MARKETS', field: 'markets', type: 'text', placeholder: 'e.g. US, EU, UK' },
                          { label: 'TOTAL LOCATIONS', field: 'num_locations', type: 'number', placeholder: 'e.g. 500' },
                          { label: 'TOTAL BUDGET ($)', field: 'budget_total', type: 'number', placeholder: 'e.g. 18000000' },
                        ].map(({ label, field, type, placeholder, hint }) => (
                          <div key={field} style={{ marginBottom: '14px' }}>
                            <label style={s.label}>{label}</label>
                            {hint && <div style={{ fontSize: '10px', color: t.subLabel, marginBottom: '6px', letterSpacing: '0.5px' }}>{hint}</div>}
                            <input style={{ ...s.input, ...(field === 'mass_production_date' ? { borderColor: t.accent + '50', backgroundColor: t.accent + '08' } : {}) }}
                              type={type} placeholder={placeholder}
                              value={wizardData[field]} onChange={e => wSet(field, e.target.value)} />
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                          <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px' }}
                            onClick={() => { setWizardIntakeAnalyzed(false); }}>
                            ← REDO
                          </button>
                          <button
                            style={{ ...s.saveBtn, flex: 2, padding: '12px', fontSize: '13px', opacity: !wizardData.name.trim() || !wizardData.launch_date ? 0.5 : 1 }}
                            disabled={!wizardData.name.trim() || !wizardData.launch_date}
                            onClick={() => {
                              setWizardStep(2);
                              if (!wizardBriefDone && !wizardBriefLoading) draftWizardBrief(wizardData);
                            }}>
                            NEXT — THE BRIEF →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── STEP 2: THE BRIEF ── */}
                {wizardStep === 2 && (
                  <div>
                    <div style={{ marginBottom: '28px' }}>
                      <h2 style={{ fontSize: isMobile ? '26px' : '34px', fontWeight: 'bold', color: t.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>What are you building?</h2>
                      <div style={{ fontSize: '13px', color: t.subLabel, lineHeight: 1.6 }}>
                        {wizardBriefLoading ? 'Nomos is writing your brief...' : 'Nomos has drafted your brief — edit to make it yours.'}
                      </div>
                    </div>

                    {wizardBriefLoading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {[120, 90, 80, 70].map((h, i) => (
                          <div key={i} style={{ height: `${h}px`, backgroundColor: t.surface, borderRadius: '8px', border: `1px solid ${t.borderDim}`, opacity: 0.6 }} />
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {[
                          { label: 'WHAT IT IS', field: 'what_is_it', placeholder: 'What is this product — what does it do, what does it replace?' },
                          { label: 'WHY IT MATTERS', field: 'why_it_matters', placeholder: 'What business problem does this solve? What does it unlock?' },
                          { label: 'END VISION', field: 'end_vision', placeholder: 'What does a finished, successful deployment look like on the ground?' },
                          { label: 'FOR WHO', field: 'target_customer', placeholder: 'Who experiences the end product and how?' },
                        ].map(({ label, field, placeholder }) => (
                          <div key={field} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '14px 16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px' }}>{label}</div>
                              <MicBtnWide fieldId={`brief-${field}`} value={wizardData[field]} onSet={v => wSet(field, v)} {...micWideP} />
                            </div>
                            <textarea
                              style={{ ...s.textarea, minHeight: '72px', fontSize: '13px', lineHeight: 1.7 }}
                              placeholder={placeholder}
                              value={wizardData[field]}
                              onChange={e => wSet(field, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                      <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px' }} onClick={() => setWizardStep(1)}>← BACK</button>
                      <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px', color: t.subLabel }} onClick={() => setWizardStep(3)}>SKIP</button>
                      <button style={{ ...s.saveBtn, flex: 2, padding: '12px', fontSize: '13px' }} disabled={wizardBriefLoading} onClick={() => setWizardStep(3)}>
                        NEXT — THE NUMBERS →
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 3: THE NUMBERS ── */}
                {wizardStep === 3 && (
                  <div>
                    <div style={{ marginBottom: '28px' }}>
                      <h2 style={{ fontSize: isMobile ? '26px' : '34px', fontWeight: 'bold', color: t.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>The business case</h2>
                      <div style={{ fontSize: '13px', color: t.subLabel, lineHeight: 1.6 }}>
                        Nomos uses these numbers to calculate the cost of any delay. Skip anything you don't have yet.
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                      {[
                        { label: 'UNIT COST', field: 'unit_cost', placeholder: 'e.g. 2000', hint: 'Cost per unit ($)' },
                        { label: 'UNITS PER LOCATION', field: 'units_per_location', placeholder: '2', hint: 'Default: 2' },
                        { label: 'DAILY REVENUE PER LOCATION', field: 'avg_daily_revenue_per_location', placeholder: 'e.g. 5000', hint: 'Revenue per location per day ($)' },
                        { label: 'EXPECTED SALES UPLIFT', field: 'expected_uplift_percent', placeholder: '2', hint: 'Uplift this program drives (%)' },
                        { label: 'LOCATIONS ALREADY LAUNCHED', field: 'launched_locations', placeholder: '0', hint: 'Stores running without this launch' },
                        { label: 'RETROFIT COST PER LOCATION', field: 'retrofit_cost_per_location', placeholder: '750', hint: 'If launch slips ($)' },
                      ].map(({ label, field, placeholder, hint }) => (
                        <div key={field}>
                          <label style={s.label}>{label}</label>
                          <input style={s.input} type="number" placeholder={placeholder}
                            value={wizardData[field]} onChange={e => wSet(field, e.target.value)} />
                          <div style={{ fontSize: '9px', color: t.subLabel, marginTop: '4px' }}>{hint}</div>
                        </div>
                      ))}
                    </div>

                    {/* Live consequence preview */}
                    {slip30 > 0 && (
                      <div style={{ backgroundColor: '#f59e0b10', border: '1px solid #f59e0b30', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
                        <div style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '2px', marginBottom: '6px' }}>CONSEQUENCE PREVIEW</div>
                        <div style={{ fontSize: '13px', color: t.text }}>
                          A <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>30-day slip</span> costs approximately{' '}
                          <span style={{ color: '#ef4444', fontWeight: 'bold', fontFamily: "'Roboto Mono', monospace" }}>${slip30.toLocaleString()}</span> in lost revenue uplift.
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px' }} onClick={() => setWizardStep(2)}>← BACK</button>
                      <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px', color: t.subLabel }} onClick={() => {
                        const auto = calcWizardGateDates(wizardData.program_start_date, wizardData.launch_date);
                        setWizardGateDates(auto.length === 6 ? auto : ['', '', '', '', '', '']);
                        setWizardStep(4);
                      }}>SKIP</button>
                      <button style={{ ...s.saveBtn, flex: 2, padding: '12px', fontSize: '13px' }} onClick={() => {
                        const auto = calcWizardGateDates(wizardData.program_start_date, wizardData.launch_date);
                        setWizardGateDates(auto.length === 6 ? auto : ['', '', '', '', '', '']);
                        setWizardStep(4);
                      }}>
                        NEXT — TIMELINE →
                      </button>
                    </div>
                  </div>
                )}

                {/* ── STEP 4: TIMELINE + LAUNCH ── */}
                {wizardStep === 4 && (
                  <div>
                    <div style={{ marginBottom: '28px' }}>
                      <h2 style={{ fontSize: isMobile ? '26px' : '34px', fontWeight: 'bold', color: t.text, margin: '0 0 8px', letterSpacing: '-0.5px' }}>Your launch timeline</h2>
                      <div style={{ fontSize: '13px', color: t.subLabel, lineHeight: 1.6 }}>
                        Nomos has mapped your 6-gate schedule across your timeline. Adjust dates in the Timeline tab after launch.
                      </div>
                    </div>

                    {/* Blueprint notice */}
                    <div style={{ backgroundColor: t.accent + '12', border: `1px solid ${t.accent}30`, borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <div style={{ fontSize: '16px', flexShrink: 0 }}>✦</div>
                      <div>
                        <div style={{ fontSize: '10px', color: t.accent, letterSpacing: '2px', fontWeight: 'bold', marginBottom: '4px' }}>NOMOS WILL BUILD YOUR BLUEPRINT</div>
                        <div style={{ fontSize: '11px', color: t.subLabel, lineHeight: 1.6 }}>After you launch, Nomos will generate a complete milestone plan across all 5 lanes and 6 gates — tailored to your product. Your team edits from there.</div>
                      </div>
                    </div>

                    {/* Program summary card */}
                    <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '16px 18px', marginBottom: '24px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {[
                          { label: 'PROGRAM', value: wizardData.name },
                          { label: 'GO-TO-MARKET DATE', value: wizardData.launch_date },
                          { label: 'MASS PRODUCTION DATE', value: wizardData.mass_production_date || '—', accent: true },
                          { label: 'MARKETS', value: wizardData.markets || '—' },
                          { label: 'BUDGET', value: wizardData.budget_total ? `$${Number(wizardData.budget_total).toLocaleString()}` : '—' },
                          { label: 'LOCATIONS', value: wizardData.num_locations || '—' },
                        ].map(({ label, value, accent }) => (
                          <div key={label}>
                            <div style={{ fontSize: '8px', color: accent ? t.accent : t.dim, letterSpacing: '2px', marginBottom: '3px' }}>{label}</div>
                            <div style={{ fontSize: '12px', color: accent ? t.accent : t.text, fontWeight: 'bold', wordBreak: 'break-word' }}>{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Gate schedule — editable */}
                    <div style={{ marginBottom: '28px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px' }}>GATE SCHEDULE</div>
                        <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>AUTO-CALCULATED — EDIT ANY DATE</div>
                      </div>
                      {gateNames.map((name, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0', borderBottom: `1px solid ${t.borderDimmer}` }}>
                          <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: t.accent, fontWeight: 'bold', flexShrink: 0 }}>
                            {i + 1}
                          </div>
                          <div style={{ flex: 1, fontSize: '11px', color: t.text }}>{name}</div>
                          <input
                            type="date"
                            value={wizardGateDates[i] || ''}
                            onChange={e => {
                              const updated = [...wizardGateDates];
                              updated[i] = e.target.value;
                              setWizardGateDates(updated);
                            }}
                            style={{ ...s.input, width: isMobile ? '130px' : '150px', padding: '5px 8px', fontSize: '11px', margin: 0, color: wizardGateDates[i] ? t.accent : t.subLabel, fontWeight: wizardGateDates[i] ? 'bold' : 'normal' }}
                          />
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button style={{ ...s.ghostBtn, flex: 1, padding: '10px', fontSize: '11px' }} onClick={() => setWizardStep(3)}>← BACK</button>
                      <button
                        style={{ ...s.saveBtn, flex: 3, padding: '16px', fontSize: '14px', opacity: createSaving || !wizardData.name.trim() || !wizardData.launch_date ? 0.5 : 1 }}
                        disabled={createSaving || !wizardData.name.trim() || !wizardData.launch_date}
                        onClick={createFromWizard}>
                        {createSaving ? 'BUILDING YOUR PROGRAM...' : 'LAUNCH INTO NOMOS →'}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        );
      })()}

      {/* ── TOP NAV ── */}
      <div style={{ borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.surface, padding: isMobile ? '0 16px' : '0 48px', display: 'flex', alignItems: 'center', height: '52px', gap: '24px', position: 'sticky', top: 0, zIndex: 100 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, cursor: 'pointer' }} onClick={goToLanding}>
          <div style={{ width: '26px', height: '26px', backgroundColor: t.accent, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: '#000', flexShrink: 0 }}>N</div>
          <span style={{ color: t.text, fontSize: '13px', letterSpacing: '3px', fontWeight: 'bold' }}>NOMOS</span>
          {!isMobile && <span style={{ color: t.subLabel, fontSize: '9px', letterSpacing: '2px' }}>LAUNCH OS</span>}
        </div>

        {/* Nav tabs */}
        {!isMobile && (
          <div style={{ display: 'flex', flex: 1 }}>
            {[
              { label: userRole === 'member' && userLane ? `${userLane.toUpperCase()} TEAM` : 'PROGRAMS', active: !showTeamDirectory && !showMyTasks && !showSuperAdmin },
              { label: 'MY TASKS', active: showMyTasks },
              ...(userRole !== 'member' ? [{ label: 'TEAM STATUS', active: showTeamDirectory }] : []),
              ...(session?.user?.email === 'dhcolema@gmail.com' ? [{ label: 'ADMIN CONSOLE', active: showSuperAdmin, color: '#f59e0b' }] : []),
            ].map(({ label, active, color }) => (
              <div key={label}
                onClick={() => {
                  if (label === 'MY TASKS') { setShowTeamDirectory(false); setShowMyTasks(true); setShowSuperAdmin(false); fetchMyTasks(); fetchMyLaneActions(); }
                  else if (label === 'TEAM STATUS') { setShowTeamDirectory(true); setShowMyTasks(false); setShowSuperAdmin(false); }
                  else if (label === 'ADMIN CONSOLE') { setShowTeamDirectory(false); setShowMyTasks(false); setShowSuperAdmin(true); fetchSuperAdminUsers(); }
                  else { setShowTeamDirectory(false); setShowMyTasks(false); setShowSuperAdmin(false); }
                }}
                style={{ padding: '0 16px', height: '52px', display: 'flex', alignItems: 'center', fontSize: '10px', letterSpacing: '2px', color: active ? (color || t.accent) : color ? color + '80' : t.dim, borderBottom: `2px solid ${active ? (color || t.accent) : 'transparent'}`, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                {label}
              </div>
            ))}
          </div>
        )}

        {/* Right: system status + preview + theme */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: sysColor }} />
              <span style={{ fontSize: '9px', color: sysColor, letterSpacing: '2px', fontWeight: 'bold' }}>{sysStatus}</span>
            </div>
          )}

          {/* PREVIEW AS — admin only, desktop only (mobile uses program ⋯ menu) */}
          {realUserRole === 'admin' && !demoMode && !isMobile && (
            <div style={{ position: 'relative' }}>
              <select
                value={previewLane || ''}
                onChange={e => setPreviewLane(e.target.value || null)}
                style={{ fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '600', background: previewLane ? '#f59e0b' : 'none', border: `1px solid ${previewLane ? '#f59e0b' : t.borderDim}`, borderRadius: '4px', color: previewLane ? '#000' : t.dim, padding: '4px 8px', cursor: 'pointer', outline: 'none' }}>
                <option value="">ADMIN VIEW</option>
                <option value="design">PREVIEW: DESIGN</option>
                <option value="finance">PREVIEW: FINANCE</option>
                <option value="operations">PREVIEW: OPS</option>
                <option value="installation">PREVIEW: INSTALL</option>
                <option value="engineering">PREVIEW: ENG</option>
              </select>
            </div>
          )}

          {(session || demoMode) && <NotificationBell notifications={notifications} open={notifOpen} onToggle={toggleNotifications} darkMode={darkMode} />}
          {session && (() => {
            const initials = (myFullName || session.user?.email || '?').split(/[\s@]/)[0]?.[0]?.toUpperCase() + ((myFullName || '').split(' ')[1]?.[0]?.toUpperCase() || '');
            return (
              <div data-avatar-menu style={{ position: 'relative' }}>
                <button onClick={() => setAvatarMenuOpen(o => !o)} title="Account" style={{ width: '30px', height: '30px', borderRadius: '50%', overflow: 'hidden', border: `1.5px solid ${t.accent}60`, backgroundColor: t.accent + '15', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {myAvatarUrl
                    ? <img src={myAvatarUrl} alt="me" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: '10px', fontWeight: '700', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>{initials}</span>}
                </button>
                {avatarMenuOpen && (
                  <div style={{ position: 'absolute', top: '38px', right: 0, backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '8px', minWidth: '160px', zIndex: 999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                    <button onClick={() => { setAvatarMenuOpen(false); setMyProfileOpen(true); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: t.text, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', textAlign: 'left' }}>MY PROFILE</button>
                    <div style={{ height: '1px', backgroundColor: t.borderDim }} />
                    <button onClick={() => { setAvatarMenuOpen(false); handleSignOut(); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#ef4444', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', textAlign: 'left' }}>SIGN OUT</button>
                  </div>
                )}
              </div>
            );
          })()}
          <ThemeToggle darkMode={darkMode} onToggle={() => setDarkMode(d => !d)} style={s.toggleBtn} />
        </div>
      </div>

      {/* Mobile sub-nav */}
      {isMobile && session && (
        <div style={{ borderBottom: `1px solid ${t.borderDim}`, backgroundColor: t.surface, display: 'flex', overflowX: 'auto', paddingLeft: '16px' }}>
          {[
            { label: userRole === 'member' && userLane ? userLane.toUpperCase() : 'PROGRAMS', active: !showTeamDirectory && !showMyTasks },
            { label: 'MY TASKS', active: showMyTasks },
            ...(userRole !== 'member' ? [{ label: 'TEAM STATUS', active: showTeamDirectory }] : []),
          ].map(({ label, active }) => (
            <div key={label}
              onClick={() => {
                if (label === 'MY TASKS') { setShowTeamDirectory(false); setShowMyTasks(true); fetchMyTasks(); fetchMyLaneActions(); }
                else if (label === 'TEAM STATUS') { setShowTeamDirectory(true); setShowMyTasks(false); }
                else { setShowTeamDirectory(false); setShowMyTasks(false); }
              }}
              style={{ padding: '0 14px', height: '40px', display: 'flex', alignItems: 'center', fontSize: '9px', letterSpacing: '2px', color: active ? t.accent : t.dim, borderBottom: `2px solid ${active ? t.accent : 'transparent'}`, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 }}>
              {label}
            </div>
          ))}
        </div>
      )}

      {/* ── CONTENT ── */}
      <div style={{ padding: isMobile ? '24px 16px' : '48px 48px', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Preview As banner */}
        {previewLane && (
          <div style={{ backgroundColor: darkMode ? '#2d1f00' : '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '10px', color: '#f59e0b', letterSpacing: '2px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>PREVIEW MODE</div>
              <div style={{ fontSize: '12px', color: darkMode ? '#fcd34d' : '#92400e' }}>
                Viewing as <strong>{previewLane.charAt(0).toUpperCase() + previewLane.slice(1)} team member</strong> — admin controls are hidden
              </div>
            </div>
            <button onClick={() => setPreviewLane(null)} style={{ background: 'none', border: '1px solid #f59e0b80', borderRadius: '4px', color: '#f59e0b', padding: '5px 12px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', cursor: 'pointer', fontWeight: '700', flexShrink: 0 }}>
              EXIT PREVIEW ✕
            </button>
          </div>
        )}

        {/* Blueprint banner — shown after AI blueprint is generated */}
        {showBlueprintBanner && (
          <div style={{ backgroundColor: t.accent + '12', border: `1px solid ${t.accent}40`, borderRadius: '8px', padding: '14px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ fontSize: '18px' }}>✦</div>
              <div>
                <div style={{ fontSize: '10px', color: t.accent, letterSpacing: '2px', fontWeight: 'bold', marginBottom: '2px' }}>NOMOS BUILT YOUR STARTING POINT</div>
                <div style={{ fontSize: '12px', color: t.subLabel, lineHeight: 1.5 }}>AI-generated milestones are seeded across all lanes and gates. Open any gate to review, assign DRIs, and make it yours.</div>
              </div>
            </div>
            <button onClick={() => setShowBlueprintBanner(false)} style={{ background: 'none', border: `1px solid ${t.accent}40`, borderRadius: '4px', color: t.accent, padding: '6px 14px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '1.5px', cursor: 'pointer', flexShrink: 0 }}>
              GOT IT
            </button>
          </div>
        )}

        {/* Demo banner */}
        {demoMode && (
          <div style={{ backgroundColor: darkMode ? '#1a1200' : '#fffbeb', border: `1px solid #f59e0b`, borderRadius: '8px', padding: '12px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: '#92400e', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
              DEMO MODE — You're viewing sample data. <span style={{ opacity: 0.7 }}>Sign in to manage your own launches.</span>
            </div>
            <button onClick={() => openAuthModal('signup')} style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '7px 16px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '1.5px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px', flexShrink: 0 }}>
              GET STARTED
            </button>
          </div>
        )}

        {/* ── SUPER ADMIN CONSOLE ── */}
        {showSuperAdmin && session?.user?.email === 'dhcolema@gmail.com' && (() => {
          const byCompany = {};
          superAdminUsers.forEach(u => {
            const cName = u.companies?.name || u.company_id || 'No Company';
            if (!byCompany[cName]) byCompany[cName] = [];
            byCompany[cName].push(u);
          });
          const fmtDate = ts => {
            if (!ts) return 'Never';
            const d = new Date(ts);
            const mins = Math.floor((Date.now() - d) / 60000);
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.floor(hrs / 24)}d ago`;
          };
          return (
            <div style={{ maxWidth: '900px' }}>
              {/* Header */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '3px', marginBottom: '8px' }}>SUPER ADMIN</div>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif" }}>Admin Console</div>
                <div style={{ fontSize: '13px', color: t.muted, marginTop: '4px' }}>
                  {superAdminUsers.length} accounts across {Object.keys(byCompany).length} companies — visible to dhcolema@gmail.com only
                </div>
              </div>

              {superAdminLoading && <div style={{ color: t.dim, fontSize: '11px', padding: '40px 0', textAlign: 'center', fontFamily: "'Roboto Mono', monospace" }}>LOADING...</div>}

              {!superAdminLoading && Object.entries(byCompany).map(([company, users]) => (
                <div key={company} style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#f59e0b', fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '16px' }}>
                    {company.toUpperCase()} · {users.length} {users.length === 1 ? 'USER' : 'USERS'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {users.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${t.borderDim}` }}>
                        {/* Avatar */}
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: t.accent, flexShrink: 0, overflow: 'hidden' }}>
                          {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (u.full_name || u.email || '?').split(/[\s@]/)[0]?.[0]?.toUpperCase()}
                        </div>
                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>{u.full_name || '—'}</div>
                          <div style={{ fontSize: '10px', color: t.muted }}>{u.email} · {u.lane || 'no lane'} · {u.role}</div>
                        </div>
                        {/* Last active */}
                        {!isMobile && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '10px', color: t.dim }}>Last active</div>
                            <div style={{ fontSize: '10px', color: t.muted }}>{fmtDate(u.last_active_at)}</div>
                          </div>
                        )}
                        {/* Actions — skip self */}
                        {u.email !== 'dhcolema@gmail.com' && (
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button
                              onClick={() => setSuperAdminAction({ type: 'reset', user: u })}
                              style={{ padding: '5px 10px', fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '600', border: `1px solid #0d948860`, borderRadius: '4px', background: 'none', color: '#0d9488', cursor: 'pointer' }}>
                              RESET PW
                            </button>
                            <button
                              onClick={() => setSuperAdminAction({ type: 'remove', user: u })}
                              style={{ padding: '5px 10px', fontSize: '9px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '600', border: `1px solid #ef444460`, borderRadius: '4px', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                              REMOVE
                            </button>
                          </div>
                        )}
                        {u.email === 'dhcolema@gmail.com' && (
                          <span style={{ fontSize: '9px', color: '#f59e0b', letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace", flexShrink: 0 }}>SUPER ADMIN</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Confirmation modal */}
              {superAdminAction && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                  <div style={{ backgroundColor: t.bg, border: `1px solid ${superAdminAction.type === 'remove' ? '#ef4444' : t.accent}`, borderRadius: '12px', padding: '32px', maxWidth: '400px', width: '100%' }}>
                    <div style={{ fontSize: '11px', color: superAdminAction.type === 'remove' ? '#ef4444' : t.accent, letterSpacing: '2px', marginBottom: '12px', fontFamily: "'Roboto Mono', monospace" }}>
                      {superAdminAction.type === 'remove' ? 'REMOVE USER' : 'RESET PASSWORD'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: t.text, marginBottom: '8px' }}>
                      {superAdminAction.type === 'remove' ? 'Remove this user?' : 'Send password reset?'}
                    </div>
                    <div style={{ fontSize: '13px', color: t.muted, marginBottom: '24px', lineHeight: 1.6 }}>
                      {superAdminAction.type === 'remove'
                        ? <>This will permanently delete <strong style={{ color: t.text }}>{superAdminAction.user.email}</strong> from Nomos — their account, profile, and program access. This cannot be undone.</>
                        : <>A password reset email will be sent to <strong style={{ color: t.text }}>{superAdminAction.user.email}</strong>.</>
                      }
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setSuperAdminAction(null)} style={{ flex: 1, padding: '10px', background: 'none', border: `1px solid ${t.borderDim}`, borderRadius: '6px', color: t.dim, cursor: 'pointer', fontSize: '11px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                        CANCEL
                      </button>
                      <button
                        disabled={superAdminWorking}
                        onClick={() => superAdminAction.type === 'remove' ? superAdminRemoveUser(superAdminAction.user) : superAdminResetPassword(superAdminAction.user)}
                        style={{ flex: 1, padding: '10px', background: superAdminAction.type === 'remove' ? '#ef4444' : t.accent, border: 'none', borderRadius: '6px', color: '#000', cursor: superAdminWorking ? 'not-allowed' : 'pointer', fontSize: '11px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '700', opacity: superAdminWorking ? 0.6 : 1 }}>
                        {superAdminWorking ? 'WORKING...' : superAdminAction.type === 'remove' ? 'REMOVE USER' : 'SEND RESET'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── MY TASKS ── */}
        {showMyTasks && session && (() => {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
          const overdue = myTasks.filter(t => t.due_date && new Date(t.due_date) < today);
          const thisWeek = myTasks.filter(t => t.due_date && new Date(t.due_date) >= today && new Date(t.due_date) <= weekEnd);
          const later = myTasks.filter(t => !t.due_date || new Date(t.due_date) > weekEnd);
          const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
          const gateNames = ['Program Inception', 'Concept Review', 'Eng Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

          const TaskRow = ({ task }) => {
            const gs = task.gate_statuses;
            const prog = gs?.programs;
            const lane = gs?.lane || '';
            const laneColor = laneColorMap[lane] || t.accent;
            const dueDate = task.due_date ? new Date(task.due_date) : null;
            const isOverdue = dueDate && dueDate < today;
            const dueFmt = dueDate ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
            return (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${t.borderDim}` }}>
                <button
                  onClick={() => completeMyTask(task.id)}
                  title="Mark complete"
                  style={{ flexShrink: 0, marginTop: '2px', width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${laneColor}60`, background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: laneColor, fontSize: '10px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = laneColor + '25'; e.currentTarget.textContent = '✓'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.textContent = ''; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: t.text, lineHeight: 1.4, marginBottom: '4px' }}>{task.text}</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {prog && (
                      <span
                        onClick={() => { const p = programs.find(x => x.id === prog.id); if (p) selectProgram(p); }}
                        style={{ fontSize: '10px', color: t.accent, cursor: 'pointer', fontWeight: '600' }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                      >{prog.name}</span>
                    )}
                    {lane && <span style={{ fontSize: '9px', color: laneColor, letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", backgroundColor: laneColor + '18', padding: '1px 6px', borderRadius: '3px' }}>{lane.toUpperCase()}</span>}
                    {gs?.gate_number && <span style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace" }}>G{gs.gate_number} · {gateNames[gs.gate_number - 1] || ''}</span>}
                    {dueFmt && <span style={{ fontSize: '9px', color: isOverdue ? '#ef4444' : t.muted, fontFamily: "'Roboto Mono', monospace", fontWeight: isOverdue ? '700' : 'normal' }}>{isOverdue ? '⚠ OVERDUE · ' : ''}{dueFmt}</span>}
                  </div>
                </div>
              </div>
            );
          };

          const Section = ({ label, tasks, emptyText, color }) => tasks.length === 0 ? null : (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ fontSize: '9px', letterSpacing: '2.5px', color: color || t.subLabel, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {label} <span style={{ color: t.dim, fontWeight: '400' }}>({tasks.length})</span>
              </div>
              {tasks.map(t2 => <TaskRow key={t2.id} task={t2} />)}
            </div>
          );

          return (
            <div style={{ maxWidth: '700px' }}>
              <div style={{ marginBottom: '32px' }}>
                <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '3px', marginBottom: '8px' }}>PERSONAL DASHBOARD</div>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif" }}>My Tasks</div>
                {!myTasksLoading && <div style={{ fontSize: '13px', color: t.muted, marginTop: '4px' }}>
                  {myTasks.length === 0 ? 'No open tasks assigned to you.' : `${myTasks.length} open task${myTasks.length === 1 ? '' : 's'} across all programs`}
                  {overdue.length > 0 && <span style={{ color: '#ef4444', marginLeft: '12px', fontWeight: '600' }}>· {overdue.length} overdue</span>}
                </div>}
              </div>

              {/* Recommended Actions — AI-generated, lane-specific */}
              {myLaneActions.length > 0 && (
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ fontSize: '9px', letterSpacing: '2.5px', color: t.accent, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '4px' }}>RECOMMENDED ACTIONS</div>
                  <div style={{ fontSize: '12px', color: t.muted, marginBottom: '16px' }}>Specific to your lane and current gate — generated from the launch brief</div>
                  {myLaneActions.map(action => {
                    const laneColor = ({ design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' })[action.lane] || t.accent;
                    const gateNames = ['Inception', 'Concept', 'Eng Review', 'Proto 1', 'Proto 2', 'Mass Prod'];
                    return (
                      <div key={action.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${t.borderDim}` }}>
                        <button
                          onClick={() => completeLaneAction(action.id)}
                          title="Mark complete"
                          style={{ flexShrink: 0, marginTop: '2px', width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${laneColor}60`, background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: laneColor, fontSize: '10px' }}
                          onMouseEnter={e => { e.currentTarget.style.background = laneColor + '25'; e.currentTarget.textContent = '✓'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.textContent = ''; }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', color: t.text, lineHeight: 1.4, marginBottom: '4px' }}>{action.text}</div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {action.programs && (
                              <span onClick={() => { const p = programs.find(x => x.id === action.program_id); if (p) selectProgram(p); }}
                                style={{ fontSize: '10px', color: t.accent, cursor: 'pointer', fontWeight: '600' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                                {action.programs.name}
                              </span>
                            )}
                            <span style={{ fontSize: '9px', color: laneColor, letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", backgroundColor: laneColor + '18', padding: '1px 6px', borderRadius: '3px' }}>{(action.lane || '').toUpperCase()}</span>
                            <span style={{ fontSize: '9px', color: t.dim, fontFamily: "'Roboto Mono', monospace" }}>G{action.gate_number} · {gateNames[action.gate_number - 1] || ''}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {(myTasksLoading || laneActionsLoading) && <div style={{ color: t.dim, fontSize: '11px', padding: '40px 0', textAlign: 'center', fontFamily: "'Roboto Mono', monospace" }}>LOADING...</div>}

              {!myTasksLoading && myTasks.length === 0 && myLaneActions.length === 0 && (
                <div style={{ border: `1px solid ${t.borderDim}`, borderRadius: '12px', padding: '48px 32px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', marginBottom: '16px' }}>✓</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: t.text, marginBottom: '8px' }}>All clear</div>
                  <div style={{ fontSize: '13px', color: t.muted }}>No tasks or recommended actions for you right now.</div>
                </div>
              )}

              {!myTasksLoading && myTasks.length > 0 && (
                <>
                  {myLaneActions.length > 0 && <div style={{ fontSize: '9px', letterSpacing: '2.5px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', marginBottom: '12px' }}>ASSIGNED MILESTONES</div>}
                  <Section label="OVERDUE" tasks={overdue} color="#ef4444" />
                  <Section label="DUE THIS WEEK" tasks={thisWeek} color="#f59e0b" />
                  <Section label="UPCOMING" tasks={later} />
                  <div style={{ fontSize: '10px', color: t.dim, marginTop: '16px', fontFamily: "'Roboto Mono', monospace" }}>Click the circle to mark complete · Click a program name to open it</div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── TEAM DIRECTORY ── */}
        {showTeamDirectory && !demoMode && session && (() => {
          const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
          const laneOrder = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const statusColor = { blocked: '#ef4444', at_risk: '#f59e0b', complete: '#10b981', on_track: '#3b82f6' };
          const emailByName = {};
          teamMembers.forEach(m => { if (m.full_name && m.email) emailByName[m.full_name.toLowerCase()] = m.email; });

          const totalPeople = new Set([
            ...programs.flatMap(p => {
              const leads = p.team_leads || {};
              const dris = p.lane_dris || {};
              return [...Object.values(leads), ...Object.values(dris).flat()].filter(Boolean);
            }),
            ...teamMembers.map(m => m.full_name),
          ]).size;

          const PersonChip = ({ name, isLead, laneColor }) => {
            const nomosUser = teamMembers.find(m => m.full_name?.toLowerCase() === name?.toLowerCase());
            const email = nomosUser?.email || emailByName[name?.toLowerCase()];
            const avatar = nomosUser?.avatar_url;
            const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
            const color = isLead ? (laneColor || t.accent) : t.dim;
            const clickable = !!nomosUser;
            const pres = nomosUser ? getPresence(nomosUser.last_active_at) : null;
            const pColor = pres ? presenceColor[pres] : null;
            return (
              <div onClick={clickable ? () => setViewingProfile({ ...nomosUser, email }) : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: t.bg, borderRadius: '6px', border: `1px solid ${isLead ? (laneColor || t.accent) + '40' : t.borderDim}`, cursor: clickable ? 'pointer' : 'default', transition: 'opacity 0.15s' }}
                onMouseEnter={e => { if (clickable) e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', backgroundColor: color + '20', border: `1px solid ${color}50`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color }}>
                    {avatar ? <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                  </div>
                  {pColor && <div style={{ position: 'absolute', bottom: '-1px', right: '-1px', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: pColor, border: `2px solid ${t.bg}` }} title={presenceLabel[pres]} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: isLead ? '700' : '500', color: t.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}{isLead && <span style={{ marginLeft: '5px', fontSize: '7px', color: laneColor || t.accent, letterSpacing: '1px' }}>LEAD</span>}
                  </div>
                  {email && <a href={`mailto:${email}`} onClick={e => e.stopPropagation()} style={{ fontSize: '9px', color: t.muted, textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</a>}
                </div>
              </div>
            );
          };

          return (
            <div style={{ padding: isMobile ? '24px 16px' : '40px 48px', maxWidth: '1200px' }}>
              <div style={{ marginBottom: '32px' }}>
                <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '3px', marginBottom: '8px' }}>COMPANY DIRECTORY</div>
                <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif" }}>{authCompany?.name || 'Your Team'}</div>
                <div style={{ fontSize: '13px', color: t.muted, marginTop: '4px' }}>{totalPeople} people · {programs.length} {programs.length === 1 ? 'program' : 'programs'}</div>
              </div>

              {/* Admin presence table */}
              {teamMembers.length > 0 && (
                <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '14px', padding: '20px 24px', marginBottom: '24px' }}>
                  <div style={{ fontSize: '9px', letterSpacing: '2px', color: t.subLabel, marginBottom: '16px', fontFamily: "'Roboto Mono', monospace" }}>TEAM PRESENCE</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {teamMembers.map(m => {
                      const pres = getPresence(m.last_active_at);
                      const pColor = presenceColor[pres];
                      const pLabel = presenceLabel[pres];
                      const signedInAgo = m.last_sign_in_at ? (() => {
                        const mins = Math.floor((Date.now() - new Date(m.last_sign_in_at).getTime()) / 60000);
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })() : 'Never';
                      const lastActiveFmt = m.last_active_at ? (() => {
                        const mins = Math.floor((Date.now() - new Date(m.last_active_at).getTime()) / 60000);
                        if (mins < 1) return 'Just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })() : 'Never';
                      const initials = (m.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                      return (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: `1px solid ${t.borderDim}` }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: t.accent + '20', border: `1px solid ${t.accent}40`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: t.accent }}>
                              {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                            </div>
                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: '9px', height: '9px', borderRadius: '50%', backgroundColor: pColor, border: `2px solid ${t.surface}` }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>{m.full_name || m.email}</div>
                            <div style={{ fontSize: '10px', color: t.muted }}>{m.lane ? m.lane.charAt(0).toUpperCase() + m.lane.slice(1) : 'No lane'} · {m.role === 'admin' ? 'Admin' : 'Member'}</div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: pColor }}>{pLabel}</div>
                            {userRole === 'admin' && <div style={{ fontSize: '10px', color: t.muted }}>Last active: {lastActiveFmt}</div>}
                          </div>
                          {userRole === 'admin' && (
                            <div style={{ textAlign: 'right', flexShrink: 0, display: isMobile ? 'none' : 'block' }}>
                              <div style={{ fontSize: '10px', color: t.dim }}>Signed in</div>
                              <div style={{ fontSize: '10px', color: t.muted }}>{signedInAgo}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {programs.map(p => {
                  const leads = p.team_leads || {};
                  const dris = p.lane_dris || {};
                  const activeLanes = laneOrder.filter(lane => leads[lane] || (dris[lane] || []).filter(Boolean).length > 0);
                  if (activeLanes.length === 0) return null;
                  const currentGate = p.current_gate || 1;
                  const gateNames = ['Program Inception', 'Concept Review', 'Engineering Review', 'Prototype 1', 'Prototype 2', 'Mass Production'];

                  return (
                    <div key={p.id} style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                      {/* Program header */}
                      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.borderDim}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>{p.name}</div>
                          <div style={{ fontSize: '10px', color: t.muted, marginTop: '2px', letterSpacing: '0.5px' }}>GATE {currentGate} · {(gateNames[currentGate - 1] || '').toUpperCase()}</div>
                        </div>
                        <button onClick={() => selectProgram(p)} style={{ fontSize: '10px', color: t.accent, background: 'none', border: `1px solid ${t.accent}40`, borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', letterSpacing: '1px' }}>OPEN →</button>
                      </div>

                      {/* Lane columns */}
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${activeLanes.length}, 1fr)`, gap: '0' }}>
                        {activeLanes.map((lane, idx) => {
                          const laneColor = laneColorMap[lane];
                          const lead = leads[lane];
                          const members = (dris[lane] || []).filter(Boolean);
                          const gate = (p.gates || []).find(g => g.lane === lane && g.gate_number === currentGate);
                          const pct = gate?.completion_percent || 0;
                          const status = gate?.status || 'on_track';
                          return (
                            <div key={lane} style={{ borderRight: idx < activeLanes.length - 1 ? `1px solid ${t.borderDim}` : 'none', padding: '14px 16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <div style={{ fontSize: '9px', fontWeight: '700', color: laneColor, letterSpacing: '2px' }}>{lane.toUpperCase()}</div>
                                <span style={{ fontSize: '9px', fontWeight: '700', color: statusColor[status] || t.dim, fontFamily: "'Roboto Mono', monospace" }}>{pct}%</span>
                              </div>
                              <div style={{ height: '3px', backgroundColor: t.borderDim, borderRadius: '2px', marginBottom: '10px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, backgroundColor: statusColor[status] || laneColor, borderRadius: '2px' }} />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {lead && <PersonChip name={lead} isLead={true} laneColor={laneColor} />}
                                {members.map(name => <PersonChip key={name} name={name} isLead={false} laneColor={laneColor} />)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {teamMembers.length > 0 && (
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${t.borderDim}` }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '3px', marginBottom: '14px' }}>NOMOS USERS · {teamMembers.length}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {teamMembers.map(m => {
                      const laneColor = laneColorMap[m.lane] || t.dim;
                      const initials = (m.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                      return (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '10px 14px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: laneColor + '20', border: `1px solid ${laneColor}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: laneColor, flexShrink: 0 }}>{initials}</div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: t.text }}>{m.full_name}</div>
                            {m.email && <div style={{ fontSize: '10px', color: t.muted }}><a href={`mailto:${m.email}`} style={{ color: t.muted, textDecoration: 'none' }}>{m.email}</a></div>}
                            <div style={{ display: 'flex', gap: '5px', marginTop: '3px' }}>
                              {m.lane && <span style={{ fontSize: '8px', color: laneColor, letterSpacing: '1px', fontWeight: '700' }}>{m.lane.toUpperCase()}</span>}
                              {m.role === 'admin' && <span style={{ fontSize: '8px', color: t.accent, letterSpacing: '1px' }}>· ADMIN</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── ADMIN: COMPANY SETTINGS ── */}
              {userRole === 'admin' && (
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${t.borderDim}` }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '3px', marginBottom: '16px' }}>COMPANY SETTINGS</div>
                  <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '10px', padding: '20px 24px', maxWidth: '480px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: t.text, letterSpacing: '1.5px', marginBottom: '6px', fontFamily: "'Roboto Mono', monospace" }}>EMAIL DOMAIN AUTO-JOIN</div>
                    <div style={{ fontSize: '11px', color: t.muted, marginBottom: '14px', lineHeight: 1.6 }}>Anyone signing up with one of these email domains will automatically join your company. Comma-separated (e.g. <span style={{ color: t.accent }}>apple.com,spacex.com</span>).</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        value={domainInput || authCompany?.email_domains || ''}
                        onChange={e => { setDomainInput(e.target.value); setDomainSaved(false); }}
                        placeholder="apple.com,spacex.com"
                        style={{ flex: 1, backgroundColor: t.bg, border: `1px solid ${t.border}`, borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: t.text, fontFamily: "'Roboto Mono', monospace", outline: 'none' }}
                      />
                      <button
                        onClick={async () => {
                          setDomainSaving(true);
                          try {
                            const r = await authFetch(`${API}/company/domains`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domains: domainInput }) });
                            if (r.ok) { const d = await r.json(); setAuthCompany(prev => ({ ...prev, email_domains: d.email_domains })); setDomainSaved(true); }
                          } finally { setDomainSaving(false); }
                        }}
                        disabled={domainSaving}
                        style={{ padding: '10px 16px', backgroundColor: t.accent, border: 'none', borderRadius: '6px', color: '#000', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', fontFamily: "'Roboto Mono', monospace", cursor: domainSaving ? 'not-allowed' : 'pointer', opacity: domainSaving ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                        {domainSaving ? 'SAVING...' : domainSaved ? 'SAVED ✓' : 'SAVE'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {!showTeamDirectory && !showMyTasks && !showSuperAdmin && (userRole === 'member' && userLane && !demoMode ? (() => {
          const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
          const laneColor = laneColorMap[userLane] || t.accent;
          const laneLabel = { design: 'Design', finance: 'Finance', operations: 'Operations', installation: 'Installation', engineering: 'Engineering' }[userLane] || userLane;
          return (
            <>
              {/* Lane identity banner */}
              <div style={{ backgroundColor: laneColor + '12', border: `1px solid ${laneColor}30`, borderRadius: '12px', padding: isMobile ? '20px' : '28px 32px', marginBottom: '36px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: laneColor, letterSpacing: '3px', marginBottom: '8px', fontWeight: '700' }}>YOUR WORKSPACE · {(authCompany?.name || '').toUpperCase()}</div>
                    <div style={{ fontSize: isMobile ? '26px' : '38px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px', lineHeight: 1.1 }}>
                      {laneLabel} Team
                    </div>
                    <div style={{ fontSize: '13px', color: t.muted, marginTop: '6px' }}>
                      {programs.length} active launch {programs.length === 1 ? 'program' : 'programs'} · open a program to see your workspace
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: laneColor }} />
                      <span style={{ fontSize: '9px', color: laneColor, letterSpacing: '2px', fontWeight: '700' }}>{laneLabel.toUpperCase()} TEAM</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Programs */}
              {programs.length > 0 && <div style={{ color: t.dim, fontSize: '10px', letterSpacing: '3px', marginBottom: '16px' }}>ACTIVE PROGRAMS</div>}

              {/* Member empty state */}
              {programs.length === 0 && (
                <div style={{ border: `1px solid ${laneColor}30`, borderRadius: '12px', padding: '40px 32px', textAlign: 'center', backgroundColor: laneColor + '08' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: laneColor + '20', border: `1px solid ${laneColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '20px' }}>⏳</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: t.text, fontFamily: "'Inter', sans-serif", marginBottom: '10px' }}>You're in. No programs yet.</div>
                  <div style={{ fontSize: '13px', color: t.muted, lineHeight: 1.8, maxWidth: '380px', margin: '0 auto' }}>
                    Your launch lead hasn't created any programs yet. Once they do, they'll show up here and you'll be able to open your {laneLabel} workspace.
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: '12px' }}>
                {programs.map((program, idx) => {
                  const dr = daysRemaining(program.launch_date);
                  const isBlocked = program.status === 'blocked';
                  const isAtRisk = program.status === 'at_risk';
                  const badgeColor = isBlocked ? '#ef4444' : isAtRisk ? '#f59e0b' : '#10b981';
                  const badgeLabel = isBlocked ? 'CRITICAL' : isAtRisk ? 'AT RISK' : program.status === 'complete' ? 'COMPLETE' : 'ON TRACK';
                  const drColor = dr < 0 ? '#ef4444' : dr < 60 ? '#f59e0b' : t.accent;
                  const code = `NMS-${String(idx + 1).padStart(3, '0')}`;
                  return (
                    <div key={program.id} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px 24px', cursor: 'pointer' }}
                      onClick={() => selectProgram(program)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>{code} · FLAGSHIP</div>
                        <div style={{ backgroundColor: badgeColor + '20', border: `1px solid ${badgeColor}60`, borderRadius: '3px', padding: '2px 8px', fontSize: '9px', color: badgeColor, letterSpacing: '2px', fontWeight: 'bold' }}>{badgeLabel}</div>
                      </div>
                      <div style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '700', color: t.text, lineHeight: 1.2, marginBottom: '6px', fontFamily: "'Inter', sans-serif" }}>{program.name}</div>
                      <div style={{ fontSize: '12px', color: t.muted, marginBottom: '18px' }}>{program.markets || 'Global'}{program.num_locations > 0 ? ` · ${program.num_locations.toLocaleString()} locations` : ''}</div>
                      <div style={{ display: 'flex', gap: '24px', marginBottom: '18px' }}>
                        <div><div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>T-MINUS</div><div style={{ fontSize: '15px', fontWeight: 'bold', color: drColor }}>{dr > 0 ? `${dr}d` : dr === 0 ? 'TODAY' : 'PAST'}</div></div>
                        <div><div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '4px' }}>GATE</div><div style={{ fontSize: '15px', fontWeight: 'bold', color: t.accent }}>G{program.current_gate}</div></div>
                      </div>
                      <div style={{ display: 'flex', gap: '3px', marginBottom: '18px' }}>
                        {[1,2,3,4,5,6].map(g => {
                          const done = g < program.current_gate;
                          const cur = g === program.current_gate;
                          return <div key={g} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: done ? laneColor : cur ? laneColor + '80' : t.borderDim, opacity: done || cur ? 1 : 0.3 }} />;
                        })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '10px', color: t.dim }}>Tap to open workspace</span>
                        <div style={{ backgroundColor: laneColor + '20', border: `1px solid ${laneColor}50`, borderRadius: '4px', padding: '5px 12px', fontSize: '10px', color: laneColor, letterSpacing: '1.5px', fontWeight: '700', fontFamily: "'Roboto Mono', monospace" }}>
                          OPEN MY WORKSPACE →
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })() : (
        <>
        {/* ── ADMIN PROGRAMS LIST ── */}
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <div style={{ width: '24px', height: '1.5px', backgroundColor: t.accent }} />
          <span style={{ color: t.dim, fontSize: '9px', letterSpacing: '2px' }}>CONSOLE</span>
          <span style={{ color: t.subLabel, fontSize: '9px' }}>/</span>
          <span style={{ color: t.accent, fontSize: '9px', letterSpacing: '2px' }}>PROGRAMS</span>
        </div>

        {/* Heading row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontSize: isMobile ? '26px' : '38px', fontWeight: '700', margin: 0, color: t.text, letterSpacing: '-0.5px', fontFamily: "'Inter', sans-serif" }}>
            {programs.filter(p => p.status !== 'complete').length > 0
              ? `${programs.filter(p => p.status !== 'complete').length} active launch program${programs.filter(p => p.status !== 'complete').length === 1 ? '' : 's'}`
              : 'Launch programs'}
            {programs.filter(p => p.status === 'complete').length > 0 && (
              <span style={{ fontSize: isMobile ? '13px' : '15px', color: '#10b981', marginLeft: '14px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', fontWeight: '600' }}>
                · {programs.filter(p => p.status === 'complete').length} launched ✓
              </span>
            )}
          </h1>
          {/* Desktop: full button row */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {userRole === 'admin' && authCompany?.code && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${t.border}`, borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontFamily: "'Roboto Mono', monospace" }}>
                  <span style={{ color: t.dim, letterSpacing: '1px' }}>INVITE:</span>
                  <span style={{ color: t.accent, letterSpacing: '2px', fontWeight: '700' }}>{authCompany.code}</span>
                  <button onClick={() => { navigator.clipboard.writeText(authCompany.code); }} title="Copy invite code" style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '12px', padding: '0 2px' }}>⎘</button>
                </div>
              )}
              {userRole === 'admin' && (
                <span style={{ fontSize: '9px', letterSpacing: '2px', color: '#000', backgroundColor: t.accent, borderRadius: '4px', padding: '3px 7px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>ADMIN</span>
              )}
              {userRole === 'admin' && (
                <button onClick={() => { setInviteOpen(o => !o); setInviteResults({}); setInviteRows([{ id: Date.now(), name: '', email: '', lane: '', existing: false }]); }} style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: '4px', padding: '8px 14px', cursor: 'pointer', fontSize: '10px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                  INVITE TEAM
                </button>
              )}
              <button style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '8px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px' }} onClick={openFirstRun}>
                + NEW LAUNCH
              </button>
            </div>
          )}
        </div>

        {/* Invite team panel */}
        {inviteOpen && userRole === 'admin' && (
          <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '10px', letterSpacing: '2px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace" }}>ADD TEAM MEMBERS</div>
                <div style={{ fontSize: '11px', color: t.muted, marginTop: '3px' }}>Accounts are created instantly — they'll receive a one-click setup link.</div>
              </div>
              <button onClick={() => { setInviteOpen(false); setInviteResults({}); }} style={{ background: 'none', border: 'none', color: t.muted, fontSize: '16px', cursor: 'pointer' }}>✕</button>
            </div>

            {/* Column headers */}
            {!isMobile && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 130px 110px 28px', gap: '8px', marginBottom: '4px', paddingLeft: '2px' }}>
                {['FULL NAME', 'EMAIL', 'LANE', 'ROLE', ''].map(h => (
                  <div key={h} style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>{h}</div>
                ))}
              </div>
            )}

            {/* Invite rows */}
            {inviteRows.map(row => {
              const nameMatches = row.name.length >= 2
                ? teamMembers.filter(m => m.full_name?.toLowerCase().includes(row.name.toLowerCase()))
                : [];
              const rowResult = inviteResults[row.id];
              return (
                <div key={row.id} style={{ marginBottom: '6px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 130px 110px 28px', gap: '8px', alignItems: 'center' }}>
                    {/* Name with typeahead */}
                    <div style={{ position: 'relative' }}>
                      <input
                        style={{ ...s.input, margin: 0, width: '100%', boxSizing: 'border-box', border: row.existing ? `1px solid ${t.accent}` : undefined }}
                        placeholder="Full name"
                        value={row.name}
                        disabled={inviteSending || row.existing}
                        onChange={e => { updateInviteRow(row.id, 'name', e.target.value); updateInviteRow(row.id, 'existing', false); }}
                        onFocus={() => setInviteTypeaheadRow(row.id)}
                        onBlur={() => setTimeout(() => setInviteTypeaheadRow(null), 150)}
                      />
                      {row.existing && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>IN NOMOS</span>}
                      {inviteTypeaheadRow === row.id && nameMatches.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '6px', zIndex: 200, maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '2px' }}>
                          {nameMatches.map(m => (
                            <div key={m.id} onMouseDown={() => {
                              updateInviteRow(row.id, 'name', m.full_name || '');
                              updateInviteRow(row.id, 'email', m.email || '');
                              updateInviteRow(row.id, 'existing', true);
                              setInviteTypeaheadRow(null);
                            }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', borderBottom: `1px solid ${t.borderDim}` }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = t.hover}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                              {m.avatar_url
                                ? <img src={m.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                : <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#000' }}>{(m.full_name || '?')[0].toUpperCase()}</div>
                              }
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: t.text }}>{m.full_name}</div>
                                <div style={{ fontSize: '10px', color: t.muted }}>{m.lane ? m.lane.charAt(0).toUpperCase() + m.lane.slice(1) + ' · ' : ''}Already in Nomos</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Email */}
                    <input
                      style={{ ...s.input, margin: 0, width: '100%', boxSizing: 'border-box' }}
                      type="email"
                      placeholder="email@company.com"
                      value={row.email}
                      disabled={inviteSending || row.existing}
                      onChange={e => updateInviteRow(row.id, 'email', e.target.value)}
                    />
                    {/* Lane */}
                    <select
                      value={row.lane}
                      disabled={inviteSending}
                      onChange={e => updateInviteRow(row.id, 'lane', e.target.value)}
                      style={{ ...s.input, margin: 0, width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">No lane</option>
                      {['design', 'finance', 'operations', 'installation', 'engineering'].map(l => (
                        <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                      ))}
                    </select>
                    {/* Role */}
                    <select
                      value={row.role || 'member'}
                      disabled={inviteSending}
                      onChange={e => updateInviteRow(row.id, 'role', e.target.value)}
                      style={{ ...s.input, margin: 0, width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="member">Member</option>
                      <option value="executive">Executive</option>
                    </select>
                    {/* Remove row */}
                    <button onClick={() => removeInviteRow(row.id)} disabled={inviteSending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.muted, fontSize: '14px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                  {rowResult && (
                    <div style={{ fontSize: '10px', color: rowResult.ok ? t.accent : '#ef4444', fontFamily: "'Roboto Mono', monospace", marginTop: '3px', paddingLeft: '2px' }}>
                      {rowResult.ok ? '✓ Invite sent' : '✗ ' + rowResult.msg}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add row + paste toggle */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px', marginBottom: '12px' }}>
              <button onClick={addInviteRow} disabled={inviteSending} style={{ background: 'none', border: `1px dashed ${t.border}`, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '11px', color: t.muted, fontFamily: "'Roboto Mono', monospace', letterSpacing: '1px'" }}>
                + ADD PERSON
              </button>
              <button onClick={() => setInvitePasteOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                PASTE A LIST
              </button>
            </div>

            {/* Paste area */}
            {invitePasteOpen && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '9px', color: t.muted, letterSpacing: '1px', marginBottom: '4px', fontFamily: "'Roboto Mono', monospace" }}>PASTE NAME, EMAIL (ONE PER LINE)</div>
                <textarea
                  value={invitePaste}
                  onChange={e => setInvitePaste(e.target.value)}
                  placeholder={"Alden Smith, alden@company.com\nMaria Garcia, maria@company.com"}
                  rows={4}
                  style={{ ...s.input, margin: 0, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                />
                <button onClick={parsePaste} disabled={!invitePaste.trim()} style={{ ...s.ghostBtn, marginTop: '6px' }}>PARSE INTO ROWS →</button>
              </div>
            )}

            {/* Send all */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '10px', color: t.subLabel, lineHeight: 1.5 }}>
                {inviteRows.filter(r => r.name && r.email && !r.existing).length} new {inviteRows.filter(r => r.name && r.email && !r.existing).length === 1 ? 'person' : 'people'} to invite
              </div>
              <button
                style={{ ...s.ghostBtn, opacity: inviteSending || !inviteRows.some(r => r.name.trim() && r.email.trim() && !r.existing) ? 0.5 : 1 }}
                onClick={sendAllInvites}
                disabled={inviteSending || !inviteRows.some(r => r.name.trim() && r.email.trim() && !r.existing)}
              >
                {inviteSending ? 'SENDING...' : 'SEND ALL INVITES →'}
              </button>
            </div>
          </div>
        )}

        {/* ── PORTFOLIO HEATMAP ── */}
        {portfolio && portfolio.programs?.length > 0 && portfolio.laneHealth && (() => {
          const lanes = ['design', 'finance', 'operations', 'installation', 'engineering'];
          const statusColor = { blocked: '#ef4444', at_risk: '#f59e0b', on_track: '#10b981', complete: '#6366f1', not_started: t.borderDim, idle: t.borderDim };
          const statusLabel = { blocked: 'BLOCKED', at_risk: 'AT RISK', on_track: 'ON TRACK', complete: 'COMPLETE', not_started: '—', idle: '—' };
          const worstLane = lanes.reduce((worst, l) => {
            const h = portfolio.laneHealth?.[l] || {};
            const score = (h.blocked || 0) * 3 + (h.at_risk || 0);
            const worstScore = (() => { const w = portfolio.laneHealth?.[worst] || {}; return (w.blocked || 0) * 3 + (w.at_risk || 0); })();
            return score > worstScore ? l : worst;
          }, lanes[0]);
          return (
            <div style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '10px', padding: isMobile ? '16px' : '20px 24px', marginBottom: '28px', overflowX: 'auto' }}>
              <div style={{ fontSize: '9px', letterSpacing: '2px', color: t.subLabel, marginBottom: '14px', fontFamily: "'Roboto Mono', monospace" }}>PORTFOLIO HEALTH — CURRENT GATE</div>
              <div style={{ display: 'grid', gridTemplateColumns: `100px repeat(${portfolio.programs.length}, 1fr)`, gap: '4px', minWidth: '400px' }}>
                {/* Header row */}
                <div />
                {portfolio.programs.map(p => (
                  <div key={p.id} style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px', textAlign: 'center', fontFamily: "'Roboto Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', padding: '0 2px' }} onClick={() => { const prog = programs.find(x => x.id === p.id); if (prog) selectProgram(prog); }} title={p.name}>
                    {(p.name || '').split(' ').slice(0, 2).join(' ').toUpperCase()}
                  </div>
                ))}
                {/* Lane rows */}
                {lanes.map(lane => (
                  <>
                    <div key={`${lane}-label`} style={{ fontSize: '9px', color: lane === worstLane ? '#f59e0b' : t.dim, letterSpacing: '1px', display: 'flex', alignItems: 'center', fontFamily: "'Roboto Mono', monospace", fontWeight: lane === worstLane ? '700' : 'normal' }}>
                      {lane === worstLane ? '⚠ ' : ''}{lane.toUpperCase()}
                    </div>
                    {portfolio.programs.map(p => {
                      const gs = p.gates?.[lane];
                      const st = gs?.status || 'not_started';
                      const pct = gs?.completion_percent || 0;
                      return (
                        <div key={`${lane}-${p.id}`} title={`${p.name} · ${lane} · ${statusLabel[st] || st} · ${pct}%`} onClick={() => { const prog = programs.find(x => x.id === p.id); if (prog) selectProgram(prog); }} style={{ borderRadius: '4px', height: '28px', backgroundColor: statusColor[st] + '25', border: `1px solid ${statusColor[st]}60`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7px', color: statusColor[st], fontWeight: '700', fontFamily: "'Roboto Mono', monospace", letterSpacing: '0.5px' }}>
                          {st === 'not_started' || st === 'idle' ? '' : st === 'complete' ? '✓' : (statusLabel[st] || st).slice(0, 2)}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
                {[['blocked', '#ef4444', 'BLOCKED'], ['at_risk', '#f59e0b', 'AT RISK'], ['on_track', '#10b981', 'ON TRACK'], ['complete', '#6366f1', 'COMPLETE']].map(([k, c, l]) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: c + '50', border: `1px solid ${c}` }} />
                    <span style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1px', fontFamily: "'Roboto Mono', monospace" }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── STAT CARDS ── */}
        {programs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '40px' }}>
          {[
            { label: 'PROGRAMS IN FLIGHT',  value: inFlight,  color: t.accent },
            { label: 'AT RISK OR CRITICAL', value: atRiskCnt, color: atRiskCnt > 0 ? '#f59e0b' : t.accent },
            { label: 'COMBINED BUDGET',     value: `$${(totalBudget / 1000000).toFixed(1)}M`, color: t.accent },
            { label: 'ACTIVE SWIM LANES',   value: inFlight * 5, color: t.accent },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: isMobile ? '16px' : '24px 28px' }}>
              <div style={{ color: t.muted, fontSize: '9px', letterSpacing: '2px', marginBottom: '14px' }}>{label}</div>
              <div style={{ color, fontSize: isMobile ? '28px' : '36px', fontWeight: '700', fontFamily: "'Inter', sans-serif", letterSpacing: '-0.5px' }}>{value}</div>
            </div>
          ))}
        </div>
        )}

        {/* ── ADMIN ONBOARDING EMPTY STATE ── */}
        {showWelcome && programs.length === 0 && (
          <div style={{ border: `1px solid ${t.border}`, borderRadius: '12px', padding: isMobile ? '32px 24px' : '56px 48px', textAlign: 'center', backgroundColor: t.surface, marginBottom: '32px' }}>
            <div style={{ fontSize: '10px', color: t.accent, letterSpacing: '3px', marginBottom: '16px' }}>WELCOME TO NOMOS</div>
            <h2 style={{ fontSize: isMobile ? '22px' : '30px', fontWeight: '700', color: t.text, margin: '0 0 12px', fontFamily: "'Inter', sans-serif" }}>
              {authCompany?.name || 'Your company'} is live.
            </h2>
            <p style={{ fontSize: '14px', color: t.muted, lineHeight: 1.8, maxWidth: '480px', margin: '0 auto 36px' }}>
              You're set up as admin. Create your first launch program, then share your invite code so your team can join their workspace.
            </p>

            {/* 3-step progress */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '0', marginBottom: '36px', flexWrap: 'wrap' }}>
              {[
                { label: 'Company created', done: true },
                { label: 'Admin account', done: true },
                { label: 'First program', done: false },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: step.done ? '#10b981' : t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: '#000', fontWeight: '700' }}>
                      {step.done ? '✓' : '3'}
                    </div>
                    <span style={{ fontSize: '10px', color: step.done ? '#10b981' : t.accent, letterSpacing: '1px', whiteSpace: 'nowrap', fontFamily: "'Roboto Mono', monospace" }}>{step.label}</span>
                  </div>
                  {i < 2 && <div style={{ width: isMobile ? '40px' : '72px', height: '1px', backgroundColor: i === 0 ? '#10b981' : t.borderDim, margin: '0 8px', marginBottom: '28px' }} />}
                </div>
              ))}
            </div>

            {/* Invite code */}
            {authCompany?.code && (
              <div style={{ backgroundColor: darkMode ? '#111' : '#f8fafc', border: `1px solid ${t.border}`, borderRadius: '8px', padding: '16px 24px', display: 'inline-flex', alignItems: 'center', gap: '20px', marginBottom: '32px' }}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '6px' }}>SHARE WITH YOUR TEAM</div>
                  <div style={{ fontSize: '22px', letterSpacing: '5px', fontWeight: '700', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>{authCompany.code}</div>
                </div>
                <button onClick={() => navigator.clipboard.writeText(authCompany.code)} style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: '4px', padding: '8px 14px', cursor: 'pointer', fontSize: '10px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>COPY</button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
              <button onClick={() => { localStorage.removeItem('nomos_new_admin'); setShowWelcome(false); openFirstRun(); }} style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '14px 36px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
                CREATE YOUR FIRST PROGRAM →
              </button>
              <button onClick={() => { localStorage.removeItem('nomos_new_admin'); setShowWelcome(false); }} style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: '6px', padding: '14px 20px', cursor: 'pointer', fontSize: '11px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>
                DISMISS
              </button>
            </div>
          </div>
        )}

        {/* ── INVITE NUDGE ── */}
        {showInviteNudge && userRole === 'admin' && (
          <div style={{ border: `1px solid ${t.accent}40`, borderLeft: `4px solid ${t.accent}`, borderRadius: '8px', padding: '20px 24px', marginBottom: '28px', backgroundColor: t.accent + '08', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '9px', color: t.accent, letterSpacing: '2px', fontWeight: '700', marginBottom: '4px' }}>STEP 2 — INVITE YOUR TEAM</div>
              <div style={{ fontSize: '13px', color: t.text, fontWeight: '600', marginBottom: '2px' }}>Your first program is live. Now bring your team in.</div>
              <div style={{ fontSize: '11px', color: t.dim }}>Share your company code or send email invites below.</div>
            </div>
            {authCompany?.code && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '6px', padding: '10px 16px' }}>
                <div>
                  <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '2px', marginBottom: '3px' }}>TEAM CODE</div>
                  <div style={{ fontSize: '18px', letterSpacing: '4px', fontWeight: '700', color: t.accent, fontFamily: "'Roboto Mono', monospace" }}>{authCompany.code}</div>
                </div>
                <button onClick={() => navigator.clipboard.writeText(authCompany.code)} style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '10px', color: t.dim, fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px' }}>COPY</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => { setInviteOpen(true); setInviteResults({}); setInviteRows([{ id: Date.now(), name: '', email: '', lane: '', existing: false }]); }} style={{ backgroundColor: t.accent, border: 'none', color: '#000', padding: '10px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', borderRadius: '6px' }}>
                INVITE BY EMAIL
              </button>
              <button onClick={() => { localStorage.removeItem('nomos_invite_nudge'); setShowInviteNudge(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.dim, fontSize: '18px', padding: '4px 8px' }}>×</button>
            </div>
          </div>
        )}

        {/* ── SEARCH + FILTER BAR ── */}
        {programs.length > 0 && (
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
              <input
                style={{ ...s.input, margin: 0, paddingLeft: '32px', fontSize: '12px', height: '36px', boxSizing: 'border-box' }}
                placeholder="Search programs..."
                value={programSearch}
                onChange={e => setProgramSearch(e.target.value)}
              />
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: t.subLabel, fontSize: '13px', pointerEvents: 'none' }}>⌕</span>
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {[['all', 'ALL'], ['on_track', 'ON TRACK'], ['at_risk', 'AT RISK'], ['blocked', 'CRITICAL'], ['complete', 'COMPLETE']].map(([val, label]) => (
                <button key={val} onClick={() => setProgramFilter(val)} style={{ background: programFilter === val ? t.accent : 'none', border: `1px solid ${programFilter === val ? t.accent : t.border}`, color: programFilter === val ? '#000' : t.dim, padding: '5px 12px', fontFamily: "'Roboto Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '4px', fontWeight: programFilter === val ? '700' : 'normal' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── PROGRAM CARDS ── */}
        {(() => {
          const q = programSearch.toLowerCase();
          const filtered = programs.filter(p =>
            (programFilter === 'all' || p.status === programFilter) &&
            (!q || p.name?.toLowerCase().includes(q) || (p.markets || '').toLowerCase().includes(q))
          );
          const activeFiltered   = filtered.filter(p => p.status !== 'complete');
          const launchedFiltered = filtered.filter(p => p.status === 'complete');
          if (programs.length > 0 && filtered.length === 0) {
            return <div style={{ color: t.subLabel, fontSize: '12px', fontFamily: "'Roboto Mono', monospace", letterSpacing: '1px', padding: '32px 0', textAlign: 'center' }}>No programs match your search.</div>;
          }
          const renderCard = (program, idx, isLaunched) => {
            const dr = daysRemaining(program.launch_date);
            const isBlocked  = program.status === 'blocked';
            const isAtRisk   = program.status === 'at_risk';
            const badgeColor = isLaunched ? '#10b981' : isBlocked ? '#ef4444' : isAtRisk ? '#f59e0b' : '#10b981';
            const badgeLabel = isLaunched ? 'LAUNCHED ✓' : isBlocked ? 'CRITICAL' : isAtRisk ? 'AT RISK' : 'ON TRACK';
            const code       = `NMS-${String(idx + 1).padStart(3, '0')}`;
            const market     = (program.markets || 'GLOBAL').split(',')[0].trim().toUpperCase();
            const drColor    = dr < 0 ? '#ef4444' : dr < 60 ? '#f59e0b' : t.accent;

            return (
              <div key={program.id}
                style={{ backgroundColor: t.surface, border: isLaunched ? '1px solid #10b98140' : `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '20px 24px', cursor: 'pointer', borderLeft: isLaunched ? '3px solid #10b981' : undefined }}
                onClick={() => selectProgram(program)}>

                {/* Top row: code · market · type + badge + delete */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1px' }}>
                    {code} · {market} · FLAGSHIP
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ backgroundColor: badgeColor + '20', border: `1px solid ${badgeColor}60`, borderRadius: '3px', padding: '2px 8px', fontSize: '9px', color: badgeColor, letterSpacing: '2px', fontWeight: 'bold' }}>
                      {badgeLabel}
                    </div>
                    {userRole === 'admin' && (
                      <button
                        title="Delete program"
                        onClick={e => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${program.name}"? This cannot be undone.`)) {
                            authFetch(`${API}/programs/${program.id}`, { method: 'DELETE' })
                              .then(() => setPrograms(prev => prev.filter(p => p.id !== program.id)));
                          }
                        }}
                        style={{ background: 'none', border: 'none', color: t.subLabel, cursor: 'pointer', fontSize: '13px', padding: '2px 4px', lineHeight: 1, opacity: 0.5 }}
                        onMouseEnter={e => e.target.style.opacity = 1}
                        onMouseLeave={e => e.target.style.opacity = 0.5}>
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Program name */}
                <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700', color: t.text, lineHeight: 1.2, marginBottom: '6px', fontFamily: "'Inter', sans-serif", letterSpacing: '-0.2px' }}>
                  {program.name}
                </div>

                {/* Region · locations */}
                <div style={{ fontSize: '12px', color: t.muted, marginBottom: '20px' }}>
                  {program.markets || 'Global'}{program.num_locations > 0 ? ` · ${program.num_locations.toLocaleString()} ${program.num_locations === 1 ? 'location' : 'locations'}` : ''}
                </div>

                {/* Metrics */}
                <div style={{ display: 'flex', gap: '28px', marginBottom: '20px' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '5px' }}>T-MINUS</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: drColor }}>
                      {dr > 0 ? `${dr}d` : dr === 0 ? 'TODAY' : 'PAST'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '5px' }}>BUDGET</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: t.text }}>
                      ${((program.budget_total || 0) / 1000000).toFixed(1)}M
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '5px' }}>GATE</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: t.accent }}>G{program.current_gate}</div>
                  </div>
                </div>

                {/* Phase progression bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px' }}>PHASE PROGRESSION</div>
                    <div style={{ fontSize: '9px', color: t.muted }}>{program.current_gate}/6</div>
                  </div>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[1,2,3,4,5,6].map(gate => {
                      const done    = gate < program.current_gate;
                      const current = gate === program.current_gate;
                      const col     = done ? '#10b981' : current ? t.accent : t.borderDim;
                      return <div key={gate} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: col, opacity: done || current ? 1 : 0.35 }} />;
                    })}
                  </div>
                </div>

              </div>
            );
          };
          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))', gap: '12px' }}>
                {activeFiltered.map((program, idx) => renderCard(program, idx, false))}
              </div>
              {launchedFiltered.length > 0 && (
                <div style={{ marginTop: '40px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ height: '1px', flex: 1, backgroundColor: t.borderDim }} />
                    <div style={{ fontSize: '9px', color: '#10b981', letterSpacing: '3px', fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>LAUNCHED — {launchedFiltered.length}</div>
                    <div style={{ height: '1px', flex: 1, backgroundColor: t.borderDim }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))', gap: '12px', opacity: 0.75 }}>
                    {launchedFiltered.map((program, idx) => renderCard(program, idx, true))}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── TEAM ROSTER ── */}
        {!demoMode && teamMembers.length > 1 && (
          <div style={{ marginTop: '48px' }}>
            <div style={{ fontSize: '10px', letterSpacing: '3px', color: t.dim, marginBottom: '16px', fontFamily: "'Roboto Mono', monospace" }}>YOUR TEAM — {teamMembers.length} MEMBERS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {teamMembers.map(member => {
                const laneColorMap = { design: '#818cf8', finance: '#10b981', operations: '#f59e0b', installation: '#ef4444', engineering: '#3b82f6' };
                const laneColor = laneColorMap[member.lane] || t.dim;
                const initials = (member.full_name || member.id || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                return (
                  <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', padding: '10px 14px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: laneColor + '25', border: `1px solid ${laneColor}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: laneColor, flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: t.text }}>{member.full_name || 'Team Member'}</div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '2px' }}>
                        {member.lane && <span style={{ fontSize: '8px', letterSpacing: '1.5px', color: laneColor, fontFamily: "'Roboto Mono', monospace", fontWeight: '700' }}>{member.lane.toUpperCase()}</span>}
                        {member.role === 'admin' && <span style={{ fontSize: '8px', letterSpacing: '1px', color: t.accent, fontFamily: "'Roboto Mono', monospace", backgroundColor: t.accent + '15', padding: '1px 5px', borderRadius: '3px' }}>ADMIN</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── RECENT ACTIVITY ── */}
        {!demoMode && session && notifications.length > 0 && (
          <div style={{ marginTop: '48px' }}>
            <div style={{ fontSize: '10px', letterSpacing: '3px', color: t.dim, marginBottom: '16px', fontFamily: "'Roboto Mono', monospace" }}>RECENT ACTIVITY</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {notifications.slice(0, 10).map(n => {
                const eventColor = { BLOCKED: '#ef4444', COMPLETE: '#10b981', AT_RISK: '#f59e0b', CASCADE: '#8b5cf6' }[n.event_type] || t.dim;
                const timeAgo = (() => {
                  const diff = Date.now() - new Date(n.created_at).getTime();
                  const mins = Math.floor(diff / 60000);
                  if (mins < 1) return 'just now';
                  if (mins < 60) return `${mins}m ago`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs}h ago`;
                  return `${Math.floor(hrs / 24)}d ago`;
                })();
                const prog = programs.find(p => p.id === n.program_id);
                return (
                  <div
                    key={n.id}
                    onClick={() => prog && selectProgram(prog)}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '8px', cursor: prog ? 'pointer' : 'default' }}
                  >
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: eventColor, flexShrink: 0 }} />
                    <span style={{ fontSize: '9px', letterSpacing: '1.5px', color: eventColor, fontFamily: "'Roboto Mono', monospace", fontWeight: '700', width: '64px', flexShrink: 0 }}>{n.event_type}</span>
                    <span style={{ fontSize: '11px', color: t.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</span>
                    <span style={{ fontSize: '9px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace", flexShrink: 0 }}>{timeAgo}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        </>
        ))}

      </div>

      {/* Mobile FAB — New Launch (admin only, programs list only) */}
      {isMobile && session && userRole === 'admin' && !selectedProgram && !showMyTasks && !showTeamDirectory && !showSuperAdmin && (
        <button
          onClick={openFirstRun}
          style={{ position: 'fixed', bottom: '24px', right: '20px', zIndex: 500, width: '56px', height: '56px', borderRadius: '50%', backgroundColor: t.accent, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(13,148,136,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1px' }}
          title="New Launch"
        >
          <span style={{ fontSize: '26px', color: '#000', lineHeight: 1, fontWeight: '300', marginTop: '-2px' }}>+</span>
        </button>
      )}

      <MyProfileModal
        open={myProfileOpen}
        onClose={() => setMyProfileOpen(false)}
        session={session}
        authFetch={authFetch}
        darkMode={darkMode}
        isMobile={isMobile}
        initialName={myFullName}
        initialTitle={myTitle}
        initialAvatar={myAvatarUrl}
        initialEmail={session?.user?.email}
        onSaved={(updates) => {
          if (updates.full_name) setMyFullName(updates.full_name);
          if (updates.title) setMyTitle(updates.title);
          if (updates.avatar_url) setMyAvatarUrl(updates.avatar_url);
        }}
      />

      <ProfileViewModal
        open={!!viewingProfile}
        onClose={() => setViewingProfile(null)}
        profile={viewingProfile}
        darkMode={darkMode}
        authFetch={authFetch}
      />

      {/* ── CONSEQUENCES DETAIL PANEL ── */}
      {consDetailPanel && selectedProgram && (() => {
        const fm = consequences?.financial_model || {};
        const p = selectedProgram;
        const gateNames = ['Program Inception','Concept Review','Engineering Review','Prototype 1','Prototype 2','Mass Production'];

        const financeContent = () => {
          const metric = consDetailPanel.metric;
          const items = {
            stranded_assets: {
              title: 'Stranded Assets',
              color: '#ef4444',
              value: fm.stranded_assets || 0,
              formula: `Launched Locations × Units per Location × Unit Cost`,
              calc: `${p.launched_locations || 0} locations × ${p.units_per_location || 2} units × $${(p.unit_cost || 0).toLocaleString()}`,
              what: 'Units already shipped to launched locations that cannot be recalled or resold if this launch is cancelled or significantly redesigned.',
              when: 'Exposure grows as more locations go live before the program achieves final sign-off.',
              fix: p.unit_cost > 0 && p.launched_locations > 0
                ? `You have ${p.launched_locations} live locations. To reduce exposure: pause additional rollouts until all gates reach ON TRACK.`
                : 'Set Unit Cost and Launched Locations in Program Settings to calculate real exposure.',
              inputs: [
                { label: 'Launched Locations', val: p.launched_locations || 0 },
                { label: 'Units per Location', val: p.units_per_location || 2 },
                { label: 'Unit Cost', val: `$${(p.unit_cost || 0).toLocaleString()}` },
              ],
            },
            retrofit_cost: {
              title: 'Retrofit Cost',
              color: '#f59e0b',
              value: fm.retrofit_cost || 0,
              formula: `Launched Locations × Retrofit Cost per Location`,
              calc: `${p.launched_locations || 0} locations × $${(p.retrofit_cost_per_location || 750).toLocaleString()}/loc`,
              what: 'Cost to physically update already-installed fixtures or hardware if a design or engineering change is required post-launch.',
              when: 'Triggered when Engineering or Design lanes are BLOCKED or AT RISK after locations are already live.',
              fix: p.retrofit_cost_per_location > 0
                ? `Your estimate is $${p.retrofit_cost_per_location}/loc. Update in Program Settings if the true cost differs.`
                : 'Set Retrofit Cost per Location in Program Settings. Default is $750.',
              inputs: [
                { label: 'Launched Locations', val: p.launched_locations || 0 },
                { label: 'Retrofit Cost / Loc', val: `$${(p.retrofit_cost_per_location || 750).toLocaleString()}` },
              ],
            },
            revenue_impact: {
              title: 'Revenue Impact',
              color: '#f59e0b',
              value: fm.revenue_impact || 0,
              formula: `Slip Days × Locations × Daily Revenue × Uplift %`,
              calc: `${consequences?.projected_slip_days || 0}d slip × ${p.num_locations || 0} locs × $${(p.avg_daily_revenue_per_location || 0).toLocaleString()}/day × ${p.expected_uplift_percent || 0}%`,
              what: 'Revenue foregone because the launch is late. Calculated as the incremental daily revenue this program was expected to generate, times the number of days of delay.',
              when: 'Grows every day the projected launch date slips past the planned date.',
              fix: p.avg_daily_revenue_per_location > 0 && p.expected_uplift_percent > 0
                ? `Every day of slip costs ~$${Math.round((p.num_locations || 0) * (p.avg_daily_revenue_per_location || 0) * ((p.expected_uplift_percent || 0) / 100)).toLocaleString()} in foregone revenue.`
                : 'Set Avg Daily Revenue per Location and Expected Uplift % in Program Settings to enable this calculation.',
              inputs: [
                { label: 'Projected Slip', val: `${consequences?.projected_slip_days || 0} days` },
                { label: 'Locations', val: p.num_locations || 0 },
                { label: 'Daily Revenue / Loc', val: `$${(p.avg_daily_revenue_per_location || 0).toLocaleString()}` },
                { label: 'Uplift %', val: `${p.expected_uplift_percent || 0}%` },
              ],
            },
            total: {
              title: 'Total Consequence',
              color: fm.total_consequence > 0 ? '#ef4444' : '#10b981',
              value: fm.total_consequence || 0,
              formula: `Stranded Assets + Retrofit Cost + Revenue Impact`,
              calc: `${fmt(fm.stranded_assets || 0)} + ${fmt(fm.retrofit_cost || 0)} + ${fmt(fm.revenue_impact || 0)}`,
              what: 'Combined financial exposure attributable to the current launch status. This is the total cost Nomos projects you can avoid by getting back on track.',
              when: 'This number is preventable — it only materializes if the program stays off track.',
              fix: fm.total_consequence > 0
                ? 'Focus first on BLOCKED gates in your critical path. Each day recovered reduces both revenue impact and stranded asset risk.'
                : 'No active exposure detected. Keep all lanes ON TRACK through gate close.',
              inputs: [
                { label: 'Stranded Assets', val: fmt(fm.stranded_assets || 0) },
                { label: 'Retrofit Cost', val: fmt(fm.retrofit_cost || 0) },
                { label: 'Revenue Impact', val: fmt(fm.revenue_impact || 0) },
              ],
            },
          };
          const item = items[metric];
          if (!item) return null;
          return (
            <div style={{ padding: '28px 24px' }}>
              <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>FINANCIAL CONSEQUENCE</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: item.color, fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>{fmt(item.value)}</div>
              <div style={{ fontSize: '11px', color: t.subLabel, letterSpacing: '1.5px', marginBottom: '28px' }}>{item.title.toUpperCase()}</div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '10px' }}>FORMULA</div>
                <div style={{ backgroundColor: darkMode ? '#0d0d0d' : '#f4f4f5', borderRadius: '6px', padding: '14px 16px', fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: t.text, lineHeight: 1.6 }}>
                  <div style={{ color: t.dim, marginBottom: '6px' }}>{item.formula}</div>
                  <div style={{ color: item.color, fontWeight: '600' }}>= {item.calc}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '24px' }}>
                {item.inputs.map(inp => (
                  <div key={inp.label} style={{ backgroundColor: t.surface, border: `1px solid ${t.borderDim}`, borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '8px', color: t.subLabel, letterSpacing: '1.5px', marginBottom: '4px' }}>{inp.label}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: t.text, fontFamily: "'Roboto Mono', monospace" }}>{String(inp.val)}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '8px' }}>WHAT THIS MEANS</div>
                <div style={{ fontSize: '12px', color: t.subLabel, lineHeight: 1.7 }}>{item.what}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px', marginBottom: '8px' }}>WHEN IT GROWS</div>
                <div style={{ fontSize: '12px', color: t.subLabel, lineHeight: 1.7 }}>{item.when}</div>
              </div>
              <div style={{ backgroundColor: item.value > 0 ? '#f59e0b18' : '#10b98118', border: `1px solid ${item.value > 0 ? '#f59e0b30' : '#10b98130'}`, borderRadius: '8px', padding: '14px 16px' }}>
                <div style={{ fontSize: '9px', color: item.value > 0 ? '#f59e0b' : '#10b981', letterSpacing: '2px', marginBottom: '6px' }}>NOMOS GUIDANCE</div>
                <div style={{ fontSize: '12px', color: t.text, lineHeight: 1.7 }}>{item.fix}</div>
              </div>
            </div>
          );
        };

        const teamContent = () => {
          const lane = consDetailPanel.lane;
          const laneGates = (selectedProgram.gates || []).filter(g => g.lane === lane).sort((a, b) => a.gate_number - b.gate_number);
          const totalDelay = laneGates.reduce((s, g) => s + (g.days_delayed || 0), 0);
          const totalCost  = laneGates.reduce((s, g) => s + (g.cost_impact  || 0), 0);
          const statusColors = { blocked: '#ef4444', at_risk: '#f59e0b', on_track: '#10b981', complete: '#10b981', idle: t.borderDim, ready: '#6366f1' };
          return (
            <div style={{ padding: '28px 24px' }}>
              <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '2px', marginBottom: '6px' }}>LANE IMPACT DETAIL</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: t.text, fontFamily: "'Roboto Mono', monospace", marginBottom: '4px' }}>{lane.toUpperCase()}</div>
              <div style={{ display: 'flex', gap: '20px', marginBottom: '28px' }}>
                {totalDelay > 0 && <div style={{ fontSize: '12px', color: '#ef4444' }}>+{totalDelay}d total slip</div>}
                {totalCost  > 0 && <div style={{ fontSize: '12px', color: '#f59e0b' }}>{fmt(totalCost)} cost impact</div>}
                {totalDelay === 0 && totalCost === 0 && <div style={{ fontSize: '12px', color: '#10b981' }}>No active impact</div>}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {laneGates.map(g => {
                  const sc = statusColors[g.status] || t.borderDim;
                  const gateNotes = g.notes || '';
                  const milestoneList = teamPanelMilestones[g.id] || [];
                  const openMs = milestoneList.filter(m => !m.completed);
                  return (
                    <div key={g.id} style={{ border: `1px solid ${g.days_delayed > 0 || g.cost_impact > 0 ? sc + '50' : t.borderDim}`, borderLeft: `3px solid ${sc}`, borderRadius: '6px', padding: '14px 16px', backgroundColor: t.surface }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontSize: '9px', color: t.subLabel, letterSpacing: '1.5px', marginBottom: '3px' }}>GATE {g.gate_number}</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>{gateNames[g.gate_number - 1] || `Gate ${g.gate_number}`}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: sc, letterSpacing: '1.5px', backgroundColor: `${sc}18`, padding: '3px 8px', borderRadius: '3px', marginBottom: '4px' }}>
                            {g.status.toUpperCase().replace('_', ' ')}
                          </div>
                          <div style={{ fontSize: '9px', color: t.subLabel, fontFamily: "'Roboto Mono', monospace" }}>{g.completion_percent || 0}% complete</div>
                        </div>
                      </div>

                      {(g.days_delayed > 0 || g.cost_impact > 0) && (
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
                          {g.days_delayed > 0 && <div style={{ fontSize: '11px', color: '#ef4444', fontFamily: "'Roboto Mono', monospace" }}>+{g.days_delayed}d slip</div>}
                          {g.cost_impact  > 0 && <div style={{ fontSize: '11px', color: '#f59e0b', fontFamily: "'Roboto Mono', monospace" }}>{fmt(g.cost_impact)} impact</div>}
                        </div>
                      )}

                      {gateNotes && (
                        <div style={{ fontSize: '11px', color: t.subLabel, lineHeight: 1.6, marginBottom: openMs.length > 0 ? '10px' : 0, borderTop: `1px solid ${t.borderDim}`, paddingTop: '8px' }}>
                          {gateNotes}
                        </div>
                      )}

                      {openMs.length > 0 && (
                        <div style={{ borderTop: `1px solid ${t.borderDim}`, paddingTop: '8px' }}>
                          <div style={{ fontSize: '8px', color: t.dim, letterSpacing: '1.5px', marginBottom: '6px' }}>OPEN TASKS ({openMs.length})</div>
                          {openMs.slice(0, 5).map(m => (
                            <div key={m.id} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '4px' }}>
                              <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0, marginTop: '4px' }} />
                              <div style={{ fontSize: '11px', color: t.text, lineHeight: 1.5 }}>
                                {m.text}{m.dri ? <span style={{ color: t.subLabel }}> · {m.dri}</span> : null}
                              </div>
                            </div>
                          ))}
                          {openMs.length > 5 && <div style={{ fontSize: '10px', color: t.subLabel, marginTop: '4px' }}>+{openMs.length - 5} more tasks</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        };

        return (
          <>
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 900 }} onClick={() => setConsDetailPanel(null)} />
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isMobile ? '100vw' : '440px', backgroundColor: darkMode ? '#0d0d0d' : '#ffffff', borderLeft: `1px solid ${t.border}`, zIndex: 901, overflowY: 'auto', display: 'flex', flexDirection: 'column', fontFamily: "'Roboto Mono', monospace" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: `1px solid ${t.borderDim}`, position: 'sticky', top: 0, backgroundColor: darkMode ? '#0d0d0d' : '#ffffff', zIndex: 1 }}>
                <div style={{ fontSize: '9px', color: t.dim, letterSpacing: '2px' }}>
                  {consDetailPanel.type === 'finance' ? 'FINANCIAL MODEL' : 'TEAM IMPACT'}
                </div>
                <button onClick={() => setConsDetailPanel(null)} style={{ background: 'none', border: 'none', color: t.dim, cursor: 'pointer', fontSize: '18px', padding: '4px', lineHeight: 1 }}>✕</button>
              </div>
              {consDetailPanel.type === 'finance' ? financeContent() : teamContent()}
            </div>
          </>
        );
      })()}

    </div>
  );
}

function AppWithBoundary() {
  return <ErrorBoundary><App /></ErrorBoundary>;
}

export default AppWithBoundary;
